import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useBackButton } from "../hooks/useBackButton";
import { API, useAuth, useSipPhone, useSharedData, getAuthenticatedMediaUrl } from "@/App";
import { toast } from "sonner";
import { splitUnit } from "@/lib/addressUnit";
import PhoneLookupPopup from "@/components/PhoneLookupPopup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import ImageLightbox from "@/components/ImageLightbox";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  Plus,
  Radio,
  MapPin,
  Phone,
  Clock,
  AlertTriangle,
  Eye,
  MessageSquare,
  ArrowRight,
  ArrowUp,
  RefreshCw,
  Users,
  X,
  Search,
  Edit,
  Tag,
  Trash2,
  Upload,
  Download,
  FileText,
  Car,
  Bell,
  Link,
  Unlink,
  User as UserIcon,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  LayoutGrid,
  LayoutList,
  ChevronLeft,
  List,
  ClipboardPaste,
  Maximize2,
  Minimize2,
  Play,
  Pause,
  Headphones,
  Loader2,
  PhoneIncoming,
  PhoneOutgoing,
  Flag,
  Star,
  TimerOff,
  CheckSquare
} from "lucide-react";

// Self-contained 1-second clock display.
// Lives in its own component so the LiveCalls page doesn't re-render every
// second just for the clock — which was tearing down Radix popovers in the
// Call Info card (Recent Incoming Calls "flashes for a second" bug).
function ClockDisplay() {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <span className="text-sm text-slate-400 font-mono sm:hidden">
        {now.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </span>
      <span className="text-base sm:text-lg text-slate-400 font-mono hidden sm:block">
        {now.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </span>
    </>
  );
}

// Helper function to build perp summary for description
const buildPerpSummary = (perp, index) => {
  const parts = [];
  parts.push(`PERP #${index}:`);
  if (perp.name) parts.push(`Name: ${perp.name}`);
  if (perp.sex) parts.push(`Sex: ${perp.sex}`);
  if (perp.race) parts.push(`Race: ${perp.race}`);
  if (perp.age) parts.push(`Age: ~${perp.age}`);
  if (perp.height) parts.push(`Height: ${perp.height}`);
  if (perp.weight) parts.push(`Weight: ${perp.weight}`);
  if (perp.build) parts.push(`Build: ${perp.build}`);
  if (perp.complexion) parts.push(`Complexion: ${perp.complexion}`);
  if (perp.hair_color_cut) parts.push(`Hair: ${perp.hair_color_cut}`);
  if (perp.eye_color) parts.push(`Eyes: ${perp.eye_color}`);
  if (perp.visible_marks) parts.push(`Marks: ${perp.visible_marks}`);
  // Clothing
  const clothing = [];
  if (perp.hat_color_style) clothing.push(`Hat: ${perp.hat_color_style}`);
  if (perp.shirt) clothing.push(`Shirt: ${perp.shirt}`);
  if (perp.jacket_coat) clothing.push(`Jacket: ${perp.jacket_coat}`);
  if (perp.pants) clothing.push(`Pants: ${perp.pants}`);
  if (perp.shoes) clothing.push(`Shoes: ${perp.shoes}`);
  if (perp.clothing) clothing.push(perp.clothing);
  if (clothing.length > 0) parts.push(`Clothing: ${clothing.join(', ')}`);
  // Location/direction
  if (perp.current_location) parts.push(`📍 Location: ${perp.current_location}`);
  if (perp.direction_of_flight) parts.push(`→ Direction: ${perp.direction_of_flight}`);
  // Vehicle
  if (perp.vehicle_description) {
    let vehicle = `🚗 Vehicle: ${perp.vehicle_description}`;
    if (perp.license_plate) vehicle += ` (Plate: ${perp.license_plate})`;
    parts.push(vehicle);
  }
  // Weapon
  if (perp.has_weapon) {
    let weapon = `⚠ ARMED`;
    if (perp.weapon_description) weapon += `: ${perp.weapon_description}`;
    parts.push(weapon);
  }
  return parts.join(' | ');
};

// Mirrors backend constant CALL_98_AUTO_LOG_AFTER_SECONDS.
const CALL_98_AUTO_LOG_AFTER_SECONDS = 3600;

function computeCall98RemainingSeconds(call) {
  if (!call || call.call_mode !== "call_98" || call.call_98_auto_log_cancelled) return null;
  const accumulated = Number(call.call_98_accumulated_seconds) || 0;
  const startedStr = call.call_98_started_at;
  let active = 0;
  if (startedStr) {
    const startedMs = Date.parse(startedStr);
    if (!Number.isNaN(startedMs)) {
      active = Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
    }
  }
  return Math.max(0, CALL_98_AUTO_LOG_AFTER_SECONDS - accumulated - active);
}

function Call98Countdown({ call, onCancel }) {
  const [remaining, setRemaining] = useState(() => computeCall98RemainingSeconds(call));

  useEffect(() => {
    setRemaining(computeCall98RemainingSeconds(call));
    const id = setInterval(() => {
      setRemaining(computeCall98RemainingSeconds(call));
    }, 1000);
    return () => clearInterval(id);
  }, [call]);

  if (remaining === null) return null;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const expired = remaining === 0;

  return (
    <div className="flex items-center gap-1">
      <Badge className={`text-[10px] px-1.5 py-0.5 ${expired ? "bg-amber-600" : "bg-green-700"} text-white`}>
        Auto-log {expired ? "pending" : `in ${mm}:${ss}`}
      </Badge>
      {onCancel && (
        <Button
          variant="ghost"
          size="sm"
          className="h-5 px-2 text-[10px]"
          onClick={onCancel}
        >
          Cancel
        </Button>
      )}
    </div>
  );
}

// Every optional box on the Quick form folded shut — the state a fresh form
// starts from. Module scope so resetting is one stable call.
const QUICK_FOLDS_CLOSED = { locNote: false, secondary: false, narrative: false };

// Adding a callback number puts it on the list, full stop. Primary
// (callback_phone — what the detail view, the WhatsApp blast and the incident
// conversion read) is the dispatcher's choice, never something a number picks
// up for having arrived first. Pure and outside the component so the Add
// button, the create path — which has to fold in a number still sitting in the
// input — and the hotline-alert pickup all produce the same shape.
const applyCallbackNumber = (call, entry) => {
  const number = (entry?.number || "").trim();
  if (!number) return call;
  return {
    ...call,
    callback_numbers: [...(call.callback_numbers || []), { number, caller_type: entry.caller_type || "", has_visual: !!entry.has_visual }],
  };
};

export default function LiveCalls() {
  const { user } = useAuth();
  const { handlePhoneClick } = useSipPhone() || {};
  const sharedData = useSharedData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [liveCalls, setLiveCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [patrolCars, setPatrolCars] = useState([]);
  const [customRadioCodes, setCustomRadioCodes] = useState([]);
  const [buildingCodes, setBuildingCodes] = useState([]);
  const [knownLocations, setKnownLocations] = useState([]);
  const [callFinalOptions, setCallFinalOptions] = useState([]);
  const [priorityCallTypes, setPriorityCallTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCallDialog, setShowNewCallDialog] = useState(false);
  // Compact card layout for the New Call form, toggled from the dialog header.
  // Remembered per browser (app8 has no server-side form-view preference).
  const [compactNewForm, setCompactNewForm] = useState(() => {
    try { return localStorage.getItem("newCallFormView") === "compact"; } catch { return false; }
  });
  const [compactExtrasOpen, setCompactExtrasOpen] = useState(false);
  // Quick Add — a separate short-form dialog behind its own + button, rather
  // than a third view of the New Call form. Shares newCall state and
  // handleCreateCall with the full form, so the create path is identical.
  const [showQuickCallDialog, setShowQuickCallDialog] = useState(false);
  // The Quick form's optional boxes are folded away until they're wanted: most
  // calls don't have a secondary type or a business name, and the call type
  // often says everything the narrative would. A usually-empty box is a box in
  // the way on a form filled while a caller is talking. Content forces one open
  // regardless, so nothing can be sent from a field nobody can see. One piece
  // of state so a single stable setter folds them all back.
  const [quickFolds, setQuickFolds] = useState(QUICK_FOLDS_CLOSED);
  const toggleCompactNewForm = () => {
    setCompactNewForm((v) => {
      const next = !v;
      try { localStorage.setItem("newCallFormView", next ? "compact" : "standard"); } catch { /* private mode */ }
      return next;
    });
  };
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateDialogTab, setUpdateDialogTab] = useState('update'); // 'update' | 'radio'
  // Quick-filter tabs for the call-detail panel (like the Response page).
  // 'all' shows everything; every other tab shows ONLY that card (header is
  // the sole always-visible exception). Collapsible to reclaim vertical space.
  const [detailTab, setDetailTab] = useState('all');
  const [detailTabsCollapsed, setDetailTabsCollapsed] = useState(true);
  const [showEditCallDialog, setShowEditCallDialog] = useState(false);
  const [unitSearch, setUnitSearch] = useState("");
  const [unitSearchHighlight, setUnitSearchHighlight] = useState(0);
  const [selectedUnitsToAssign, setSelectedUnitsToAssign] = useState([]);
  
  // Confirmation dialog states
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [unitToUnassign, setUnitToUnassign] = useState(null);
  const [showPatrolCarUnassignConfirm, setShowPatrolCarUnassignConfirm] = useState(false);
  const [patrolCarToUnassign, setPatrolCarToUnassign] = useState(null);
  const [showLogConfirm, setShowLogConfirm] = useState(false);
  const [showPatrolCarConfirm, setShowPatrolCarConfirm] = useState(false);
  const [patrolCarToAssign, setPatrolCarToAssign] = useState(null);
  const [showAll84Confirm, setShowAll84Confirm] = useState(false);
  const [showAll98Confirm, setShowAll98Confirm] = useState(false);
  // When dropping priority from priority -> normal while WA Alerts are still on,
  // ask the dispatcher whether to also turn WA Alerts off (the auto-on rule from
  // call creation no longer applies for a normal call).
  const [showPriorityNormalWaConfirm, setShowPriorityNormalWaConfirm] = useState(false);
  // When upgrading normal -> priority, OR when the dispatcher changes the
  // call type, offer to enable WA Alerts going forward and roll a Resend in
  // one go (call/perp re-broadcast). priorityUpReason controls the wording.
  const [showPriorityUpDialog, setShowPriorityUpDialog] = useState(false);
  const [priorityUpEnableWa, setPriorityUpEnableWa] = useState(true);
  const [priorityUpReason, setPriorityUpReason] = useState('priority'); // 'priority' | 'type'
  const [show1085Confirm, setShow1085Confirm] = useState(false);
  const [show1013Confirm, setShow1013Confirm] = useState(false);
  const [pending1085Value, setPending1085Value] = useState(false);
  const [pending1013Value, setPending1013Value] = useState(false);
  // 85/13 confirm dialog: opt-in notify when "All 85/13" is OFF on this call.
  // Mode "all"  → flips whatsapp_8513_enabled true (force broadcast to everyone)
  // Mode "preset" → keeps 8513 off, fires whatsapp-resend using call's preset
  // Either mode also auto-enables WA Alerts (whatsapp_updates_enabled).
  const [notify8513Enabled, setNotify8513Enabled] = useState(false);
  const [notify8513Mode, setNotify8513Mode] = useState("all");
  const [showNewCall1085Confirm, setShowNewCall1085Confirm] = useState(false);
  const [showNewCall1013Confirm, setShowNewCall1013Confirm] = useState(false);
  const [showDeletePerpConfirm, setShowDeletePerpConfirm] = useState(false);
  const [pendingDeletePerpId, setPendingDeletePerpId] = useState(null);
  const [showDeleteNewCallPerpConfirm, setShowDeleteNewCallPerpConfirm] = useState(false);
  const [pendingDeleteNewCallPerpIndex, setPendingDeleteNewCallPerpIndex] = useState(null);
  const [showDeleteEditCallPerpConfirm, setShowDeleteEditCallPerpConfirm] = useState(false);
  const [pendingDeleteEditCallPerpIndex, setPendingDeleteEditCallPerpIndex] = useState(null);

  // New call submission confirmation dialogs
  const [showCreateCallConfirm, setShowCreateCallConfirm] = useState(false);
  const [showSubmitWithoutClosingConfirm, setShowSubmitWithoutClosingConfirm] = useState(false);
  const [showSendToIncidentsConfirm, setShowSendToIncidentsConfirm] = useState(false);
  const [showIncidentExtraDetails, setShowIncidentExtraDetails] = useState(false);
  const [incidentExtras, setIncidentExtras] = useState({
    date_time: "",
    dispatch_id: "", is_hotline_call: false, report_type: "reported_live",
    status: "report", confirmation_status: "", assigned_detective: "",
    is_hate_crime: false, hctf_notified: false, weapon_displayed: false,
    weapon_notes: "", ems_required: false, ems_notes: "", injuries_reported: false,
    injury_notes: "", police_followup_required: false, police_followup_notes: "",
    pd_activated: false, police_report_filed: false, police_report_notes: "",
    police_action_notes: "", needs_followup: false, followup_notes: "",
    footage_requested: false, footage_obtained: false, footage_locations: "",
    call_final: "",
    send_whatsapp_to_taru: true
  });
  const [dispatchers, setDispatchers] = useState([]);
  const [showAddUpdateConfirm, setShowAddUpdateConfirm] = useState(false);
  const [createdCallForEdit, setCreatedCallForEdit] = useState(null); // Track call created via "Submit W/O Closing"

  // Sort state
  const [sortOrder, setSortOrder] = useState("newest"); // "newest" or "oldest"

  // Desktop list minimized state (to maximize call details view)
  const [listMinimized, setListMinimized] = useState(false);
  
  // Ref to track known call IDs for notification comparison
  const knownCallIdsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);
  
  // Ref to track currently selected call ID (stable across renders)
  const selectedCallIdRef = useRef(null);
  const refDataFetchedAtRef = useRef(0);

  // Read receipt tracking
  const markCallAsReadRef = useRef(null);
  const lastMarkedCallRef = useRef(null);

  // Real-time clock
  // ClockDisplay owns its own per-second tick so the parent stops re-rendering every second.
  
  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Media upload state
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxMediaType, setLightboxMediaType] = useState(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedCallMedia, setSelectedCallMedia] = useState([]); // Lazy loaded media for selected call
  const [loadingMedia, setLoadingMedia] = useState(false);
  const mediaInputRef = useRef(null);
  const detailsPanelRef = useRef(null);
  const savedScrollPositionRef = useRef(0);
  const longPressTimerRef = useRef(null);
  const unitSearchInputRef = useRef(null);
  // When a long-press fires on a search-dropdown unit row, suppress the click
  // that would otherwise also fire on release (so it opens the status prompt
  // instead of plain-assigning).
  const dropdownLongPressFiredRef = useRef(false);
  const [longPressUnit, setLongPressUnit] = useState(null); // Track which unit is being long-pressed
  const [longPressPatrolCar, setLongPressPatrolCar] = useState(null); // Track which patrol car is being long-pressed
  // When the current user creates a call we remember its id + creation
  // time. If THAT user opens the call detail within 30 seconds, the page
  // auto-scrolls to the Assigned Units section so they can keep dispatching
  // without hunting for it. Other users opening the same call get the
  // normal scroll behavior.
  const justCreatedCallRef = useRef(null); // { call_id, createdAt }
  const justCreatedScrollDoneRef = useRef(false);
  const JUST_CREATED_SCROLL_WINDOW_MS = 30_000;

  // New call form
  const [newCall, setNewCall] = useState({
    priority: "normal",
    location: "",
    location_title: "",  // Sub-title for original location
    location_notes: "",
    cross_streets: "",
    call_type: "",
    secondary_call_type: "",
    call_note: "",
    callback_phone: "",
    callback_numbers: [], // Multiple callback numbers
    description: "",
    direction_of_flight: "",
    cv_or_third_party: "",
    caller_has_visual: false,
    transmitted_10_85: false,
    transmitted_10_13: false,
    pd_activated: false,
    hems_activated: false,
    confirmation_status: "",
    call_mode: "live_call", // Default to live call
    victims: [],
    witnesses: [],
    block_from_response: false, // Block from response page
    self_respond_enabled: false, // Per-call override; default off, dispatcher opts in
    send_notifications: true, // Send push notifications for this call
    send_whatsapp: true, // Send WhatsApp notification for this call
    send_whatsapp_to_bot_groups: true, // Also send to WhatsApp bot groups/channels
    wa_bot_group_jids: [], // Selected WhatsApp bot groups (empty = all configured groups)
    notification_group_ids: [], // Target groups (empty = all users)
    whatsapp_8513_enabled: true, // All 85/13 default ON
    whatsapp_updates_enabled: false, // Send WhatsApp for every update
    additional_locations: [] // Extra location cards
  });

  // Picking a 10-85 or 10-13 call type already says it went out over the radio,
  // so tick the matching box rather than making dispatch do it twice. Matches
  // the same shapes as the radio-code handling elsewhere ("10-85" and "1085").
  // Only ever turns a box ON — changing the call type afterwards shouldn't
  // silently undo a transmission the dispatcher ticked deliberately.
  useEffect(() => {
    const type = (newCall.call_type || "").toLowerCase();
    const is1085 = type.includes("10-85") || type.includes("1085");
    const is1013 = type.includes("10-13") || type.includes("1013");
    if (!is1085 && !is1013) return;
    setNewCall((prev) => {
      const next = { ...prev };
      let changed = false;
      if (is1085 && !prev.transmitted_10_85) {
        next.transmitted_10_85 = true;
        changed = true;
      }
      if (is1013 && !prev.transmitted_10_13) {
        next.transmitted_10_13 = true;
        changed = true;
      }
      // Same object back when nothing moved, so this can't loop on itself.
      return changed ? next : prev;
    });
  }, [newCall.call_type]);

  // Additional location dialog
  const [showAddLocDialog, setShowAddLocDialog] = useState(false);
  const [editingLocIndex, setEditingLocIndex] = useState(null); // null = adding, number = editing
  // Holds the action to run when the dispatcher confirms the "edit, not update"
  // warning on a location edit button. Setting this opens the confirm dialog;
  // running it (or Cancel) clears it.
  const [pendingLocEdit, setPendingLocEdit] = useState(null);
  const [addLocData, setAddLocData] = useState({ location: "", location_notes: "", cross_streets: "", direction_of_flight: "" });
  const [addLocPredictions, setAddLocPredictions] = useState([]);
  const [showAddLocPredictions, setShowAddLocPredictions] = useState(false);
  const [addLocCrossLoading, setAddLocCrossLoading] = useState(false);
  const addLocInputRef = useRef(null);
  const [deleteLocIndex, setDeleteLocIndex] = useState(null); // index to confirm delete (new call)
  const [deleteEditLocIndex, setDeleteEditLocIndex] = useState(null); // index to confirm delete (edit call)

  // Groups for notification targeting
  const [notificationGroups, setNotificationGroups] = useState([]);
  // WhatsApp bot groups (channels) and collections
  const [waBotGroups, setWaBotGroups] = useState([]);
  const [waBotConfiguredGroups, setWaBotConfiguredGroups] = useState([]); // saved JIDs from config
  const [waBotGroupCollections, setWaBotGroupCollections] = useState([]);

  // Cross streets loading state
  const [crossStreetsLoading, setCrossStreetsLoading] = useState(false);
  const [enableGoogleLookup, setEnableGoogleLookup] = useState(true); // Default ON
  const crossStreetsTimeoutRef = useRef(null);
  
  // Location autocomplete state
  const [locationPredictions, setLocationPredictions] = useState([]);
  const [showLocationPredictions, setShowLocationPredictions] = useState(false);
  const [locationAutocompleteLoading, setLocationAutocompleteLoading] = useState(false);
  const [enableLocationPredictions, setEnableLocationPredictions] = useState(true);
  const [locationUndoStack, setLocationUndoStack] = useState([]);
  const lastLocationSnapshot = useRef(null);

  // Snapshot location fields before any change — call this before updating location data
  const snapshotLocation = (call = newCall) => {
    const current = {
      location: call.location || "",
      location_notes: call.location_notes || "",
      cross_streets: call.cross_streets || "",
      direction_of_flight: call.direction_of_flight || "",
    };
    // Only push if different from last snapshot
    const last = lastLocationSnapshot.current;
    if (!last || last.location !== current.location || last.location_notes !== current.location_notes || last.cross_streets !== current.cross_streets || last.direction_of_flight !== current.direction_of_flight) {
      setLocationUndoStack(prev => [...prev.slice(-19), current]); // Keep max 20
      lastLocationSnapshot.current = current;
    }
  };
  const [locationPredictionIndex, setLocationPredictionIndex] = useState(-1);
  const locationAutocompleteTimeoutRef = useRef(null);
  const locationInputRef = useRef(null);
  
  // Edit call location autocomplete state
  const [editLocationPredictions, setEditLocationPredictions] = useState([]);
  const [showEditLocationPredictions, setShowEditLocationPredictions] = useState(false);
  const editLocationInputRef = useRef(null);
  
  // New callback number input
  const [newCallbackNumber, setNewCallbackNumber] = useState({ number: "", caller_type: "", has_visual: false });
  
  // Edit callback number input (#3)
  const [editCallbackInput, setEditCallbackInput] = useState({ number: "", caller_type: "", has_visual: false });
  
  // Card callback input state (requirement #1 - add callbacks from card)
  const [cardCallbackInput, setCardCallbackInput] = useState({ number: "", caller_type: "", has_visual: false });
  const [showCardCallbackForm, setShowCardCallbackForm] = useState(false);
  const [showAddVicWitDialog, setShowAddVicWitDialog] = useState(false);
  const [addVicWitData, setAddVicWitData] = useState({ victimName: "", victimContact: "", witnessName: "", witnessContact: "" });
  const [vicWitContactSearch, setVicWitContactSearch] = useState("");
  const [vicWitSearchResults, setVicWitSearchResults] = useState([]);
  const [vicWitSearchLoading, setVicWitSearchLoading] = useState(false);
  const vicWitSearchTimeout = useRef(null);

  // Recent incoming calls (Twilio/FusionPBX webhooks)
  const [showRecentCalls, setShowRecentCalls] = useState(null); // null or string ID: "primary", "newAdditional", "editAdditional", "cardAdditional"
  const [recentIncomingCalls, setRecentIncomingCalls] = useState([]);
  const [loadingRecentCalls, setLoadingRecentCalls] = useState(false);

  // Hotline contacts cache for showing flag/name on callback numbers
  const [hotlineContactsMap, setHotlineContactsMap] = useState({});

  // Edit existing callback states
  const [editingNewCallCallbackIdx, setEditingNewCallCallbackIdx] = useState(null);
  const [editingNewCallCallbackData, setEditingNewCallCallbackData] = useState({ number: "", caller_type: "", has_visual: false });
  const [editingEditCallCallbackIdx, setEditingEditCallCallbackIdx] = useState(null);
  const [editingEditCallCallbackData, setEditingEditCallCallbackData] = useState({ number: "", caller_type: "", has_visual: false });
  const [editingCardCallbackIdx, setEditingCardCallbackIdx] = useState(null);
  const [deleteCallbackConfirm, setDeleteCallbackConfirm] = useState(null); // { idx, number }
  const [editingCardCallbackData, setEditingCardCallbackData] = useState({ number: "", caller_type: "", has_visual: false });
  
  // Phone lookup popup
  const [phoneLookupNumber, setPhoneLookupNumber] = useState(null);

  // Phone number formatting helper - handles US and international formats
  // Auto-detects international by + prefix or >10 digits
  const formatPhoneNumber = (value) => {
    if (!value) return "";
    
    // Check if it starts with + (international format)
    const hasPlus = value.startsWith('+');
    
    // Keep the + if present, remove all other non-digits
    let digits = value.replace(/\D/g, '');
    
    // If it has a + prefix, it's international
    if (hasPlus) {
      // US number with +1: format as +1-XXX-XXX-XXXX only when complete (11 digits)
      // If deleting back below 11 digits, drop the +1 and use standard US format
      if (digits.startsWith('1') && digits.length <= 11) {
        const rest = digits.slice(1);
        if (digits.length === 11) {
          // Complete US number with country code
          return `+1-${rest.slice(0, 3)}-${rest.slice(3, 6)}-${rest.slice(6, 10)}`;
        }
        // Incomplete — drop +1, format as standard US
        if (rest.length === 0) return "";
        if (rest.length <= 3) return rest;
        if (rest.length <= 6) return `${rest.slice(0, 3)}-${rest.slice(3)}`;
        return `${rest.slice(0, 3)}-${rest.slice(3, 6)}-${rest.slice(6, 10)}`;
      }
      
      // Other international numbers - format with country code then rest
      // Common formats: +44 (UK), +972 (Israel), +61 (Australia), etc.
      if (digits.length <= 3) return `+${digits}`;
      
      // Detect common country code lengths: 1 digit (US/CA), 2 digits (most), 3 digits (some)
      // Keep simple formatting: +XX-XXXXXXXXXX
      let countryCodeLen = 1;
      if (!digits.startsWith('1')) {
        countryCodeLen = digits.length >= 12 ? 3 : 2; // Assume 2-digit country code unless very long
      }
      
      const countryCode = digits.slice(0, countryCodeLen);
      const localNumber = digits.slice(countryCodeLen);
      
      if (localNumber.length === 0) return `+${countryCode}`;
      return `+${countryCode}-${localNumber}`;
    }
    
    // If just 11 digits starting with 1 (without +), format as US number
    if (digits.startsWith('1') && digits.length === 11) {
      return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
    }
    
    // If more than 10 digits, assume international and add +
    if (digits.length > 10) {
      // Auto-detect international: just add + and format
      if (digits.startsWith('1')) {
        // US with country code
        return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
      }
      // Other international
      return `+${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    
    // Standard 10-digit US format: XXX-XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Building Code address matching — match on numeric + first street word, ignoring
  // suffix tokens like "st", "ave", "blvd" so "1234 Eastern Pkwy" matches "1234 Eastern".
  const STREET_SUFFIX_TOKENS = new Set([
    "st","street","ave","avenue","av","blvd","boulevard","rd","road","pkwy","parkway",
    "dr","drive","ln","lane","pl","place","ct","court","ter","terrace","way","hwy",
    "highway","sq","square","cir","circle","trl","trail","loop","aly","alley"
  ]);
  const normalizeAddressForMatch = (addr) => {
    if (!addr) return "";
    // Take only the part before the first comma (drop city/state/zip).
    const head = String(addr).split(",")[0].toLowerCase();
    // Strip punctuation, collapse whitespace.
    const tokens = head.replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return "";
    // First token must be the house number (digits, possibly with letter suffix like 123A).
    const numMatch = tokens[0].match(/^(\d+)[a-z]?$/);
    if (!numMatch) return "";
    const num = numMatch[1];
    // Find the first street-name token that isn't a directional or suffix.
    const directionals = new Set(["n","s","e","w","north","south","east","west","ne","nw","se","sw"]);
    let streetWord = "";
    for (let i = 1; i < tokens.length; i++) {
      const t = tokens[i];
      if (directionals.has(t)) continue;
      if (STREET_SUFFIX_TOKENS.has(t)) continue;
      streetWord = t;
      break;
    }
    if (!streetWord) return "";
    return `${num} ${streetWord}`;
  };
  const findBuildingCode = (addr) => {
    const key = normalizeAddressForMatch(addr);
    if (!key || !buildingCodes?.length) return null;
    const hit = buildingCodes.find(b => normalizeAddressForMatch(b.address) === key);
    return hit?.building_code || null;
  };

  // Match a free-text location to a saved Known Location with pinned coords.
  // Tries normalized address first, then falls back to a case-insensitive
  // exact-name match (so a saved name-only location like "Headquarters" still
  // resolves once the dispatcher picks it via autocomplete).
  const findKnownLocationCoords = (addr) => {
    if (!addr || !knownLocations?.length) return null;
    const key = normalizeAddressForMatch(addr);
    let hit = key ? knownLocations.find(k => normalizeAddressForMatch(k.address) === key) : null;
    if (!hit) {
      const trimmed = String(addr).trim().toLowerCase();
      hit = knownLocations.find(k => (k.name || "").trim().toLowerCase() === trimmed);
    }
    if (!hit || hit.latitude == null || hit.longitude == null) return null;
    return { lat: hit.latitude, lng: hit.longitude };
  };
  // Resolve to a Google Maps URL — saved coords if matched, otherwise the
  // existing "<address>, Brooklyn, NY" search.
  const mapHrefFor = (addr) => {
    const coords = findKnownLocationCoords(addr);
    if (coords) {
      return `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((addr || "") + ", Brooklyn, NY")}`;
  };

  // Handle callback query param from Hotline page
  useEffect(() => {
    const callbackParam = searchParams.get("callback");
    if (callbackParam) {
      setNewCall(prev => ({ ...prev, callback_phone: callbackParam }));
      setShowNewCallDialog(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("callback");
      setSearchParams(newParams, { replace: true });
    }
    // Open new call form via keyboard shortcut (Ctrl+Shift+N)
    if (searchParams.get("new") === "1") {
      setShowNewCallDialog(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("new");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch hotline contacts for showing flag/name on callback numbers
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/hotline/contacts`, { withCredentials: true });
        const map = {};
        for (const c of res.data || []) {
          const norm = (c.phone_number || "").replace(/[\s\-\(\)\+]/g, "");
          if (norm) map[norm] = { name: c.name || "", status: c.status || "" };
        }
        setHotlineContactsMap(map);
      } catch {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch dispatchers for "To Incidents" dialog
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/units/dispatchers`, { withCredentials: true });
        setDispatchers(res.data || []);
      } catch {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile: when the soft keyboard collapses (tap outside, Return /
  // down-arrow dismiss, etc.) blur whatever input is still focused so
  // the cursor stops blinking and the input is no longer "selected"
  // visually. Detected via visualViewport: the keyboard is up while
  // visualViewport.height is materially smaller than window.innerHeight.
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    let keyboardWasOpen = false;
    const handleResize = () => {
      const isKeyboardOpen = (window.innerHeight - vv.height) > 150;
      if (keyboardWasOpen && !isKeyboardOpen) {
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
          active.blur();
        }
      }
      keyboardWasOpen = isKeyboardOpen;
    };
    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, []);

  // When the user who just created a call opens that call's details
  // within JUST_CREATED_SCROLL_WINDOW_MS, scroll the Assigned Units
  // section into view so they can immediately start assigning.
  // Mobile only — on desktop the panel layout shows units alongside
  // call info, so auto-scrolling is disorienting rather than helpful.
  // Fires once per create; expires after the window.
  useEffect(() => {
    const justCreated = justCreatedCallRef.current;
    if (!selectedCall || !justCreated) return;
    if (justCreatedScrollDoneRef.current) return;
    if (selectedCall.call_id !== justCreated.call_id) return;
    if (Date.now() - justCreated.createdAt > JUST_CREATED_SCROLL_WINDOW_MS) {
      justCreatedCallRef.current = null;
      return;
    }
    const isDesktopNow = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
    if (isDesktopNow) {
      justCreatedCallRef.current = null;
      return;
    }
    justCreatedScrollDoneRef.current = true;
    // Defer one tick so the detail panel has rendered the section.
    setTimeout(() => {
      const el = document.getElementById("section-units");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      // Once consumed, clear the ref so re-opening the same call later
      // doesn't trigger another scroll.
      justCreatedCallRef.current = null;
    }, 150);
  }, [selectedCall?.call_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch notification groups and WhatsApp bot groups (cached).
  // Used to be admin-gated, which silently hid the Notify-groups and WA-Groups
  // pickers on the new-call form and the Edit Call dialog for every regular
  // dispatcher. The backend GETs (/groups, /audit/whatsapp/bot-groups,
  // /audit/whatsapp/config) now accept any authenticated user for the
  // read-only listing; mutation routes are still admin-only.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/groups`, { withCredentials: true });
        const groups = res.data || [];
        setNotificationGroups(groups);
        const dispatchDefault = groups.find(g => g.name === "Dispatch Default");
        if (dispatchDefault) {
          setNewCall(prev => prev.notification_group_ids?.length === 0 ? { ...prev, notification_group_ids: [dispatchDefault.group_id] } : prev);
        }
      } catch {}
      try {
        const data = await sharedData?.getWaBotGroups();
        setWaBotGroups(data?.groups || []);
      } catch {}
      try {
        const data = await sharedData?.getWaConfig();
        const collections = data?.bot_group_collections || [];
        setWaBotGroupCollections(collections);
        if (collections.length > 0 && collections[0].jids?.length > 0) {
          setNewCall(prev => ({ ...prev, wa_bot_group_jids: [...collections[0].jids] }));
        }
      } catch {}
    })();
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lookup hotline contact info for a phone number
  const getContactInfo = useCallback((phone) => {
    if (!phone) return null;
    const norm = phone.replace(/[\s\-\(\)\+]/g, "");
    return hotlineContactsMap[norm] || null;
  }, [hotlineContactsMap]);

  // Victim/Witness input states
  const [newVictim, setNewVictim] = useState({ name: "", contact: "", notes: "" });
  const [newWitness, setNewWitness] = useState({ name: "", contact: "", notes: "" });
  
  // New call perp dialog state (#4)
  const [showNewCallPerpDialog, setShowNewCallPerpDialog] = useState(false);
  const [editingNewCallPerpIndex, setEditingNewCallPerpIndex] = useState(null);
  
  // Edit call perp dialog state
  const [showEditCallPerpDialog, setShowEditCallPerpDialog] = useState(false);

  // Per-perp collapse state (tracks which perps are collapsed by perp_id)
  const [collapsedPerps, setCollapsedPerps] = useState({});
  // Per-perp Updates section collapse — independent of the whole-card collapse
  // so the user can hide just the noisy update list while keeping description /
  // location / arrest controls visible.
  const [collapsedPerpUpdates, setCollapsedPerpUpdates] = useState({});
  
  // Perpetrator profile linking state
  const [perpetratorProfiles, setPerpetratorProfiles] = useState([]);
  const [profileSearchQuery, setProfileSearchQuery] = useState("");
  const [showProfileBrowse, setShowProfileBrowse] = useState(false);
  const [profileBrowseFilter, setProfileBrowseFilter] = useState("");
  const [profileBrowseStatusFilter, setProfileBrowseStatusFilter] = useState("all");
  const [showProfileLinkDialog, setShowProfileLinkDialog] = useState(false);
  
  // Call type search states
  const [callTypeSearch, setCallTypeSearch] = useState("");
  const [secondaryCallTypeSearch, setSecondaryCallTypeSearch] = useState("");
  const [showCallTypeDropdown, setShowCallTypeDropdown] = useState(false);
  const [showSecondaryCallTypeDropdown, setShowSecondaryCallTypeDropdown] = useState(false);
  const [callTypeHighlightedIndex, setCallTypeHighlightedIndex] = useState(-1);
  const [secondaryCallTypeHighlightedIndex, setSecondaryCallTypeHighlightedIndex] = useState(-1);
  
  // Edit call type search states
  const [editCallTypeSearch, setEditCallTypeSearch] = useState("");
  const [editSecondaryCallTypeSearch, setEditSecondaryCallTypeSearch] = useState("");
  const [showEditCallTypeDropdown, setShowEditCallTypeDropdown] = useState(false);
  const [showEditSecondaryCallTypeDropdown, setShowEditSecondaryCallTypeDropdown] = useState(false);
  // Lightweight dialog that edits ONLY call_type + secondary_call_type, opened
  // from the Status / Radio action row.
  const [showCallTypeOnlyDialog, setShowCallTypeOnlyDialog] = useState(false);
  const [callTypeOnlyData, setCallTypeOnlyData] = useState({ call_type: "", secondary_call_type: "", priority: "normal" });
  const [ctOnlyPrimarySearch, setCtOnlyPrimarySearch] = useState("");
  const [ctOnlySecondarySearch, setCtOnlySecondarySearch] = useState("");
  const [showCtOnlyPrimaryDropdown, setShowCtOnlyPrimaryDropdown] = useState(false);
  const [showCtOnlySecondaryDropdown, setShowCtOnlySecondaryDropdown] = useState(false);
  const [savingCallTypeOnly, setSavingCallTypeOnly] = useState(false);

  // Edit call form (for updating existing call info)
  const [editCallData, setEditCallData] = useState({});

  // When an EXISTING call is set to Priority in the edit form, auto-flip WA
  // Updates on. The new-call form deliberately does NOT do this: a new urgent
  // call no longer force-enables WA Updates — the dispatcher opts in via the
  // WA Updates checkbox. Setter triggers in many places (manual checkbox,
  // call-type auto-priority, 10-85/10-13 auto-priority), so a watcher effect is
  // the simplest single source of truth. We only force it ON when priority
  // becomes "priority"; if the user manually turns WA Updates off afterward, we
  // leave it alone.
  useEffect(() => {
    if (editCallData.priority === "priority" && !editCallData.whatsapp_updates_enabled) {
      setEditCallData(prev => ({ ...prev, whatsapp_updates_enabled: true }));
    }
  }, [editCallData.priority]); // eslint-disable-line react-hooks/exhaustive-deps

  // Updates sort order
  const [updatesSortOrder, setUpdatesSortOrder] = useState("newest");
  const [locationSortOrder, setLocationSortOrder] = useState("newest");

  // Card visibility states - all default to true (shown), except some that depend on screen size
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
  const [showRadioOptions, setShowRadioOptions] = useState(isDesktop); // Hidden on mobile, shown on desktop
  const [showAssignedUnits, setShowAssignedUnits] = useState(true);
  const [showLocationInfo, setShowLocationInfo] = useState(true);
  // Hide/Show toggle for the combined Location Updates list at the bottom of
  // the Location Info card. Default: shown on desktop, hidden on mobile so
  // the panel stays compact on small screens.
  const [showLocationUpdates, setShowLocationUpdates] = useState(
    typeof window === 'undefined' || window.innerWidth >= 768
  );
  // Header card's location block (address + cross streets + additional
  // locations) is collapsed by default — the same data lives in the
  // Location Info card already, so this section is just there for quick
  // visibility and shouldn't take vertical space until requested.
  const [showHeaderLocations, setShowHeaderLocations] = useState(false);
  // Per-location/per-perp expanded update lists inside the Location Info
  // card. Keys: 'orig' / 'add-{idx}' / 'perp-{perp_id}'.
  // Defaults: orig + additionals are shown by default (most-used reading
  // path), perp updates are hidden by default (perp boxes already carry
  // a lot of info — keep the box compact unless asked).
  const [expandedLocUpdates, setExpandedLocUpdates] = useState({});
  const isLocUpdatesShown = (key, defaultShown) => (
    expandedLocUpdates[key] === undefined ? !!defaultShown : expandedLocUpdates[key]
  );
  const toggleLocUpdates = (key, defaultShown) => setExpandedLocUpdates(prev => ({ ...prev, [key]: !isLocUpdatesShown(key, defaultShown) }));
  const [showJobBody, setShowJobBody] = useState(true);
  const [showPerps, setShowPerps] = useState(true);
  const [showVictims, setShowVictims] = useState(true);
  const [showWitnesses, setShowWitnesses] = useState(true);
  const [showUpdates, setShowUpdates] = useState(true);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [allUpdatesSearch, setAllUpdatesSearch] = useState("");
  const [showMedia, setShowMedia] = useState(true);
  const [showUnitNotes, setShowUnitNotes] = useState(false);
  const [showDelivered, setShowDelivered] = useState(false);
  const [showReadReceipts, setShowReadReceipts] = useState(false);
  const [unitNoteText, setUnitNoteText] = useState("");
  const [submittingUnitNote, setSubmittingUnitNote] = useState(false);
  const [deleteUnitNoteId, setDeleteUnitNoteId] = useState(null);
  const [showCallInfo, setShowCallInfo] = useState(() => window.innerWidth >= 1024);
  // Track the lg breakpoint via media query so only one renderCallInfoBody
  // call sites mounts at a time. CSS-hiding the other left both <Popover>
  // triggers in the DOM, and Radix Popover (modal mode) was anchoring its
  // portal to the hidden trigger's zero-rect at (0,0) — popover content
  // would render in the top-left corner of the screen alongside the real one.
  const [isLgUp, setIsLgUp] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(min-width: 1024px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsLgUp(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler); };
  }, []);
  const [showBasicDescription, setShowBasicDescription] = useState(true);
  const [showActions, setShowActions] = useState(isDesktop); // Hidden on mobile, shown on desktop
  const [showStatus, setShowStatus] = useState(isDesktop); // Hidden on mobile, shown on desktop
  const [expandedUnitsCallIds, setExpandedUnitsCallIds] = useState(new Set()); // Track which calls have units expanded in list view

  // Linked radio recordings state
  const [linkedRecordings, setLinkedRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [showLinkedRecordings, setShowLinkedRecordings] = useState(false);
  const [transcribingLinkedFile, setTranscribingLinkedFile] = useState(null);
  const cancelTranscribeAllRef = useRef(false);
  const [playingRecording, setPlayingRecording] = useState(null);
  const linkedAudioRef = useRef(new Audio());

  // Radio code dropdown selection state
  const [selectedRadioCode, setSelectedRadioCode] = useState("");
  const [radioCodePopoverOpen, setRadioCodePopoverOpen] = useState(false);
  const [radioCodeQuery, setRadioCodeQuery] = useState("");
  // Stores the instanceId of the card whose Other-Radio-Codes popover is open
  // (null when closed). renderStatusRadioCard is rendered in up to 3 places at
  // once (inline detail card + Add Update dialog tab + standalone Add Update
  // dialog); a plain boolean caused all instances to open at the same time,
  // showing duplicate dropdown menus.
  const [radioCodesPopoverOpen, setRadioCodesPopoverOpen] = useState(null);

  // Unit location tracking state
  const [unitLocationTracking, setUnitLocationTracking] = useState({});  // { unit_name: { call_id, enabled_at, ... } }

  // Update form
  const [updateData, setUpdateData] = useState({
    updated_location: "",
    updated_direction: "",
    // location_label tri-state: null = nothing selected (location/direction
    // fields stay hidden when there are multiple possible targets), "" =
    // user explicitly clicked Original, "Additional #N" / custom title =
    // an additional location was clicked.
    location_label: null,
    updated_perp_description: "",
    perp_id: "",  // Reference to specific perp being updated (legacy single)
    perp_ids: [],  // Multi-select perp IDs
    general_notes: "",  // Job Narrative
    update_text: "",    // General update for timeline
    transmitted_10_85: false,
    transmitted_10_13: false,
    no_further: false,
    // Perp status toggles (only used when perp_id is set)
    set_arrested: false,
    set_armed: false,
    set_holding: false,
    set_detained: false,
    set_visual: false,
    set_positive_id: false,
    // Status detail fields
    weapon_description: "",
    holding_location: "",
    detained_location: "",
    units_84: false,
    units_98: false,
    call_reactivated: false
  });

  const [showMultiPerpConfirm, setShowMultiPerpConfirm] = useState(false);

  // Perp description dialog state
  const [showPerpDialog, setShowPerpDialog] = useState(false);
  const [editingPerp, setEditingPerp] = useState(null);
  const [newPerp, setNewPerp] = useState({
    name: "",
    address: "",
    current_location: "",
    sex: "",
    race: "",
    age: "",
    height: "",
    weight: "",
    build: "",
    complexion: "",
    hair_color_cut: "",
    eye_color: "",
    hat_color_style: "",
    shirt: "",
    pants: "",
    jacket_coat: "",
    shoes: "",
    visible_marks: "",
    clothing: "",
    direction_of_flight: "",
    has_weapon: false,
    weapon_description: "",
    vehicle_description: "",
    license_plate: "",
    was_arrested: false,
    notes: "",
    linked_profile_id: ""
  });

  // Armed dialog state
  const [showArmedDialog, setShowArmedDialog] = useState(false);
  const [armedDialogPerpId, setArmedDialogPerpId] = useState(null);
  const [weaponDescInput, setWeaponDescInput] = useState("");

  // Holding/Detained/Visual dialog state
  const [showHoldingDialog, setShowHoldingDialog] = useState(false);
  const [showDetainedDialog, setShowDetainedDialog] = useState(false);
  const [showVisualDialog, setShowVisualDialog] = useState(false);
  const [showPositiveIdDialog, setShowPositiveIdDialog] = useState(false);
  const [showDispatcherChecklist, setShowDispatcherChecklist] = useState(false);
  const [showCvObtainedDialog, setShowCvObtainedDialog] = useState(false);
  const [cvObtainedPendingCode, setCvObtainedPendingCode] = useState(null); // { codeId, label }
  const [cvObtainedUnitInput, setCvObtainedUnitInput] = useState("");
  const [holdingDetainedPerpId, setHoldingDetainedPerpId] = useState(null);
  const [holdingDetainedPerp, setHoldingDetainedPerp] = useState(null);
  const [holdingLocationInput, setHoldingLocationInput] = useState("");
  const [detainedLocationInput, setDetainedLocationInput] = useState("");

  // Quick nav menu state
  const [showQuickNav, setShowQuickNav] = useState(false);
  const [quickNavSearch, setQuickNavSearch] = useState("");

  const fetchData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Fetch live calls first (most critical) with timeout
      const callsRes = await axios.get(`${API}/live-calls`, { 
        withCredentials: true,
        timeout: 10000 // 10 second timeout
      });
      const calls = Array.isArray(callsRes.data) ? callsRes.data : [];

      // Set live calls immediately so they render ASAP
      setLiveCalls(calls);
      // Also set loading to false immediately so page renders
      setLoading(false);
      
      // Capture the current selected call ID from ref (stable across renders)
      const currentSelectedId = selectedCallIdRef.current;
      
      // Reference data that effectively never changes during a session.
      // The first poll fetches everything; subsequent polls (every 5s) skip
      // the three uncached endpoints (priority-call-types, building-codes,
      // known-locations) and only refresh once per refStaleMs. Without this
      // every 5-second tick was firing three needless round-trips against
      // static data and contending with the user's in-flight perp PUTs.
      const refStaleMs = 60_000;
      const refNow = Date.now();
      const refFresh = !refDataFetchedAtRef.current || (refNow - refDataFetchedAtRef.current) > refStaleMs;
      const [typesData, unitsData, radioCodesData, callFinalsData, patrolData, profilesData, priorityTypesData, buildingCodesData, knownLocationsData] = await Promise.all([
        sharedData?.getIncidentTypes().catch(() => []),
        sharedData?.getUnits().catch(() => []),
        sharedData?.getRadioCodes().catch(() => ({ codes: [] })),
        sharedData?.getCallFinals().catch(() => ({ options: [] })),
        sharedData?.getPatrolCars().catch(() => []),
        sharedData?.getProfiles().catch(() => []),
        refFresh ? axios.get(`${API}/app-config/priority-call-types`, { withCredentials: true }).then(r => r.data).catch(() => null) : Promise.resolve(null),
        refFresh ? axios.get(`${API}/building-codes`, { withCredentials: true }).then(r => r.data).catch(() => null) : Promise.resolve(null),
        refFresh ? axios.get(`${API}/known-locations`, { withCredentials: true }).then(r => r.data).catch(() => null) : Promise.resolve(null),
      ]);
      if (refFresh) refDataFetchedAtRef.current = refNow;

      const types = Array.isArray(typesData) ? typesData : [];
      const unitsList = Array.isArray(unitsData) ? unitsData : [];
      const radioCodes = radioCodesData?.codes || [];
      const callFinals = callFinalsData?.options || [];
      const patrol = Array.isArray(patrolData) ? patrolData : [];
      const profiles = Array.isArray(profilesData) ? profilesData : (profilesData?.profiles || []);

      setIncidentTypes(types);
      setUnits(unitsList);
      setCustomRadioCodes(radioCodes);
      setCallFinalOptions(callFinals);
      setPatrolCars(patrol);
      setPerpetratorProfiles(profiles);
      if (priorityTypesData) setPriorityCallTypes(priorityTypesData?.type_names || []);
      if (buildingCodesData !== null) {
        const bldgCodes = Array.isArray(buildingCodesData) ? buildingCodesData : (buildingCodesData?.items || []);
        setBuildingCodes(bldgCodes);
      }
      if (knownLocationsData !== null) {
        setKnownLocations(Array.isArray(knownLocationsData) ? knownLocationsData : (knownLocationsData?.items || []));
      }
      
      // Update selected call if it exists - use the ref to find the updated call
      if (currentSelectedId) {
        const updated = calls.find(c => c.call_id === currentSelectedId);
        if (updated) {
          setSelectedCall(updated);
        }
        // If not found, keep current selection (call might be logged but we don't want to jump)
      }
      
      // Check for NEW calls (not on initial load) using ref to track known call IDs
      const currentCallIds = new Set(calls.map(c => c.call_id));
      const newCalls = calls.filter(c => !knownCallIdsRef.current.has(c.call_id));
      
      // Update known call IDs
      knownCallIdsRef.current = currentCallIds;
      
      // Only notify if this is NOT the initial load and there are genuinely new calls
      if (!isInitialLoadRef.current && newCalls.length > 0) {
        // Check notification settings from localStorage
        const notifSettings = JSON.parse(localStorage.getItem('notificationSettings') || '{}');
        const notificationsEnabled = notifSettings.enabled !== false;
        
        if (notificationsEnabled) {
          // Always show toast notification (works everywhere)
          toast.info(`${newCalls.length} new live call${newCalls.length > 1 ? 's' : ''} received!`, {
            duration: 5000
          });
          
          // Try vibration for mobile devices
          if ('vibrate' in navigator) {
            try {
              navigator.vibrate([200, 100, 200]); // Vibrate pattern
            } catch (e) {
              console.log("Vibration not supported");
            }
          }
          
          // Try audio notification
          try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {}); // Silently fail if autoplay blocked
          } catch (e) {
            console.log("Audio notification failed");
          }
          // Note: Push notifications are handled by service worker, no need for browser notification here
        }
      }
      
      // Mark initial load as complete
      isInitialLoadRef.current = false;
    } catch (error) {
      console.error("Failed to fetch live calls:", error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [user?.role]);

  // Fetch unit location tracking status
  const fetchLocationTracking = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/location/unit/tracking-status`, { withCredentials: true });
      const trackingMap = {};
      (response.data.tracking || []).forEach(t => {
        trackingMap[t.unit_name] = t;
      });
      setUnitLocationTracking(trackingMap);
    } catch (error) {
      console.error("Failed to fetch location tracking:", error);
    }
  }, []);

  // Mark call as read
  const markCallAsRead = useCallback(async (callId) => {
    if (!callId) return;
    if (markCallAsReadRef.current) {
      clearTimeout(markCallAsReadRef.current);
    }
    markCallAsReadRef.current = setTimeout(async () => {
      try {
        const response = await axios.post(
          `${API}/live-calls/${callId}/read`,
          { source: "live_calls" },
          { withCredentials: true }
        );
        setSelectedCall(prev => {
          if (prev?.call_id === callId) {
            return { ...prev, read_receipts: response.data.read_receipts };
          }
          return prev;
        });
        setLiveCalls(prev => prev.map(c =>
          c.call_id === callId ? { ...c, read_receipts: response.data.read_receipts } : c
        ));
      } catch (error) {
        console.log('Failed to mark as read:', error);
      }
    }, 500);
  }, []);

  // Mark selected call as read when it changes
  useEffect(() => {
    if (selectedCall?.call_id && selectedCall.call_id !== lastMarkedCallRef.current) {
      lastMarkedCallRef.current = selectedCall.call_id;
      markCallAsRead(selectedCall.call_id);
    }
  }, [selectedCall?.call_id, markCallAsRead]);

  // Handle mobile back button for call detail view
  const prepareNavigate = useBackButton(selectedCall !== null, () => {
    setSelectedCall(null);
    setShowUpdateDialog(false);
    setShowEditCallDialog(false);
    setUnitSearch("");
  });

  // Back button closes the Quick Add form rather than leaving the page.
  useBackButton(showQuickCallDialog, () => setShowQuickCallDialog(false));

  // Toggle location tracking for a unit
  const handleToggleLocationTracking = async (unitName, callId, callAddress) => {
    try {
      const isCurrentlyTracking = unitLocationTracking[unitName]?.call_id === callId;
      
      if (isCurrentlyTracking) {
        // Disable tracking
        await axios.post(`${API}/location/unit/disable/${unitName}`, {}, { withCredentials: true });
        toast.success(`Location tracking stopped for ${unitName}`);
      } else {
        // Enable tracking
        await axios.post(`${API}/location/unit/enable/${unitName}`, {
          call_id: callId,
          call_address: callAddress
        }, { withCredentials: true });
        toast.success(`Location tracking enabled for ${unitName}`);
      }
      
      // Refresh tracking status
      fetchLocationTracking();
    } catch (error) {
      console.error("Failed to toggle location tracking:", error);
      toast.error("Failed to toggle location tracking");
    }
  };

  // Send location prompt notification to a unit
  const handleSendLocationPrompt = async (unitName, callId, callAddress) => {
    try {
      const response = await axios.post(`${API}/location/unit/prompt/${unitName}`, {
        call_id: callId,
        call_address: callAddress
      }, { withCredentials: true });
      
      // Check the response message for errors
      if (response.data?.message?.includes("No active push subscriptions")) {
        toast.warning(`${unitName} has no push notifications enabled. They need to enable notifications in their browser.`);
      } else if (response.data?.message?.includes("error")) {
        toast.warning(`Location prompt issue: ${response.data.message}`);
      } else {
        toast.success(`Location prompt sent to ${unitName}`);
      }
    } catch (error) {
      console.error("Failed to send location prompt:", error);
      if (error.response?.data?.detail?.includes("No users found")) {
        toast.error(`No users are linked to unit ${unitName}`);
      } else {
        toast.error(error.response?.data?.detail || "Failed to send location prompt");
      }
    }
  };

  // Keep ref in sync with selectedCall state
  useEffect(() => {
    selectedCallIdRef.current = selectedCall?.call_id || null;
  }, [selectedCall]);

  // Default the header Locations block open for single-location calls and
  // collapsed when there are 2+ locations. Re-evaluated only when switching
  // calls, so a manual toggle sticks while viewing the same call.
  useEffect(() => {
    if (!selectedCall) return;
    const totalLocations =
      (selectedCall.location ? 1 : 0) + (selectedCall.additional_locations?.length || 0);
    setShowHeaderLocations(totalLocations <= 1);
  }, [selectedCall?.call_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch media when a call is selected (media is excluded from list query for performance)
  useEffect(() => {
    if (selectedCall?.call_id) {
      setLoadingMedia(true);
      axios.get(`${API}/live-calls/${selectedCall.call_id}/media`, { withCredentials: true })
        .then(res => {
          setSelectedCallMedia(res.data.media_files || []);
        })
        .catch(err => {
          console.error("Failed to fetch media:", err);
          setSelectedCallMedia([]);
        })
        .finally(() => {
          setLoadingMedia(false);
        });
    } else {
      setSelectedCallMedia([]);
    }
  }, [selectedCall?.call_id]);

  useEffect(() => {
    fetchData();
    fetchLocationTracking();
    const interval = setInterval(fetchData, 5000); // Refresh every 5 seconds
    const trackingInterval = setInterval(fetchLocationTracking, 10000); // Refresh tracking every 10 seconds
    
    // Request notification permission on mount if not set
    if (typeof Notification !== 'undefined' && Notification.permission === "default") {
      Notification.requestPermission().catch(console.error);
    }
    
    return () => {
      clearInterval(interval);
      clearInterval(trackingInterval);
    };
  }, [fetchData, fetchLocationTracking]);

  // Pickup from hotline alert — pre-fill callback phone and open new call dialog
  const pickupHotlineAlert = useCallback(() => {
    const stored = sessionStorage.getItem("hotline_callback_numbers");
    if (!stored) return;
    // Which form the alert asked for — its Quick Dispatch button sets this.
    const quick = sessionStorage.getItem("hotline_callback_quick") === "1";
    sessionStorage.removeItem("hotline_callback_quick");
    try {
      const numbers = JSON.parse(stored);
      if (Array.isArray(numbers) && numbers.length > 0) {
        if (quick) {
          // Fold them through the same rule the Quick form's own Add button
          // uses, so the first number is the primary however it arrived.
          // Cleared first: what the alert handed over IS the callback list,
          // not an addition to whatever the form was last left holding.
          setNewCall(prev => numbers.reduce(applyCallbackNumber, {
            ...prev,
            callback_phone: "",
            callback_numbers: [],
            is_hotline_call: true,
          }));
          setQuickFolds(QUICK_FOLDS_CLOSED);
          setShowQuickCallDialog(true);
        } else if (numbers.length === 1) {
          // Single number: put in main callback_phone field (like hotline page does)
          setNewCall(prev => ({
            ...prev,
            callback_phone: numbers[0].number || "",
            is_hotline_call: true,
          }));
        } else {
          // Multiple numbers: use callback_numbers array
          setNewCall(prev => ({
            ...prev,
            callback_numbers: numbers,
            is_hotline_call: true,
          }));
        }
        if (!quick) setShowNewCallDialog(true);
      }
      sessionStorage.removeItem("hotline_callback_numbers");
    } catch (e) {
      console.error("Failed to parse hotline callback numbers:", e);
    }
    if (new URLSearchParams(window.location.search).get("from_hotline_alert")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("from_hotline_alert") === "1") {
      pickupHotlineAlert();
    }
    const handler = (e) => {
      if (e.detail?.target && e.detail.target !== "/live-calls") return;
      pickupHotlineAlert();
    };
    window.addEventListener("hotline-alert-pickup", handler);
    return () => window.removeEventListener("hotline-alert-pickup", handler);
  }, [pickupHotlineAlert]);

  // Sync and fetch linked radio recordings for a call
  const syncAndFetchLinkedRecordings = async (call) => {
    if (!user?.recording_admin_access && user?.role !== 'admin') return;
    setLoadingRecordings(true);
    try {
      await axios.get(`${API}/radio/recordings/sync`, { withCredentials: true });
      await fetchLinkedRecordings(call);
    } catch (error) {
      console.error("Error syncing recordings:", error);
      toast.error("Failed to sync recordings");
      setLoadingRecordings(false);
    }
  };

  // Fetch linked radio recordings for a call
  const fetchLinkedRecordings = async (call) => {
    if (!user?.recording_admin_access && user?.role !== 'admin') return;
    if (!call?.created_at) return;

    setLoadingRecordings(true);
    try {
      // Calculate time range: 2 min before created_at to 2 min after call_98
      const createdAt = new Date(call.created_at);
      const startTime = new Date(createdAt.getTime() - 2 * 60 * 1000); // 2 min before

      // Find when call_98 was set (look in updates array)
      const call98Update = call.updates?.find(u => u.call_mode === "call_98");
      const endTime = call98Update
        ? new Date(new Date(call98Update.timestamp).getTime() + 2 * 60 * 1000) // 2 min after call_98
        : new Date(); // Current time if no call_98 yet

      // Format for API in ET (backend expects Eastern Time)
      const formatETDate = (d) => {
        return d.toLocaleDateString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
          .split('/').reverse().join('-').replace(/(\d{4})-(\d{2})-(\d{2})/, '$1-$2-$3');
      };
      const formatETTime = (d) => {
        return d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false });
      };
      // Simpler: use Intl to get ET parts
      const getETParts = (d) => {
        const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(d);
        const p = {};
        parts.forEach(({ type, value }) => { p[type] = value; });
        return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
      };
      const startET = getETParts(startTime);
      const endET = getETParts(endTime);
      const dateFrom = startET.date;
      const dateTo = endET.date;
      const timeFrom = startET.time;
      const timeTo = endET.time;

      const params = new URLSearchParams({
        channel: "chsp1",
        date_from: dateFrom,
        date_to: dateTo,
        time_from: timeFrom,
        time_to: timeTo
      });

      const response = await axios.get(`${API}/radio/recordings?${params}`, { withCredentials: true });
      setLinkedRecordings(response.data.recordings || []);
    } catch (error) {
      console.error("Error fetching linked recordings:", error);
      setLinkedRecordings([]);
    } finally {
      setLoadingRecordings(false);
    }
  };

  // Play linked recording inline
  const playLinkedRecording = async (filename) => {
    if (playingRecording === filename) {
      linkedAudioRef.current.pause();
      setPlayingRecording(null);
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${API}/radio/stream/${filename}`, {
        credentials: 'include',
        headers: sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}
      });
      if (!response.ok) throw new Error('Failed to fetch');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      linkedAudioRef.current.src = url;
      linkedAudioRef.current.play();
      setPlayingRecording(filename);

      linkedAudioRef.current.onended = () => setPlayingRecording(null);
    } catch (err) {
      console.error("Error playing recording:", err);
      toast.error("Failed to play recording");
    }
  };

  // Transcribe a linked recording
  const transcribeLinkedRecording = async (filename) => {
    setTranscribingLinkedFile(filename);
    try {
      const decodedFilename = decodeURIComponent(filename);
      const response = await axios.post(`${API}/radio/recordings/transcribe`, null, { params: { filename: decodedFilename }, withCredentials: true });
      setLinkedRecordings(prev => prev.map(r => r.filename === filename ? { ...r, transcription: response.data.transcription, transcription_status: "completed" } : r));
      toast.success("Transcription complete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribingLinkedFile(null);
    }
  };

  // Fetch linked recordings when selectedCall changes
  useEffect(() => {
    if (selectedCall && (user?.recording_admin_access || user?.role === 'admin')) {
      fetchLinkedRecordings(selectedCall);
    } else {
      setLinkedRecordings([]);
    }
  }, [selectedCall?.call_id, selectedCall?.updates?.length, user?.recording_admin_access]);

  // Check if address starts with a house number (e.g. "580 Crown St" not just "Crown St")
  const hasHouseNumber = (addr) => /^\d+\s/.test(addr?.trim() || "");
  // A fully entered address: a house number and a street name (a word of at
  // least 3 letters). The backend also refuses a street Google only guessed
  // at from a partial name (typed_street_matches).
  const isCompleteAddress = (addr) =>
    hasHouseNumber(addr) && /^\d+[-\w]*\s+.*[a-z]{3,}/i.test((addr || "").trim());
  // Cross streets once the address is fully entered — on leaving the field,
  // never while typing. The same address with its cross streets already in
  // the box isn't looked up again (each lookup is ~56 Google requests);
  // an edited one is. field: which box ("new", "addLoc").
  const crossLookedUpRef = useRef({});
  // onBlur of an address box: floor / apt / unit text typed into it ("44
  // Pierce St Second floor") moves to the Unit/Apt box (location_notes) —
  // the address is only the number and street (lib/addressUnit, same rules
  // as the backend). Added after anything already there. Returns the address
  // to look up. Same as CCHD's.
  const moveUnitOut = (value, setForm) => {
    const { address, unit } = splitUnit(value);
    if (!unit) return value;
    setForm((prev) => {
      const cur = (prev.location_notes || "").trim();
      return { ...prev, location: address, location_notes: cur ? `${cur} ${unit}` : unit };
    });
    toast.info(`"${unit}" moved to Unit/Apt`);
    return address;
  };
  const crossStreetsOnBlur = (field, value, setterFn, currentCross = "") => {
    const a = (value || "").trim();
    if (!enableGoogleLookup || !isCompleteAddress(a)) return;
    if ((currentCross || "").trim() && crossLookedUpRef.current[field] === a.toLowerCase()) return;
    crossLookedUpRef.current[field] = a.toLowerCase();
    fetchCrossStreets(a, setterFn);
  };

  // Fetch cross streets from Google Maps API
  const fetchCrossStreets = async (address, setterFn) => {
    if (!address || address.length < 5) return;
    
    console.log("Fetching cross streets for:", address);
    setCrossStreetsLoading(true);
    try {
      const response = await axios.post(
        `${API}/geocoding/cross-streets`,
        { address },
        { withCredentials: true }
      );
      
      console.log("Cross streets response:", response.data);
      
      if (response.data.cross_streets) {
        setterFn(response.data.cross_streets);
      } else if (response.data.error) {
        console.log("Cross streets error:", response.data.error);
      }
    } catch (error) {
      console.error("Cross streets lookup failed:", error.response?.data || error.message);
      // Don't show error to user - just silently fail
    } finally {
      setCrossStreetsLoading(false);
    }
  };
  
  // Fetch location autocomplete predictions
  const fetchLocationPredictions = async (input, setterFn, setShowFn) => {
    if (!input || input.length < 3) {
      setterFn([]);
      setShowFn(false);
      return;
    }
    
    setLocationAutocompleteLoading(true);
    try {
      const response = await axios.post(
        `${API}/geocoding/autocomplete`,
        { input },
        { withCredentials: true }
      );
      
      if (response.data.predictions?.length > 0) {
        setterFn(response.data.predictions);
        setShowFn(true);
      } else {
        setterFn([]);
        setShowFn(false);
      }
    } catch (error) {
      console.error("Location autocomplete failed:", error);
      setterFn([]);
      setShowFn(false);
    } finally {
      setLocationAutocompleteLoading(false);
    }
  };

  // Fetch recent incoming calls from Twilio/FusionPBX
  const fetchRecentIncomingCalls = async () => {
    setLoadingRecentCalls(true);
    try {
      const response = await axios.get(`${API}/incoming-calls/recent`, { withCredentials: true });
      setRecentIncomingCalls(response.data);
    } catch (error) {
      console.error("Failed to fetch recent calls:", error);
      toast.error("Failed to fetch recent calls");
    } finally {
      setLoadingRecentCalls(false);
    }
  };

  // Format call timestamp for display (e.g. "1:49 AM")
  const formatCallTime = (isoString) => {
    if (!isoString) return "";
    try {
      const d = new Date(isoString);
      return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true });
    } catch { return ""; }
  };

  // Select a location prediction (new call)
  const selectLocationPrediction = async (prediction) => {
    snapshotLocation();
    // Dismiss mobile keyboard on selection
    locationInputRef.current?.blur();
    const isEstablishment = prediction.types?.includes("establishment") && !prediction.types?.includes("street_address") && !prediction.types?.includes("premise");

    if (isEstablishment && prediction.place_id) {
      // Establishment: resolve address via Place Details, put name in notes
      setLocationPredictions([]);
      setShowLocationPredictions(false);
      try {
        const res = await axios.get(`${API}/geocoding/place-details/${prediction.place_id}`, { withCredentials: true });
        const address = res.data.address || prediction.secondary_text || prediction.main_text;
        const name = res.data.name || prediction.main_text;
        // Display only the street segment in the field — drop ", Brooklyn,
        // NY 11211, USA" tail. mapHrefFor() re-appends ', Brooklyn, NY'
        // when building the navigation URL, so the city/state is still
        // present for Google Maps but doesn't clutter the input.
        const cleanAddress = (address.split(',')[0] || address).trim();
        setNewCall(prev => ({
          ...prev,
          location: cleanAddress,
          location_notes: prev.location_notes ? `${name}; ${prev.location_notes}` : name
        }));
        if (enableGoogleLookup && cleanAddress.length >= 5 && hasHouseNumber(cleanAddress)) {
          fetchCrossStreets(cleanAddress, (crossStreets) => {
            setNewCall(prev => ({ ...prev, cross_streets: crossStreets }));
          });
        }
      } catch {
        // Fallback: just use the text as-is
        setNewCall({ ...newCall, location: prediction.main_text });
      }
    } else {
      // Regular address
      const locationValue = prediction.main_text || prediction.description;
      setNewCall({ ...newCall, location: locationValue });
      setLocationPredictions([]);
      setShowLocationPredictions(false);
      if (enableGoogleLookup && locationValue.length >= 5 && hasHouseNumber(locationValue)) {
        fetchCrossStreets(locationValue, (crossStreets) => {
          setNewCall(prev => ({ ...prev, cross_streets: crossStreets }));
        });
      }
    }
  };

  // Select a location prediction (edit call)
  const selectEditLocationPrediction = (prediction) => {
    const locationValue = prediction.main_text || prediction.description;
    setEditCallData({ ...editCallData, location: locationValue });
    setEditLocationPredictions([]);
    setShowEditLocationPredictions(false);

    // Trigger cross streets lookup if enabled and address has a house number
    if (enableGoogleLookup && locationValue.length >= 5 && hasHouseNumber(locationValue)) {
      fetchCrossStreets(locationValue, (crossStreets) => {
        setEditCallData(prev => ({ ...prev, cross_streets: crossStreets }));
      });
    }
  };
  
  // Additional location dialog handlers
  const openAddLocDialog = (index = null) => {
    if (index !== null) {
      const loc = newCall.additional_locations[index];
      setAddLocData({ ...loc });
      setEditingLocIndex(index);
    } else {
      setAddLocData({ title: "", location: "", location_notes: "", cross_streets: "", direction_of_flight: "" });
      setEditingLocIndex(null);
    }
    setAddLocPredictions([]);
    setShowAddLocPredictions(false);
    setShowAddLocDialog(true);
  };

  const saveAddLoc = async () => {
    if (!addLocData.location.trim()) { toast.error("Enter a location"); return; }

    // Editing the original location directly on the selected (live) call.
    if (editingLocIndex === "live-original" && selectedCall) {
      try {
        const prev = {
          location: selectedCall.location || "",
          location_title: selectedCall.location_title || "",
          location_notes: selectedCall.location_notes || "",
          cross_streets: selectedCall.cross_streets || "",
          direction_of_flight: selectedCall.direction_of_flight || "",
        };
        const next = {
          location: addLocData.location || "",
          location_title: addLocData.title || "",
          location_notes: addLocData.location_notes || "",
          cross_streets: addLocData.cross_streets || "",
          direction_of_flight: addLocData.direction_of_flight || "",
        };
        await axios.put(
          `${API}/live-calls/${selectedCall.call_id}`,
          next,
          { withCredentials: true }
        );

        // The backend PUT only auto-logs / notifies for `location` itself.
        // location_notes/cross_streets/direction_of_flight/title are in
        // tracking_excluded_fields on the server to avoid duplicating the
        // Edit Info flow's explicit POSTs, so edits made from the location
        // card need to log their own non-address changes here. Posting with
        // updated_location/direction also gives the Location Updates
        // timeline a proper "Original" entry.
        const sideChanges = [];
        if (next.location_title !== prev.location_title) sideChanges.push(`Title: ${prev.location_title || 'N/A'} → ${next.location_title || 'N/A'}`);
        if (next.cross_streets !== prev.cross_streets) sideChanges.push(`Cross Streets: ${prev.cross_streets || 'N/A'} → ${next.cross_streets || 'N/A'}`);
        if (next.location_notes !== prev.location_notes) sideChanges.push(`Location Notes: ${prev.location_notes || 'N/A'} → ${next.location_notes || 'N/A'}`);
        const directionChanged = next.direction_of_flight !== prev.direction_of_flight;
        const locationChanged = next.location !== prev.location;
        if (directionChanged) sideChanges.push(`Direction: ${prev.direction_of_flight || 'N/A'} → ${next.direction_of_flight || 'N/A'}`);
        if (sideChanges.length > 0 || directionChanged) {
          await axios.post(
            `${API}/live-calls/${selectedCall.call_id}/updates`,
            {
              general_notes: locationChanged
                ? `Original location edited:\n${sideChanges.join('\n')}`
                : `Location info updated:\n${sideChanges.join('\n')}`,
              // Surface the direction in the Location Updates timeline.
              updated_direction: directionChanged ? (next.direction_of_flight || "") : undefined,
              location_label: "",
            },
            { withCredentials: true }
          );
        }

        const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
        setLiveCalls(refreshed.data);
        const updated = refreshed.data.find(c => c.call_id === selectedCall.call_id);
        if (updated) setSelectedCall(updated);
        toast.success("Location updated");
      } catch (err) {
        toast.error("Failed to update location");
      }
      setShowAddLocDialog(false);
      return;
    }

    // Editing one of the additional_locations entries directly on the live call.
    if (typeof editingLocIndex === "string" && editingLocIndex.startsWith("live-add-") && selectedCall) {
      const idx = parseInt(editingLocIndex.slice("live-add-".length), 10);
      const list = [...(selectedCall.additional_locations || [])];
      if (Number.isInteger(idx) && idx >= 0 && idx < list.length) {
        list[idx] = { ...list[idx], ...addLocData };
        try {
          await axios.put(
            `${API}/live-calls/${selectedCall.call_id}`,
            { additional_locations: list },
            { withCredentials: true }
          );
          const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
          setLiveCalls(refreshed.data);
          const updated = refreshed.data.find(c => c.call_id === selectedCall.call_id);
          if (updated) setSelectedCall(updated);
          toast.success("Location updated");
        } catch (err) {
          toast.error("Failed to update location");
        }
      }
      setShowAddLocDialog(false);
      return;
    }

    // Adding a location from the Add Update dialog: PUT the call immediately
    // with the appended additional_locations array (same shape as Edit Info).
    if (editingLocIndex === "update-add" && selectedCall) {
      const newLocs = [...(selectedCall.additional_locations || []), { ...addLocData }];
      try {
        await axios.put(
          `${API}/live-calls/${selectedCall.call_id}`,
          { additional_locations: newLocs },
          { withCredentials: true }
        );
        const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
        setLiveCalls(refreshed.data);
        const updated = refreshed.data.find(c => c.call_id === selectedCall.call_id);
        if (updated) setSelectedCall(updated);
        toast.success("Location added");
      } catch (err) {
        toast.error("Failed to add location");
      }
      setShowAddLocDialog(false);
      return;
    }

    // Saving the original location (new call form)
    if (editingLocIndex === "original") {
      setNewCall({
        ...newCall,
        location: addLocData.location,
        location_title: addLocData.title || "",
        location_notes: addLocData.location_notes || "",
        cross_streets: addLocData.cross_streets || "",
        direction_of_flight: addLocData.direction_of_flight || "",
      });
      setShowAddLocDialog(false);
      return;
    }

    // Saving the original location (edit call form)
    if (editingLocIndex === "original-edit") {
      setEditCallData({
        ...editCallData,
        location: addLocData.location,
        location_title: addLocData.title || "",
        location_notes: addLocData.location_notes || "",
        cross_streets: addLocData.cross_streets || "",
        direction_of_flight: addLocData.direction_of_flight || "",
      });
      setShowAddLocDialog(false);
      return;
    }

    const isEditMode = showAddLocDialog === "edit";
    const source = isEditMode ? (editCallData.additional_locations || []) : (newCall.additional_locations || []);
    const locs = [...source];
    if (editingLocIndex !== null) {
      locs[editingLocIndex] = { ...addLocData };
    } else {
      locs.push({ ...addLocData });
    }
    if (isEditMode) {
      setEditCallData({ ...editCallData, additional_locations: locs });
    } else {
      setNewCall({ ...newCall, additional_locations: locs });
    }
    setShowAddLocDialog(false);
  };

  const confirmDeleteAddLoc = () => {
    if (deleteLocIndex === null) return;
    const locs = (newCall.additional_locations || []).filter((_, i) => i !== deleteLocIndex);
    setNewCall({ ...newCall, additional_locations: locs });
    setDeleteLocIndex(null);
  };

  const handleAddLocLocationChange = (value) => {
    setAddLocData(prev => ({ ...prev, location: value }));
    if (enableLocationPredictions && value.length >= 3) {
      if (locationAutocompleteTimeoutRef.current) clearTimeout(locationAutocompleteTimeoutRef.current);
      locationAutocompleteTimeoutRef.current = setTimeout(() => {
        fetchLocationPredictions(value, setAddLocPredictions, setShowAddLocPredictions);
      }, 300);
    } else {
      setAddLocPredictions([]);
      setShowAddLocPredictions(false);
    }
    // Cross streets: on leaving the field (crossStreetsOnBlur), not while typing.
  };

  const selectAddLocPrediction = async (prediction) => {
    setAddLocPredictions([]);
    setShowAddLocPredictions(false);
    // Mirror the New Call form: when the user picks an establishment (e.g.
    // a restaurant by name), resolve to its street address and stash the
    // establishment name in location_notes.
    const isEstablishment = prediction.types?.includes("establishment") && !prediction.types?.includes("street_address") && !prediction.types?.includes("premise");
    if (isEstablishment && prediction.place_id) {
      try {
        const res = await axios.get(`${API}/geocoding/place-details/${prediction.place_id}`, { withCredentials: true });
        const address = res.data.address || prediction.secondary_text || prediction.main_text;
        const name = res.data.name || prediction.main_text;
        // Strip city/state/zip/country from the displayed address — the
        // navigation URL re-appends ', Brooklyn, NY' via mapHrefFor()
        // anyway, so the input field stays uncluttered.
        const cleanAddress = (address.split(',')[0] || address).trim();
        setAddLocData(prev => ({
          ...prev,
          location: cleanAddress,
          location_notes: prev.location_notes ? `${name}; ${prev.location_notes}` : name,
        }));
        if (enableGoogleLookup && cleanAddress.length >= 5 && hasHouseNumber(cleanAddress)) {
          fetchCrossStreets(cleanAddress, (cs) => setAddLocData(prev => ({ ...prev, cross_streets: cs })));
        }
        return;
      } catch {
        // Fall through to the regular address handling if Place Details fails
      }
    }
    const val = prediction.main_text || prediction.description;
    setAddLocData(prev => ({ ...prev, location: val }));
    if (enableGoogleLookup && val.length >= 5 && hasHouseNumber(val)) {
      fetchCrossStreets(val, (cs) => setAddLocData(prev => ({ ...prev, cross_streets: cs })));
    }
  };

  // Bring the location field (and its auto-suggest list) up near the top of
  // the dialog when it's focused. The mobile keyboard animates in and resizes
  // the visual viewport over ~250-350ms; the old single 300ms timeout raced
  // that and landed wrong about half the time ("works sometimes"). Use
  // rect-based math (robust to offsetParent) and re-run as the viewport
  // settles: once after a short delay and on each visualViewport resize for a
  // brief window, so whichever fires after the keyboard finishes wins.
  const scrollLocationFieldIntoView = useCallback(() => {
    const settle = () => {
      const input = locationInputRef.current;
      const scrollContainer = input?.closest('.overflow-y-auto');
      const locationBlock = input?.closest('.relative');
      if (!scrollContainer || !locationBlock) return;
      const delta = locationBlock.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top;
      scrollContainer.scrollTo({ top: Math.max(0, scrollContainer.scrollTop + delta - 8), behavior: 'smooth' });
    };
    setTimeout(settle, 350);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', settle);
      setTimeout(() => vv.removeEventListener('resize', settle), 1200);
    }
  }, []);

  // Debounced cross streets lookup for new call
  const handleLocationChange = (value) => {
    snapshotLocation();
    setNewCall({ ...newCall, location: value });
    setLocationPredictionIndex(-1);

    // Clear any pending timeouts
    if (crossStreetsTimeoutRef.current) {
      clearTimeout(crossStreetsTimeoutRef.current);
    }
    if (locationAutocompleteTimeoutRef.current) {
      clearTimeout(locationAutocompleteTimeoutRef.current);
    }
    
    // Fetch location predictions with debounce - only if enabled
    if (enableLocationPredictions && value.length >= 3) {
      locationAutocompleteTimeoutRef.current = setTimeout(() => {
        fetchLocationPredictions(value, setLocationPredictions, setShowLocationPredictions);
      }, 300);
    } else {
      setLocationPredictions([]);
      setShowLocationPredictions(false);
    }
    
    // Cross streets: looked up once the address is fully entered, on leaving
    // the field (crossStreetsOnBlur) — never while typing. Each lookup is ~56
    // Google requests, and every typing pause used to fire one.
  };

  // Debounced cross streets lookup for edit call
  const handleEditLocationChange = (value) => {
    setEditCallData({ ...editCallData, location: value });

    // Clear any pending timeouts
    if (crossStreetsTimeoutRef.current) {
      clearTimeout(crossStreetsTimeoutRef.current);
    }
    if (locationAutocompleteTimeoutRef.current) {
      clearTimeout(locationAutocompleteTimeoutRef.current);
    }

    // Fetch location predictions with debounce
    if (value.length >= 3) {
      locationAutocompleteTimeoutRef.current = setTimeout(() => {
        fetchLocationPredictions(value, setEditLocationPredictions, setShowEditLocationPredictions);
      }, 300);
    } else {
      setEditLocationPredictions([]);
      setShowEditLocationPredictions(false);
    }

    // Cross streets: on leaving the field, not while typing (see handleLocationChange).
  };

  // Helper to reset the new call form
  const resetNewCallForm = () => {
    setNewCall({
      priority: "normal",
      location: "",
      location_title: "",
      location_notes: "",
      cross_streets: "",
      additional_locations: [],
      // The reset used to drop these three, so after the first call of a
      // session All 85/13 came back unticked (its default is ON) and the
      // cross-streets input flipped from controlled to uncontrolled.
      call_mode: "live_call",
      whatsapp_8513_enabled: true,
      whatsapp_updates_enabled: false,
      call_type: "",
      secondary_call_type: "",
      call_note: "",
      callback_phone: "",
      callback_numbers: [],
      description: "",
      direction_of_flight: "",
      cv_or_third_party: "",
      caller_has_visual: false,
      transmitted_10_85: false,
      transmitted_10_13: false,
      pd_activated: false,
      hems_activated: false,
      confirmation_status: "",
      victims: [],
      witnesses: [],
      block_from_response: false,
      self_respond_enabled: false,
      send_notifications: true,
      send_whatsapp: true,
      send_whatsapp_to_bot_groups: true,
      wa_bot_group_jids: waBotGroupCollections.length > 0 && waBotGroupCollections[0].jids?.length > 0 ? [...waBotGroupCollections[0].jids] : [],
      notification_group_ids: (() => { const dd = notificationGroups.find(g => g.name === "Dispatch Default"); return dd ? [dd.group_id] : []; })()
    });
    setNewCallbackNumber({ number: "", caller_type: "", has_visual: false });
    setNewVictim({ name: "", contact: "", notes: "" });
    setNewWitness({ name: "", contact: "", notes: "" });
    setCallTypeSearch("");
    setSecondaryCallTypeSearch("");
    setCreatedCallForEdit(null);
    setIncidentExtras({
      date_time: "",
      dispatch_id: "", is_hotline_call: false, report_type: "reported_live",
      status: "report", confirmation_status: "", assigned_detective: "",
      is_hate_crime: false, hctf_notified: false, weapon_displayed: false,
      weapon_notes: "", ems_required: false, ems_notes: "", injuries_reported: false,
      injury_notes: "", police_followup_required: false, police_followup_notes: "",
      pd_activated: false, police_report_filed: false, police_report_notes: "",
      police_action_notes: "", needs_followup: false, followup_notes: "",
      footage_requested: false, footage_obtained: false, footage_locations: "",
      call_final: ""
    });
  };

  // Helper to auto-add call types if they don't exist
  const autoAddCallTypes = async () => {
    if (newCall.call_type && !incidentTypes.some(t => t.name.toLowerCase() === newCall.call_type.toLowerCase())) {
      try {
        const newTypeRes = await axios.post(`${API}/incident-types`, { name: newCall.call_type }, { withCredentials: true });
        setIncidentTypes(prev => [...prev, newTypeRes.data]);
      } catch (e) {
        console.log("Call type may already exist");
      }
    }
    if (newCall.secondary_call_type && !incidentTypes.some(t => t.name.toLowerCase() === newCall.secondary_call_type.toLowerCase())) {
      try {
        const newTypeRes = await axios.post(`${API}/incident-types`, { name: newCall.secondary_call_type }, { withCredentials: true });
        setIncidentTypes(prev => [...prev, newTypeRes.data]);
      } catch (e) {
        console.log("Secondary call type may already exist");
      }
    }
  };

  // Radio transmission flags. Sits at the very top of both New Call forms —
  // it's the first thing dispatch reaches for on a 10-85/10-13, so it shouldn't
  // be buried under the rest of the form. Kept as its own function so the
  // standard and compact forms share one copy bound to the same state.
  // withPriority adds Urgent to the row — the Quick Add form keeps its priority
  // toggle here rather than in a card of its own; the standard and compact
  // forms have theirs alongside the call type.
  const renderNewCallRadio = (opts = {}) => (
    <div className="p-3 border border-slate-700 rounded-lg space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">Radio</span>
        <label htmlFor="new_10_85" className={`flex items-center gap-1.5 cursor-pointer ${newCall.transmitted_10_85 ? "text-red-400 font-bold" : ""}`}>
          <Checkbox
            id="new_10_85"
            checked={newCall.transmitted_10_85}
            onCheckedChange={(checked) => {
              if (checked) {
                setShowNewCall1085Confirm(true);
              } else {
                setNewCall({ ...newCall, transmitted_10_85: false });
              }
            }}
            className="h-3.5 w-3.5"
          />
          <span className="text-xs">10-85</span>
        </label>
        <label htmlFor="new_10_13" className={`flex items-center gap-1.5 cursor-pointer ${newCall.transmitted_10_13 ? "text-red-400 font-bold" : ""}`}>
          <Checkbox
            id="new_10_13"
            checked={newCall.transmitted_10_13}
            onCheckedChange={(checked) => {
              if (checked) {
                setShowNewCall1013Confirm(true);
              } else {
                setNewCall({ ...newCall, transmitted_10_13: false });
              }
            }}
            className="h-3.5 w-3.5"
          />
          <span className="text-xs">10-13</span>
        </label>
        <label htmlFor="new_all_8513" className={`flex items-center gap-1.5 cursor-pointer ${newCall.whatsapp_8513_enabled ? "text-red-400 font-bold" : ""}`}>
          <Checkbox
            id="new_all_8513"
            checked={newCall.whatsapp_8513_enabled || false}
            onCheckedChange={(checked) => setNewCall({ ...newCall, whatsapp_8513_enabled: checked })}
            className="h-3.5 w-3.5"
          />
          <span className="text-xs">All 85/13</span>
        </label>
        {opts.withPriority && (
          <label htmlFor="new_priority" className={`flex items-center gap-1.5 cursor-pointer ${newCall.priority === "priority" ? "text-red-400 font-bold" : ""}`}>
            <Checkbox
              id="new_priority"
              checked={newCall.priority === "priority"}
              onCheckedChange={(checked) => setNewCall({ ...newCall, priority: checked ? "priority" : "normal" })}
              className="h-3.5 w-3.5"
            />
            <span className="text-xs">Urgent</span>
          </label>
        )}
      </div>
      {/* On is the default and the safe state, so it needs no warning. Off is
          the one worth flagging — it means some users won't get the 85/13. */}
      {!newCall.whatsapp_8513_enabled && (
        <p className="text-[10px] text-amber-400/80">Override off — 10-85/10-13 follow each user's own notification settings</p>
      )}
    </div>
  );

  // Option 1: Create Live Call (current behavior - dispatch and close form)
  // Call settings, radio 10-85/10-13, victims and witnesses. Extracted so the
  // standard form and the compact form's "More options" section render the
  // exact same controls bound to the exact same state — one source of truth.
  const renderNewCallExtras = () => (
    <>
        <div className="p-3 border border-slate-700 rounded-lg space-y-2.5">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Call Settings</p>

          {/* Toggles — compact 2x2 */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox id="new_send_notifications" checked={newCall.send_notifications !== false} onCheckedChange={(checked) => setNewCall({ ...newCall, send_notifications: checked })} className="h-3.5 w-3.5" />
              <span className="text-xs"><Bell className="w-3 h-3 inline mr-0.5 opacity-50" />Push</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox id="new_send_whatsapp" checked={newCall.send_whatsapp !== false} onCheckedChange={(checked) => setNewCall({ ...newCall, send_whatsapp: checked, send_whatsapp_to_bot_groups: checked })} className="h-3.5 w-3.5" />
              <span className="text-xs"><MessageSquare className="w-3 h-3 inline mr-0.5 opacity-50" />WhatsApp</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox id="new_wa_updates" checked={newCall.whatsapp_updates_enabled || false} onCheckedChange={(checked) => setNewCall({ ...newCall, whatsapp_updates_enabled: checked })} className="h-3.5 w-3.5" />
              <span className={`text-xs ${newCall.whatsapp_updates_enabled ? "text-green-400 font-semibold" : ""}`}>WA Updates</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox id="new_block_from_response" checked={newCall.block_from_response || false} onCheckedChange={(checked) => setNewCall({ ...newCall, block_from_response: checked })} className="h-3.5 w-3.5" />
              <span className="text-xs"><Eye className="w-3 h-3 inline mr-0.5 opacity-50" />Hide from Response</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer" title="When off, no responder can self-respond to this call regardless of their permission">
              <Checkbox id="new_self_respond_enabled" checked={newCall.self_respond_enabled !== false} onCheckedChange={(checked) => setNewCall({ ...newCall, self_respond_enabled: checked })} className="h-3.5 w-3.5" />
              <span className="text-xs"><Users className="w-3 h-3 inline mr-0.5 opacity-50" />Allow Self-Respond</span>
            </label>
          </div>

          {/* Notify groups */}
          {notificationGroups.length > 0 && (newCall.send_notifications || newCall.send_whatsapp) && (
            <div className="flex items-center gap-x-2.5 gap-y-1 flex-wrap pt-2 border-t border-slate-700/60">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider mr-0.5">Notify</span>
              {(() => {
                const ids = newCall.notification_group_ids || [];
                const isNone = ids.includes("__none__");
                const isAll = !isNone && ids.length === 0;
                return (
                  <>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={isAll} onChange={() => setNewCall({ ...newCall, notification_group_ids: [] })} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                      <span className="text-[11px] text-slate-300">All</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={isNone} onChange={() => setNewCall({ ...newCall, notification_group_ids: ["__none__"] })} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                      <span className="text-[11px] text-red-400">None</span>
                    </label>
                    {notificationGroups.map((g) => (
                      <label key={g.group_id} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={ids.includes(g.group_id)}
                          onChange={(e) => {
                            const current = ids.filter(id => id !== "__none__");
                            if (e.target.checked) {
                              setNewCall({ ...newCall, notification_group_ids: [...current, g.group_id] });
                            } else {
                              setNewCall({ ...newCall, notification_group_ids: current.filter(id => id !== g.group_id) });
                            }
                          }}
                          className="w-3 h-3 rounded border-slate-600 bg-slate-800"
                        />
                        <span className="text-[11px] text-slate-300">{g.name}</span>
                      </label>
                    ))}
                  </>
                );
              })()}
            </div>
          )}

          {/* WhatsApp bot group selector */}
          {(waBotGroupCollections.length > 0 || waBotGroups.length > 0) && newCall.send_whatsapp !== false && (
            <div className="flex items-center gap-x-2.5 gap-y-1 flex-wrap pt-2 border-t border-slate-700/60">
              <div className="w-full mb-0.5">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">WA Groups</span>
                <span className="text-[10px] text-slate-500/70 ml-1">— Specific WA Groups in addition to selected above</span>
              </div>
              {(() => {
                const jids = newCall.wa_bot_group_jids || [];
                const isNone = jids.includes("__none__");
                const isAll = !isNone && jids.length === 0;
                return (
                  <>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={isAll} onChange={() => setNewCall({ ...newCall, wa_bot_group_jids: [] })} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                      <span className="text-[11px] text-slate-300">All</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={isNone} onChange={() => setNewCall({ ...newCall, wa_bot_group_jids: ["__none__"] })} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                      <span className="text-[11px] text-red-400">None</span>
                    </label>
                    {waBotGroupCollections.map((col) => {
                      const colJids = col.jids || [];
                      const selected = jids.filter(j => j !== "__none__");
                      const allIncluded = colJids.length > 0 && colJids.every(j => selected.includes(j));
                      return (
                        <label key={col.name} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allIncluded}
                            onChange={(e) => {
                              let updated = [...selected];
                              if (e.target.checked) {
                                colJids.forEach(j => { if (!updated.includes(j)) updated.push(j); });
                              } else {
                                updated = updated.filter(j => !colJids.includes(j));
                              }
                              setNewCall({ ...newCall, wa_bot_group_jids: updated });
                            }}
                            className="w-3 h-3 rounded border-slate-600 bg-slate-800"
                          />
                          <span className="text-[11px] text-green-300 font-semibold">{col.name}</span>
                        </label>
                      );
                    })}
                    {waBotGroups.map((g) => (
                      <label key={g.id} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={jids.includes(g.id)}
                          onChange={(e) => {
                            const current = jids.filter(j => j !== "__none__");
                            if (e.target.checked) {
                              setNewCall({ ...newCall, wa_bot_group_jids: [...current, g.id] });
                            } else {
                              setNewCall({ ...newCall, wa_bot_group_jids: current.filter(id => id !== g.id) });
                            }
                          }}
                          className="w-3 h-3 rounded border-slate-600 bg-slate-800"
                        />
                        <span className="text-[11px] text-slate-300">{g.name}</span>
                      </label>
                    ))}
                  </>
                );
              })()}
            </div>
          )}

        </div>

        {/* Victims Section - Optional with Add button */}
        <div className="p-3 bg-slate-800 rounded-sm space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-slate-500 uppercase">Victims ({newCall.victims?.length || 0})</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs flex-shrink-0"
              onClick={() => setNewVictim({ name: "", contact: "", notes: "", showInput: true })}
            >
              <Plus className="w-3 h-3 mr-1" />Add
            </Button>
          </div>
          {/* List of victims */}
          {newCall.victims?.length > 0 && (
            <div className="space-y-1">
              {newCall.victims.map((v, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm bg-slate-800 px-2 py-1 rounded">
                  <span className="font-medium">{v.name || 'Victim ' + (idx + 1)}</span>
                  {v.contact && <span className="text-slate-400">| {v.contact}</span>}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-6 w-6 p-0 text-red-400"
                    onClick={() => setNewCall({
                      ...newCall,
                      victims: newCall.victims.filter((_, i) => i !== idx)
                    })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {newVictim.showInput && (
            <div className="flex gap-2 border-t border-slate-700 pt-2">
              <Input
                value={newVictim.name}
                onChange={(e) => setNewVictim({ ...newVictim, name: e.target.value })}
                placeholder="Name"
                className="input-dark flex-1"
              />
              <Input
                value={newVictim.contact}
                onChange={(e) => setNewVictim({ ...newVictim, contact: formatPhoneNumber(e.target.value) })}
                placeholder="Contact"
                className="input-dark flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (newVictim.name.trim() || newVictim.contact.trim()) {
                    setNewCall({
                      ...newCall,
                      victims: [...(newCall.victims || []), { victim_id: `vic_${Date.now()}`, name: newVictim.name, contact: newVictim.contact }]
                    });
                  }
                  setNewVictim({ name: "", contact: "", notes: "" });
                }}
              >
                Add
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setNewVictim({ name: "", contact: "", notes: "" })}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Witnesses Section - Optional with Add button */}
        <div className="p-3 bg-slate-800 rounded-sm space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-slate-500 uppercase">Witnesses ({newCall.witnesses?.length || 0})</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs flex-shrink-0"
              onClick={() => setNewWitness({ name: "", contact: "", notes: "", showInput: true })}
            >
              <Plus className="w-3 h-3 mr-1" />Add
            </Button>
          </div>
          {/* List of witnesses */}
          {newCall.witnesses?.length > 0 && (
            <div className="space-y-1">
              {newCall.witnesses.map((w, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm bg-slate-800 px-2 py-1 rounded">
                  <span className="font-medium">{w.name || 'Witness ' + (idx + 1)}</span>
                  {w.contact && <span className="text-slate-400">| {w.contact}</span>}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-6 w-6 p-0 text-red-400"
                    onClick={() => setNewCall({
                      ...newCall,
                      witnesses: newCall.witnesses.filter((_, i) => i !== idx)
                    })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {newWitness.showInput && (
            <div className="flex gap-2 border-t border-slate-700 pt-2">
              <Input
                value={newWitness.name}
                onChange={(e) => setNewWitness({ ...newWitness, name: e.target.value })}
                placeholder="Name"
                className="input-dark flex-1"
              />
              <Input
                value={newWitness.contact}
                onChange={(e) => { const v = e.target.value; setNewWitness({ ...newWitness, contact: /^[\d\s\-\(\)\+]*$/.test(v) ? formatPhoneNumber(v) : v }); }}
                placeholder="Contact"
                className="input-dark flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (newWitness.name.trim() || newWitness.contact.trim()) {
                    setNewCall({
                      ...newCall,
                      witnesses: [...(newCall.witnesses || []), { witness_id: `wit_${Date.now()}`, name: newWitness.name, contact: newWitness.contact }]
                    });
                  }
                  setNewWitness({ name: "", contact: "", notes: "" });
                }}
              >
                Add
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setNewWitness({ name: "", contact: "", notes: "" })}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
    </>
  );


  // Compact card layout for the New Call form. Same state and handlers as the
  // standard form — only the markup differs, so the two stay in sync. The
  // fields app8 has beyond the compact card set (call settings, radio, victims,
  // witnesses) live behind "More options" rather than being dropped.
  const compactCardCls = "rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 space-y-2";

  const renderCompactNewCall = () => {
    const filteredCt = incidentTypes
      .filter(t => t.name.toLowerCase().includes((callTypeSearch || "").toLowerCase()))
      .sort((a, b) => { const an = /^\d/.test(a.name); const bn = /^\d/.test(b.name); if (an !== bn) return an ? 1 : -1; return a.name.localeCompare(b.name); });
    return (
      <div className="space-y-3 mt-3 pb-4">
        {renderNewCallRadio()}
        {/* Card 1 — Location */}
        <div className={compactCardCls}>
          <div className="grid grid-cols-3 gap-2">
            <div className="relative col-span-2">
              <Input
                placeholder="Number & Street"
                value={newCall.location}
                onChange={(e) => handleLocationChange(e.target.value)}
                onFocus={() => { if (enableLocationPredictions && locationPredictions.length > 0) setShowLocationPredictions(true); }}
                onBlur={() => { setTimeout(() => setShowLocationPredictions(false), 200); crossStreetsOnBlur("new", moveUnitOut(newCall.location, setNewCall),(cs) => setNewCall(prev => ({ ...prev, cross_streets: cs })), newCall.cross_streets); }}
                className="input-dark"
                autoComplete="off"
              />
              {showLocationPredictions && locationPredictions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {locationPredictions.map((pr, idx) => (
                    <div key={pr.place_id || idx} className="px-3 py-2 cursor-pointer text-sm hover:bg-slate-700" onMouseDown={(e) => e.preventDefault()} onClick={() => selectLocationPrediction(pr)}>
                      <div className="font-medium text-slate-200">{pr.main_text}</div>
                      {pr.secondary_text && <div className="text-xs text-slate-400">{pr.secondary_text}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Input placeholder="Unit/Apt" value={newCall.location_notes} onChange={(e) => setNewCall({ ...newCall, location_notes: e.target.value })} className="input-dark" />
          </div>
          <div className="flex gap-2">
            <Input placeholder="Cross Streets" value={newCall.cross_streets} onChange={(e) => setNewCall({ ...newCall, cross_streets: e.target.value })} className="input-dark flex-1" />
            <Button type="button" variant="outline" size="icon" title="Auto cross streets" disabled={crossStreetsLoading || !newCall.location} onClick={() => { if (newCall.location && newCall.location.length >= 5) { fetchCrossStreets(newCall.location, (cs) => setNewCall(prev => ({ ...prev, cross_streets: cs }))); } else { toast.error("Enter a location first"); } }}>
              {crossStreetsLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            </Button>
          </div>
          <Input placeholder="Direction of flight" value={newCall.direction_of_flight} onChange={(e) => setNewCall({ ...newCall, direction_of_flight: e.target.value })} className="input-dark" />
          <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => { setEditingLocIndex(null); setShowAddLocDialog(true); }}>
            <Plus className="w-3 h-3 mr-1" />Additional Location{newCall.additional_locations?.length > 0 ? " (" + newCall.additional_locations.length + ")" : ""}
          </Button>
        </div>

        {/* Card 2 — Caller */}
        <div className={compactCardCls}>
          <div className="flex gap-2">
            <Input placeholder="Contact Number" value={newCall.callback_phone} onChange={(e) => setNewCall({ ...newCall, callback_phone: formatPhoneNumber(e.target.value) })} className="input-dark flex-1" type="tel" inputMode="numeric" />
            <Button type="button" variant="outline" size="icon" title="Lookup phone number" disabled={!newCall.callback_phone} onClick={() => setPhoneLookupNumber(newCall.callback_phone)}>
              <Search className="w-4 h-4" />
            </Button>
            <Popover open={showRecentCalls === "compactPrimary"} onOpenChange={(open) => { setShowRecentCalls(open ? "compactPrimary" : null); if (open) fetchRecentIncomingCalls(); }}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" size="icon" title="Recent incoming calls">
                  <PhoneIncoming className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[10001]">
                <div className="text-xs text-slate-400 mb-2">Recent Incoming Calls (30 min)</div>
                {loadingRecentCalls ? (
                  <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                ) : recentIncomingCalls.length === 0 ? (
                  <div className="text-xs text-slate-500 text-center py-2">No recent calls</div>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {recentIncomingCalls.map((c, idx) => (
                      <Button key={idx} variant="ghost" size="sm" className="w-full justify-between text-xs h-8 px-2"
                        onClick={() => { setNewCall(prev => ({ ...prev, callback_phone: formatPhoneNumber(c.caller_number) })); setShowRecentCalls(null); }}>
                        <span className="flex items-center gap-1.5 truncate">
                          <Phone className="w-3 h-3 shrink-0" />
                          {c.caller_number}
                        </span>
                        <span className="text-[10px] text-slate-500 shrink-0 ml-1">{formatCallTime(c.received_at)}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center gap-2">
            <Select value={newCall.cv_or_third_party || "none"} onValueChange={(v) => setNewCall({ ...newCall, cv_or_third_party: v === "none" ? "" : v })}>
              <SelectTrigger className="input-dark flex-1"><SelectValue placeholder="CV / 3rd party" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">CV / 3rd party</SelectItem>
                <SelectItem value="cv">CV</SelectItem>
                {/* "3rd_party" — the spelling the standard form, the call
                    detail and the edit dropdown all store and match on. This
                    one said "third_party", so a call created from the compact
                    form came back with a blank CV/3rd on edit. */}
                <SelectItem value="3rd_party">3rd Party</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
              <Checkbox checked={newCall.caller_has_visual || false} onCheckedChange={(checked) => setNewCall({ ...newCall, caller_has_visual: checked })} className="h-3.5 w-3.5" />
              <span className="text-xs">Has visual</span>
            </label>
          </div>
        </div>

        {/* Card 3 — Nature */}
        <div className={compactCardCls}>
          <div className="relative">
            <Input
              placeholder="Nature (Call Type)"
              value={callTypeSearch || newCall.call_type}
              onChange={(e) => { setCallTypeSearch(e.target.value); setNewCall({ ...newCall, call_type: e.target.value }); setShowCallTypeDropdown(true); }}
              onFocus={() => setShowCallTypeDropdown(true)}
              onBlur={() => setTimeout(() => setShowCallTypeDropdown(false), 200)}
              className="input-dark"
              autoComplete="off"
            />
            {showCallTypeDropdown && (callTypeSearch || !newCall.call_type) && (
              <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredCt.map((t) => (
                  <div key={t.type_id} className="px-3 py-2 cursor-pointer text-sm hover:bg-slate-700 text-slate-200" onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { const ap = (priorityCallTypes.includes(t.name) || priorityCallTypes.includes(newCall.secondary_call_type)) ? "priority" : "normal"; setNewCall({ ...newCall, call_type: t.name, priority: ap }); setCallTypeSearch(""); setShowCallTypeDropdown(false); }}>
                    {t.name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Input placeholder="Secondary call type" value={newCall.secondary_call_type} onChange={(e) => setNewCall({ ...newCall, secondary_call_type: e.target.value })} className="input-dark" />
          <button
            type="button"
            role="switch"
            aria-checked={newCall.priority === "priority"}
            onClick={() => setNewCall({ ...newCall, priority: newCall.priority === "priority" ? "normal" : "priority" })}
            className={"w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all border " + (
              newCall.priority === "priority"
                ? "bg-red-600 text-white border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.55)]"
                : "bg-transparent text-slate-400 border-slate-600 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/60"
            )}
          >
            Urgent
          </button>
        </div>

        {/* Card 4 — Notes */}
        <div className={compactCardCls}>
          <Textarea placeholder="Notes" value={newCall.call_note} onChange={(e) => setNewCall({ ...newCall, call_note: e.target.value })} className="input-dark" rows={2} />
        </div>

        {/* Everything app8 has beyond the compact set — collapsed, not dropped */}
        <div className={compactCardCls}>
          <button type="button" className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400" onClick={() => setCompactExtrasOpen(v => !v)}>
            <span>More options</span>
            {compactExtrasOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {compactExtrasOpen && <div className="space-y-3 pt-2">{renderNewCallExtras()}</div>}
        </div>
      </div>
    );
  };


  // Call-type autocomplete for the Quick Add form — same behaviour as the full
  // form's two type inputs (free text allowed, unknown types auto-created on
  // save, either type being a priority type marks the call priority), driven by
  // the same search/dropdown state so the two never disagree.
  const renderQuickCallTypeInput = ({ field, search, setSearch, open, setOpen, highlighted, setHighlighted, placeholder }) => {
    const filteredTypes = incidentTypes
      .filter(type => type.name.toLowerCase().includes((search || "").toLowerCase()))
      .sort((a, b) => { const an = /^\d/.test(a.name); const bn = /^\d/.test(b.name); if (an !== bn) return an ? 1 : -1; return a.name.localeCompare(b.name); });
    const otherType = field === "call_type" ? newCall.secondary_call_type : newCall.call_type;
    const choose = (name) => {
      const autoPriority = (priorityCallTypes.includes(name) || priorityCallTypes.includes(otherType)) ? "priority" : "normal";
      setNewCall({ ...newCall, [field]: name, priority: autoPriority });
      setSearch("");
      setOpen(false);
      setHighlighted(-1);
    };
    return (
      <div className="relative">
        <Input
          value={search || newCall[field] || ""}
          onChange={(e) => { setSearch(e.target.value); setNewCall({ ...newCall, [field]: e.target.value }); setOpen(true); setHighlighted(-1); }}
          onFocus={() => { setOpen(true); setHighlighted(-1); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlighted(prev => prev < filteredTypes.length - 1 ? prev + 1 : 0); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(prev => prev > 0 ? prev - 1 : filteredTypes.length - 1); }
            else if ((e.key === 'Enter' || e.key === 'Tab') && highlighted >= 0) { e.preventDefault(); if (filteredTypes[highlighted]) choose(filteredTypes[highlighted].name); }
            else if (e.key === 'Escape') { setOpen(false); setHighlighted(-1); }
          }}
          placeholder={placeholder}
          className="input-dark"
          autoComplete="off"
        />
        {open && (search || !newCall[field]) && (
          <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {filteredTypes.map((type, idx) => (
              <div
                key={type.type_id}
                className={`px-3 py-2 cursor-pointer text-sm select-none ${idx === highlighted ? 'bg-blue-600 text-white' : 'hover:bg-slate-700 text-slate-200'}`}
                /* Pick on click, not pointerdown. Choosing on pointerdown
                   unmounted the list before the browser dispatched the click,
                   which then landed on whatever had moved under the cursor —
                   the "+ Secondary Call Type" link sitting right beneath this
                   dropdown, which duly unfolded itself. preventDefault on
                   mousedown still keeps the input from losing focus. */
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(type.name)}
                onMouseEnter={() => setHighlighted(idx)}
              >{type.name}</div>
            ))}
            {search && !incidentTypes.some(t => t.name.toLowerCase() === search.toLowerCase()) && (
              <div className="px-3 py-2 text-xs text-slate-400 border-t border-slate-700">Press Enter to use "{search}"</div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Quick Add — only what dispatch fills on a routine call, in the order it
  // comes over the phone. Call settings are deliberately absent: a quick add
  // blasts on the defaults (push and WhatsApp on, All 85/13 on). Anything
  // beyond this set is what the full New Call form is for.
  // Call type, location and callback are what a call is: everything else on
  // this form qualifies one of them. They're marked with an accent gutter that
  // sits amber while the field is empty and goes quiet blue once it isn't, so
  // a glance at the form says what's still missing. Not validation — the
  // create path still lets a call go out without them.
  const quickCoreCls = (filled) => `border-l-2 pl-3 transition-colors ${filled ? "border-blue-600/50" : "border-amber-500"}`;
  const quickCoreLabelCls = (filled) => filled ? "" : "text-amber-300";

  // Promote a listed number to the call's primary, sending whatever held that
  // spot back to the list rather than dropping it.
  const promoteQuickPrimary = (idx) => {
    setNewCall(prev => {
      const list = prev.callback_numbers || [];
      const entry = list[idx];
      if (!entry) return prev;
      const rest = list.filter((_, i) => i !== idx);
      if (prev.callback_phone) {
        rest.push({ number: prev.callback_phone, caller_type: prev.cv_or_third_party || "", has_visual: !!prev.caller_has_visual });
      }
      return {
        ...prev,
        callback_phone: entry.number,
        cv_or_third_party: entry.caller_type || "",
        caller_has_visual: !!entry.has_visual,
        callback_numbers: rest,
      };
    });
  };

  // Tapping the star on the primary sends it back to the list — the call then
  // has callback numbers and no primary, which is a legitimate state.
  const demoteQuickPrimary = () => {
    setNewCall(prev => {
      if (!prev.callback_phone) return prev;
      return {
        ...prev,
        callback_phone: "",
        cv_or_third_party: "",
        caller_has_visual: false,
        callback_numbers: [...(prev.callback_numbers || []), { number: prev.callback_phone, caller_type: prev.cv_or_third_party || "", has_visual: !!prev.caller_has_visual }],
      };
    });
  };

  // A listed callback number with its qualifiers editable in place. The hotline
  // alert hands over a bare number, so CV/3rd and Vis have to be settable after
  // the fact, not only on the way in. Two lines rather than one: seven controls
  // on a phone-width row is how the add box got unusable.
  const renderQuickCallbackRow = ({ number, callerType, hasVisual, isPrimary, onType, onVisual, onTogglePrimary, onRemove, key }) => {
    const pill = (active) => `text-[10px] font-medium px-2 py-0.5 rounded border transition-colors ${
      active ? "bg-blue-600 text-white border-blue-500" : "bg-transparent text-slate-400 border-slate-600 hover:border-slate-400"
    }`;
    return (
      <div key={key} className="bg-slate-800 px-2 py-1.5 rounded space-y-1">
        <div className="flex items-center gap-2">
          <Phone className="w-3 h-3 text-slate-400 shrink-0" />
          <span className="font-mono text-sm truncate flex-1 min-w-0">{number}</span>
          <button
            type="button"
            onClick={onTogglePrimary}
            title={isPrimary ? "Primary callback — tap to unset" : "Make this the primary callback"}
            className={`shrink-0 p-1 rounded transition-colors ${isPrimary ? "text-amber-400" : "text-slate-500 hover:text-slate-300"}`}
          >
            <Star className={`w-3.5 h-3.5 ${isPrimary ? "fill-amber-400" : ""}`} />
          </button>
          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 shrink-0" onClick={onRemove}>
            <X className="w-3 h-3" />
          </Button>
        </div>
        <div className="flex items-center gap-1.5 pl-5">
          <button type="button" className={pill(callerType === "cv")} onClick={() => onType(callerType === "cv" ? "" : "cv")}>CV</button>
          <button type="button" className={pill(callerType === "3rd_party")} onClick={() => onType(callerType === "3rd_party" ? "" : "3rd_party")}>3RD</button>
          <button type="button" className={pill(!!hasVisual)} onClick={() => onVisual(!hasVisual)}>VIS</button>
          {isPrimary && <span className="text-[10px] uppercase tracking-wider text-amber-400/70 ml-auto">Primary</span>}
        </div>
      </div>
    );
  };

  const renderQuickCallForm = () => (
    <div className="space-y-4 mt-4 pb-4">
      {renderNewCallRadio({ withPriority: true })}

      {/* Call type */}
      <div className={quickCoreCls(!!(newCall.call_type || "").trim())}>
        <Label className={quickCoreLabelCls(!!(newCall.call_type || "").trim())}>Call Type</Label>
        {renderQuickCallTypeInput({
          field: "call_type",
          search: callTypeSearch,
          setSearch: setCallTypeSearch,
          open: showCallTypeDropdown,
          setOpen: setShowCallTypeDropdown,
          highlighted: callTypeHighlightedIndex,
          setHighlighted: setCallTypeHighlightedIndex,
          placeholder: "Nature (call type)...",
        })}
      </div>

      {/* Secondary call type — folded away; forced open by content */}
      {(quickFolds.secondary || !!(newCall.secondary_call_type || "").trim()) ? (
        <div>
          <Label>Secondary Call Type</Label>
          {renderQuickCallTypeInput({
            field: "secondary_call_type",
            search: secondaryCallTypeSearch,
            setSearch: setSecondaryCallTypeSearch,
            open: showSecondaryCallTypeDropdown,
            setOpen: setShowSecondaryCallTypeDropdown,
            highlighted: secondaryCallTypeHighlightedIndex,
            setHighlighted: setSecondaryCallTypeHighlightedIndex,
            placeholder: "Secondary call type...",
          })}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setQuickFolds(f => ({ ...f, secondary: true }))}
          className="block text-[11px] text-slate-400 hover:text-slate-200 px-1"
        >
          + Secondary Call Type
        </button>
      )}

      {/* Location */}
      <div className={quickCoreCls(!!(newCall.location || "").trim())}>
        <Label className={quickCoreLabelCls(!!(newCall.location || "").trim())}>Location</Label>
        <div className="relative">
          <Input
            value={newCall.location || ""}
            onChange={(e) => handleLocationChange(e.target.value)}
            onFocus={() => { if (enableLocationPredictions && locationPredictions.length > 0) setShowLocationPredictions(true); }}
            onBlur={() => { setTimeout(() => setShowLocationPredictions(false), 200); crossStreetsOnBlur("new", moveUnitOut(newCall.location, setNewCall),(cs) => setNewCall(prev => ({ ...prev, cross_streets: cs })), newCall.cross_streets); }}
            placeholder="Number & street..."
            className="input-dark"
            autoComplete="off"
            data-testid="quick-call-location"
          />
          {showLocationPredictions && locationPredictions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
              {locationPredictions.map((pr, idx) => (
                <div key={pr.place_id || idx} className="px-3 py-2 cursor-pointer text-sm hover:bg-slate-700" onMouseDown={(e) => e.preventDefault()} onClick={() => selectLocationPrediction(pr)}>
                  <div className="font-medium text-slate-200">{pr.main_text}</div>
                  {pr.secondary_text && <div className="text-xs text-slate-400">{pr.secondary_text}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Cross streets, with the same auto-fill the full form has */}
        <div className="flex gap-2 mt-2">
          <Input
            value={newCall.cross_streets || ""}
            onChange={(e) => setNewCall({ ...newCall, cross_streets: e.target.value })}
            placeholder="Cross streets..."
            className="input-dark flex-1"
            data-testid="quick-call-cross-streets"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            title="Auto cross streets"
            disabled={crossStreetsLoading || !newCall.location}
            onClick={() => {
              if (newCall.location && newCall.location.length >= 5) {
                fetchCrossStreets(newCall.location, (cs) => setNewCall(prev => ({ ...prev, cross_streets: cs })));
              } else {
                toast.error("Enter a location first");
              }
            }}
          >
            {crossStreetsLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
          </Button>
        </div>
        {/* Folded away by default, and forced open by content — including a
            business name that picking an establishment drops in here. */}
        {(quickFolds.locNote || !!(newCall.location_notes || "").trim()) ? (
          <Input
            value={newCall.location_notes || ""}
            onChange={(e) => setNewCall({ ...newCall, location_notes: e.target.value })}
            placeholder="Location notes (unit/apt, business name, landmark)"
            className="input-dark mt-2"
            autoFocus={quickFolds.locNote && !newCall.location_notes}
            data-testid="quick-call-loc-notes"
          />
        ) : (
          <button
            type="button"
            onClick={() => setQuickFolds(f => ({ ...f, locNote: true }))}
            className="mt-2 text-[11px] text-slate-400 hover:text-slate-200 px-1"
          >
            + Location Notes
          </button>
        )}
      </div>

      {/* Job narrative — folded away; forced open by content */}
      {(quickFolds.narrative || !!(newCall.call_note || "").trim()) ? (
        <div>
          <Label>Job Narrative</Label>
          <Textarea
            value={newCall.call_note || ""}
            onChange={(e) => setNewCall({ ...newCall, call_note: e.target.value })}
            placeholder="Job narrative / call note..."
            className="input-dark min-h-[80px]"
            autoFocus={quickFolds.narrative && !newCall.call_note}
            data-testid="quick-call-narrative"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setQuickFolds(f => ({ ...f, narrative: true }))}
          className="block text-[11px] text-slate-400 hover:text-slate-200 px-1"
        >
          + Job Narrative
        </button>
      )}

      {/* Callback numbers — first one becomes the call's primary number */}
      <div className={`space-y-2 ${quickCoreCls(!!newCall.callback_phone || (newCall.callback_numbers || []).length > 0)}`}>
        <Label className={quickCoreLabelCls(!!newCall.callback_phone || (newCall.callback_numbers || []).length > 0)}>Callback Numbers</Label>
        {/* Two rows, not one: five controls sharing a phone-width row left the
            number itself about sixty pixels wide. The number gets a row of its
            own; what qualifies it sits underneath. */}
        <div className="flex gap-2 items-center">
          <Input
            value={newCallbackNumber.number}
            onChange={(e) => setNewCallbackNumber({ ...newCallbackNumber, number: formatPhoneNumber(e.target.value) })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newCallbackNumber.number.trim()) {
                e.preventDefault();
                setNewCall(prev => applyCallbackNumber(prev, newCallbackNumber));
                setNewCallbackNumber({ number: "", caller_type: "", has_visual: false });
              }
            }}
            placeholder="Phone number..."
            className="input-dark flex-1 min-w-0"
            type="tel"
            inputMode="numeric"
            data-testid="quick-call-callback"
          />
          <Button type="button" variant="outline" size="icon" title="Paste from clipboard" className="shrink-0" onClick={async () => {
            try { const text = await navigator.clipboard.readText(); if (text) setNewCallbackNumber({ ...newCallbackNumber, number: formatPhoneNumber(text.trim()) }); }
            catch (err) { toast.error("Could not access clipboard"); }
          }}><ClipboardPaste className="w-4 h-4" /></Button>
          <Popover open={showRecentCalls === "quickCallback"} onOpenChange={(open) => { setShowRecentCalls(open ? "quickCallback" : null); if (open) fetchRecentIncomingCalls(); }}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="icon" className="shrink-0" title="Recent incoming calls">
                <PhoneIncoming className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[10001]" align="end">
              <div className="text-xs text-slate-400 mb-2">Recent Incoming Calls (30 min)</div>
              {loadingRecentCalls ? (
                <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
              ) : recentIncomingCalls.length === 0 ? (
                <div className="text-xs text-slate-500 text-center py-2">No recent calls</div>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {recentIncomingCalls.map((c, idx) => (
                    <Button
                      key={idx}
                      variant="ghost"
                      size="sm"
                      className="w-full justify-between text-xs h-8 px-2"
                      /* Fills the input rather than adding outright, so CV/3rd
                         and Vis can still be set before it goes in. */
                      onClick={() => { setNewCallbackNumber(prev => ({ ...prev, number: formatPhoneNumber(c.caller_number) })); setShowRecentCalls(null); }}
                    >
                      <span className="flex items-center gap-1.5 truncate">
                        <Phone className="w-3 h-3 shrink-0" />
                        {c.caller_number}
                        {c.contact_name && <span className="text-slate-400 truncate">{c.contact_name}</span>}
                      </span>
                      <span className="flex items-center gap-1 shrink-0 ml-1">
                        <span className="text-[10px] text-slate-500">{formatCallTime(c.received_at)}</span>
                        {c.contact_status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                        {c.contact_status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">K</span>}
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={newCallbackNumber.caller_type || ""} onValueChange={(v) => setNewCallbackNumber({ ...newCallbackNumber, caller_type: v })}>
            <SelectTrigger className="input-dark flex-1"><SelectValue placeholder="CV / 3rd party" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cv">CV</SelectItem>
              <SelectItem value="3rd_party">3rd Party</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 shrink-0 cursor-pointer whitespace-nowrap px-1" title="Caller has visual">
            <Checkbox checked={newCallbackNumber.has_visual || false} onCheckedChange={(checked) => setNewCallbackNumber({ ...newCallbackNumber, has_visual: checked })} className="h-3.5 w-3.5" />
            <span className="text-xs">Has visual</span>
          </label>
          <Button type="button" variant="outline" size="icon" className="shrink-0" title="Add callback number" onClick={() => {
            if (newCallbackNumber.number.trim()) {
              setNewCall(prev => applyCallbackNumber(prev, newCallbackNumber));
              setNewCallbackNumber({ number: "", caller_type: "", has_visual: false });
            }
          }}><Plus className="w-4 h-4" /></Button>
        </div>
        {(newCall.callback_phone || newCall.callback_numbers?.length > 0) && (
          <div className="space-y-1 pl-2 border-l-2 border-slate-700">
            {newCall.callback_phone && renderQuickCallbackRow({
              number: newCall.callback_phone,
              callerType: newCall.cv_or_third_party,
              hasVisual: newCall.caller_has_visual,
              isPrimary: true,
              onType: (v) => setNewCall({ ...newCall, cv_or_third_party: v }),
              onVisual: (v) => setNewCall({ ...newCall, caller_has_visual: v }),
              onTogglePrimary: demoteQuickPrimary,
              onRemove: () => setNewCall({ ...newCall, callback_phone: "", cv_or_third_party: "", caller_has_visual: false }),
              key: "primary",
            })}
            {newCall.callback_numbers?.map((cb, idx) => renderQuickCallbackRow({
              number: cb.number,
              callerType: cb.caller_type,
              hasVisual: cb.has_visual,
              isPrimary: false,
              onType: (v) => setNewCall({ ...newCall, callback_numbers: newCall.callback_numbers.map((c, i) => i === idx ? { ...c, caller_type: v } : c) }),
              onVisual: (v) => setNewCall({ ...newCall, callback_numbers: newCall.callback_numbers.map((c, i) => i === idx ? { ...c, has_visual: v } : c) }),
              onTogglePrimary: () => promoteQuickPrimary(idx),
              onRemove: () => setNewCall({ ...newCall, callback_numbers: newCall.callback_numbers.filter((_, i) => i !== idx) }),
              key: idx,
            }))}
          </div>
        )}
      </div>
    </div>
  );

  const handleCreateCall = async () => {
    try {
      await autoAddCallTypes();

      // Auto-add any pending victim/witness inputs before submitting
      let callToSubmit = { ...newCall };
      if (newVictim.showInput && (newVictim.name?.trim() || newVictim.contact?.trim())) {
        callToSubmit.victims = [...(callToSubmit.victims || []), { victim_id: `vic_${Date.now()}`, name: newVictim.name, contact: newVictim.contact }];
      }
      if (newWitness.showInput && (newWitness.name?.trim() || newWitness.contact?.trim())) {
        callToSubmit.witnesses = [...(callToSubmit.witnesses || []), { witness_id: `wit_${Date.now()}`, name: newWitness.name, contact: newWitness.contact }];
      }
      // A callback number typed but never confirmed with Enter or + would
      // otherwise be dropped on create.
      if (newCallbackNumber.number?.trim()) {
        callToSubmit = applyCallbackNumber(callToSubmit, newCallbackNumber);
      }

      const response = await axios.post(`${API}/live-calls`, callToSubmit, { withCredentials: true });
      setLiveCalls([response.data, ...liveCalls]);
      // Regular Submit closes the form immediately, so we arm the
      // creator-scroll window right now.
      armCreatorScrollWindow(response.data?.call_id);
      setShowNewCallDialog(false);
      setShowQuickCallDialog(false);
      resetNewCallForm();
      toast.success("Live call created");
    } catch (error) {
      toast.error("Failed to create live call");
    }
  };

  // Check if WhatsApp-relevant fields changed between saved call and current form
  const WA_FIELDS = ["location", "location_notes", "cross_streets", "description", "call_note",
    "call_type", "secondary_call_type", "priority", "transmitted_10_85", "transmitted_10_13",
    "confirmation_status", "direction_of_flight", "cv_or_third_party", "caller_has_visual"];

  const hasWhatsAppChanges = (saved) => {
    if (!saved) return false;
    for (const f of WA_FIELDS) {
      if ((newCall[f] || "") !== (saved[f] || "")) return true;
    }
    // Check perp_descriptions length or content change
    const oldPerps = JSON.stringify(saved.perp_descriptions || []);
    const newPerps = JSON.stringify(newCall.perp_descriptions || []);
    if (oldPerps !== newPerps) return true;
    return false;
  };

  // Auto-resend WhatsApp after update if WA is enabled and relevant fields changed
  const autoResendIfNeeded = async (callId, savedCall) => {
    if (!newCall.send_whatsapp) return;
    if (!hasWhatsAppChanges(savedCall)) return;
    try {
      await axios.post(`${API}/live-calls/${callId}/whatsapp-resend`, {
        send_whatsapp: true,
        send_push: false,
        send_call: true,
        perp_ids: [],
        custom_message: "",
        group_ids: newCall.notification_group_ids?.length > 0 ? newCall.notification_group_ids : null,
      }, { withCredentials: true });
      toast.success("WhatsApp update sent automatically");
    } catch (err) {
      console.error("Auto-resend WhatsApp failed:", err);
    }
  };

  // Option 2: Submit Without Closing - creates call but keeps form open for updates
  const handleSubmitWithoutClosing = async () => {
    try {
      await autoAddCallTypes();

      // Auto-add any pending victim/witness inputs before submitting
      let callToSubmit = { ...newCall };
      if (newVictim.showInput && (newVictim.name?.trim() || newVictim.contact?.trim())) {
        callToSubmit.victims = [...(callToSubmit.victims || []), { victim_id: `vic_${Date.now()}`, name: newVictim.name, contact: newVictim.contact }];
        setNewVictim({ name: "", contact: "", notes: "" });
      }
      if (newWitness.showInput && (newWitness.name?.trim() || newWitness.contact?.trim())) {
        callToSubmit.witnesses = [...(callToSubmit.witnesses || []), { witness_id: `wit_${Date.now()}`, name: newWitness.name, contact: newWitness.contact }];
        setNewWitness({ name: "", contact: "", notes: "" });
      }
      setNewCall(callToSubmit);

      if (createdCallForEdit) {
        const savedBefore = { ...createdCallForEdit };
        const response = await axios.put(`${API}/live-calls/${createdCallForEdit.call_id}`, callToSubmit, { withCredentials: true });
        setLiveCalls(prev => prev.map(c => c.call_id === createdCallForEdit.call_id ? response.data : c));
        setCreatedCallForEdit(response.data);
        toast.success("Live call updated");
        // Don't fire WhatsApp auto-resend while the deferred (Submit-Open)
        // blast is still pending — that's what was leaking out a WA message
        // every time the dispatcher pressed "Update & Open" with new fields.
        // Priority / 10-85 / 10-13 calls were never deferred, so their
        // whatsapp_pending stays false and the existing auto-resend behavior
        // continues for them.
        if (!response.data?.whatsapp_pending) {
          await autoResendIfNeeded(createdCallForEdit.call_id, savedBefore);
        }
      } else {
        // Create new call but keep form open. Defer the WhatsApp blast until
        // the dispatcher finalizes via "Update & Close" or closes the form.
        // Backend ignores defer_whatsapp for priority / 10-85 / 10-13 calls
        // (those always blast immediately). Push notifications are not
        // affected by this flag.
        const response = await axios.post(
          `${API}/live-calls`,
          { ...callToSubmit, defer_whatsapp: true },
          { withCredentials: true }
        );
        setLiveCalls([response.data, ...liveCalls]);
        setCreatedCallForEdit(response.data);
        // Note: do NOT arm the creator-scroll window yet — Submit Open
        // keeps the form open. The 10-second clock starts only when the
        // form actually closes (Update & Close / X / outside-tap).
        toast.success("Live call created - continue editing");
      }
    } catch (error) {
      toast.error(createdCallForEdit ? "Failed to update live call" : "Failed to create live call");
    }
  };

  // Fire any deferred WhatsApp blast (set up by Submit-Open) for this call.
  // No-op if the call has no pending WhatsApp; idempotent on the backend.
  // Arm the 10-second window where the creator opening the call
  // auto-scrolls to Assigned Units. Called at the moment the form
  // actually closes — for regular Submit that's right after the POST,
  // for Submit-Open that's Update & Close / X / outside-tap (mirrors
  // the deferred-WhatsApp flush triggers).
  const armCreatorScrollWindow = (callId) => {
    if (!callId) return;
    justCreatedCallRef.current = { call_id: callId, createdAt: Date.now() };
    justCreatedScrollDoneRef.current = false;
  };

  const flushPendingWhatsApp = async (callId) => {
    if (!callId) return;
    try {
      await axios.post(
        `${API}/live-calls/${callId}/send-pending-whatsapp`,
        {},
        { withCredentials: true }
      );
    } catch (err) {
      console.error("flushPendingWhatsApp failed:", err);
    }
  };

  // Update and Close - updates the call and closes the form
  const handleUpdateAndClose = async () => {
    if (!createdCallForEdit) return;
    try {
      await autoAddCallTypes();
      const savedBefore = { ...createdCallForEdit };
      const response = await axios.put(`${API}/live-calls/${createdCallForEdit.call_id}`, newCall, { withCredentials: true });
      setLiveCalls(prev => prev.map(c => c.call_id === createdCallForEdit.call_id ? response.data : c));
      // Capture the call_id BEFORE the form reset wipes createdCallForEdit
      // — that's the call we want to arm the 10-second creator-scroll
      // window for, since Submit-Open is now closing.
      const closingCallId = createdCallForEdit.call_id;
      setShowNewCallDialog(false);
      resetNewCallForm();
      armCreatorScrollWindow(closingCallId);
      toast.success("Live call updated");
      // If this call still has a deferred WhatsApp blast pending, the flush
      // is the FIRST notification (covers the latest state we just PUT).
      // Don't auto-resend on top of it — that would double-blast as
      // "[2nd Notification]". Priority / 10-85 / 10-13 calls weren't
      // deferred, so they keep the existing auto-resend behavior.
      const wasDeferred = !!response.data?.whatsapp_pending;
      await flushPendingWhatsApp(createdCallForEdit.call_id);
      if (!wasDeferred) {
        await autoResendIfNeeded(createdCallForEdit.call_id, savedBefore);
      }
    } catch (error) {
      toast.error("Failed to update live call");
    }
  };

  // Option 3: Send to Incidents - skip live calls, create directly as incident
  // Uses dedicated endpoint that handles perp profile linking, arrest records, and contact mapping
  const handleSendToIncidents = async () => {
    try {
      await autoAddCallTypes();

      // Send live call data directly to new endpoint that properly handles:
      // - Perp profile linking and status updates
      // - Arrest records on profiles
      // - Victim contact -> phone1 mapping
      // - Witness contact -> contact_info mapping
      // - is_arrest flag based on perp arrest status
      const response = await axios.post(`${API}/live-calls/direct-to-incident`, { ...newCall, incident_extras: incidentExtras }, { withCredentials: true });

      setShowNewCallDialog(false);
      resetNewCallForm();
      toast.success("Incident created");
      prepareNavigate();
      navigate(`/incidents/${response.data.incident_id}`);
    } catch (error) {
      toast.error("Failed to create incident");
    }
  };

  // Get effective perp IDs (support both single perp_id and multi perp_ids)
  const getEffectivePerpIds = () => {
    if (updateData.perp_ids?.length > 0) return updateData.perp_ids;
    if (updateData.perp_id) return [updateData.perp_id];
    return [];
  };

  const handleAddUpdate = async () => {
    if (!selectedCall) return;

    const perpIds = getEffectivePerpIds();

    const hasContent = !!(
      (updateData.update_text || "").trim() ||
      (updateData.general_notes || "").trim() ||
      (updateData.updated_location || "").trim() ||
      (updateData.updated_direction || "").trim() ||
      (updateData.updated_perp_description || "").trim() ||
      updateData.transmitted_10_85 ||
      updateData.transmitted_10_13 ||
      updateData.no_further ||
      updateData.units_84 ||
      updateData.units_98 ||
      updateData.call_reactivated ||
      updateData.set_arrested ||
      updateData.set_armed ||
      updateData.set_holding ||
      updateData.set_detained ||
      updateData.set_visual ||
      updateData.set_positive_id
    );
    if (!hasContent) {
      toast.info("Nothing to add — pick a radio code or type an update");
      return;
    }

    // If multiple perps selected, show confirmation first
    if (perpIds.length > 1 && !showMultiPerpConfirm) {
      setShowMultiPerpConfirm(true);
      return;
    }
    setShowMultiPerpConfirm(false);

    // Use the scroll position saved when dialog opened
    const scrollTop = savedScrollPositionRef.current;

    try {
      // For each perp (or once if no perp), submit the update
      const perpsToProcess = perpIds.length > 0 ? perpIds : [""];
      let updatedCall = null;

      for (const pid of perpsToProcess) {
        const singleUpdateData = { ...updateData, perp_id: pid, perp_ids: undefined };
        const response = await axios.post(
          `${API}/live-calls/${selectedCall.call_id}/updates`,
          singleUpdateData,
          { withCredentials: true }
        );
        updatedCall = response.data;

        // Apply perp status changes if a perp is attached and any status checkbox is checked
        if (pid && (updateData.set_arrested || updateData.set_armed || updateData.set_holding || updateData.set_detained || updateData.set_visual || updateData.set_positive_id)) {
          const perp = updatedCall.perp_descriptions?.find(p => p.perp_id === pid);
          if (perp) {
            if (updateData.set_arrested) {
              const arrestRes = await axios.post(
                `${API}/live-calls/${selectedCall.call_id}/perps/${pid}/arrest`,
                { was_arrested: true },
                { withCredentials: true }
              );
              updatedCall = arrestRes.data;
            }

            if (updateData.set_armed || updateData.set_holding || updateData.set_detained || updateData.set_visual || updateData.set_positive_id) {
              const perpUpdate = { ...perp };
              if (updateData.set_armed) {
                perpUpdate.has_weapon = true;
                perpUpdate.weapon_description = updateData.weapon_description || "";
              }
              if (updateData.set_holding) {
                perpUpdate.is_holding = true;
                perpUpdate.holding_location = updateData.holding_location || "";
                if (updateData.holding_location) perpUpdate.current_location = updateData.holding_location;
              }
              if (updateData.set_detained) {
                perpUpdate.is_detained = true;
                perpUpdate.detained_location = updateData.detained_location || "";
                if (updateData.detained_location) perpUpdate.current_location = updateData.detained_location;
              }
              if (updateData.set_visual) {
                perpUpdate.is_visual = true;
              }
              if (updateData.set_positive_id) {
                perpUpdate.is_positive_id = true;
              }
              const perpRes = await axios.put(
                `${API}/live-calls/${selectedCall.call_id}/perps/${pid}`,
                perpUpdate,
                { withCredentials: true }
              );
              updatedCall = perpRes.data;
            }
          }
        }
      }

      setSelectedCall(updatedCall);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? updatedCall : c));
      if (createdCallForEdit && createdCallForEdit.call_id === updatedCall.call_id) {
        setCreatedCallForEdit(updatedCall);
      }
      setShowUpdateDialog(false);
      setUpdateData({ updated_location: "", updated_direction: "", location_label: null, updated_perp_description: "", perp_id: "", perp_ids: [], general_notes: "", update_text: "", transmitted_10_85: false, transmitted_10_13: false, no_further: false, units_84: false, units_98: false, call_reactivated: false, set_arrested: false, set_armed: false, set_holding: false, set_detained: false, set_visual: false, set_positive_id: false, weapon_description: "", holding_location: "", detained_location: "" });
      toast.success(perpIds.length > 1 ? `Update applied to ${perpIds.length} perps` : "Update added");

      // Restore scroll position after state update and dialog close with multiple attempts
      const restoreScroll = () => {
        if (detailsPanelRef.current) {
          detailsPanelRef.current.scrollTop = scrollTop;
        }
      };
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 150);
      setTimeout(restoreScroll, 300);
    } catch (error) {
      toast.error("Failed to add update");
    }
  };

  // Shared helper: when transmitting 10-85/10-13 with All-85/13 OFF, the user
  // can opt in to a one-time notification via the confirm dialog. notify is
  // {enabled, mode} where mode is "all" (force broadcast to everyone via
  // whatsapp_8513_enabled) or "preset" (call's selected groups via resend).
  const handleUpdate8513Code = async (codeKey, value, notify = { enabled: false, mode: "all" }) => {
    if (!selectedCall) return;
    const isCode85 = codeKey === "85";
    const codeLabel = isCode85 ? "10-85" : "10-13";
    const fieldKey = isCode85 ? "transmitted_10_85" : "transmitted_10_13";
    const otherFieldKey = isCode85 ? "transmitted_10_13" : "transmitted_10_85";
    try {
      const updatePayload = { [fieldKey]: value };
      if (value) {
        updatePayload.priority = "priority";
      }
      const useNotify = value && notify.enabled;
      if (useNotify && notify.mode === "all") {
        // "All" mode: flip All-85/13 ON in the same PUT so the backend's
        // existing force_8513 path fires the full alert to ALL users.
        // Auto-enable WA Alerts here too — the backend skips the update-WA
        // when force_8513 already sent (already_sent_8513_wa).
        updatePayload.whatsapp_8513_enabled = true;
        updatePayload.whatsapp_updates_enabled = true;
      }
      // "Preset" mode: do NOT enable whatsapp_updates_enabled in this PUT,
      // otherwise the backend would also send a "Call Update" WA on top of
      // our resend. We turn WA Alerts on AFTER the resend completes.

      const response = await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        updatePayload,
        { withCredentials: true }
      );

      // For "preset" mode: send the full alert via the call's preset
      // recipients (build_whatsapp_message → notification_group_ids +
      // wa_bot_group_jids), then turn WA Alerts on as a follow-up PUT.
      if (useNotify && notify.mode === "preset") {
        const groupIds = selectedCall.notification_group_ids?.length > 0
          ? selectedCall.notification_group_ids
          : null;
        try {
          await axios.post(
            `${API}/live-calls/${selectedCall.call_id}/whatsapp-resend`,
            {
              group_ids: groupIds,
              send_whatsapp: true,
              send_push: false,
              send_call: true,
              perp_ids: [],
              custom_message: "",
              resend_wa_individuals: true,
              resend_wa_bot_groups: true,
              resend_wa_bot_group_jids: null,
            },
            { withCredentials: true }
          );
        } catch (resendErr) {
          console.error(`${codeLabel} preset notify failed:`, resendErr);
          toast.error("WhatsApp send failed");
        }
        try {
          await axios.put(
            `${API}/live-calls/${selectedCall.call_id}`,
            { whatsapp_updates_enabled: true },
            { withCredentials: true }
          );
        } catch (waErr) {
          console.error("Failed to enable WA Alerts:", waErr);
        }
      }

      // Auto-add to updates list
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        {
          general_notes: value ? `${codeLabel} TRANSMITTED` : `${codeLabel} CLEARED`,
          [fieldKey]: value,
          [otherFieldKey]: false,
        },
        { withCredentials: true }
      );

      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      } else {
        setSelectedCall(response.data);
        setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      }
      const successMsg = value
        ? `${codeLabel} Transmitted (Priority Set)${useNotify ? notify.mode === "all" ? " — Notified ALL" : " — Notified Preset" : ""}`
        : `${codeLabel} Cleared`;
      toast.success(successMsg);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleUpdate1085 = (value, notify) => handleUpdate8513Code("85", value, notify);
  const handleUpdate1013 = (value, notify) => handleUpdate8513Code("13", value, notify);

  const applyPriorityChange = async (newPriority, alsoDisableWa = false) => {
    if (!selectedCall) return;
    try {
      const updates = { priority: newPriority };
      if (alsoDisableWa) updates.whatsapp_updates_enabled = false;
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        updates,
        { withCredentials: true }
      );

      // Auto-add to updates list
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        {
          update_text: `Priority changed to ${newPriority.toUpperCase()}${alsoDisableWa ? ' (WA Alerts disabled)' : ''}`
        },
        { withCredentials: true }
      );

      // Refresh the call data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      }
      toast.success(`Priority: ${newPriority.toUpperCase()}${alsoDisableWa ? ' • WA Alerts off' : ''}`);
    } catch (error) {
      toast.error("Failed to update priority");
    }
  };

  const handlePriorityChange = async (newPriority) => {
    if (!selectedCall) return;
    // When stepping a priority call back down to normal, the auto-enabled WA
    // Alerts no longer matches the priority — confirm with the dispatcher
    // whether to keep them on or turn them off.
    if (
      newPriority === 'normal' &&
      selectedCall.priority === 'priority' &&
      selectedCall.whatsapp_updates_enabled
    ) {
      setShowPriorityNormalWaConfirm(true);
      return;
    }
    // Upgrading from normal -> priority: apply the change, then open the
    // upgrade dialog offering to enable WA Alerts going forward and to
    // re-broadcast call / perp data via a Resend.
    if (
      newPriority === 'priority' &&
      selectedCall.priority !== 'priority'
    ) {
      await applyPriorityChange(newPriority);
      openBroadcastDialog('priority');
      return;
    }
    applyPriorityChange(newPriority);
  };

  // Shared opener: seeds Resend defaults + the WA-going-forward toggle, then
  // opens the broadcast dialog. `reason` controls the wording; `opts.priority`
  // lets the caller override what counts as "priority" for default checks
  // (used by the Change Type flow, because selectedCall.priority hasn't yet
  // been applied via setState when this fires).
  //
  // WA defaults: only check the WA boxes by default when the call is
  // priority. Non-priority dispatchers explicitly opt-in to WhatsApp.
  const openBroadcastDialog = (reason, opts = {}) => {
    const isPriority = opts.priority !== undefined
      ? opts.priority === 'priority'
      : (selectedCall?.priority === 'priority');
    setResendWhatsApp(isPriority);
    setResendPush(false);
    setResendCall(true);
    setResendPerpIds([]);
    setResendCustomMessage('');
    setResendWaIndividuals(true);
    setResendWaBotGroups(true);
    setResendWaBotGroupJids([]);
    setPriorityUpEnableWa(isPriority && !(selectedCall && selectedCall.whatsapp_updates_enabled));
    setPriorityUpReason(reason || 'priority');
    setShowPriorityUpDialog(true);
  };

  const handleQuickConfirmationChange = async (newStatus) => {
    if (!selectedCall) return;
    try {
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        { confirmation_status: newStatus },
        { withCredentials: true }
      );
      
      // Auto-add to updates list
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        {
          general_notes: `Status changed to ${(newStatus || "No Confirm").toUpperCase()}`
        },
        { withCredentials: true }
      );
      
      // Refresh the call data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      }
      toast.success(`Status: ${newStatus.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleNoFurtherToggle = async () => {
    if (!selectedCall) return;
    const newValue = !selectedCall.no_further;
    try {
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        { no_further: newValue },
        { withCredentials: true }
      );
      
      // Add to timeline updates
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        {
          update_text: newValue ? "NO FURTHER" : "No Further status removed"
        },
        { withCredentials: true }
      );
      
      // Refresh the call data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      }
      toast.success(newValue ? "Marked as No Further" : "No Further removed");
    } catch (error) {
      console.error("No Further toggle error:", error);
      toast.error("Failed to update No Further status");
    }
  };

  // Handle unit status selection (none, units_required, slowdown, no_further, forthwith)
  const handleUnitStatusChange = async (value) => {
    if (!selectedCall) return;

    let nextState = { no_further: false, units_required: false, slowdown: false, forthwith: false };
    let updateText = "";
    let toastMsg = "";

    if (value === "units_required") {
      nextState = { ...nextState, units_required: true };
      updateText = "UNITS REQ'D";
      toastMsg = "Marked as Units Req'd";
    } else if (value === "slowdown") {
      nextState = { ...nextState, slowdown: true };
      updateText = "SLOWDOWN";
      toastMsg = "Marked as Slowdown";
    } else if (value === "no_further") {
      nextState = { ...nextState, no_further: true };
      updateText = "NO FURTHER";
      toastMsg = "Marked as No Further";
    } else if (value === "forthwith") {
      nextState = { ...nextState, forthwith: true };
      updateText = "FORTHWITH";
      toastMsg = "Marked as Forthwith";
    } else {
      updateText = "Status cleared";
      toastMsg = "Status cleared";
    }

    try {
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        nextState,
        { withCredentials: true }
      );

      // Add to timeline updates
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        { update_text: updateText },
        { withCredentials: true }
      );

      // Refresh the call data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      }
      toast.success(toastMsg);
    } catch (error) {
      console.error("Status change error:", error);
      toast.error("Failed to update status");
    }
  };

  const handleCallModeChange = async (newMode) => {
    if (!selectedCall) return;
    // Convert "none" to empty string for storage
    const modeToSave = newMode === "none" ? "" : newMode;
    try {
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        { call_mode: modeToSave },
        { withCredentials: true }
      );

      // Add to timeline updates and send notification
      const modeLabel = newMode === "live_call" ? "LIVE CALL" : newMode === "call_98" ? "CALL 98" : "Cleared";
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        {
          update_text: `Call mode changed to ${modeLabel}`,
          call_mode: modeToSave
        },
        { withCredentials: true }
      );

      // Refresh the call data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      }
      toast.success(`Call mode: ${modeLabel}`);
    } catch (error) {
      toast.error("Failed to update call mode");
    }
  };

  const handleCancelAutoLog = async () => {
    if (!selectedCall) return;
    try {
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/cancel-auto-log`,
        {},
        { withCredentials: true }
      );
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      setLiveCalls(refreshed.data);
      const updated = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updated) setSelectedCall(updated);
      toast.success("Auto-log timer cancelled");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel auto-log timer");
    }
  };

  const handlePDActivated = async (value) => {
    if (!selectedCall) return;
    try {
      // Update the call
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        { pd_activated: value },
        { withCredentials: true }
      );
      
      // Auto-add to updates list
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        {
          general_notes: value ? "PD ACTIVATED" : "PD DEACTIVATED"
        },
        { withCredentials: true }
      );
      
      // Refresh the call data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      }
      toast.success(value ? "PD Activated" : "PD Deactivated");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleHEMSActivated = async (value) => {
    if (!selectedCall) return;
    try {
      // Update the call
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        { hems_activated: value },
        { withCredentials: true }
      );
      
      // Auto-add to updates list
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        {
          general_notes: value ? "H/EMS ACTIVATED" : "H/EMS DEACTIVATED"
        },
        { withCredentials: true }
      );
      
      // Refresh the call data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      }
      toast.success(value ? "H/EMS Activated" : "H/EMS Deactivated");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Helper function to capture video frame as thumbnail
  const captureVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';

      let hasResolved = false;
      const cleanup = () => {
        if (!hasResolved) {
          hasResolved = true;
          URL.revokeObjectURL(video.src);
        }
      };

      // Timeout fallback - resolve null after 10 seconds
      const timeout = setTimeout(() => {
        cleanup();
        resolve(null);
      }, 10000);

      video.onloadedmetadata = () => {
        // Seek to 1 second or 10% of duration
        video.currentTime = Math.min(1, video.duration * 0.1);
      };

      video.onseeked = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 360;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob((blob) => {
            cleanup();
            resolve(blob);
          }, 'image/jpeg', 0.85);
        } catch (err) {
          cleanup();
          resolve(null);
        }
      };

      video.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        resolve(null);
      };

      video.src = URL.createObjectURL(file);
      video.load();
    });
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCall) return;

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("File too large. Max 100MB allowed.");
      return;
    }

    const isVideoFile = file.type.startsWith('video/');

    setIsUploadingMedia(true);
    setUploadProgress(0);
    try {
      // Upload to R2 via /api/upload endpoint
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await axios.post(
        `${API}/upload`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(progress);
          }
        }
      );

      // Get the URL from response
      if (uploadResponse.data.url) {
        // If it's a video, capture and upload thumbnail
        if (isVideoFile && uploadResponse.data.file_id) {
          console.log('[LiveCalls] Video upload - attempting thumbnail capture for:', uploadResponse.data.file_id);
          try {
            const thumbnailBlob = await captureVideoThumbnail(file);
            console.log('[LiveCalls] Thumbnail blob:', thumbnailBlob ? `${thumbnailBlob.size} bytes` : 'null');
            if (thumbnailBlob) {
              const thumbFormData = new FormData();
              thumbFormData.append('file', thumbnailBlob, 'thumbnail.jpg');
              const thumbResponse = await axios.post(
                `${API}/upload/${uploadResponse.data.file_id}/thumbnail`,
                thumbFormData,
                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
              );
              console.log('[LiveCalls] Thumbnail uploaded:', thumbResponse.data);
            } else {
              console.warn('[LiveCalls] Thumbnail capture returned null');
            }
          } catch (thumbError) {
            console.error("[LiveCalls] Thumbnail upload failed:", thumbError);
            // Continue even if thumbnail fails
          }
        } else if (isVideoFile) {
          console.warn('[LiveCalls] Video file but no file_id in response:', uploadResponse.data);
        }

        // Link the uploaded file to the live call
        const response = await axios.post(
          `${API}/live-calls/${selectedCall.call_id}/media`,
          { media_files: [uploadResponse.data.url] },
          { withCredentials: true }
        );

        setSelectedCall(response.data);
        setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
        // Refresh media list
        setSelectedCallMedia(prev => [...prev, uploadResponse.data.url]);
        toast.success("Media uploaded");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload media");
    } finally {
      setIsUploadingMedia(false);
      setUploadProgress(0);
    }

    // Clear input
    if (mediaInputRef.current) {
      mediaInputRef.current.value = "";
    }
  };

  const handleDeleteMedia = async (mediaIndex) => {
    if (!selectedCall) return;
    try {
      const response = await axios.delete(
        `${API}/live-calls/${selectedCall.call_id}/media/${mediaIndex}`,
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      toast.success("Media removed");
    } catch (error) {
      toast.error("Failed to remove media");
    }
  };

  // Download media file to device
  const handleDownloadMedia = async (mediaUrl, index) => {
    try {
      // Check if it's a video and user is non-admin
      const isVideoFile = /\.(mp4|webm|mov|avi|mkv)($|\?)/i.test(mediaUrl) ||
        mediaUrl.includes('video');
      if (isVideoFile && user?.role !== "admin") {
        toast.error("Video download is not available");
        return;
      }

      toast.info("Downloading...");

      // ALWAYS route through backend API to get watermarked files for non-admin
      // Extract filename from URL - handle various URL formats
      let filename = mediaUrl.split('/').pop().split('?')[0];

      // If the URL contains token param, remove it from filename extraction
      if (filename.includes('&token=') || filename.includes('?token=')) {
        filename = filename.split('token=')[0].replace(/[?&]$/, '');
      }

      let downloadUrl = `${API}/uploads/${filename}?download=true`;
      console.log('Download URL:', downloadUrl, 'Original:', mediaUrl);

      // Fetch through backend (applies watermarks for non-admin)
      const response = await fetch(downloadUrl, { credentials: 'include' });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download response error:', response.status, errorText);
        throw new Error(errorText || `Server returned ${response.status}`);
      }

      const blob = await response.blob();

      // Get file extension from URL or content type
      const contentType = response.headers.get('content-type') || '';
      let ext = 'jpg';
      if (contentType.includes('png') || mediaUrl.includes('.png')) ext = 'png';
      else if (contentType.includes('gif') || mediaUrl.includes('.gif')) ext = 'gif';
      else if (contentType.includes('mp4') || mediaUrl.includes('.mp4')) ext = 'mp4';
      else if (contentType.includes('webm') || mediaUrl.includes('.webm')) ext = 'webm';
      else if (contentType.includes('mov') || mediaUrl.includes('.mov')) ext = 'mov';
      else if (contentType.includes('pdf') || mediaUrl.includes('.pdf')) ext = 'pdf';

      // Create download filename
      const timestamp = Date.now();
      const downloadFilename = `CHSP_${timestamp}_${index + 1}.${ext}`;

      // Create blob URL and download
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      toast.success("Downloaded!");
    } catch (err) {
      console.error("Download failed:", err);
      toast.error(`Download failed: ${err.message}`);
    }
  };

  const isCvObtainedLabel = (lbl) => /cv\s*obtain/i.test(lbl || "");

  const confirmCvObtainedUnit = async () => {
    if (!selectedCall || !cvObtainedPendingCode) return;
    const unit = (cvObtainedUnitInput || "").trim();
    if (!unit) { toast.error("Enter a unit"); return; }
    try {
      const currentCodes = selectedCall.custom_codes || {};
      const newCodes = { ...currentCodes, [cvObtainedPendingCode.codeId]: true };
      const newUnits = [...(selectedCall.cv_obtained_units || []), unit];
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        { custom_codes: newCodes, cv_obtained_units: newUnits },
        { withCredentials: true }
      );
      // Number the entry in the timeline when more than one exists so each
      // badge's label matches its timeline note (e.g. "CV obtained #2/CH12").
      const totalAfter = newUnits.length;
      const entryLabel = totalAfter > 1 ? `CV obtained #${totalAfter}/${unit}` : `CV obtained/${unit}`;
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        { update_text: entryLabel },
        { withCredentials: true }
      );
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updated = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updated) { setSelectedCall(updated); setLiveCalls(refreshed.data); }
      toast.success(entryLabel);
      setShowCvObtainedDialog(false);
      setCvObtainedPendingCode(null);
      setCvObtainedUnitInput("");
    } catch (error) {
      toast.error("Failed to add CV");
    }
  };

  const removeCvObtainedUnitAt = async (idx) => {
    if (!selectedCall) return;
    const list = selectedCall.cv_obtained_units || [];
    if (idx < 0 || idx >= list.length) return;
    const removed = list[idx];
    const newUnits = list.filter((_, i) => i !== idx);
    try {
      const body = { cv_obtained_units: newUnits };
      // If no units remain, clear the custom_codes flag so the underlying code
      // toggles off and the checklist "CV obtained" item un-ticks.
      if (newUnits.length === 0) {
        const cvCode = customRadioCodes.find(c => isCvObtainedLabel(c.label));
        if (cvCode) {
          body.custom_codes = { ...(selectedCall.custom_codes || {}), [cvCode.id]: false };
        }
      }
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        body,
        { withCredentials: true }
      );
      // If more than one remains after removal, show the removed one's position
      // so timeline reads "CV obtained #2/CH12 CLEARED".
      const remainingCount = newUnits.length;
      const removedLabel = remainingCount >= 1
        ? `CV obtained #${idx + 1}/${removed} CLEARED`
        : `CV obtained/${removed} CLEARED`;
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        { update_text: removedLabel },
        { withCredentials: true }
      );
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updated = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updated) { setSelectedCall(updated); setLiveCalls(refreshed.data); }
      toast.success(removedLabel);
    } catch (error) {
      toast.error("Failed to remove CV");
    }
  };

  const handleCustomCodeToggle = async (codeId, codeLabel, value) => {
    if (!selectedCall) return;
    // "CV obtained" is special: prompt for the unit that has the CV and store
    // one entry per use in cv_obtained_units so multiple selections render
    // as multiple badges ("CV obtained/CH86", "CV obtained/CH12", ...).
    if (isCvObtainedLabel(codeLabel) && value) {
      setCvObtainedPendingCode({ codeId, label: codeLabel });
      setCvObtainedUnitInput("");
      setShowCvObtainedDialog(true);
      return;
    }
    try {
      // Update custom_codes in the call
      const currentCodes = selectedCall.custom_codes || {};
      const newCodes = { ...currentCodes, [codeId]: value };

      const body = { custom_codes: newCodes };
      // Clearing "CV obtained" also clears all unit entries so the badges match.
      if (isCvObtainedLabel(codeLabel) && !value) {
        body.cv_obtained_units = [];
      }

      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        body,
        { withCredentials: true }
      );
      
      // Only 10-85 and 10-13 go to job narrative (general_notes)
      // Other radio codes go to timeline only (update_text)
      const is1085or1013 = codeLabel.toLowerCase().includes('10-85') || codeLabel.toLowerCase().includes('10-13') ||
                          codeLabel.toLowerCase().includes('1085') || codeLabel.toLowerCase().includes('1013');
      
      // Just show the code label, without ACTIVATED/CLEARED suffix
      const statusText = value ? `${codeLabel}` : `${codeLabel} CLEARED`;
      const updatePayload = is1085or1013 
        ? { general_notes: statusText }
        : { update_text: statusText };
      
      await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/updates`,
        updatePayload,
        { withCredentials: true }
      );
      
      // Refresh the call data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      }
      toast.success(statusText);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleAssignUnit = async (unitName, clearSearch = false) => {
    if (!selectedCall) return;
    try {
      // If call was "call_98", reactivate to "live_call" before assigning
      if (selectedCall.call_mode === "call_98") {
        await axios.put(
          `${API}/live-calls/${selectedCall.call_id}`,
          { call_mode: "live_call" },
          { withCredentials: true }
        );
      }

      const response = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/assign-unit/${unitName}`,
        {},
        { withCredentials: true }
      );
      // Fetch updated call to get the update history
      const updatedCall = await axios.get(`${API}/live-calls`, { withCredentials: true });
      let thisCall = updatedCall.data.find(c => c.call_id === selectedCall.call_id);
      if (thisCall) {
        // Ensure call_mode is updated if it was reactivated
        if (selectedCall.call_mode === "call_98") {
          thisCall = { ...thisCall, call_mode: "live_call" };
        }
        setSelectedCall(thisCall);
        setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? thisCall : c));
      } else {
        setSelectedCall(response.data);
        setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      }
      toast.success(`${unitName} assigned`);
      // Clear search input after successful assignment
      if (clearSearch) {
        setUnitSearch("");
      }
      // Refocus the unit search input to keep keyboard open
      setTimeout(() => {
        unitSearchInputRef.current?.focus();
      }, 100);
    } catch (error) {
      toast.error("Failed to assign unit");
    }
  };

  // Assign multiple units at once
  const handleAssignMultipleUnits = async (unitNames) => {
    if (!selectedCall || unitNames.length === 0) return;
    try {
      // If call was "call_98", reactivate to "live_call" before assigning
      if (selectedCall.call_mode === "call_98") {
        await axios.put(
          `${API}/live-calls/${selectedCall.call_id}`,
          { call_mode: "live_call" },
          { withCredentials: true }
        );
      }

      // Assign all units
      for (const unitName of unitNames) {
        await axios.post(
          `${API}/live-calls/${selectedCall.call_id}/assign-unit/${unitName}`,
          {},
          { withCredentials: true }
        );
      }
      // Fetch updated call
      const updatedCall = await axios.get(`${API}/live-calls`, { withCredentials: true });
      let thisCall = updatedCall.data.find(c => c.call_id === selectedCall.call_id);
      if (thisCall) {
        // Ensure call_mode is updated if it was reactivated
        if (selectedCall.call_mode === "call_98") {
          thisCall = { ...thisCall, call_mode: "live_call" };
        }
        setSelectedCall(thisCall);
        setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? thisCall : c));
      }
      toast.success(`${unitNames.length} unit(s) assigned`);
      setUnitSearch("");
      setSelectedUnitsToAssign([]);
    } catch (error) {
      toast.error("Failed to assign units");
    }
  };

  const handleUnassignUnit = async (unitName) => {
    if (!selectedCall) return;
    try {
      const response = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/unassign-unit/${unitName}`,
        {},
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      toast.success(`${unitName} unassigned`);
    } catch (error) {
      toast.error("Failed to unassign unit");
    }
  };

  // Handler for assigning a patrol car (supports multiple cars)
  const handleAssignPatrolCar = async (carId) => {
    if (!selectedCall) return;
    try {
      const car = patrolCars.find(c => c.car_id === carId);
      if (!car) return;

      // Check if already assigned
      const currentCars = selectedCall.patrol_cars_assigned || [];
      if (currentCars.includes(carId)) {
        toast.info("Patrol car already assigned");
        return;
      }

      // Assign the patrol car
      await axios.post(`${API}/live-calls/${selectedCall.call_id}/patrol-car`, { patrol_car_id: carId, action: 'assign' }, { withCredentials: true });

      // Get all units from the car
      const carUnits = [car.assigned_unit, ...(car.joined_units || [])].filter(Boolean);

      // Assign each unit from the car
      for (const unitName of carUnits) {
        if (!selectedCall.assigned_units?.includes(unitName)) {
          await axios.post(`${API}/live-calls/${selectedCall.call_id}/assign-unit`, { unit_name: unitName }, { withCredentials: true });
        }
        // If car has 84 status, set unit to 84
        if (car.status === '84') {
          await axios.post(`${API}/live-calls/${selectedCall.call_id}/unit-status`,
            { unit_name: unitName, status_84: true, status_98: false },
            { withCredentials: true }
          );
        }
      }

      // Update local state - add to arrays
      const newCarsAssigned = [...currentCars, carId];
      const currentDetails = selectedCall.patrol_cars_details || [];
      const newCarsDetails = [...currentDetails, { car_name: car.name, car_id: carId }];

      const newUnits = [...new Set([...(selectedCall.assigned_units || []), ...carUnits])];
      const newHistory = [
        ...(selectedCall.unit_assignment_history || []),
        { unit: car.name, action: 'patrol car assigned', timestamp: new Date().toISOString(), by: user?.name },
        ...carUnits.filter(u => !selectedCall.assigned_units?.includes(u)).map(u => ({
          unit: u,
          action: 'assigned',
          timestamp: new Date().toISOString(),
          by: `via ${car.name}`
        }))
      ];

      // Update unit statuses if car is 84
      let newStatuses = { ...selectedCall.unit_statuses };
      if (car.status === '84') {
        carUnits.forEach(u => {
          newStatuses[u] = { ...newStatuses[u], status_84: true };
        });
        newHistory.push(...carUnits.map(u => ({
          unit: u,
          action: 'status_84 set',
          timestamp: new Date().toISOString(),
          by: `via ${car.name}`
        })));
      }

      setSelectedCall({
        ...selectedCall,
        patrol_cars_assigned: newCarsAssigned,
        patrol_cars_details: newCarsDetails,
        assigned_units: newUnits,
        unit_assignment_history: newHistory,
        unit_statuses: newStatuses
      });

      toast.success(`${car.name} assigned with ${carUnits.length > 0 ? carUnits.join(', ') : 'no units'}`);
    } catch (error) {
      toast.error("Failed to assign patrol car");
    }
  };

  // Handler for unassigning a patrol car
  const handleUnassignPatrolCar = async (carId) => {
    if (!selectedCall) return;
    try {
      const currentDetails = selectedCall.patrol_cars_details || [];
      const carDetail = currentDetails.find(d => d.car_id === carId);
      const carName = carDetail?.car_name || "Unknown";

      await axios.post(`${API}/live-calls/${selectedCall.call_id}/patrol-car`, { patrol_car_id: carId, action: 'unassign' }, { withCredentials: true });

      // Update local state - remove from arrays
      const newCarsAssigned = (selectedCall.patrol_cars_assigned || []).filter(c => c !== carId);
      const newCarsDetails = currentDetails.filter(d => d.car_id !== carId);
      const newHistory = [...(selectedCall.unit_assignment_history || []), {
        unit: carName,
        action: 'patrol car unassigned',
        timestamp: new Date().toISOString(),
        by: user?.name
      }];

      setSelectedCall({
        ...selectedCall,
        patrol_cars_assigned: newCarsAssigned,
        patrol_cars_details: newCarsDetails,
        unit_assignment_history: newHistory
      });

      toast.success("Patrol car unassigned");
    } catch (error) {
      toast.error("Failed to unassign patrol car");
    }
  };

  // Handler for updating dispatcher units
  const handleUpdateDispatcher = async (dispatcherUnits) => {
    if (!selectedCall) return;
    try {
      const response = await axios.patch(
        `${API}/live-calls/${selectedCall.call_id}`,
        { dispatcher_units: dispatcherUnits },
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      toast.success("Dispatcher updated");
    } catch (error) {
      toast.error("Failed to update dispatcher");
    }
  };

  // Handler for updating hotline call status
  const handleUpdateHotline = async (isHotline) => {
    if (!selectedCall) return;
    try {
      const response = await axios.patch(
        `${API}/live-calls/${selectedCall.call_id}`,
        { is_hotline_call: isHotline },
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      toast.success(isHotline ? "Marked as hotline call" : "Hotline status cleared");
    } catch (error) {
      toast.error("Failed to update hotline status");
    }
  };

  // Handler for updating unit 84/98 status
  const handleUpdateUnitStatus = async (unitName, status84, status98, statusStandby = false) => {
    if (!selectedCall) return;
    // When the status is set straight from the search dropdown (long-press),
    // the unit isn't assigned yet. The backend rejects a status update for an
    // unassigned unit, so assign it first, then apply the status.
    const wasAssigned = !!selectedCall.assigned_units?.includes(unitName);
    try {
      if (!wasAssigned) {
        // If the call was wrapped (call_98), reactivate before assigning.
        if (selectedCall.call_mode === "call_98") {
          await axios.put(
            `${API}/live-calls/${selectedCall.call_id}`,
            { call_mode: "live_call" },
            { withCredentials: true }
          );
        }
        await axios.post(
          `${API}/live-calls/${selectedCall.call_id}/assign-unit/${unitName}`,
          {},
          { withCredentials: true }
        );
      }
      const response = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/unit-status/${unitName}`,
        { status_84: status84, status_98: status98, status_standby: statusStandby },
        { withCredentials: true }
      );
      let updatedCall = response.data;

      // Auto-switch call_mode based on unit statuses
      const assignedUnits = updatedCall.assigned_units || [];
      const allUnits98 = assignedUnits.length > 0 &&
        assignedUnits.every(unit => updatedCall.unit_statuses?.[unit]?.status_98);

      if (allUnits98 && updatedCall.call_mode !== "call_98") {
        // All units are 98 → auto-switch to "call_98"
        await axios.put(
          `${API}/live-calls/${selectedCall.call_id}`,
          { call_mode: "call_98" },
          { withCredentials: true }
        );
        updatedCall = { ...updatedCall, call_mode: "call_98" };
      } else if (!status98 && updatedCall.call_mode === "call_98") {
        // Unit lost 98 status while call was "call_98" → reactivate to "live_call"
        await axios.put(
          `${API}/live-calls/${selectedCall.call_id}`,
          { call_mode: "live_call" },
          { withCredentials: true }
        );
        updatedCall = { ...updatedCall, call_mode: "live_call" };
      }

      setSelectedCall(updatedCall);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? updatedCall : c));
      const statusText = [];
      if (status84) statusText.push('84');
      if (status98) statusText.push('98');
      if (statusStandby) statusText.push('STBY');
      toast.success(`${unitName} ${wasAssigned ? '' : 'assigned, '}marked as ${statusText.length > 0 ? statusText.join(', ') : 'cleared'}`);
      // Assigned straight from the search dropdown — clear the search and
      // refocus so the keyboard stays open for the next unit.
      if (!wasAssigned) {
        setUnitSearch("");
        setTimeout(() => unitSearchInputRef.current?.focus(), 100);
      }
    } catch (error) {
      toast.error("Failed to update unit status");
    }
  };

  // Handler for cycling unit status: default → 84 (blue) → 98 (green) → default.
  // A unit on Standby is treated as "off-cycle" — clicking it kicks it back to
  // 84 (clearing standby). Standby is only set via long-press → dialog.
  const handleCycleUnitStatus = async (unitName) => {
    if (!selectedCall) return;
    const unitStatus = selectedCall.unit_statuses?.[unitName] || {};
    let newStatus84 = false;
    let newStatus98 = false;

    if (unitStatus.status_standby) {
      // Standby → 84 (clears standby implicitly via the always-false default)
      newStatus84 = true;
    } else if (!unitStatus.status_84 && !unitStatus.status_98) {
      // Default → 84 (blue)
      newStatus84 = true;
    } else if (unitStatus.status_84 && !unitStatus.status_98) {
      // 84 → 98 (green)
      newStatus98 = true;
    }
    // If status_98 is true → clear all (default)

    await handleUpdateUnitStatus(unitName, newStatus84, newStatus98, false);
  };

  // Long press handlers for unit unassignment (3 seconds)
  const handleUnitLongPressStart = (unitName) => {
    setLongPressUnit(unitName);
    longPressTimerRef.current = setTimeout(() => {
      setUnitToUnassign(unitName);
      setShowUnassignConfirm(true);
      setLongPressUnit(null);
    }, 1000);
  };

  const handleUnitLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setLongPressUnit(null);
  };

  // Long press handlers for a unit in the search dropdown: hold to open the
  // status prompt (84 / 98 / Standby) which assigns the unit and applies the
  // status in one go — no need to assign first and then long-press the badge.
  const handleDropdownUnitLongPressStart = (unitName) => {
    dropdownLongPressFiredRef.current = false;
    setLongPressUnit(unitName);
    longPressTimerRef.current = setTimeout(() => {
      dropdownLongPressFiredRef.current = true;
      setLongPressUnit(null);
      setUnitToUnassign(unitName);
      setShowUnassignConfirm(true);
    }, 1000);
  };

  const handleDropdownUnitLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setLongPressUnit(null);
  };

  // Long press handlers for patrol car unassignment (3 seconds)
  const handlePatrolCarLongPressStart = (carId) => {
    setLongPressPatrolCar(carId);
    longPressTimerRef.current = setTimeout(() => {
      setPatrolCarToUnassign(carId);
      setShowPatrolCarUnassignConfirm(true);
      setLongPressPatrolCar(null);
    }, 1000);
  };

  const handlePatrolCarLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setLongPressPatrolCar(null);
  };

  // Bulk status update handlers
  const handleAllUnits84 = async () => {
    if (!selectedCall || !selectedCall.assigned_units?.length) return;
    try {
      let updatedCall = selectedCall;
      for (const unitName of selectedCall.assigned_units) {
        const response = await axios.post(
          `${API}/live-calls/${selectedCall.call_id}/unit-status/${unitName}`,
          { status_84: true, status_98: false },
          { withCredentials: true }
        );
        updatedCall = response.data;
      }
      setSelectedCall(updatedCall);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? updatedCall : c));
      toast.success(`All ${selectedCall.assigned_units.length} units marked as 84`);
    } catch (error) {
      toast.error("Failed to update unit statuses");
    }
    setShowAll84Confirm(false);
  };

  const handleAllUnits98 = async () => {
    if (!selectedCall || !selectedCall.assigned_units?.length) return;
    try {
      let updatedCall = selectedCall;
      for (const unitName of selectedCall.assigned_units) {
        const response = await axios.post(
          `${API}/live-calls/${selectedCall.call_id}/unit-status/${unitName}`,
          { status_84: false, status_98: true },
          { withCredentials: true }
        );
        updatedCall = response.data;
      }
      // Auto-switch call_mode to "call_98" when all units are 98
      // Include notify_call_98 flag to trigger notification only from "All 98" button
      const modeResponse = await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        { call_mode: "call_98", notify_call_98: true },
        { withCredentials: true }
      );
      updatedCall = { ...updatedCall, call_mode: "call_98" };
      setSelectedCall(updatedCall);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? updatedCall : c));
      toast.success(`All ${selectedCall.assigned_units.length} units marked as 98`);
    } catch (error) {
      toast.error("Failed to update unit statuses");
    }
    setShowAll98Confirm(false);
  };

  // Perp description handlers
  const handleAddPerp = async () => {
    if (!selectedCall) return;
    try {
      const response = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/perps`,
        newPerp,
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      setShowPerpDialog(false);
      setNewPerp({
        name: "", address: "", current_location: "", sex: "", race: "", age: "", height: "", weight: "",
        build: "", complexion: "", hair_color_cut: "", eye_color: "", hat_color_style: "",
        shirt: "", pants: "", jacket_coat: "", shoes: "", visible_marks: "", clothing: "",
        direction_of_flight: "", has_weapon: false, weapon_description: "",
        vehicle_description: "", license_plate: "", was_arrested: false,
        is_holding: false, holding_location: "", is_detained: false, detained_location: "", is_visual: false, is_positive_id: false,
        notes: "", linked_profile_id: ""
      });
      toast.success("Perpetrator added");
    } catch (error) {
      toast.error("Failed to add perpetrator");
    }
  };

  const handleUpdatePerp = async () => {
    if (!selectedCall || !editingPerp) return;
    try {
      const response = await axios.put(
        `${API}/live-calls/${selectedCall.call_id}/perps/${editingPerp.perp_id}`,
        newPerp,
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      setShowPerpDialog(false);
      setEditingPerp(null);
      toast.success("Perpetrator updated");
    } catch (error) {
      toast.error("Failed to update perpetrator");
    }
  };

  const handleDeletePerp = async (perpId) => {
    if (!selectedCall) return;
    try {
      const response = await axios.delete(
        `${API}/live-calls/${selectedCall.call_id}/perps/${perpId}`,
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      toast.success("Perpetrator removed");
    } catch (error) {
      toast.error("Failed to remove perpetrator");
    }
  };

  const [sendingPerpNotif, setSendingPerpNotif] = useState(null);
  const [confirmPerpNotif, setConfirmPerpNotif] = useState(null); // perp_id to confirm
  const [perpNotifWhatsApp, setPerpNotifWhatsApp] = useState(true);
  const [perpNotifPush, setPerpNotifPush] = useState(false);
  const [perpNotifWaIndividuals, setPerpNotifWaIndividuals] = useState(true);
  const [perpNotifWaBotGroups, setPerpNotifWaBotGroups] = useState(true);
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  const [sendingResend, setSendingResend] = useState(false);
  const [resendWhatsApp, setResendWhatsApp] = useState(true);
  const [resendPush, setResendPush] = useState(false);
  const [resendCall, setResendCall] = useState(true);
  const [resendPerpIds, setResendPerpIds] = useState([]);
  const [resendCustomMessage, setResendCustomMessage] = useState("");
  const [resendWaIndividuals, setResendWaIndividuals] = useState(true);
  const [resendWaBotGroups, setResendWaBotGroups] = useState(true);
  const [resendWaBotGroupJids, setResendWaBotGroupJids] = useState([]); // empty = use call's setting
  const [showAllPerpsWhatsAppConfirm, setShowAllPerpsWhatsAppConfirm] = useState(false);
  const [sendingAllPerpsWhatsApp, setSendingAllPerpsWhatsApp] = useState(false);
  const [confirmUpdateNotif, setConfirmUpdateNotif] = useState(null); // update text to confirm
  const [sendingUpdateNotif, setSendingUpdateNotif] = useState(false);
  const [updateNotifWhatsApp, setUpdateNotifWhatsApp] = useState(true);
  const [updateNotifPush, setUpdateNotifPush] = useState(false);
  const [updateNotifWaIndividuals, setUpdateNotifWaIndividuals] = useState(true);
  const [updateNotifWaBotGroups, setUpdateNotifWaBotGroups] = useState(true);

  const handleSendPerpNotif = async (perpId) => {
    if (!selectedCall) return;
    setSendingPerpNotif(perpId);
    setConfirmPerpNotif(null);
    try {
      const groupIds = selectedCall.notification_group_ids?.length > 0 ? selectedCall.notification_group_ids : null;
      const res = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/perps/${perpId}/whatsapp`,
        {
          send_whatsapp: perpNotifWhatsApp,
          send_push: perpNotifPush,
          group_ids: groupIds,
          send_wa_individuals: perpNotifWaIndividuals,
          send_wa_bot_groups: perpNotifWaBotGroups,
        },
        { withCredentials: true }
      );
      const parts = [];
      if (perpNotifWhatsApp && res.data.whatsapp_sent > 0) parts.push(`WhatsApp (${res.data.whatsapp_sent})`);
      if (perpNotifPush && res.data.push_sent) parts.push("Push");
      toast.success(`Perp description sent via ${parts.join(" & ") || "notification"}`);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to send notification";
      toast.error(msg);
    } finally {
      setSendingPerpNotif(null);
    }
  };

  const handleSendUpdateNotif = async (updateText) => {
    if (!selectedCall || !updateText) return;
    setSendingUpdateNotif(true);
    setConfirmUpdateNotif(null);
    try {
      const groupIds = selectedCall.notification_group_ids?.length > 0 ? selectedCall.notification_group_ids : null;
      const res = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/whatsapp-resend`,
        {
          group_ids: groupIds,
          send_whatsapp: updateNotifWhatsApp,
          send_push: updateNotifPush,
          send_call: false,
          perp_ids: [],
          custom_message: `🔄 *UPDATE:* ${updateText}`,
          resend_wa_individuals: updateNotifWaIndividuals,
          resend_wa_bot_groups: updateNotifWaBotGroups,
          resend_wa_bot_group_jids: null,
        },
        { withCredentials: true }
      );
      const parts = [];
      if (updateNotifWhatsApp && res.data.whatsapp_sent > 0) parts.push(`WhatsApp (${res.data.whatsapp_sent})`);
      if (updateNotifPush && res.data.push_sent) parts.push("Push");
      toast.success(`Update sent via ${parts.join(" & ") || "selected method"}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send update");
    } finally {
      setSendingUpdateNotif(false);
    }
  };

  const handleResendNotification = async () => {
    if (!selectedCall) return;
    setSendingResend(true);
    setShowResendConfirm(false);
    try {
      const groupIds = selectedCall.notification_group_ids?.length > 0 ? selectedCall.notification_group_ids : null;
      const res = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/whatsapp-resend`,
        {
          group_ids: groupIds,
          send_whatsapp: resendWhatsApp,
          send_push: resendPush,
          send_call: resendCall,
          perp_ids: resendPerpIds,
          custom_message: resendCustomMessage,
          resend_wa_individuals: resendWaIndividuals,
          resend_wa_bot_groups: resendWaBotGroups,
          resend_wa_bot_group_jids: null, // uses call's actions card setting
        },
        { withCredentials: true }
      );
      if (res.data.send_count) {
        const updatedCall = { ...selectedCall, whatsapp_send_count: res.data.send_count };
        setSelectedCall(updatedCall);
        setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? updatedCall : c));
      }
      const parts = [];
      if (resendWhatsApp && res.data.whatsapp_sent > 0) parts.push(`WhatsApp (${res.data.whatsapp_sent})`);
      if (resendPush && res.data.push_sent) parts.push("Push");
      const items = res.data.items_sent || [];
      toast.success(`Sent ${items.join(", ")} via ${parts.join(" & ") || "selected method"}`);
      setResendCustomMessage("");
      setResendPerpIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send notification");
    } finally {
      setSendingResend(false);
    }
  };

  const handleSendAllPerpsWhatsApp = async () => {
    if (!selectedCall) return;
    setSendingAllPerpsWhatsApp(true);
    setShowAllPerpsWhatsAppConfirm(false);
    try {
      const groupIds = selectedCall.notification_group_ids?.length > 0 ? selectedCall.notification_group_ids : null;
      const res = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/perps/whatsapp-all`,
        {
          group_ids: groupIds,
          send_wa_individuals: perpNotifWaIndividuals,
          send_wa_bot_groups: perpNotifWaBotGroups,
        },
        { withCredentials: true }
      );
      toast.success(`All ${res.data.perp_count} perp description(s) sent to WhatsApp (${res.data.sent} recipient${res.data.sent !== 1 ? 's' : ''})`);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to send WhatsApp";
      toast.error(msg);
    } finally {
      setSendingAllPerpsWhatsApp(false);
    }
  };


  const getOrdinal = (n) => {
    if (n === 1) return "1st";
    if (n === 2) return "2nd";
    if (n === 3) return "3rd";
    return `${n}th`;
  };

  const openPerpDialog = (perp = null) => {
    if (perp) {
      setEditingPerp(perp);
      setNewPerp({
        name: perp.name || "",
        address: perp.address || "",
        current_location: perp.current_location || "",
        sex: perp.sex || "",
        race: perp.race || "",
        age: perp.age || "",
        height: perp.height || "",
        weight: perp.weight || "",
        build: perp.build || "",
        complexion: perp.complexion || "",
        hair_color_cut: perp.hair_color_cut || "",
        eye_color: perp.eye_color || "",
        hat_color_style: perp.hat_color_style || "",
        shirt: perp.shirt || "",
        pants: perp.pants || "",
        jacket_coat: perp.jacket_coat || "",
        shoes: perp.shoes || "",
        visible_marks: perp.visible_marks || "",
        clothing: perp.clothing || "",
        direction_of_flight: perp.direction_of_flight || "",
        has_weapon: perp.has_weapon || false,
        weapon_description: perp.weapon_description || "",
        vehicle_description: perp.vehicle_description || "",
        license_plate: perp.license_plate || "",
        was_arrested: perp.was_arrested || false,
        is_holding: perp.is_holding || false,
        holding_location: perp.holding_location || "",
        is_detained: perp.is_detained || false,
        detained_location: perp.detained_location || "",
        is_visual: perp.is_visual || false,
        is_positive_id: perp.is_positive_id || false,
        notes: perp.notes || "",
        linked_profile_id: perp.linked_profile_id || ""
      });
    } else {
      setEditingPerp(null);
      setNewPerp({
        name: "", address: "", current_location: "", sex: "", race: "", age: "", height: "", weight: "",
        build: "", complexion: "", hair_color_cut: "", eye_color: "", hat_color_style: "",
        shirt: "", pants: "", jacket_coat: "", shoes: "", visible_marks: "", clothing: "",
        direction_of_flight: "", has_weapon: false, weapon_description: "",
        vehicle_description: "", license_plate: "", was_arrested: false,
        is_holding: false, holding_location: "", is_detained: false, detained_location: "", is_visual: false, is_positive_id: false,
        notes: "", linked_profile_id: ""
      });
    }
    setProfileSearchQuery("");
    setShowPerpDialog(true);
  };

  // Toggle perp arrest status
  const handleTogglePerpArrest = async (perpId, wasArrested) => {
    if (!selectedCall) return;
    try {
      const response = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/perps/${perpId}/arrest`,
        { was_arrested: wasArrested },
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      toast.success(wasArrested ? "Perpetrator marked as arrested" : "Arrest status cleared");
    } catch (error) {
      toast.error("Failed to update arrest status");
    }
  };

  // Open armed dialog for a perp
  const openArmedDialog = (perp) => {
    setArmedDialogPerpId(perp.perp_id);
    setWeaponDescInput(perp.weapon_description || "");
    setShowArmedDialog(true);
  };

  // Toggle perp armed status
  const handleTogglePerpArmed = async (isArmed, weaponDescription = "") => {
    if (!selectedCall || !armedDialogPerpId) return;
    try {
      // Find the perp to get current data
      const perp = selectedCall.perp_descriptions?.find(p => p.perp_id === armedDialogPerpId);
      if (!perp) return;

      // Update the perp with armed status
      const response = await axios.put(
        `${API}/live-calls/${selectedCall.call_id}/perps/${armedDialogPerpId}`,
        { ...perp, has_weapon: isArmed, weapon_description: weaponDescription },
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      toast.success(isArmed ? "Perpetrator marked as armed" : "Armed status cleared");
      setShowArmedDialog(false);
      setArmedDialogPerpId(null);
      setWeaponDescInput("");
    } catch (error) {
      toast.error("Failed to update armed status");
    }
  };

  // Open holding dialog for a perp
  const openHoldingDialog = (perp) => {
    setHoldingDetainedPerpId(perp.perp_id);
    setHoldingDetainedPerp(perp);
    setHoldingLocationInput(perp.holding_location || "");
    setShowHoldingDialog(true);
  };

  // Open detained dialog for a perp
  const openDetainedDialog = (perp) => {
    setHoldingDetainedPerpId(perp.perp_id);
    setHoldingDetainedPerp(perp);
    setDetainedLocationInput(perp.detained_location || "");
    setShowDetainedDialog(true);
  };

  // Handle holding status update
  const handleHoldingUpdate = async (isHolding, location = "") => {
    if (!selectedCall || !holdingDetainedPerpId) return;
    try {
      const perp = selectedCall.perp_descriptions?.find(p => p.perp_id === holdingDetainedPerpId);
      if (!perp) return;

      // Update the perp with holding status and current_location if location provided
      const perpUpdate = {
        ...perp,
        is_holding: isHolding,
        holding_location: location,
        ...(location && isHolding ? { current_location: location } : {})
      };
      // The PUT response already contains the fully-updated call (including
      // the new update entry and per-status timestamps), so use it directly
      // instead of paying for another /live-calls round-trip.
      const res = await axios.put(
        `${API}/live-calls/${selectedCall.call_id}/perps/${holdingDetainedPerpId}`,
        perpUpdate,
        { withCredentials: true }
      );
      const updatedCall = res.data;
      setSelectedCall(updatedCall);
      setLiveCalls(prev => prev.map(c => c.call_id === selectedCall.call_id ? updatedCall : c));

      toast.success(isHolding ? "Marked as Holding 1" : "Holding status cleared");
      setShowHoldingDialog(false);
      setHoldingDetainedPerpId(null);
      setHoldingDetainedPerp(null);
      setHoldingLocationInput("");
    } catch (error) {
      toast.error("Failed to update holding status");
    }
  };

  // Handle detained status update
  const handleDetainedUpdate = async (isDetained, location = "") => {
    if (!selectedCall || !holdingDetainedPerpId) return;
    try {
      const perp = selectedCall.perp_descriptions?.find(p => p.perp_id === holdingDetainedPerpId);
      if (!perp) return;

      // Update the perp with detained status and current_location if location provided
      const perpUpdate = {
        ...perp,
        is_detained: isDetained,
        detained_location: location,
        ...(location && isDetained ? { current_location: location } : {})
      };
      const res = await axios.put(
        `${API}/live-calls/${selectedCall.call_id}/perps/${holdingDetainedPerpId}`,
        perpUpdate,
        { withCredentials: true }
      );
      const updatedCall = res.data;
      setSelectedCall(updatedCall);
      setLiveCalls(prev => prev.map(c => c.call_id === selectedCall.call_id ? updatedCall : c));

      toast.success(isDetained ? "Marked as 1 Detained" : "Detained status cleared");
      setShowDetainedDialog(false);
      setHoldingDetainedPerpId(null);
      setHoldingDetainedPerp(null);
      setDetainedLocationInput("");
    } catch (error) {
      toast.error("Failed to update detained status");
    }
  };

  // Open visual dialog for a perp
  const openVisualDialog = (perp) => {
    setHoldingDetainedPerpId(perp.perp_id);
    setHoldingDetainedPerp(perp);
    setShowVisualDialog(true);
  };

  // Handle visual status update
  const handleVisualUpdate = async (isVisual) => {
    if (!selectedCall || !holdingDetainedPerpId) return;
    try {
      const perp = selectedCall.perp_descriptions?.find(p => p.perp_id === holdingDetainedPerpId);
      if (!perp) return;

      const perpUpdate = {
        ...perp,
        is_visual: isVisual
      };
      const res = await axios.put(
        `${API}/live-calls/${selectedCall.call_id}/perps/${holdingDetainedPerpId}`,
        perpUpdate,
        { withCredentials: true }
      );
      const updatedCall = res.data;
      setSelectedCall(updatedCall);
      setLiveCalls(prev => prev.map(c => c.call_id === selectedCall.call_id ? updatedCall : c));

      toast.success(isVisual ? "Marked as Visual" : "Visual status cleared");
      setShowVisualDialog(false);
      setHoldingDetainedPerpId(null);
      setHoldingDetainedPerp(null);
    } catch (error) {
      toast.error("Failed to update visual status");
    }
  };

  // Open positive-id dialog for a perp
  const openPositiveIdDialog = (perp) => {
    setHoldingDetainedPerpId(perp.perp_id);
    setHoldingDetainedPerp(perp);
    setShowPositiveIdDialog(true);
  };

  // Handle positive-id status update
  const handlePositiveIdUpdate = async (isPositiveId) => {
    if (!selectedCall || !holdingDetainedPerpId) return;
    try {
      const perp = selectedCall.perp_descriptions?.find(p => p.perp_id === holdingDetainedPerpId);
      if (!perp) return;
      const perpUpdate = { ...perp, is_positive_id: isPositiveId };
      const res = await axios.put(
        `${API}/live-calls/${selectedCall.call_id}/perps/${holdingDetainedPerpId}`,
        perpUpdate,
        { withCredentials: true }
      );
      const updatedCall = res.data;
      setSelectedCall(updatedCall);
      setLiveCalls(prev => prev.map(c => c.call_id === selectedCall.call_id ? updatedCall : c));
      toast.success(isPositiveId ? "Marked as Positive ID" : "Positive ID cleared");
      setShowPositiveIdDialog(false);
      setHoldingDetainedPerpId(null);
      setHoldingDetainedPerp(null);
    } catch (error) {
      toast.error("Failed to update positive ID status");
    }
  };

  // Link perpetrator profile to call
  const handleLinkProfile = async (profileId) => {
    if (!selectedCall) return;
    try {
      const response = await axios.post(
        `${API}/live-calls/${selectedCall.call_id}/link-profile/${profileId}`,
        {},
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      setShowProfileLinkDialog(false);
      setProfileSearchQuery("");
      toast.success("Profile linked");
    } catch (error) {
      toast.error("Failed to link profile");
    }
  };

  // Unlink perpetrator profile from call
  const handleUnlinkProfile = async (profileId) => {
    if (!selectedCall) return;
    try {
      const response = await axios.delete(
        `${API}/live-calls/${selectedCall.call_id}/link-profile/${profileId}`,
        { withCredentials: true }
      );
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
      toast.success("Profile unlinked");
    } catch (error) {
      toast.error("Failed to unlink profile");
    }
  };

  const handleLogCall = async (callId) => {
    try {
      const encodedCallId = encodeURIComponent(callId);
      const response = await axios.post(
        `${API}/live-calls/${encodedCallId}/log`,
        {},
        { withCredentials: true }
      );
      
      // Stop location sharing for this call (backend already cleans up active_locations,
      // but we need to tell the user's browser to stop the geolocation watcher)
      // Broadcast a custom event that the LocationSharingButton can listen to
      window.dispatchEvent(new CustomEvent('callLogged', { detail: { callId } }));
      
      setLiveCalls(liveCalls.filter(c => c.call_id !== callId));
      setSelectedCall(null);
      selectedCallIdRef.current = null;
      toast.success("Call logged as incident", {
        action: {
          label: "View Incident",
          onClick: () => window.location.href = `/incidents/${response.data.incident_id}`
        }
      });
    } catch (error) {
      console.error("Log call error:", error);
      toast.error("Failed to log call");
    }
  };

  // Open the lightweight Call Type / Secondary Type edit dialog
  const openCallTypeOnlyDialog = () => {
    if (!selectedCall) return;
    setCallTypeOnlyData({
      call_type: selectedCall.call_type || "",
      secondary_call_type: selectedCall.secondary_call_type || "",
      priority: selectedCall.priority || "normal",
    });
    setCtOnlyPrimarySearch("");
    setCtOnlySecondarySearch("");
    setShowCtOnlyPrimaryDropdown(false);
    setShowCtOnlySecondaryDropdown(false);
    setShowCallTypeOnlyDialog(true);
  };

  // Auto-promote to Priority when either type lands on something from the
  // priority-call-types list. Doesn't auto-demote — a dispatcher might
  // intentionally keep Priority set after picking a non-priority secondary.
  useEffect(() => {
    if (!showCallTypeOnlyDialog) return;
    const isCallTypePriority = priorityCallTypes.includes(callTypeOnlyData.call_type);
    const isSecPriority = priorityCallTypes.includes(callTypeOnlyData.secondary_call_type);
    if ((isCallTypePriority || isSecPriority) && callTypeOnlyData.priority !== "priority") {
      setCallTypeOnlyData(d => ({ ...d, priority: "priority" }));
    }
  }, [showCallTypeOnlyDialog, callTypeOnlyData.call_type, callTypeOnlyData.secondary_call_type, priorityCallTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCallTypeOnly = async () => {
    if (!selectedCall) return;
    const next = {
      call_type: (callTypeOnlyData.call_type || "").trim(),
      secondary_call_type: (callTypeOnlyData.secondary_call_type || "").trim(),
      priority: callTypeOnlyData.priority || "normal",
    };
    if (
      next.call_type === (selectedCall.call_type || "") &&
      next.secondary_call_type === (selectedCall.secondary_call_type || "") &&
      next.priority === (selectedCall.priority || "normal")
    ) {
      setShowCallTypeOnlyDialog(false);
      return;
    }
    setSavingCallTypeOnly(true);
    try {
      const res = await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        next,
        { withCredentials: true }
      );
      // Log the change as an update entry so the timeline reflects it
      const parts = [];
      if (next.call_type !== (selectedCall.call_type || "")) parts.push(`Type: ${selectedCall.call_type || 'N/A'} → ${next.call_type || 'N/A'}`);
      if (next.secondary_call_type !== (selectedCall.secondary_call_type || "")) parts.push(`Secondary: ${selectedCall.secondary_call_type || 'N/A'} → ${next.secondary_call_type || 'N/A'}`);
      if (next.priority !== (selectedCall.priority || "normal")) parts.push(`Priority: ${(selectedCall.priority || 'normal').toUpperCase()} → ${next.priority.toUpperCase()}`);
      if (parts.length > 0) {
        await axios.post(
          `${API}/live-calls/${selectedCall.call_id}/updates`,
          { update_text: parts.join(' · ') },
          { withCredentials: true }
        );
      }
      // Refresh
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updated = refreshed.data.find(c => c.call_id === selectedCall.call_id) || res.data;
      setSelectedCall(updated);
      setLiveCalls(refreshed.data);
      toast.success('Call type updated');
      setShowCallTypeOnlyDialog(false);
      // Same follow-up dialog as priority-up: offer to enable WA Alerts going
      // forward and re-broadcast the call with the new type. Pass the
      // just-saved priority so the dialog can default WA Alerts accordingly
      // (the selectedCall state doesn't update synchronously, so we can't
      // rely on it inside openBroadcastDialog yet).
      openBroadcastDialog('type', { priority: next.priority });
    } catch (err) {
      toast.error('Failed to update call type');
    } finally {
      setSavingCallTypeOnly(false);
    }
  };

  // Handler for opening edit call dialog
  const openEditCallDialog = () => {
    if (selectedCall) {
      setEditCallData({
        location: selectedCall.location || "",
        location_title: selectedCall.location_title || "",
        location_notes: selectedCall.location_notes || "",
        cross_streets: selectedCall.cross_streets || "",
        call_type: selectedCall.call_type || "",
        secondary_call_type: selectedCall.secondary_call_type || "",
        priority: selectedCall.priority || "normal",
        callback_phone: selectedCall.callback_phone || "",
        callback_numbers: selectedCall.callback_numbers || [],
        cv_or_third_party: selectedCall.cv_or_third_party || "",
        caller_has_visual: selectedCall.caller_has_visual || false,
        direction_of_flight: selectedCall.direction_of_flight || "",
        confirmation_status: selectedCall.confirmation_status || "",
        call_note: selectedCall.call_note || "",
        description: selectedCall.description || "",
        victims: selectedCall.victims || [],
        witnesses: selectedCall.witnesses || [],
        perp_descriptions: selectedCall.perp_descriptions || [],
        send_notifications: selectedCall.send_notifications !== false,
        send_whatsapp: selectedCall.send_whatsapp !== false,
        send_whatsapp_to_bot_groups: selectedCall.send_whatsapp_to_bot_groups !== false,
        whatsapp_updates_enabled: selectedCall.whatsapp_updates_enabled || false,
        block_from_response: selectedCall.block_from_response || false,
        notification_group_ids: selectedCall.notification_group_ids || [],
        wa_bot_group_jids: selectedCall.wa_bot_group_jids || [],
        transmitted_10_85: selectedCall.transmitted_10_85 || false,
        transmitted_10_13: selectedCall.transmitted_10_13 || false,
        whatsapp_8513_enabled: selectedCall.whatsapp_8513_enabled !== false,
        additional_locations: selectedCall.additional_locations || [],
      });
      setEditCallTypeSearch("");
      setEditSecondaryCallTypeSearch("");
      setEditCallbackInput({ number: "", caller_type: "", has_visual: false });
      setShowEditCallDialog(true);
    }
  };

  // Handler for saving call info changes (creates an update)
  const handleSaveCallInfo = async () => {
    if (!selectedCall) return;
    
    try {
      // Find what changed
      // Note: location is NOT tracked here — the backend PUT already creates an update entry
      // with updated_location for the Location Updates box, avoiding duplicate entries
      const changes = [];
      if (editCallData.cross_streets !== (selectedCall.cross_streets || "")) changes.push(`Cross Streets: ${selectedCall.cross_streets || 'N/A'} → ${editCallData.cross_streets || 'N/A'}`);
      if (editCallData.location_notes !== (selectedCall.location_notes || "")) changes.push(`Location Notes: ${selectedCall.location_notes || 'N/A'} → ${editCallData.location_notes || 'N/A'}`);
      if (editCallData.call_type !== selectedCall.call_type) changes.push(`Call Type: ${selectedCall.call_type || 'N/A'} → ${editCallData.call_type || 'N/A'}`);
      if (editCallData.secondary_call_type !== (selectedCall.secondary_call_type || "")) changes.push(`Secondary Type: ${selectedCall.secondary_call_type || 'N/A'} → ${editCallData.secondary_call_type || 'N/A'}`);
      if (editCallData.priority !== selectedCall.priority) changes.push(`Priority: ${selectedCall.priority || 'N/A'} → ${editCallData.priority || 'N/A'}`);
      if (editCallData.callback_phone !== selectedCall.callback_phone) changes.push(`Callback: ${selectedCall.callback_phone || 'N/A'} → ${editCallData.callback_phone || 'N/A'}`);
      
      // Track callback_numbers changes
      const oldCallbacks = selectedCall.callback_numbers || [];
      const newCallbacks = editCallData.callback_numbers || [];
      if (JSON.stringify(oldCallbacks) !== JSON.stringify(newCallbacks)) {
        const oldNumbers = oldCallbacks.map(c => c.number);
        const newNumbers = newCallbacks.map(c => c.number);
        const added = newCallbacks.filter(c => !oldNumbers.includes(c.number));
        const removed = oldCallbacks.filter(c => !newNumbers.includes(c.number));
        added.forEach(cb => {
          changes.push(`Callback added: ${cb.number}${cb.has_visual ? ' [VISUAL]' : ''}`);
        });
        removed.forEach(cb => {
          changes.push(`Callback removed: ${cb.number}`);
        });
      }
      
      if (editCallData.cv_or_third_party !== selectedCall.cv_or_third_party) changes.push(`CV/3rd Party: ${selectedCall.cv_or_third_party || 'N/A'} → ${editCallData.cv_or_third_party || 'N/A'}`);
      if (editCallData.caller_has_visual !== selectedCall.caller_has_visual) changes.push(`Has Visual: ${selectedCall.caller_has_visual ? 'Yes' : 'No'} → ${editCallData.caller_has_visual ? 'Yes' : 'No'}`);
      if (editCallData.direction_of_flight !== selectedCall.direction_of_flight) changes.push(`Dir of Flight: ${selectedCall.direction_of_flight || 'N/A'} → ${editCallData.direction_of_flight || 'N/A'}`);
      if (editCallData.confirmation_status !== selectedCall.confirmation_status) changes.push(`Confirmation: ${(selectedCall.confirmation_status || 'No Confirm').toUpperCase()} → ${(editCallData.confirmation_status || 'No Confirm').toUpperCase()}`);
      if (editCallData.call_note !== (selectedCall.call_note || "")) changes.push(`Job Narrative: "${selectedCall.call_note || 'N/A'}" → "${editCallData.call_note || 'N/A'}"`);
      if (editCallData.description !== (selectedCall.description || "")) changes.push(`Description updated`);
      
      // Track perp changes - each change on its own line
      const oldPerps = selectedCall.perp_descriptions || [];
      const newPerps = editCallData.perp_descriptions || [];
      const perpUpdates = [];
      const perpLocationUpdates = []; // Track location/direction changes separately for Location Updates section

      newPerps.forEach((newPerp) => {
        const oldPerp = oldPerps.find(p => p.perp_id === newPerp.perp_id);
        if (oldPerp) {
          const perpNum = oldPerps.indexOf(oldPerp) + 1;
          const fieldChanges = [];
          if (oldPerp.name !== newPerp.name && newPerp.name) fieldChanges.push(`Name: ${newPerp.name}`);
          if (oldPerp.sex !== newPerp.sex && newPerp.sex) fieldChanges.push(`Sex: ${newPerp.sex}`);
          if (oldPerp.race !== newPerp.race && newPerp.race) fieldChanges.push(`Race: ${newPerp.race}`);
          if (oldPerp.age !== newPerp.age && newPerp.age) fieldChanges.push(`Age: ${newPerp.age}`);
          if (oldPerp.height !== newPerp.height && newPerp.height) fieldChanges.push(`Height: ${newPerp.height}`);
          if (oldPerp.weight !== newPerp.weight && newPerp.weight) fieldChanges.push(`Weight: ${newPerp.weight}`);
          if (oldPerp.build !== newPerp.build && newPerp.build) fieldChanges.push(`Build: ${newPerp.build}`);
          if (oldPerp.hair_color_cut !== newPerp.hair_color_cut && newPerp.hair_color_cut) fieldChanges.push(`Hair: ${newPerp.hair_color_cut}`);
          if (oldPerp.clothing !== newPerp.clothing && newPerp.clothing) fieldChanges.push(`Clothing: ${newPerp.clothing}`);
          if (oldPerp.shirt !== newPerp.shirt && newPerp.shirt) fieldChanges.push(`Shirt: ${newPerp.shirt}`);
          if (oldPerp.pants !== newPerp.pants && newPerp.pants) fieldChanges.push(`Pants: ${newPerp.pants}`);

          // Track location/direction changes separately for Location Updates section
          const locationChanged = oldPerp.current_location !== newPerp.current_location && newPerp.current_location;
          const directionChanged = oldPerp.direction_of_flight !== newPerp.direction_of_flight && newPerp.direction_of_flight;
          if (locationChanged) fieldChanges.push(`Location: ${newPerp.current_location}`);
          if (directionChanged) fieldChanges.push(`DOF: ${newPerp.direction_of_flight}`);
          if (locationChanged || directionChanged) {
            perpLocationUpdates.push({
              perp_id: newPerp.perp_id,
              updated_location: locationChanged ? newPerp.current_location : "",
              updated_direction: directionChanged ? newPerp.direction_of_flight : ""
            });
          }

          if (oldPerp.vehicle_description !== newPerp.vehicle_description && newPerp.vehicle_description) fieldChanges.push(`Vehicle: ${newPerp.vehicle_description}`);
          if (oldPerp.has_weapon !== newPerp.has_weapon) fieldChanges.push(`Armed: ${newPerp.has_weapon ? 'Yes' : 'No'}`);
          if (oldPerp.weapon_description !== newPerp.weapon_description && newPerp.weapon_description) fieldChanges.push(`Weapon: ${newPerp.weapon_description}`);
          if (oldPerp.was_arrested !== newPerp.was_arrested) fieldChanges.push(`Arrested: ${newPerp.was_arrested ? 'Yes' : 'No'}`);

          if (fieldChanges.length > 0) {
            perpUpdates.push(`Perp #${perpNum} updated:`);
            fieldChanges.forEach(fc => perpUpdates.push(`  • ${fc}`));
          }
        }
      });
      
      // Check for added perps
      const oldPerpIds = oldPerps.map(p => p.perp_id);
      const addedPerps = newPerps.filter(p => !oldPerpIds.includes(p.perp_id));
      addedPerps.forEach(p => {
        perpUpdates.push(`Perp added: ${[p.sex, p.race, p.age].filter(Boolean).join(' ') || 'Unknown'}`);
      });
      
      // Update the call
      await axios.put(
        `${API}/live-calls/${selectedCall.call_id}`,
        editCallData,
        { withCredentials: true }
      );
      
      // If there were changes, create an update entry
      // IMPORTANT: Only non-perp changes go to general_notes (which gets appended to job narrative)
      // Perp changes are tracked in the update object but NOT appended to job narrative
      if (changes.length > 0 || perpUpdates.length > 0) {
        // Build the update object
        // Note: updated_location is NOT included here because the PUT endpoint
        // already creates its own update entry with the location change
        const updateObj = {
          transmitted_10_85: false,
          transmitted_10_13: false
        };

        // Only add general_notes for non-perp changes (these go to job narrative)
        if (changes.length > 0) {
          updateObj.general_notes = `Call info updated:\n${changes.join('\n')}`;
        }

        // Perp updates go to a separate field that won't be appended to job narrative
        if (perpUpdates.length > 0) {
          // Store perp updates in updated_perp_description field (tracked in updates but not job narrative)
          updateObj.updated_perp_description = perpUpdates.join('\n');
        }

        await axios.post(
          `${API}/live-calls/${selectedCall.call_id}/updates`,
          updateObj,
          { withCredentials: true }
        );
      }

      // Create separate update entries for perp location/direction changes
      // This ensures they show up properly in the Location Updates section
      for (const perpLocUpdate of perpLocationUpdates) {
        await axios.post(
          `${API}/live-calls/${selectedCall.call_id}/updates`,
          {
            perp_id: perpLocUpdate.perp_id,
            updated_location: perpLocUpdate.updated_location,
            updated_direction: perpLocUpdate.updated_direction,
            transmitted_10_85: false,
            transmitted_10_13: false
          },
          { withCredentials: true }
        );
      }
      
      // Fetch the latest call data to ensure we have all updates
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
      
      if (updatedCall) {
        setSelectedCall(updatedCall);
        setLiveCalls(refreshed.data);
      }
      
      setShowEditCallDialog(false);
      toast.success("Call info updated");

      // If the call type or secondary type changed, offer the same WA-Alerts
      // / Resend follow-up dialog as the Status / Radio Change Type button.
      const typeChanged =
        editCallData.call_type !== selectedCall.call_type ||
        (editCallData.secondary_call_type || "") !== (selectedCall.secondary_call_type || "");
      if (typeChanged) {
        openBroadcastDialog('type');
      }
    } catch (error) {
      toast.error("Failed to update call info");
    }
  };

  // Handler for closing the detail view - reset related state
  const handleBackToList = () => {
    setSelectedCall(null);
    setShowUpdateDialog(false);
    setShowEditCallDialog(false);
    setUnitSearch("");
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatTimeOnly = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const filteredUnits = units.filter(u => 
    u.name.toLowerCase().includes(unitSearch.toLowerCase())
  );

  // Sorted calls
  const sortedCalls = [...liveCalls].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Call Info body extracted so it can render both as a standalone
  // card on mobile AND inline inside the Header card on desktop.
  const renderCallInfoBody = () => (
    <>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs uppercase text-slate-500">Call Info</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 px-2 text-[10px]"
                      onClick={() => setShowCallInfo(!showCallInfo)}
                    >
                      <Eye className="w-2.5 h-2.5 mr-1" />
                      {showCallInfo ? "Hide" : "Show"}
                    </Button>
                  </div>
                  {showCallInfo && <div className="space-y-2 text-sm">
                    {/* Callback Numbers - Show all with has_visual */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 text-xs uppercase">Callbacks:</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 px-2 text-[10px] text-blue-400 hover:text-blue-300 flex-shrink-0"
                          onClick={() => setShowCardCallbackForm(!showCardCallbackForm)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      </div>
                      {/* Add Callback Form (requirement #1) */}
                      {showCardCallbackForm && (
                        <div className="p-2 bg-slate-800 rounded border border-slate-700 space-y-1.5">
                          <div className="flex gap-1 items-center">
                            <Input
                              placeholder="Phone..."
                              value={cardCallbackInput.number}
                              onChange={(e) => setCardCallbackInput({ ...cardCallbackInput, number: formatPhoneNumber(e.target.value) })}
                              className="input-dark h-6 text-[11px] flex-1 min-w-0"
                              type="tel"
                              inputMode="numeric"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              title="Paste from clipboard"
                              onClick={async () => {
                                try {
                                  const text = await navigator.clipboard.readText();
                                  if (text) setCardCallbackInput({ ...cardCallbackInput, number: formatPhoneNumber(text.trim()) });
                                } catch (e) { toast.error("Could not access clipboard"); }
                              }}
                              className="h-6 px-1.5"
                            >
                              <ClipboardPaste className="w-3 h-3" />
                            </Button>
                            <Popover modal open={showRecentCalls === "cardAdditional"} onOpenChange={(open) => { setShowRecentCalls(open ? "cardAdditional" : null); if (open) fetchRecentIncomingCalls(); }}>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  title="Recent incoming calls"
                                  className="h-6 px-1.5"
                                >
                                  <PhoneIncoming className="w-3 h-3" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[10001]">
                                <div className="text-xs text-slate-400 mb-2">Recent Incoming Calls (30 min)</div>
                                {loadingRecentCalls ? (
                                  <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                                ) : recentIncomingCalls.length === 0 ? (
                                  <div className="text-xs text-slate-500 text-center py-2">No recent calls</div>
                                ) : (
                                  <div className="space-y-1 max-h-48 overflow-y-auto">
                                    {recentIncomingCalls.map((call, idx) => (
                                      <Button
                                        key={idx}
                                        variant="ghost"
                                        size="sm"
                                        className="w-full justify-between text-xs h-8 px-2"
                                        onClick={() => {
                                          setCardCallbackInput({ ...cardCallbackInput, number: call.caller_number });
                                          setShowRecentCalls(null);
                                          // Auto-mark as hotline call
                                          if (!selectedCall.is_hotline_call) {
                                            handleUpdateHotline(true);
                                          }
                                        }}
                                      >
                                        <span className="flex items-center gap-1.5 truncate">
                                          <Phone className="w-3 h-3 shrink-0" />
                                          {call.caller_number}
                                          {call.contact_name && <span className="text-slate-400 truncate">{call.contact_name}</span>}
                                        </span>
                                        <span className="flex items-center gap-1 shrink-0 ml-1">
                                          <span className="text-[10px] text-slate-500">{formatCallTime(call.received_at)}</span>
                                          {call.contact_status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                                          {call.contact_status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">K</span>}
                                          {call.contact_status && !["flagged","known"].includes(call.contact_status) && <span className="text-[9px] text-slate-500 uppercase">{call.contact_status.slice(0,3)}</span>}
                                        </span>
                                      </Button>
                                    ))}
                                  </div>
                                )}
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div className="flex gap-1">
                            <select
                              value={cardCallbackInput.caller_type}
                              onChange={(e) => setCardCallbackInput({ ...cardCallbackInput, caller_type: e.target.value })}
                              className="flex-1 h-6 text-[11px] bg-slate-800 border border-slate-700 rounded px-1"
                            >
                              <option value="">Type...</option>
                              <option value="cv">CV</option>
                              <option value="3rd_party">3rd Party</option>
                            </select>
                            <label className="flex items-center gap-1 text-[10px] text-slate-400">
                              <Checkbox
                                checked={cardCallbackInput.has_visual}
                                onCheckedChange={(c) => setCardCallbackInput({ ...cardCallbackInput, has_visual: c })}
                                className="h-3 w-3"
                              />
                              Visual
                            </label>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              className="h-6 text-[10px] flex-1"
                              disabled={!cardCallbackInput.number}
                              onClick={async () => {
                                try {
                                  const newCallback = {
                                    callback_id: `cb_${Date.now()}`,
                                    number: cardCallbackInput.number,
                                    caller_type: cardCallbackInput.caller_type,
                                    has_visual: cardCallbackInput.has_visual
                                  };
                                  const updatedCallbacks = [...(selectedCall.callback_numbers || []), newCallback];
                                  await axios.put(
                                    `${API}/live-calls/${selectedCall.call_id}`,
                                    { callback_numbers: updatedCallbacks },
                                    { withCredentials: true }
                                  );
                                  setSelectedCall({ ...selectedCall, callback_numbers: updatedCallbacks });
                                  setCardCallbackInput({ number: "", caller_type: "", has_visual: false });
                                  setShowCardCallbackForm(false);
                                  toast.success("Callback added");
                                } catch (error) {
                                  toast.error("Failed to add callback");
                                }
                              }}
                            >
                              Add
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-[10px]"
                              onClick={() => {
                                setCardCallbackInput({ number: "", caller_type: "", has_visual: false });
                                setShowCardCallbackForm(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                      {((selectedCall.callback_phone || selectedCall.callback_numbers?.length > 0) ? (
                        <div className="space-y-1 pl-2 border-l-2 border-slate-700">
                          {/* Primary callback */}
                          {selectedCall.callback_phone && !selectedCall.callback_numbers?.some(c => c.number === selectedCall.callback_phone) && (
                            editingCardCallbackIdx === 'primary' ? (
                              <div className="flex gap-1 flex-wrap bg-slate-800 p-1 rounded">
                                <Input
                                  value={editingCardCallbackData.number}
                                  onChange={(e) => setEditingCardCallbackData({ ...editingCardCallbackData, number: formatPhoneNumber(e.target.value) })}
                                  placeholder="Number..."
                                  className="input-dark flex-1 min-w-[90px] h-6 text-xs"
                                  type="tel"
                                  inputMode="numeric"
                                  autoFocus
                                />
                                <select
                                  value={editingCardCallbackData.caller_type || ""}
                                  onChange={(e) => setEditingCardCallbackData({ ...editingCardCallbackData, caller_type: e.target.value })}
                                  className="h-6 text-[10px] bg-slate-700 border border-slate-600 rounded px-1"
                                >
                                  <option value="">Type</option>
                                  <option value="cv">CV</option>
                                  <option value="3rd_party">3rd</option>
                                </select>
                                <label className="flex items-center gap-0.5 text-[10px] text-slate-400">
                                  <Checkbox
                                    checked={editingCardCallbackData.has_visual || false}
                                    onCheckedChange={(c) => setEditingCardCallbackData({ ...editingCardCallbackData, has_visual: c })}
                                    className="h-3 w-3"
                                  />
                                  Vis
                                </label>
                                <Button
                                  size="sm"
                                  className="h-6 px-2 text-[10px]"
                                  onClick={async () => {
                                    if (editingCardCallbackData.number.trim()) {
                                      try {
                                        const patch = {
                                          callback_phone: editingCardCallbackData.number,
                                          cv_or_third_party: editingCardCallbackData.caller_type || "",
                                          caller_has_visual: !!editingCardCallbackData.has_visual,
                                        };
                                        await axios.put(
                                          `${API}/live-calls/${selectedCall.call_id}`,
                                          patch,
                                          { withCredentials: true }
                                        );
                                        setSelectedCall({ ...selectedCall, ...patch });
                                        setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? { ...c, ...patch } : c));
                                        toast.success("Callback updated");
                                      } catch (error) {
                                        toast.error("Failed to update callback");
                                      }
                                    }
                                    setEditingCardCallbackIdx(null);
                                  }}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-1 text-[10px]"
                                  onClick={() => setEditingCardCallbackIdx(null)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                            <div className="text-xs">
                              <div className="flex items-center gap-2">
                                <button onClick={() => handlePhoneClick?.(selectedCall.callback_phone)} className="text-blue-400 hover:underline flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {formatPhoneNumber(selectedCall.callback_phone)}
                                </button>
                                {handlePhoneClick && (
                                  <button onClick={(e) => { e.stopPropagation(); handlePhoneClick(selectedCall.callback_phone); }} className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0" title="Call">
                                    <PhoneOutgoing className="w-3 h-3" />
                                  </button>
                                )}
                                <button onClick={() => setPhoneLookupNumber(selectedCall.callback_phone)} className="text-slate-400 hover:text-blue-400" title="Lookup">
                                  <Search className="w-3 h-3" />
                                </button>
                                {selectedCall.cv_or_third_party && (
                                  <Badge variant="outline" className="text-[10px] px-1">
                                    {selectedCall.cv_or_third_party === 'cv' ? 'CV' : '3rd'}
                                  </Badge>
                                )}
                                {selectedCall.caller_has_visual && (
                                  <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1">VISUAL</Badge>
                                )}
                                {(() => {
                                  const info = getContactInfo(selectedCall.callback_phone);
                                  const status = selectedCall.callback_phone_contact_status || info?.status;
                                  return <>
                                    {status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                                    {status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">K</span>}
                                  </>;
                                })()}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-blue-400 ml-auto"
                                  onClick={() => {
                                    setEditingCardCallbackData({
                                      number: selectedCall.callback_phone,
                                      caller_type: selectedCall.cv_or_third_party || "",
                                      has_visual: !!selectedCall.caller_has_visual,
                                    });
                                    setEditingCardCallbackIdx('primary');
                                  }}
                                >
                                  <Edit className="w-2.5 h-2.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-red-400"
                                  onClick={() => setDeleteCallbackConfirm({ isPrimary: true, number: selectedCall.callback_phone })}
                                >
                                  <X className="w-2.5 h-2.5" />
                                </Button>
                              </div>
                              {(() => {
                                const info = getContactInfo(selectedCall.callback_phone);
                                const name = selectedCall.callback_phone_contact_name || info?.name;
                                return name ? <div className="text-[10px] text-yellow-400/80 pl-4 mt-0.5">{name}</div> : null;
                              })()}
                            </div>
                            )
                          )}
                          {/* Additional callback numbers */}
                          {selectedCall.callback_numbers?.map((cb, idx) => (
                            editingCardCallbackIdx === idx ? (
                              <div key={cb.callback_id || idx} className="flex gap-1 flex-wrap bg-slate-800 p-1 rounded">
                                <Input
                                  value={editingCardCallbackData.number}
                                  onChange={(e) => setEditingCardCallbackData({ ...editingCardCallbackData, number: formatPhoneNumber(e.target.value) })}
                                  placeholder="Number..."
                                  className="input-dark flex-1 min-w-[90px] h-6 text-xs"
                                  type="tel"
                                  inputMode="numeric"
                                  autoFocus
                                />
                                <select
                                  value={editingCardCallbackData.caller_type || ""}
                                  onChange={(e) => setEditingCardCallbackData({ ...editingCardCallbackData, caller_type: e.target.value })}
                                  className="h-6 text-[10px] bg-slate-700 border border-slate-600 rounded px-1"
                                >
                                  <option value="">Type</option>
                                  <option value="cv">CV</option>
                                  <option value="3rd_party">3rd</option>
                                </select>
                                <label className="flex items-center gap-0.5 text-[10px] text-slate-400">
                                  <Checkbox
                                    checked={editingCardCallbackData.has_visual || false}
                                    onCheckedChange={(c) => setEditingCardCallbackData({ ...editingCardCallbackData, has_visual: c })}
                                    className="h-3 w-3"
                                  />
                                  Vis
                                </label>
                                <Button
                                  size="sm"
                                  className="h-6 px-2 text-[10px]"
                                  onClick={async () => {
                                    if (editingCardCallbackData.number.trim()) {
                                      try {
                                        const updated = [...selectedCall.callback_numbers];
                                        updated[idx] = { ...updated[idx], ...editingCardCallbackData };
                                        await axios.put(
                                          `${API}/live-calls/${selectedCall.call_id}`,
                                          { callback_numbers: updated },
                                          { withCredentials: true }
                                        );
                                        setSelectedCall({ ...selectedCall, callback_numbers: updated });
                                        toast.success("Callback updated");
                                      } catch (error) {
                                        toast.error("Failed to update callback");
                                      }
                                    }
                                    setEditingCardCallbackIdx(null);
                                  }}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-1 text-[10px]"
                                  onClick={() => setEditingCardCallbackIdx(null)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <div key={cb.callback_id || idx} className="text-xs">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => handlePhoneClick?.(cb.number)} className="text-blue-400 hover:underline flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {formatPhoneNumber(cb.number)}
                                  </button>
                                  {handlePhoneClick && (
                                    <button onClick={(e) => { e.stopPropagation(); handlePhoneClick(cb.number); }} className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0" title="Call">
                                      <PhoneOutgoing className="w-3 h-3" />
                                    </button>
                                  )}
                                  <button onClick={() => setPhoneLookupNumber(cb.number)} className="text-slate-400 hover:text-blue-400" title="Lookup">
                                    <Search className="w-3 h-3" />
                                  </button>
                                  {cb.label && <span className="text-slate-500">({cb.label})</span>}
                                  {cb.caller_type && (
                                    <Badge variant="outline" className="text-[10px] px-1">
                                      {cb.caller_type === 'cv' ? 'CV' : '3rd'}
                                    </Badge>
                                  )}
                                  {cb.has_visual && (
                                    <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1">VISUAL</Badge>
                                  )}
                                  {(() => {
                                    const info = getContactInfo(cb.number);
                                    const status = cb.contact_status || info?.status;
                                    const name = cb.contact_name || info?.name;
                                    return <>
                                      {status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                                      {status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">K</span>}
                                    </>;
                                  })()}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0 text-blue-400 ml-auto"
                                  onClick={() => {
                                    setEditingCardCallbackData({ number: cb.number, caller_type: cb.caller_type, has_visual: cb.has_visual });
                                    setEditingCardCallbackIdx(idx);
                                  }}
                                >
                                  <Edit className="w-2.5 h-2.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-red-400"
                                  onClick={() => setDeleteCallbackConfirm({ idx, number: cb.number })}
                                >
                                  <X className="w-2.5 h-2.5" />
                                </Button>
                                </div>
                                {(() => {
                                  const info = getContactInfo(cb.number);
                                  const name = cb.contact_name || info?.name;
                                  return name ? <div className="text-[10px] text-yellow-400/80 pl-4 mt-0.5">{name}</div> : null;
                                })()}
                              </div>
                            )
                          ))}
                        </div>
                      ) : !showCardCallbackForm && (
                        <span className="text-slate-500 text-xs">N/A</span>
                      ))}
                    </div>

                    {/* Victims - in Call Info */}
                    {(selectedCall.victims?.length > 0 || selectedCall.primary_victim_name) && (
                      <div className="mt-3 pt-3 border-t border-slate-800">
                        <span className="text-slate-500 text-xs uppercase">Victim Contact:</span>
                        <div className="space-y-1.5 mt-1.5">
                          {selectedCall.primary_victim_name && !selectedCall.victims?.some(v => v.name === selectedCall.primary_victim_name) && (
                            <div className="p-1.5 bg-emerald-950/30 border border-emerald-800/50 rounded text-xs">
                              <div className="flex items-center gap-1.5">
                                {selectedCall.primary_victim_contact ? (
                                  <>
                                    <button onClick={() => handlePhoneClick?.(selectedCall.primary_victim_contact)} className="text-blue-400 hover:underline font-medium">{selectedCall.primary_victim_contact}</button>
                                    {handlePhoneClick && (
                                      <button onClick={(e) => { e.stopPropagation(); handlePhoneClick(selectedCall.primary_victim_contact); }} className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0" title="Call">
                                        <PhoneOutgoing className="w-3 h-3" />
                                      </button>
                                    )}
                                    <button onClick={() => setPhoneLookupNumber(selectedCall.primary_victim_contact)} className="text-slate-400 hover:text-blue-400" title="Lookup">
                                      <Search className="w-3 h-3" />
                                    </button>
                                  </>
                                ) : null}
                                <Badge className="text-[9px] px-1 py-0 bg-emerald-700">VICTIM</Badge>
                              </div>
                              <div className="text-slate-400 mt-0.5">{selectedCall.primary_victim_name}</div>
                            </div>
                          )}
                          {selectedCall.victims?.map((v, idx) => {
                            const num = v.contact || v.phone1;
                            return (
                              <div key={idx} className="p-1.5 bg-emerald-950/30 border border-emerald-800/50 rounded text-xs">
                                <div className="flex items-center gap-1.5">
                                  {num ? (
                                    <>
                                      <button onClick={() => handlePhoneClick?.(num)} className="text-blue-400 hover:underline font-medium">{num}</button>
                                      {handlePhoneClick && (
                                        <button onClick={(e) => { e.stopPropagation(); handlePhoneClick(num); }} className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0" title="Call">
                                          <PhoneOutgoing className="w-3 h-3" />
                                        </button>
                                      )}
                                      <button onClick={() => setPhoneLookupNumber(num)} className="text-slate-400 hover:text-blue-400" title="Lookup">
                                        <Search className="w-3 h-3" />
                                      </button>
                                    </>
                                  ) : null}
                                  <Badge className="text-[9px] px-1 py-0 bg-emerald-700">VICTIM</Badge>
                                </div>
                                {v.name && <div className="text-slate-400 mt-0.5">{v.name}</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Witnesses - in Call Info */}
                    {(selectedCall.witnesses?.length > 0 || selectedCall.primary_witness_name) && (
                      <div className="mt-3 pt-3 border-t border-slate-800">
                        <span className="text-slate-500 text-xs uppercase">Witness Contact:</span>
                        <div className="space-y-1.5 mt-1.5">
                          {selectedCall.primary_witness_name && !selectedCall.witnesses?.some(w => w.name === selectedCall.primary_witness_name) && (
                            <div className="p-1.5 bg-sky-950/30 border border-sky-800/50 rounded text-xs">
                              <div className="flex items-center gap-1.5">
                                {selectedCall.primary_witness_contact ? (
                                  <>
                                    <button onClick={() => handlePhoneClick?.(selectedCall.primary_witness_contact)} className="text-blue-400 hover:underline font-medium">{selectedCall.primary_witness_contact}</button>
                                    {/^\+?[\d\s\-\(\)]{7,}$/.test(String(selectedCall.primary_witness_contact).trim()) && handlePhoneClick && (
                                      <button onClick={(e) => { e.stopPropagation(); handlePhoneClick(selectedCall.primary_witness_contact); }} className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0" title="Call">
                                        <PhoneOutgoing className="w-3 h-3" />
                                      </button>
                                    )}
                                    {/^\+?[\d\s\-\(\)]{7,}$/.test(String(selectedCall.primary_witness_contact).trim()) && (
                                      <button onClick={() => setPhoneLookupNumber(selectedCall.primary_witness_contact)} className="text-slate-400 hover:text-blue-400" title="Lookup">
                                        <Search className="w-3 h-3" />
                                      </button>
                                    )}
                                  </>
                                ) : null}
                                <Badge className="text-[9px] px-1 py-0 bg-sky-700">WITNESS</Badge>
                              </div>
                              <div className="text-slate-400 mt-0.5">{selectedCall.primary_witness_name}</div>
                            </div>
                          )}
                          {selectedCall.witnesses?.map((w, idx) => {
                            const c = w.contact || w.contact_info;
                            const isPhone = c && /^\+?[\d\s\-\(\)]{7,}$/.test(String(c).trim());
                            return (
                              <div key={idx} className="p-1.5 bg-sky-950/30 border border-sky-800/50 rounded text-xs">
                                <div className="flex items-center gap-1.5">
                                  {c ? (
                                    <>
                                      <button onClick={() => handlePhoneClick?.(c)} className="text-blue-400 hover:underline font-medium">{c}</button>
                                      {isPhone && handlePhoneClick && (
                                        <button onClick={(e) => { e.stopPropagation(); handlePhoneClick(c); }} className="p-0.5 rounded hover:bg-slate-700 text-green-400 hover:text-green-300 shrink-0" title="Call">
                                          <PhoneOutgoing className="w-3 h-3" />
                                        </button>
                                      )}
                                      {isPhone && (
                                        <button onClick={() => setPhoneLookupNumber(c)} className="text-slate-400 hover:text-blue-400" title="Lookup">
                                          <Search className="w-3 h-3" />
                                        </button>
                                      )}
                                    </>
                                  ) : null}
                                  <Badge className="text-[9px] px-1 py-0 bg-sky-700">WITNESS</Badge>
                                </div>
                                {w.name && <div className="text-slate-400 mt-0.5">{w.name}</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Add Victim/Witness Button */}
                    <div className="mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[10px] w-full border-dashed"
                        onClick={() => { setAddVicWitData({ victimName: "", victimContact: "", witnessName: "", witnessContact: "" }); setShowAddVicWitDialog(true); }}
                      >
                        <Plus className="w-3 h-3 mr-1" />Add Victim / Witness
                      </Button>
                    </div>

                    {/* Add Victim/Witness Dialog */}
                    <Dialog open={showAddVicWitDialog} onOpenChange={(open) => { setShowAddVicWitDialog(open); if (open) { fetchRecentIncomingCalls(); setVicWitContactSearch(""); } }}>
                      <DialogContent className="bg-slate-900 border-slate-800 max-w-sm max-h-[85vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
                        <DialogHeader>
                          <DialogTitle>Add Victim / Witness</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 mt-2">
                          {/* Quick Fill from Recent Calls / Hotline Contacts */}
                          <div className="p-2 bg-slate-800 rounded space-y-2">
                            <p className="text-[10px] text-slate-400 uppercase font-semibold">Quick Fill from Hotline</p>
                            <Input
                              value={vicWitContactSearch}
                              onChange={(e) => {
                                const val = e.target.value;
                                setVicWitContactSearch(val);
                                if (vicWitSearchTimeout.current) clearTimeout(vicWitSearchTimeout.current);
                                if (val.trim().length >= 2) {
                                  setVicWitSearchLoading(true);
                                  vicWitSearchTimeout.current = setTimeout(async () => {
                                    try {
                                      const res = await axios.get(`${API}/hotline/contacts?search=${encodeURIComponent(val.trim())}`, { withCredentials: true });
                                      setVicWitSearchResults(res.data?.contacts || res.data || []);
                                    } catch { setVicWitSearchResults([]); }
                                    setVicWitSearchLoading(false);
                                  }, 300);
                                } else {
                                  setVicWitSearchResults([]);
                                  setVicWitSearchLoading(false);
                                }
                              }}
                              placeholder="Search contacts by name or phone..."
                              className="input-dark h-7 text-xs"
                            />
                            {/* Search results */}
                            {vicWitContactSearch.trim().length >= 2 && (
                              vicWitSearchLoading ? (
                                <div className="text-center py-1"><RefreshCw className="w-3 h-3 animate-spin mx-auto text-slate-500" /></div>
                              ) : vicWitSearchResults.length === 0 ? (
                                <p className="text-[10px] text-slate-500">No contacts found</p>
                              ) : (
                                <div className="space-y-1 max-h-28 overflow-y-auto">
                                  {vicWitSearchResults.map((c, idx) => (
                                    <div key={c.contact_id || idx} className="flex items-center gap-2 text-xs bg-slate-900 rounded px-2 py-1">
                                      <div className="flex-1 min-w-0">
                                        {c.name && <span className="text-slate-300 truncate">{c.name}</span>}
                                        <span className="text-slate-500 ml-1">{c.phone_number}</span>
                                      </div>
                                      <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] text-emerald-400" onClick={() => setAddVicWitData({ ...addVicWitData, victimName: c.name || "", victimContact: formatPhoneNumber(c.phone_number) })}>Victim</Button>
                                      <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] text-sky-400" onClick={() => setAddVicWitData({ ...addVicWitData, witnessName: c.name || "", witnessContact: formatPhoneNumber(c.phone_number) })}>Witness</Button>
                                    </div>
                                  ))}
                                </div>
                              )
                            )}
                            {/* Recent incoming calls */}
                            {!vicWitContactSearch.trim() && (
                              <>
                                <p className="text-[10px] text-slate-500">Recent Calls (30 min)</p>
                                {loadingRecentCalls ? (
                                  <div className="text-center py-1"><RefreshCw className="w-3 h-3 animate-spin mx-auto text-slate-500" /></div>
                                ) : recentIncomingCalls.length === 0 ? (
                                  <p className="text-[10px] text-slate-500 text-center">No recent calls</p>
                                ) : (
                                  <div className="space-y-1 max-h-28 overflow-y-auto">
                                    {recentIncomingCalls.map((call, idx) => {
                                      const contactInfo = getContactInfo(call.caller_number);
                                      return (
                                        <div key={idx} className="flex items-center gap-2 text-xs bg-slate-900 rounded px-2 py-1">
                                          <div className="flex-1 min-w-0">
                                            <span className="text-slate-300">{call.caller_number}</span>
                                            {contactInfo?.name && <span className="text-slate-500 ml-1">{contactInfo.name}</span>}
                                            <span className="text-[10px] text-slate-500 ml-1">{formatCallTime(call.received_at)}</span>
                                          </div>
                                          <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] text-emerald-400" onClick={() => setAddVicWitData({ ...addVicWitData, victimName: contactInfo?.name || "", victimContact: formatPhoneNumber(call.caller_number) })}>Victim</Button>
                                          <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] text-sky-400" onClick={() => setAddVicWitData({ ...addVicWitData, witnessName: contactInfo?.name || "", witnessContact: formatPhoneNumber(call.caller_number) })}>Witness</Button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          {/* Victim Section */}
                          <div className="p-3 bg-emerald-950/30 border border-emerald-800/50 rounded space-y-2">
                            <p className="text-xs text-emerald-400 font-semibold uppercase">Victim</p>
                            <Input
                              value={addVicWitData.victimName}
                              onChange={(e) => setAddVicWitData({ ...addVicWitData, victimName: e.target.value })}
                              placeholder="Victim Name"
                              className="input-dark h-8"
                            />
                            <div className="flex gap-1">
                              <Input
                                value={addVicWitData.victimContact}
                                onChange={(e) => setAddVicWitData({ ...addVicWitData, victimContact: formatPhoneNumber(e.target.value) })}
                                placeholder="Victim Phone"
                                className="input-dark h-8 flex-1"
                                inputMode="tel"
                              />
                              <Button variant="outline" size="sm" className="h-8 px-2" title="Paste" onClick={async () => { try { const t = await navigator.clipboard.readText(); if (t) setAddVicWitData({ ...addVicWitData, victimContact: formatPhoneNumber(t.trim()) }); } catch { toast.error("Could not access clipboard"); } }}>
                                <ClipboardPaste className="w-3.5 h-3.5" />
                              </Button>
                              {addVicWitData.victimContact && (
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-blue-400" title="Lookup" onClick={() => setPhoneLookupNumber(addVicWitData.victimContact)}>
                                  <Search className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Witness Section */}
                          <div className="p-3 bg-sky-950/30 border border-sky-800/50 rounded space-y-2">
                            <p className="text-xs text-sky-400 font-semibold uppercase">Witness</p>
                            <Input
                              value={addVicWitData.witnessName}
                              onChange={(e) => setAddVicWitData({ ...addVicWitData, witnessName: e.target.value })}
                              placeholder="Witness Name"
                              className="input-dark h-8"
                            />
                            <div className="flex gap-1">
                              <Input
                                value={addVicWitData.witnessContact}
                                onChange={(e) => { const v = e.target.value; setAddVicWitData({ ...addVicWitData, witnessContact: /^[\d\s\-\(\)\+]*$/.test(v) ? formatPhoneNumber(v) : v }); }}
                                placeholder="Witness Phone / Email"
                                className="input-dark h-8 flex-1"
                              />
                              <Button variant="outline" size="sm" className="h-8 px-2" title="Paste" onClick={async () => { try { const t = await navigator.clipboard.readText(); if (t) setAddVicWitData({ ...addVicWitData, witnessContact: formatPhoneNumber(t.trim()) }); } catch { toast.error("Could not access clipboard"); } }}>
                                <ClipboardPaste className="w-3.5 h-3.5" />
                              </Button>
                              {addVicWitData.witnessContact && /^\+?[\d\s\-\(\)]{7,}$/.test(addVicWitData.witnessContact.trim()) && (
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-blue-400" title="Lookup" onClick={() => setPhoneLookupNumber(addVicWitData.witnessContact)}>
                                  <Search className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <Button
                            className="w-full"
                            onClick={async () => {
                              const updates = {};
                              if (addVicWitData.victimName.trim() || addVicWitData.victimContact.trim()) {
                                updates.victims = [...(selectedCall.victims || []), { victim_id: `vic_${Date.now()}`, name: addVicWitData.victimName, contact: addVicWitData.victimContact }];
                              }
                              if (addVicWitData.witnessName.trim() || addVicWitData.witnessContact.trim()) {
                                updates.witnesses = [...(selectedCall.witnesses || []), { witness_id: `wit_${Date.now()}`, name: addVicWitData.witnessName, contact: addVicWitData.witnessContact }];
                              }
                              if (Object.keys(updates).length === 0) { setShowAddVicWitDialog(false); return; }
                              try {
                                const response = await axios.put(`${API}/live-calls/${selectedCall.call_id}`, updates, { withCredentials: true });
                                setSelectedCall(response.data);
                                setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                                toast.success("Victim/Witness added");
                                setShowAddVicWitDialog(false);
                              } catch (error) { toast.error("Failed to add"); }
                            }}
                          >
                            Save
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Job Narrative - Merged into Call Info */}
                    <div id="section-jobbody" className="mt-3 pt-3 border-t border-slate-800">
                      <span className="text-slate-500 text-xs uppercase">Job Narrative:</span>
                      <div className="bg-slate-800 border border-slate-800 rounded-sm p-2 mt-2">
                        {selectedCall.call_note ? (
                          <p className="text-sm text-slate-300 whitespace-pre-wrap">{selectedCall.call_note}</p>
                        ) : (
                          <p className="text-sm text-slate-500 italic">No job narrative</p>
                        )}
                      </div>
                    </div>
                  </div>}
    </>
  );
  // Extracted so it can render in two places: mobile (above Actions) and desktop (in section-units)
  const renderAssignedUnitsCard = () => selectedCall ? (
                <Card className="bg-slate-800 border-slate-800 h-full flex flex-col min-h-0">
                  <CardHeader className="pb-2 flex-shrink-0">
                    <CardTitle className="text-xs uppercase text-slate-400 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Assigned Units ({selectedCall.assigned_units?.length || 0})
                      </div>
                      <div className="flex items-center gap-1">
                        {selectedCall.assigned_units?.length > 0 && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[10px] font-bold hover:bg-blue-900/30 hover:text-blue-400 hover:border-blue-500"
                              onClick={() => setShowAll84Confirm(true)}
                            >
                              All 84
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[10px] font-bold hover:bg-green-900/30 hover:text-green-400 hover:border-green-500"
                              onClick={() => setShowAll98Confirm(true)}
                            >
                              All 98
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => setShowAssignedUnits(!showAssignedUnits)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          {showAssignedUnits ? "Hide" : "Show"}
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  {showAssignedUnits && (
                  <CardContent className="p-3 pt-0 flex-1 overflow-auto min-h-0">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(selectedCall.patrol_cars_assigned || []).map((carId) => {
                        const assignedCar = patrolCars.find(c => c.car_id === carId);
                        if (!assignedCar) return null;
                        const carUnits = [assignedCar.assigned_unit, ...(assignedCar.joined_units || [])].filter(Boolean);
                        const anyUnit98 = carUnits.some(u => selectedCall.unit_statuses?.[u]?.status_98);
                        const anyUnit84 = carUnits.some(u => selectedCall.unit_statuses?.[u]?.status_84);
                        const badgeColor = anyUnit98
                          ? 'bg-green-600 text-white border-green-500'
                          : anyUnit84
                          ? 'bg-blue-600 text-white border-blue-500'
                          : 'bg-purple-600 text-white border-purple-500';
                        const statusLabel = anyUnit98 ? '98' : anyUnit84 ? '84' : '';
                        const badgeHoverColor = anyUnit98
                          ? 'hover:bg-green-700'
                          : anyUnit84
                          ? 'hover:bg-blue-700'
                          : 'hover:bg-purple-700';
                        const isLongPressing = longPressPatrolCar === carId;
                        return (
                          <Badge
                            key={carId}
                            className={`font-mono flex items-center gap-1 cursor-pointer transition-all select-none px-2.5 py-1 ${badgeColor} ${badgeHoverColor} ${isLongPressing ? 'ring-2 ring-red-500 animate-pulse' : ''}`}
                            onClick={async () => {
                              try {
                                let newStatus84 = false;
                                let newStatus98 = false;
                                if (!anyUnit84 && !anyUnit98) {
                                  newStatus84 = true;
                                } else if (anyUnit84 && !anyUnit98) {
                                  newStatus98 = true;
                                }
                                let newStatuses = { ...selectedCall.unit_statuses };
                                for (const unitName of carUnits) {
                                  if (selectedCall.assigned_units?.includes(unitName)) {
                                    await axios.post(`${API}/live-calls/${selectedCall.call_id}/unit-status`,
                                      { unit_name: unitName, status_84: newStatus84, status_98: newStatus98 },
                                      { withCredentials: true }
                                    );
                                    newStatuses[unitName] = { ...newStatuses[unitName], status_84: newStatus84, status_98: newStatus98 };
                                  }
                                }
                                const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
                                const updatedCall = refreshed.data.find(c => c.call_id === selectedCall.call_id);
                                if (updatedCall) {
                                  setSelectedCall(updatedCall);
                                  setLiveCalls(refreshed.data);
                                }
                                const statusMsg = newStatus98 ? '98' : newStatus84 ? '84' : 'cleared';
                                toast.success(`${assignedCar.name} units → ${statusMsg}`);
                              } catch (error) {
                                toast.error("Failed to update status");
                              }
                            }}
                            onMouseDown={() => handlePatrolCarLongPressStart(carId)}
                            onMouseUp={handlePatrolCarLongPressEnd}
                            onMouseLeave={handlePatrolCarLongPressEnd}
                            onTouchStart={() => handlePatrolCarLongPressStart(carId)}
                            onTouchEnd={handlePatrolCarLongPressEnd}
                            title={`${[assignedCar.assigned_unit, ...(assignedCar.joined_units || [])].filter(Boolean).join(', ')} - Click to cycle. Hold 1s to remove.`}
                          >
                            <Car className="w-3 h-3" />
                            {assignedCar.name}{statusLabel && <span className="ml-1 opacity-80">({statusLabel})</span>}
                          </Badge>
                        );
                      })}
                      {[...(selectedCall.assigned_units || [])].sort((a, b) => {
                        const numA = parseInt(a.replace(/\D/g, '')) || 0;
                        const numB = parseInt(b.replace(/\D/g, '')) || 0;
                        if (numA !== numB) return numA - numB;
                        return a.localeCompare(b);
                      }).map((unit) => {
                        const unitStatus = selectedCall.unit_statuses?.[unit] || {};
                        const isLongPressing = longPressUnit === unit;
                        const unitColor = unitStatus.status_standby
                          ? 'bg-amber-500 text-white border-amber-400 hover:bg-amber-600'
                          : unitStatus.status_98
                          ? 'bg-green-600 text-white border-green-500 hover:bg-green-700'
                          : unitStatus.status_84
                          ? 'bg-blue-600 text-white border-blue-500 hover:bg-blue-700'
                          : 'bg-slate-300 dark:bg-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-400 dark:hover:bg-slate-500';
                        const statusLabel = unitStatus.status_standby ? 'STBY' : unitStatus.status_98 ? '98' : unitStatus.status_84 ? '84' : '';
                        return (
                          <Badge
                            key={unit}
                            className={`font-mono cursor-pointer transition-all select-none px-2.5 py-1 ${unitColor} ${isLongPressing ? 'ring-2 ring-red-500 animate-pulse' : ''}`}
                            onClick={() => handleCycleUnitStatus(unit)}
                            onMouseDown={() => handleUnitLongPressStart(unit)}
                            onMouseUp={handleUnitLongPressEnd}
                            onMouseLeave={handleUnitLongPressEnd}
                            onTouchStart={() => handleUnitLongPressStart(unit)}
                            onTouchEnd={handleUnitLongPressEnd}
                            title="Click to cycle status. Hold 1s to remove."
                          >
                            {unit}{statusLabel && <span className="ml-1 opacity-80">({statusLabel})</span>}
                          </Badge>
                        );
                      })}
                      {(!selectedCall.assigned_units || selectedCall.assigned_units.length === 0) && (!selectedCall.patrol_cars_assigned || selectedCall.patrol_cars_assigned.length === 0) && (
                        <span className="text-xs text-slate-500">No units assigned</span>
                      )}
                    </div>
                    {(() => {
                      // Compute the keyboard-navigable list once so the input handler
                      // and the rendered list stay in sync.
                      const availableUnits = filteredUnits.filter(u => !selectedCall.assigned_units?.includes(u.name));
                      return (
                        <>
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <Input
                              ref={unitSearchInputRef}
                              value={unitSearch}
                              onChange={(e) => { setUnitSearch(e.target.value); setUnitSearchHighlight(0); }}
                              // No auto-scroll on tap: every variant we tried (onFocus,
                              // onPointerDown, onClick + delayed scrollIntoView, direct
                              // scrollTop) ended up wedging the mobile layout where the
                              // panel content extended past the floating buttons. Users
                              // can still jump to Assigned Units via the bottom-left
                              // Quick Nav.
                              onKeyDown={(e) => {
                                if (!unitSearch || availableUnits.length === 0) return;
                                if (e.key === "ArrowDown") {
                                  e.preventDefault();
                                  setUnitSearchHighlight(prev => (prev < availableUnits.length - 1 ? prev + 1 : 0));
                                } else if (e.key === "ArrowUp") {
                                  e.preventDefault();
                                  setUnitSearchHighlight(prev => (prev > 0 ? prev - 1 : availableUnits.length - 1));
                                } else if (e.key === "Enter") {
                                  e.preventDefault();
                                  const idx = unitSearchHighlight >= 0 && unitSearchHighlight < availableUnits.length ? unitSearchHighlight : 0;
                                  handleAssignUnit(availableUnits[idx].name, true);
                                  setUnitSearchHighlight(0);
                                } else if (e.key === "Escape") {
                                  e.preventDefault();
                                  setUnitSearch("");
                                  setUnitSearchHighlight(0);
                                }
                              }}
                              placeholder="Search units to assign..."
                              className="input-dark pl-8 h-8 text-sm"
                              inputMode="numeric"
                              autoComplete="off"
                            />
                          </div>
                          {unitSearch && (
                            <div className="mt-2">
                              <ScrollArea className="max-h-40">
                                <div className="space-y-1">
                                  {availableUnits.map((unit, idx) => (
                                    <div
                                      key={unit.unit_id}
                                      className={`p-2 text-sm rounded flex items-center gap-2 cursor-pointer ${longPressUnit === unit.name ? "ring-2 ring-purple-500 animate-pulse" : idx === unitSearchHighlight ? "bg-blue-900/40 ring-1 ring-blue-500" : "hover:bg-slate-800"}`}
                                      onMouseDown={(e) => { e.preventDefault(); handleDropdownUnitLongPressStart(unit.name); }}
                                      onMouseUp={handleDropdownUnitLongPressEnd}
                                      onMouseLeave={handleDropdownUnitLongPressEnd}
                                      onTouchStart={(e) => { e.preventDefault(); handleDropdownUnitLongPressStart(unit.name); }}
                                      onTouchEnd={handleDropdownUnitLongPressEnd}
                                      onMouseEnter={() => setUnitSearchHighlight(idx)}
                                      onClick={() => {
                                        if (dropdownLongPressFiredRef.current) { dropdownLongPressFiredRef.current = false; return; }
                                        handleAssignUnit(unit.name, true);
                                      }}
                                      title="Tap to assign · Hold to assign with a status (84 / 98 / Standby)"
                                    >
                                      <Plus className="w-4 h-4 text-blue-400" />
                                      <span className="flex-1">{unit.name}</span>
                                    </div>
                                  ))}
                                </div>
                              </ScrollArea>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {(() => {
                      const availableCars = patrolCars.filter(c => {
                        if (c.status !== 'active_patrol') return false;
                        const isAssigned = (selectedCall.patrol_cars_assigned || []).includes(c.car_id);
                        if (!isAssigned) return true;
                        const carUnits = [c.assigned_unit, ...(c.joined_units || [])].filter(Boolean);
                        const allUnits98 = carUnits.length > 0 && carUnits.every(u => selectedCall.unit_statuses?.[u]?.status_98);
                        return allUnits98;
                      });
                      return availableCars.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-800">
                          <p className="text-xs text-slate-500 uppercase mb-2 flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            Active Patrol ({availableCars.length})
                          </p>
                          <div className="space-y-1.5">
                            {availableCars.map((car) => (
                              <div
                                key={car.car_id}
                                className="flex items-center justify-between p-1.5 rounded border text-xs bg-emerald-950/30 border-emerald-800/50 hover:bg-emerald-950/50 cursor-pointer hover:ring-1 hover:ring-slate-600"
                                onClick={() => {
                                  setPatrolCarToAssign(car);
                                  setShowPatrolCarConfirm(true);
                                }}
                                title={`Click to assign ${car.name}`}
                              >
                                <div className="flex items-center gap-2">
                                  <Car className="w-3 h-3 text-emerald-400" />
                                  <span className="font-medium text-emerald-300">{car.name}</span>
                                </div>
                                <div className="flex items-center gap-1 flex-wrap justify-end">
                                  <Badge className="bg-emerald-600 text-white text-[10px] px-1.5 py-0">{car.assigned_unit}</Badge>
                                  {car.joined_units?.map((u) => (
                                    <Badge key={u} variant="outline" className="border-emerald-600 text-emerald-400 text-[10px] px-1.5 py-0">+{u}</Badge>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    {selectedCall.unit_assignment_history?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-800">
                        <p className="text-xs text-slate-500 uppercase mb-2">
                          Assignment History ({selectedCall.unit_assignment_history.length})
                        </p>
                        <ScrollArea className="h-24">
                          <div className="space-y-1 text-xs pr-3">
                            {[...selectedCall.unit_assignment_history].reverse().map((h, i) => {
                              const isPatrolCar = h.action.includes('patrol car');
                              const is84 = h.action.includes('84');
                              const is98 = h.action.includes('98');
                              const isStby = h.action.includes('STBY') || h.action.toLowerCase().includes('standby');
                              const actionColor = h.action === 'assigned' || h.action.includes('assigned') ? 'text-green-400' : isStby ? 'text-amber-400' : is98 ? 'text-green-400' : is84 ? 'text-blue-400' : h.action.includes('status') ? 'text-blue-400' : h.action.includes('unassigned') || h.action.includes('removed') || h.action.includes('cleared') ? 'text-red-400' : 'text-slate-400';
                              return (
                                <div key={i} className="text-slate-400 text-[11px]">
                                  <span className="font-mono text-slate-500 text-[10px]">{formatTimeOnly(h.timestamp)}</span>
                                  {' '}
                                  <span className={actionColor}>{h.action}</span>
                                  {' '}<span className="font-medium text-slate-300">{h.unit}</span>
                                  {h.by && <span className="text-slate-600"> by {h.by}</span>}
                                </div>
                              );
                            })}
                          </div>
                        </ScrollArea>
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-slate-800">
                      <p className="text-xs text-slate-500 uppercase mb-2 flex items-center gap-1">
                        <Radio className="w-3 h-3" />
                        Dispatcher
                      </p>
                      <div className="space-y-2">
                        {selectedCall.dispatcher_units?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {selectedCall.dispatcher_units.map((unit) => (
                              <Badge key={unit} variant="outline" className="border-amber-500 text-amber-400 flex items-center gap-1">
                                {unit}
                                <X
                                  className="w-3 h-3 cursor-pointer hover:text-red-400"
                                  onClick={() => handleUpdateDispatcher(selectedCall.dispatcher_units.filter(u => u !== unit))}
                                />
                              </Badge>
                            ))}
                          </div>
                        )}
                        <Select
                          value=""
                          onValueChange={(value) => {
                            const current = selectedCall.dispatcher_units || [];
                            if (!current.includes(value)) {
                              handleUpdateDispatcher([...current, value]);
                            }
                          }}
                        >
                          <SelectTrigger className="input-dark h-8 text-sm">
                            <SelectValue placeholder="Add dispatcher..." />
                          </SelectTrigger>
                          <SelectContent>
                            {units
                              .filter(u => u.is_dispatcher && !selectedCall.dispatcher_units?.includes(u.name))
                              .map((unit) => (
                                <SelectItem key={unit.unit_id} value={unit.name}>{unit.name}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-800">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-purple-400" />
                          <Label className="text-xs text-slate-400 uppercase">Hotline Call</Label>
                        </div>
                        <Checkbox
                          checked={selectedCall.is_hotline_call || false}
                          onCheckedChange={(checked) => handleUpdateHotline(checked)}
                        />
                      </div>
                    </div>
                  </CardContent>
                  )}
                </Card>
  ) : null;

  // Notify section shown inside the 10-85 / 10-13 confirm dialogs when
  // "All 85/13" is OFF on this call. Lets the user opt in to a one-time
  // notification — either forced to ALL users (flips whatsapp_8513_enabled
  // on) or routed via the call's preset recipients (notification_group_ids
  // + wa_bot_group_jids). Either choice also auto-enables WA Alerts.
  const renderNotify8513Section = () => {
    if (!selectedCall) return null;
    const ids = selectedCall.notification_group_ids || [];
    const isNoneIndividuals = ids.includes("__none__");
    const presetIndividualsLabel = isNoneIndividuals
      ? "None"
      : ids.length > 0
        ? notificationGroups.filter(g => ids.includes(g.group_id)).map(g => g.name).join(", ") || "Selected groups"
        : "All Users";
    const jids = selectedCall.wa_bot_group_jids || [];
    const isNoneGroups = jids.includes("__none__");
    let presetGroupsLabel;
    if (isNoneGroups) {
      presetGroupsLabel = "None";
    } else if (jids.length === 0) {
      presetGroupsLabel = "All configured groups";
    } else {
      const names = jids.map(jid => {
        const g = waBotGroups.find(g => g.id === jid);
        if (g) return g.name;
        const col = waBotGroupCollections.find(c => (c.jids || []).includes(jid));
        return col ? col.name : jid.split("@")[0];
      });
      presetGroupsLabel = [...new Set(names)].join(", ");
    }
    return (
      <div className="mt-2 mb-2 rounded-md border border-slate-700 bg-slate-800/60 p-3 space-y-2">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={notify8513Enabled}
            onChange={(e) => setNotify8513Enabled(e.target.checked)}
            className="w-5 h-5 rounded border-slate-500 bg-slate-900 accent-red-500"
          />
          <span className="text-sm font-semibold text-red-300">Notify</span>
        </label>
        <p className="text-[10px] text-slate-500 ml-7">
          All 85/13 is OFF on this call — opt in here to fire push + WhatsApp alert (uses the same presets as Resend). WA Alerts will auto-enable.
        </p>
        {notify8513Enabled && (
          <div className="ml-7 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Send to:</span>
              <Select value={notify8513Mode} onValueChange={setNotify8513Mode}>
                <SelectTrigger className="h-7 w-44 text-xs bg-slate-900 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All 85/13 (everyone)</SelectItem>
                  <SelectItem value="preset">Use call preset</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-0.5 text-[10px] text-slate-500">
              {notify8513Mode === "all" ? (
                <p>Notify: <span className="text-red-300 font-medium">ALL users + ALL configured WhatsApp groups</span></p>
              ) : (
                <>
                  <p>Notify individuals: <span className={isNoneIndividuals ? "text-red-400" : "text-slate-300"}>{presetIndividualsLabel}</span></p>
                  <p>WA Groups: <span className={isNoneGroups ? "text-red-400" : "text-slate-300"}>{presetGroupsLabel}</span></p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Dispatcher checklist data — auto-derived from the call. Shared by the
  // detail-panel "Checklist" tab and the standalone checklist dialog so the two
  // never drift.
  const computeChecklist = () => {
    if (!selectedCall) return { items: [], completed: 0 };
    const isRadioCodeActive = (patterns) => {
      if (!selectedCall.custom_codes || !customRadioCodes?.length) return false;
      return customRadioCodes.some(code => {
        if (!selectedCall.custom_codes[code.id]) return false;
        const label = (code.label || "").toLowerCase();
        return patterns.some(p => p.test(label));
      });
    };
    const perps = selectedCall.perp_descriptions || [];
    const perpLocated = (p) => !!(p.is_visual || p.is_detained || p.is_holding || p.was_arrested);
    const perpHasClothing = (p) => !!(p.hat_color_style || p.shirt || p.pants || p.jacket_coat || p.shoes || p.clothing);
    const perpCount = perps.length;
    const locatedCount = perps.filter(perpLocated).length;
    const positiveIdCount = perps.filter(p => !!p.is_positive_id).length;
    const descripCount = perps.filter(perpHasClothing).length;
    const unitStatuses = selectedCall.unit_statuses || {};
    const assignedUnits = selectedCall.assigned_units || [];
    const assignedCount = assignedUnits.length;
    const units84Count = assignedUnits.filter(u => unitStatuses[u]?.status_84).length;
    const units98Count = assignedUnits.filter(u => unitStatuses[u]?.status_98).length;
    const cvCount = (selectedCall.cv_obtained_units || []).length;
    const items = [
      { label: "Call priority",               done: selectedCall.priority === "priority" },
      { label: "Units assigned",              done: assignedCount > 0 },
      { label: "Units 84",                    done: units84Count > 0,               count: units84Count,    total: assignedCount },
      { label: "CV obtained",                 done: cvCount > 0 || isRadioCodeActive([/cv\s*obtain/, /^cv$/]), count: cvCount },
      { label: "Incident Confirmed",          done: selectedCall.confirmation_status === "confirmed" },
      { label: "Perp Descrip obtained",       done: descripCount > 0,               count: descripCount,    total: perpCount },
      { label: "Perp located",                done: locatedCount > 0,               count: locatedCount,    total: perpCount },
      { label: "Positive ID",                 done: positiveIdCount > 0,            count: positiveIdCount, total: perpCount },
      { label: "PD activated",                done: !!selectedCall.pd_activated || isRadioCodeActive([/pd\s*activat/, /\bpd\s*84\b/]) },
      { label: "Report Filed",                done: isRadioCodeActive([/report\s*fil/]) },
      { label: "Injuries / EMS",              done: !!selectedCall.hems_activated || !!selectedCall.ems_activated || isRadioCodeActive([/injur/, /ems\s*request/, /ems\s*needed/, /ems\s*84/, /\bh\s*84\b/, /\bh\s*activat/, /\bh\s*request/]) },
      { label: "Hate Crime",                  done: !!selectedCall.is_hate_crime || isRadioCodeActive([/hate\s*crime/]) },
      { label: "Security Footage Required",   done: isRadioCodeActive([/security\s*footage/, /footage\s*needed/, /footage\s*requested/]) },
      { label: "No Outstanding Perps",        done: perpCount > 0 && locatedCount === perpCount, count: locatedCount, total: perpCount },
      { label: "Units 98",                    done: assignedCount > 0 && units98Count === assignedCount, count: units98Count, total: assignedCount },
      { label: "Final Obtained",              done: !!(selectedCall.call_final && String(selectedCall.call_final).trim()) },
    ];
    return { items, completed: items.filter(i => i.done).length };
  };

  // The checklist item rows (shared markup for the tab + dialog).
  const renderChecklistRows = (items) => (
    <div className="space-y-1.5 mt-2">
      {items.map((it, idx) => {
        const showFraction = typeof it.total === 'number' && it.total > 1;
        const showCountOnly = !showFraction && typeof it.count === 'number' && it.count > 0;
        return (
          <div key={idx} className={`flex items-center gap-3 p-2 rounded border ${it.done ? 'bg-emerald-900/20 border-emerald-700/50' : 'bg-slate-800/40 border-slate-700/50'}`}>
            <Checkbox checked={it.done} disabled className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" />
            <span className={`text-sm flex-1 ${it.done ? 'text-emerald-300' : 'text-slate-400'}`}>{idx + 1}. {it.label}</span>
            {showFraction && (
              <span className={`text-xs font-mono ${it.count === it.total ? 'text-emerald-400' : it.count > 0 ? 'text-amber-400' : 'text-slate-500'}`}>{it.count}/{it.total}</span>
            )}
            {showCountOnly && (<span className="text-xs font-mono text-emerald-400">×{it.count}</span>)}
          </div>
        );
      })}
    </div>
  );

  // Add Update form body — a standalone copy of the Add Update dialog's form
  // fields so the "Update" detail tab can render it inline (no popup) without
  // touching the two existing dialogs. Shares the same `updateData` state and
  // the same submit path (setShowAddUpdateConfirm). Submit button is rendered
  // by the inline caller.
  const renderAddUpdateForm = () => {
    if (!selectedCall) return null;
    return (
      <div className="space-y-4">
        {(() => {
          const hasAdditionals = (selectedCall?.additional_locations?.length || 0) > 0;
          const hasPerps = (selectedCall?.perp_descriptions?.length || 0) > 0;
          const targetSelected = (updateData.location_label !== null && updateData.location_label !== undefined)
            || (updateData.perp_ids?.length || 0) > 0
            || !!updateData.perp_id;
          const showLocFields = (!hasPerps && !hasAdditionals) || targetSelected;
          const isOriginalSelected = updateData.location_label === "";
          return (
            <>
              {(hasPerps || hasAdditionals) && (
                <div>
                  <Label className="mb-2 block">
                    Update Target
                    {hasPerps && <span className="text-xs text-slate-500 font-normal"> (perps multi-select)</span>}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant={isOriginalSelected ? "default" : "outline"}
                      size="sm"
                      className={isOriginalSelected ? "bg-amber-600 hover:bg-amber-700" : ""}
                      onClick={() => {
                        if (isOriginalSelected) {
                          setUpdateData({ ...updateData, perp_id: "", perp_ids: [], location_label: null });
                        } else {
                          setUpdateData({ ...updateData, perp_id: "", perp_ids: [], location_label: "" });
                        }
                      }}
                    >
                      Original
                    </Button>
                    {hasPerps && selectedCall.perp_descriptions.map((p, idx) => {
                      const ids = updateData.perp_ids || [];
                      const isSelected = ids.includes(p.perp_id) || updateData.perp_id === p.perp_id;
                      const label = p.linked_profile_name
                        ? `#${idx + 1} ${p.linked_profile_name}`
                        : `#${idx + 1} ${p.sex || ''} ${p.race || ''}`.trim();
                      return (
                        <Button
                          key={p.perp_id}
                          type="button"
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          className={isSelected ? "bg-purple-600 hover:bg-purple-700" : ""}
                          onClick={() => {
                            const currentIds = updateData.perp_ids?.length > 0
                              ? [...updateData.perp_ids]
                              : updateData.perp_id ? [updateData.perp_id] : [];
                            const newIds = currentIds.includes(p.perp_id)
                              ? currentIds.filter(id => id !== p.perp_id)
                              : [...currentIds, p.perp_id];
                            const next = { ...updateData, perp_id: newIds[0] || "", perp_ids: newIds };
                            if (newIds.length > 0) next.location_label = null;
                            setUpdateData(next);
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                    {hasAdditionals && selectedCall.additional_locations.map((loc, idx) => {
                      const label = loc.title || `Additional #${idx + 1}`;
                      const isSelected = updateData.location_label === label;
                      return (
                        <Button
                          key={`add-${idx}`}
                          type="button"
                          size="sm"
                          variant={isSelected ? "default" : "outline"}
                          className={isSelected ? "bg-blue-600 hover:bg-blue-700" : ""}
                          onClick={() => {
                            if (isSelected) {
                              setUpdateData({ ...updateData, location_label: null });
                            } else {
                              setUpdateData({ ...updateData, location_label: label, perp_id: "", perp_ids: [] });
                            }
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
              {showLocFields && (
                <div>
                  <Label>Updated Location</Label>
                  <Input
                    value={updateData.updated_location}
                    onChange={(e) => setUpdateData({ ...updateData, updated_location: e.target.value })}
                    className="input-dark"
                    placeholder="New location if changed..."
                  />
                </div>
              )}
              {showLocFields && (
                <div>
                  <Label>Direction of Flight</Label>
                  <Input
                    value={updateData.updated_direction}
                    onChange={(e) => setUpdateData({ ...updateData, updated_direction: e.target.value })}
                    className="input-dark"
                    placeholder="e.g., Northbound on Main St..."
                  />
                </div>
              )}
            </>
          );
        })()}
        {(updateData.perp_id || (updateData.perp_ids || []).length > 0) && (
          <div className="p-2 bg-slate-800 rounded border border-slate-700 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inline-update-arrested"
                  checked={updateData.set_arrested}
                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_arrested: checked })}
                />
                <Label htmlFor="inline-update-arrested" className="text-sm cursor-pointer text-green-400">Arrested</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inline-update-armed"
                  checked={updateData.set_armed}
                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_armed: checked })}
                />
                <Label htmlFor="inline-update-armed" className="text-sm cursor-pointer text-red-400">Armed</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inline-update-holding"
                  checked={updateData.set_holding}
                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_holding: checked })}
                />
                <Label htmlFor="inline-update-holding" className="text-sm cursor-pointer text-amber-400">Holding</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inline-update-detained"
                  checked={updateData.set_detained}
                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_detained: checked })}
                />
                <Label htmlFor="inline-update-detained" className="text-sm cursor-pointer text-orange-400">Detained</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inline-update-visual"
                  checked={updateData.set_visual}
                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_visual: checked })}
                />
                <Label htmlFor="inline-update-visual" className="text-sm cursor-pointer text-cyan-400">Visual</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inline-update-positive-id"
                  checked={updateData.set_positive_id}
                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_positive_id: checked })}
                />
                <Label htmlFor="inline-update-positive-id" className="text-sm cursor-pointer text-emerald-400">Positive ID</Label>
              </div>
            </div>
            {updateData.set_armed && (
              <Input
                value={updateData.weapon_description}
                onChange={(e) => setUpdateData({ ...updateData, weapon_description: e.target.value })}
                className="input-dark"
                placeholder="Weapon description (optional)..."
              />
            )}
          </div>
        )}
        <div>
          <Label>{updateData.perp_id ? 'Perp Description Update' : 'Updated Perp Description'}</Label>
          <Textarea
            value={updateData.updated_perp_description}
            onChange={(e) => setUpdateData({ ...updateData, updated_perp_description: e.target.value })}
            className="input-dark"
            placeholder={updateData.perp_id ? "Additional info about this perp..." : "Updated description..."}
          />
        </div>
        <div>
          <Label>Job Narrative</Label>
          <Textarea
            value={updateData.general_notes}
            onChange={(e) => setUpdateData({ ...updateData, general_notes: e.target.value })}
            className="input-dark"
            placeholder="Job narrative / call note..."
          />
        </div>
        <div>
          <Label>Updates <span className="text-xs text-slate-500 font-normal">(timeline note)</span></Label>
          <Textarea
            value={updateData.update_text}
            onChange={(e) => setUpdateData({ ...updateData, update_text: e.target.value })}
            className="input-dark"
            placeholder="General update for timeline..."
          />
        </div>
        <div className="pt-2 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <Label>Additional Locations ({(selectedCall.additional_locations || []).length})</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setAddLocData({ title: "", location: "", location_notes: "", cross_streets: "", direction_of_flight: "" });
                setEditingLocIndex("update-add");
                setShowAddLocDialog(true);
              }}
              className="h-7 text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Location
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Sticky quick-filter tab bar for the call-detail panel. Rendered twice with
  // breakpoint visibility: under the header on mobile/tablet (lg:hidden) where
  // the layout is a single column, and at the top on desktop (hidden lg:block)
  // where the masonry grid makes "under the header" ambiguous.
  const renderDetailTabsBar = () => {
    const detailTabs = [
      { key: "all", label: "All" },
      { key: "update", label: "Update" },
      { key: "callinfo", label: "Call Info" },
      { key: "status", label: "Status" },
      { key: "actions", label: "Actions" },
      { key: "units", label: "Units" },
      { key: "perps", label: "Perps" },
      { key: "location", label: "Location" },
      { key: "updates", label: "Updates" },
      { key: "allupdates", label: "All Updates" },
      { key: "read", label: "Read" },
      { key: "media", label: "Media" },
      { key: "notes", label: "Notes" },
      { key: "checklist", label: "Checklist" },
      ...((user?.recording_admin_access || user?.role === 'admin') ? [{ key: "radio", label: "Radio" }] : []),
    ];
    const activeLabel = detailTabs.find(t => t.key === detailTab)?.label || "All";
    return (
      <div className="sticky top-0 z-30 -mx-4 px-4 py-2 mb-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-start gap-2">
          <button
            onClick={() => setDetailTabsCollapsed(c => !c)}
            title={detailTabsCollapsed ? "Show tabs" : "Hide tabs"}
            className="shrink-0 px-1.5 py-1 rounded border border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
          >
            {detailTabsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          {detailTabsCollapsed ? (
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 self-center">{activeLabel}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {detailTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setDetailTab(detailTab === tab.key ? "all" : tab.key)}
                  className={`px-2.5 py-1 text-xs font-medium rounded border transition-colors ${
                    detailTab === tab.key
                      ? "bg-blue-600 border-blue-500 text-white"
                      : "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-400 dark:hover:border-slate-500"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Status/Radio card — rendered both in the main detail view and inside the Add Update dialog.
  // showActions: render the full action row (Add Update / Resend / Log Incident /
  // Change Type / Edit Info). When false, only the Edit Info button is rendered.
  const renderStatusRadioCard = (forceShow = false, showActions = true, instanceId = 'main') => {
    if (!selectedCall) return null;
    const expanded = forceShow || showRadioOptions;
    return (
    <Card id="section-radio" className="bg-slate-800 border-slate-800">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500 uppercase">
            <span className="sm:hidden">STATUS / RADIO</span>
            <span className="hidden sm:inline">STATUS / RADIO TRANSMISSIONS</span>
          </p>
        {!forceShow && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-slate-400 hover:text-slate-200"
          onClick={() => setShowRadioOptions(!showRadioOptions)}
        >
          {showRadioOptions ? (
            <>
              <Eye className="w-3 h-3 mr-1" />
              Hide
            </>
          ) : (
            <>
              <Eye className="w-3 h-3 mr-1" />
              Show
            </>
          )}
        </Button>
        )}
      </div>
      <div
        className="space-y-3 overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: expanded ? '1000px' : '0px',
          opacity: expanded ? 1 : 0,
          marginTop: expanded ? '12px' : '0px',
        }}
      >
          <div className={`grid gap-2 ${showActions ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : 'grid-cols-1'}`}>
            {showActions && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-2 border-amber-600/50 text-amber-400 hover:bg-amber-600/20 hover:text-amber-300 hover:border-amber-500"
                  data-testid="add-update-btn"
                  onClick={() => {
                    savedScrollPositionRef.current = detailsPanelRef.current?.scrollTop || 0;
                    setUpdateDialogTab('update');
                    setShowUpdateDialog(true);
                  }}
                >
                  Add Update
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-9 px-2 border-green-600/50 text-green-400 hover:bg-green-600/20 hover:text-green-300 hover:border-green-500 ${sendingResend ? 'animate-pulse' : ''}`}
                  disabled={sendingResend}
                  onClick={() => { setResendWhatsApp(true); setResendPush(false); setResendCall(true); setResendPerpIds([]); setResendCustomMessage(""); setResendWaIndividuals(true); setResendWaBotGroups(true); setResendWaBotGroupJids([]); setShowResendConfirm(true); }}
                >
                  Resend
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-2 border-indigo-600/50 text-indigo-400 hover:bg-indigo-600/20 hover:text-indigo-300 hover:border-indigo-500"
                  onClick={(e) => { e.stopPropagation(); setShowLogConfirm(true); }}
                  data-testid="log-call-btn"
                >
                  Log Incident
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-2 border-purple-600/50 text-purple-400 hover:bg-purple-600/20 hover:text-purple-300 hover:border-purple-500"
                  onClick={openCallTypeOnlyDialog}
                >
                  Change Type
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-9 px-2 border-blue-600/50 text-blue-400 hover:bg-blue-600/20 hover:text-blue-300 hover:border-blue-500"
              onClick={openEditCallDialog}
            >
              Edit Info
            </Button>
          </div>
          {/* Status Dropdowns */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Priority Dropdown */}
            <Select
              value={selectedCall.priority || "normal"}
              onValueChange={handlePriorityChange}
            >
              <SelectTrigger
                className={`h-6 w-auto px-2 py-0 text-xs font-semibold border-0 ${
                  selectedCall.priority === "priority"
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-slate-600 text-white hover:bg-slate-700"
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">NORMAL</SelectItem>
                <SelectItem value="priority">PRIORITY</SelectItem>
              </SelectContent>
            </Select>
            {/* Confirmation Status Dropdown */}
            <Select
              value={selectedCall.confirmation_status || "no_confirm"}
              onValueChange={(v) => handleQuickConfirmationChange(v === "no_confirm" ? "" : v)}
            >
              <SelectTrigger
                className={`h-6 w-auto px-2 py-0 text-xs font-semibold border-0 ${
                  selectedCall.confirmation_status === "confirmed"
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : selectedCall.confirmation_status === "unconfirmed"
                    ? "bg-amber-600 text-white hover:bg-amber-700"
                    : "bg-slate-600 text-white hover:bg-slate-700"
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no_confirm">NO CONFIRM</SelectItem>
                <SelectItem value="unconfirmed">UNCONFIRMED</SelectItem>
                <SelectItem value="confirmed">CONFIRMED</SelectItem>
              </SelectContent>
            </Select>
            {/* Unit Status: None / Units Req'd / Slowdown / No Further / Forthwith */}
            <Select
              value={selectedCall.forthwith ? "forthwith" : selectedCall.no_further ? "no_further" : selectedCall.slowdown ? "slowdown" : selectedCall.units_required ? "units_required" : "none"}
              onValueChange={handleUnitStatusChange}
            >
              <SelectTrigger
                className={`h-6 w-auto px-2 py-0 text-xs font-semibold border-0 ${
                  selectedCall.forthwith
                    ? "bg-pink-500 text-white hover:bg-pink-600"
                    : selectedCall.no_further
                    ? "bg-slate-600 text-white hover:bg-slate-700"
                    : selectedCall.slowdown
                    ? "bg-purple-500 text-white hover:bg-purple-600"
                    : selectedCall.units_required
                    ? "bg-purple-500 text-white hover:bg-purple-600"
                    : "bg-slate-600 text-white hover:bg-slate-700"
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— STATUS —</SelectItem>
                <SelectItem value="units_required">UNITS REQ'D</SelectItem>
                <SelectItem value="forthwith">FORTHWITH</SelectItem>
                <SelectItem value="slowdown">SLOWDOWN</SelectItem>
                <SelectItem value="no_further">NO FURTHER</SelectItem>
              </SelectContent>
            </Select>
            {/* Call Mode Dropdown */}
            <Select
              value={selectedCall.call_mode || "none"}
              onValueChange={handleCallModeChange}
            >
              <SelectTrigger
                className={`h-6 w-auto px-2 py-0 text-xs font-semibold border-0 ${
                  selectedCall.call_mode === "live_call"
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : selectedCall.call_mode === "call_98"
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-slate-600 text-white hover:bg-slate-700"
                }`}
              >
                <SelectValue placeholder="MODE" />
              </SelectTrigger>
              <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                <SelectItem value="none">— NONE —</SelectItem>
                <SelectItem value="live_call">LIVE CALL</SelectItem>
                <SelectItem value="call_98">CALL 98</SelectItem>
              </SelectContent>
            </Select>
            {selectedCall.call_mode === "call_98" && !selectedCall.call_98_auto_log_cancelled && (
              <Call98Countdown call={selectedCall} onCancel={handleCancelAutoLog} />
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-700"></div>

          {/* 10-85 and 10-13 checkboxes - kept separate per requirement #10 */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Checkbox
                id="call_10_85"
                checked={selectedCall.transmitted_10_85}
                onCheckedChange={(checked) => {
                  setPending1085Value(checked);
                  setNotify8513Enabled(false);
                  setNotify8513Mode("all");
                  setShow1085Confirm(true);
                }}
              />
              <Label htmlFor="call_10_85" className={selectedCall.transmitted_10_85 ? "text-red-400 font-bold" : ""}>
                10-85
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="call_10_13"
                checked={selectedCall.transmitted_10_13}
                onCheckedChange={(checked) => {
                  setPending1013Value(checked);
                  setNotify8513Enabled(false);
                  setNotify8513Mode("all");
                  setShow1013Confirm(true);
                }}
              />
              <Label htmlFor="call_10_13" className={selectedCall.transmitted_10_13 ? "text-red-400 font-bold" : ""}>
                10-13
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="call_all_8513"
                checked={selectedCall.whatsapp_8513_enabled !== false}
                onCheckedChange={async (checked) => {
                  try {
                    const response = await axios.put(
                      `${API}/live-calls/${selectedCall.call_id}`,
                      { whatsapp_8513_enabled: checked },
                      { withCredentials: true }
                    );
                    setSelectedCall(response.data);
                    setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                    toast.success(checked ? "All 85/13 notifications enabled" : "All 85/13 notifications disabled");
                  } catch (error) {
                    toast.error("Failed to update");
                  }
                }}
              />
              <Label htmlFor="call_all_8513" className={selectedCall.whatsapp_8513_enabled !== false ? "text-red-400 font-bold" : ""}>
                All 85/13
              </Label>
            </div>
          </div>
          {selectedCall.whatsapp_8513_enabled !== false && (
            <p className="text-[10px] text-red-400/70">Forces push & WA to ALL users for 10-85/10-13</p>
          )}
          
          {/* Custom Radio Codes - Searchable dropdown within radio transmissions per requirement #11 */}
          {customRadioCodes.length > 0 && (
            <div className="pt-2 border-t border-slate-800">
              <Label className="text-xs text-slate-500 mb-1 block">Other Radio Codes</Label>
              {/* Popover + filterable list. Was previously a Radix Select, but
                  Select intercepts pointer events on non-SelectItem children
                  during the capture phase — so tapping the search input on
                  mobile would dismiss the popper before it could focus.
                  Popover doesn't have that constraint and the search input
                  takes focus normally. cmdk's Command also handles arrow-key
                  nav + auto-focus on open out of the box. */}
              <Popover
                // modal=true so the popover owns its focus while open. Without
                // this, opening the popover INSIDE the Add Update dialog
                // immediately closed it again — Dialog's focus trap was
                // pulling focus back as soon as cmdk handed it to the popover
                // content, and Radix reads that as a dismiss signal.
                modal
                open={radioCodesPopoverOpen === instanceId}
                onOpenChange={(open) => {
                  setRadioCodesPopoverOpen(open ? instanceId : null);
                  if (open) {
                    requestAnimationFrame(() => {
                      document.getElementById(`other-radio-codes-trigger-${instanceId}`)
                        ?.scrollIntoView({ block: "center", behavior: "smooth" });
                    });
                  } else {
                    setRadioCodeQuery("");
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    id={`other-radio-codes-trigger-${instanceId}`}
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={radioCodesPopoverOpen === instanceId}
                    className="input-dark h-8 text-sm w-full justify-between font-normal"
                  >
                    <span className="text-slate-400">Select radio code to toggle...</span>
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0 bg-popover border-slate-700 z-[250]"
                  align="start"
                  sideOffset={4}
                  collisionPadding={{ top: 60, bottom: 16 }}
                  // Block Radix's default 'focus the first focusable child' on
                  // open — that was hoisting focus into CommandInput and
                  // popping the soft keyboard on mobile even though the input
                  // itself doesn't carry autoFocus on touch devices. We still
                  // get desktop autofocus via CommandInput's autoFocus prop
                  // (also gated to non-touch).
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  style={{
                    width: 'var(--radix-popover-trigger-width)',
                    maxHeight: 'min(60vh, var(--radix-popover-content-available-height, 60vh))',
                  }}
                >
                  <Command shouldFilter={false}>
                    <div
                      className="sticky top-0 z-10 bg-popover border-b border-slate-700"
                      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
                    >
                      <CommandInput
                        // Auto-focus on desktop only — focusing on a touch
                        // device pops up the on-screen keyboard, which the
                        // user doesn't want when they just opened the
                        // dropdown to scan codes.
                        autoFocus={typeof window !== "undefined" &&
                          !(("ontouchstart" in window) ||
                            (window.matchMedia && window.matchMedia("(pointer: coarse)").matches))}
                        value={radioCodeQuery}
                        onValueChange={setRadioCodeQuery}
                        placeholder="Search radio codes..."
                        className="h-9"
                      />
                    </div>
                    <CommandList className="max-h-[50vh] overflow-y-auto">
                      {(() => {
                        const q = radioCodeQuery.trim().toLowerCase();
                        const list = [...customRadioCodes]
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .filter(c => !q || (c.label || "").toLowerCase().includes(q));
                        if (list.length === 0) {
                          return <CommandEmpty>No radio code found.</CommandEmpty>;
                        }
                        return (
                          <CommandGroup>
                            {list.map((code) => {
                              const isCv = isCvObtainedLabel(code.label);
                              const isActive = selectedCall.custom_codes?.[code.id] || false;
                              return (
                                <CommandItem
                                  key={code.id}
                                  value={code.id}
                                  onSelect={() => {
                                    const nextValue = isCv ? true : !isActive;
                                    handleCustomCodeToggle(code.id, code.label, nextValue);
                                    setRadioCodeQuery("");
                                    setRadioCodesPopoverOpen(null);
                                  }}
                                  className={`cursor-pointer ${isActive ? "text-purple-400 font-bold" : ""}`}
                                >
                                  {code.label} {isActive ? (isCv ? "+" : "✓") : ""}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        );
                      })()}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {/* Show active custom codes as badges */}
              {(Object.entries(selectedCall.custom_codes || {}).filter(([_, active]) => active).length > 0 ||
                (selectedCall.cv_obtained_units || []).length > 0) && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {/* One badge per CV-obtained unit so the dispatcher sees each assignment separately */}
                  {(() => {
                    const cvUnits = selectedCall.cv_obtained_units || [];
                    return cvUnits.map((unit, idx) => (
                      <Badge
                        key={`cv_${idx}_${unit}`}
                        className="bg-purple-600 text-white text-xs cursor-pointer hover:bg-purple-700"
                        onClick={() => removeCvObtainedUnitAt(idx)}
                      >
                        CV obtained{cvUnits.length > 1 ? ` #${idx + 1}` : ''}/{unit} ×
                      </Badge>
                    ));
                  })()}
                  {Object.entries(selectedCall.custom_codes || {})
                    .filter(([_, active]) => active)
                    .map(([codeId]) => {
                      const code = customRadioCodes.find(c => c.id === codeId);
                      if (!code) return null;
                      // Skip the CV obtained code itself — it's represented by per-unit badges above.
                      if (isCvObtainedLabel(code.label)) return null;
                      return (
                        <Badge
                          key={codeId}
                          className="bg-purple-600 text-white text-xs cursor-pointer hover:bg-purple-700"
                          onClick={() => handleCustomCodeToggle(codeId, code.label, false)}
                        >
                          {code.label} ×
                        </Badge>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Call Final */}
          {callFinalOptions.length > 0 && (
            <div className="pt-2 border-t border-slate-800">
              <Label className="text-xs text-slate-500 mb-1 block">Call Final</Label>
              <Select
                value={selectedCall.call_final || "_none"}
                onValueChange={async (v) => {
                  const value = v === "_none" ? null : v;
                  try {
                    const response = await axios.put(
                      `${API}/live-calls/${selectedCall.call_id}`,
                      { call_final: value },
                      { withCredentials: true }
                    );
                    setSelectedCall(response.data);
                    setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                    // Add timeline update
                    await axios.post(
                      `${API}/live-calls/${selectedCall.call_id}/updates`,
                      { update_text: value ? `Call Final: ${value}` : "Call Final cleared" },
                      { withCredentials: true }
                    ).then(res => {
                      setSelectedCall(res.data);
                      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? res.data : c));
                    });
                    toast.success(value ? `Call Final: ${value}` : "Call Final cleared");
                  } catch (error) {
                    toast.error("Failed to update call final");
                  }
                }}
              >
                <SelectTrigger className="input-dark h-8 text-sm">
                  <SelectValue placeholder="Select call final..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— None —</SelectItem>
                  {callFinalOptions.map((opt) => (
                    <SelectItem key={opt.id} value={opt.label}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCall.call_final && (
                <Badge className="mt-1.5 bg-green-600 text-white text-xs">{selectedCall.call_final}</Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    );
  };


  return (
    <div className="h-[calc(100vh-8rem)] overflow-hidden flex flex-col" data-testid="live-calls-page">
      {/* Header */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-3">
          {/* Logo - desktop only */}
          <div className="hidden md:flex items-center gap-2 pr-3 mr-3 border-r border-slate-700">
            <img
              src="/CHSP_PR_2026.png"
              alt="CHSP"
              className="w-7 h-7 object-contain pointer-events-none select-none"
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
            />
            <span className="text-lg font-bold tracking-tight text-blue-400 uppercase select-none">
              CHSP
            </span>
          </div>
          <Radio className="w-5 h-5 text-blue-500 animate-pulse" />
          <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">Dispatch</h1>
          <Badge variant="outline" className="ml-2">
            {liveCalls.length} Active
          </Badge>
          {/* Quick Add — beside the count rather than out with New Call, so the
              short form is the one within reach of where dispatch is looking.
              Its dialog lives further down, next to the full form's. */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 border-slate-600 text-slate-200 hover:bg-slate-800 gap-1 px-2"
            data-testid="quick-call-btn"
            title="New live call — short form"
            onClick={() => { resetNewCallForm(); setQuickFolds(QUICK_FOLDS_CLOSED); setShowQuickCallDialog(true); }}
          >
            <Plus className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-wider">Quick</span>
          </Button>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
            </SelectContent>
          </Select>
          <ClockDisplay />
          {selectedCall && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDispatcherChecklist(true)}
              className="h-8 border-cyan-600/50 text-cyan-400 hover:bg-cyan-600/20 hover:text-cyan-300"
              title="Dispatcher checklist"
            >
              <CheckSquare className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Checklist</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={isRefreshing}
            className="h-8"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Dialog open={showNewCallDialog} onOpenChange={(open) => {
            if (!open) {
              // Closing via X / outside-tap. If a Submit-Open call was
              // created and never finalized via "Update & Close", fire its
              // deferred WhatsApp blast now (the helper is a no-op when
              // nothing is pending) AND arm the 10-second creator-scroll
              // window for that call — same trigger points as the deferred
              // WhatsApp.
              const pendingCallId = createdCallForEdit?.call_id;
              if (pendingCallId) {
                flushPendingWhatsApp(pendingCallId);
                armCreatorScrollWindow(pendingCallId);
              }
              resetNewCallForm();
            }
            setShowNewCallDialog(open);
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="new-call-btn">
                <Plus className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">New Call</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-lg bg-slate-900 border-slate-800 max-h-[90vh] overflow-hidden flex flex-col p-0">
              <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
                <div className="flex items-center justify-between gap-2">
                  <DialogTitle className="uppercase tracking-wider">
                    {createdCallForEdit ? 'Edit Live Call' : 'New Live Call'}
                    {createdCallForEdit && <span className="text-sm text-green-400 ml-2">(Created - Editing)</span>}
                  </DialogTitle>
                  {!createdCallForEdit && (
                    <Button type="button" variant="outline" size="icon" className="h-7 w-7 shrink-0 mr-6" onClick={toggleCompactNewForm} title={compactNewForm ? "Standard view" : "Compact view"}>
                      {compactNewForm ? <LayoutList className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
              {compactNewForm && !createdCallForEdit ? renderCompactNewCall() : (
              <div className="space-y-4 mt-4 pb-4">
                {renderNewCallRadio()}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="mb-0">Call Type *</Label>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={newCall.priority === "priority"}
                      onClick={() => setNewCall({ ...newCall, priority: newCall.priority === "priority" ? "normal" : "priority" })}
                      data-testid="priority-select"
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider transition-all border ${
                        newCall.priority === "priority"
                          ? "bg-red-600 text-white border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.55)]"
                          : "bg-transparent text-slate-400 border-slate-600 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/60"
                      }`}
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Priority
                    </button>
                  </div>
                    <div className="relative">
                      <Input
                        value={callTypeSearch || newCall.call_type}
                        onChange={(e) => {
                          setCallTypeSearch(e.target.value);
                          setNewCall({ ...newCall, call_type: e.target.value });
                          setShowCallTypeDropdown(true);
                          setCallTypeHighlightedIndex(-1);
                        }}
                        onFocus={() => {
                          setShowCallTypeDropdown(true);
                          setCallTypeHighlightedIndex(-1);
                        }}
                        onBlur={() => setTimeout(() => setShowCallTypeDropdown(false), 200)}
                        onKeyDown={(e) => {
                          const filteredTypes = incidentTypes
                            .filter(type => type.name.toLowerCase().includes((callTypeSearch || "").toLowerCase()))
                            .sort((a, b) => { const aNum = /^\d/.test(a.name); const bNum = /^\d/.test(b.name); if (aNum !== bNum) return aNum ? 1 : -1; return a.name.localeCompare(b.name); });
                          
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setShowCallTypeDropdown(true);
                            setCallTypeHighlightedIndex(prev => 
                              prev < filteredTypes.length - 1 ? prev + 1 : 0
                            );
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setCallTypeHighlightedIndex(prev => 
                              prev > 0 ? prev - 1 : filteredTypes.length - 1
                            );
                          } else if ((e.key === 'Enter' || e.key === 'Tab') && callTypeHighlightedIndex >= 0) {
                            e.preventDefault();
                            const selectedType = filteredTypes[callTypeHighlightedIndex];
                            if (selectedType) {
                              const autoPriority = (priorityCallTypes.includes(selectedType.name) || priorityCallTypes.includes(newCall.secondary_call_type)) ? "priority" : "normal";
                              setNewCall({ ...newCall, call_type: selectedType.name, priority: autoPriority });
                              setCallTypeSearch("");
                              setShowCallTypeDropdown(false);
                              setCallTypeHighlightedIndex(-1);
                              // Move focus to next field on Tab/Enter
                              const nextInput = document.querySelector('[data-testid="secondary-call-type-input"]');
                              if (nextInput) nextInput.focus();
                            }
                          } else if (e.key === 'Escape') {
                            setShowCallTypeDropdown(false);
                            setCallTypeHighlightedIndex(-1);
                          }
                        }}
                        placeholder="Search or enter call type..."
                        className="input-dark"
                        data-testid="call-type-input"
                      />
                      {showCallTypeDropdown && (callTypeSearch || !newCall.call_type) && (
                        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {incidentTypes
                            .filter(type => type.name.toLowerCase().includes((callTypeSearch || "").toLowerCase()))
                            .sort((a, b) => { const aNum = /^\d/.test(a.name); const bNum = /^\d/.test(b.name); if (aNum !== bNum) return aNum ? 1 : -1; return a.name.localeCompare(b.name); })
                            .map((type, idx) => (
                              <div
                                key={type.type_id}
                                className={`px-3 py-2 cursor-pointer text-sm select-none ${idx === callTypeHighlightedIndex ? 'bg-blue-600 text-white' : 'hover:bg-slate-700'}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  const autoPriority = (priorityCallTypes.includes(type.name) || priorityCallTypes.includes(newCall.secondary_call_type)) ? "priority" : "normal";
                                  setNewCall({ ...newCall, call_type: type.name, priority: autoPriority });
                                  setCallTypeSearch("");
                                  setShowCallTypeDropdown(false);
                                  setCallTypeHighlightedIndex(-1);
                                  document.activeElement?.blur();
                                }}
                                onMouseEnter={() => setCallTypeHighlightedIndex(idx)}
                              >
                                {type.name}
                              </div>
                            ))}
                          {callTypeSearch && !incidentTypes.some(t => t.name.toLowerCase() === callTypeSearch.toLowerCase()) && (
                            <div className="px-3 py-2 text-xs text-slate-400 border-t border-slate-700">
                              Press Enter or click outside to use &quot;{callTypeSearch}&quot;
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                </div>

                {/* Secondary Call Type */}
                <div>
                  <Label>Secondary Call Type (Optional)</Label>
                  <div className="relative">
                    <Input
                      value={secondaryCallTypeSearch || newCall.secondary_call_type}
                      onChange={(e) => {
                        setSecondaryCallTypeSearch(e.target.value);
                        setNewCall({ ...newCall, secondary_call_type: e.target.value });
                        setShowSecondaryCallTypeDropdown(true);
                        setSecondaryCallTypeHighlightedIndex(-1);
                      }}
                      onFocus={() => {
                        setShowSecondaryCallTypeDropdown(true);
                        setSecondaryCallTypeHighlightedIndex(-1);
                      }}
                      onBlur={() => setTimeout(() => setShowSecondaryCallTypeDropdown(false), 200)}
                      onKeyDown={(e) => {
                        const filteredTypes = incidentTypes
                          .filter(type => type.name.toLowerCase().includes((secondaryCallTypeSearch || "").toLowerCase()))
                          .sort((a, b) => { const aNum = /^\d/.test(a.name); const bNum = /^\d/.test(b.name); if (aNum !== bNum) return aNum ? 1 : -1; return a.name.localeCompare(b.name); });
                        
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setShowSecondaryCallTypeDropdown(true);
                          setSecondaryCallTypeHighlightedIndex(prev => 
                            prev < filteredTypes.length - 1 ? prev + 1 : 0
                          );
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setSecondaryCallTypeHighlightedIndex(prev => 
                            prev > 0 ? prev - 1 : filteredTypes.length - 1
                          );
                        } else if (e.key === 'Enter' && secondaryCallTypeHighlightedIndex >= 0) {
                          e.preventDefault();
                          const selectedType = filteredTypes[secondaryCallTypeHighlightedIndex];
                          if (selectedType) {
                            const autoPriority = (priorityCallTypes.includes(selectedType.name) || priorityCallTypes.includes(newCall.call_type)) ? "priority" : "normal";
                            setNewCall({ ...newCall, secondary_call_type: selectedType.name, priority: autoPriority });
                            setSecondaryCallTypeSearch("");
                            setShowSecondaryCallTypeDropdown(false);
                            setSecondaryCallTypeHighlightedIndex(-1);
                          }
                        } else if (e.key === 'Tab') {
                          // Allow Tab to pass through - just close dropdown if open
                          if (secondaryCallTypeHighlightedIndex >= 0) {
                            const selectedType = filteredTypes[secondaryCallTypeHighlightedIndex];
                            if (selectedType) {
                              const autoPriority = (priorityCallTypes.includes(selectedType.name) || priorityCallTypes.includes(newCall.call_type)) ? "priority" : "normal";
                              setNewCall({ ...newCall, secondary_call_type: selectedType.name, priority: autoPriority });
                              setSecondaryCallTypeSearch("");
                            }
                          }
                          setShowSecondaryCallTypeDropdown(false);
                          setSecondaryCallTypeHighlightedIndex(-1);
                          // Don't prevent default - let Tab navigate to next field
                        } else if (e.key === 'Escape') {
                          setShowSecondaryCallTypeDropdown(false);
                          setSecondaryCallTypeHighlightedIndex(-1);
                        }
                      }}
                      placeholder="Search or enter secondary type..."
                      className="input-dark"
                      data-testid="secondary-call-type-input"
                    />
                    {showSecondaryCallTypeDropdown && (secondaryCallTypeSearch || !newCall.secondary_call_type) && (
                      <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {incidentTypes
                          .filter(type => type.name.toLowerCase().includes((secondaryCallTypeSearch || "").toLowerCase()))
                          .sort((a, b) => { const aNum = /^\d/.test(a.name); const bNum = /^\d/.test(b.name); if (aNum !== bNum) return aNum ? 1 : -1; return a.name.localeCompare(b.name); })
                          .map((type, idx) => (
                            <div
                              key={type.type_id}
                              className={`px-3 py-2 cursor-pointer text-sm select-none ${idx === secondaryCallTypeHighlightedIndex ? 'bg-blue-600 text-white' : 'hover:bg-slate-700'}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                const autoPriority = (priorityCallTypes.includes(type.name) || priorityCallTypes.includes(newCall.call_type)) ? "priority" : "normal";
                                setNewCall({ ...newCall, secondary_call_type: type.name, priority: autoPriority });
                                setSecondaryCallTypeSearch("");
                                setShowSecondaryCallTypeDropdown(false);
                                setSecondaryCallTypeHighlightedIndex(-1);
                                document.activeElement?.blur();
                              }}
                              onMouseEnter={() => setSecondaryCallTypeHighlightedIndex(idx)}
                            >
                              {type.name}
                            </div>
                          ))}
                        {secondaryCallTypeSearch && !incidentTypes.some(t => t.name.toLowerCase() === secondaryCallTypeSearch.toLowerCase()) && (
                          <div className="px-3 py-2 text-xs text-slate-400 border-t border-slate-700">
                            Press Enter or click outside to use &quot;{secondaryCallTypeSearch}&quot;
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Original Location - inline fields */}
                <div className="p-3 border border-slate-700 rounded-lg space-y-3">
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <Label>Location *</Label>
                    <div className="flex items-center gap-1.5">
                      {(newCall.location || newCall.location_notes || newCall.cross_streets || newCall.direction_of_flight) && (
                        <button
                          type="button"
                          className="text-[10px] text-red-400 hover:text-red-300 px-1"
                          onClick={() => {
                            snapshotLocation();
                            setNewCall(prev => ({ ...prev, location: "", location_notes: "", cross_streets: "", direction_of_flight: "" }));
                          }}
                          title="Clear all location data"
                        >Clear</button>
                      )}
                      {locationUndoStack.length > 0 && (
                        <button
                          type="button"
                          className="text-[10px] text-blue-400 hover:text-blue-300 px-1"
                          onClick={() => {
                            const restored = locationUndoStack[locationUndoStack.length - 1];
                            setNewCall(prev => ({ ...prev, ...restored }));
                            setLocationUndoStack(s => s.slice(0, -1));
                            lastLocationSnapshot.current = restored;
                          }}
                          title={`Undo (${locationUndoStack.length})`}
                        >Undo{locationUndoStack.length > 1 ? ` (${locationUndoStack.length})` : ""}</button>
                      )}
                      <Checkbox
                        id="enableLocationPredictions"
                        checked={enableLocationPredictions}
                        onCheckedChange={setEnableLocationPredictions}
                        className="h-3 w-3"
                      />
                      <label htmlFor="enableLocationPredictions" className="text-xs text-slate-400 cursor-pointer">
                        Auto-suggest
                      </label>
                    </div>
                  </div>
                  <Input
                    ref={locationInputRef}
                    value={newCall.location}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    onFocus={() => {
                      snapshotLocation();
                      if (enableLocationPredictions && locationPredictions.length > 0) setShowLocationPredictions(true);
                      // Scroll the field up so the auto-suggest list is visible.
                      // Robust to the mobile keyboard animation — see helper.
                      scrollLocationFieldIntoView();
                    }}
                    onBlur={() => {
                      // Delay hiding to allow click on prediction
                      setTimeout(() => { setShowLocationPredictions(false); setLocationPredictionIndex(-1); }, 200);
                      crossStreetsOnBlur("new", moveUnitOut(newCall.location, setNewCall),(cs) => setNewCall(prev => ({ ...prev, cross_streets: cs })), newCall.cross_streets);
                    }}
                    onKeyDown={(e) => {
                      if (!showLocationPredictions || locationPredictions.length === 0) return;
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setLocationPredictionIndex(prev => prev < locationPredictions.length - 1 ? prev + 1 : 0);
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setLocationPredictionIndex(prev => prev > 0 ? prev - 1 : locationPredictions.length - 1);
                      } else if (e.key === "Enter") {
                        e.preventDefault();
                        if (locationPredictionIndex >= 0) {
                          selectLocationPrediction(locationPredictions[locationPredictionIndex]);
                        }
                        setShowLocationPredictions(false);
                        setLocationPredictionIndex(-1);
                      } else if (e.key === "Escape") {
                        setShowLocationPredictions(false);
                        setLocationPredictionIndex(-1);
                      }
                    }}
                    placeholder="Enter location..."
                    className="input-dark"
                    data-testid="location-input"
                    autoComplete="off"
                  />
                  {/* Location autocomplete dropdown */}
                  {showLocationPredictions && locationPredictions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Select on click (not pointerdown) and keep input focus
                          via onMouseDown preventDefault — same as the call-type
                          list. Selecting on pointerdown made a touch-scroll pick
                          whatever item the finger first landed on. */}
                      <div
                        className="px-3 py-2 cursor-pointer text-sm select-none text-emerald-300 bg-slate-900 hover:bg-slate-700 border-b border-slate-600 sticky top-0"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setShowLocationPredictions(false); setLocationPredictionIndex(-1); }}
                      >
                        ✓ Use "{newCall.location}" as typed
                      </div>
                      {locationPredictions.map((prediction, idx) => (
                        <div
                          key={prediction.place_id || idx}
                          className={`px-3 py-2 cursor-pointer text-sm select-none border-b border-slate-700/50 ${idx === locationPredictionIndex ? "bg-slate-600" : "hover:bg-slate-700"}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectLocationPrediction(prediction)}
                        >
                          <div className="font-medium text-slate-200">{prediction.main_text}</div>
                          {prediction.secondary_text && (
                            <div className="text-xs text-slate-400">{prediction.secondary_text}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {locationAutocompleteLoading && (
                    <div className="absolute right-3 top-8">
                      <RefreshCw className="w-4 h-4 text-slate-500 animate-spin" />
                    </div>
                  )}
                </div>

                <div>
                  <Label>Location Notes <span className="text-xs text-slate-400">(landmarks, apt #, etc.)</span></Label>
                  <Textarea
                    value={newCall.location_notes}
                    onFocus={() => snapshotLocation()}
                    onChange={(e) => setNewCall({ ...newCall, location_notes: e.target.value })}
                    placeholder="Additional location details..."
                    className="input-dark min-h-12"
                    rows={2}
                  />
                </div>

                {/* Cross Streets - with auto-lookup button */}
                <div>
                  <Label className="flex items-center justify-between">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      Cross Streets <span className="text-xs text-slate-400">(Auto: CH only)</span>
                      {crossStreetsLoading && <span className="text-xs text-blue-400 ml-1">(looking up...)</span>}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Checkbox
                        id="enableGoogleLookup"
                        checked={enableGoogleLookup}
                        onCheckedChange={setEnableGoogleLookup}
                        className="h-3 w-3"
                      />
                      <label htmlFor="enableGoogleLookup" className="text-xs text-slate-400 cursor-pointer">
                        Auto-lookup
                      </label>
                    </div>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={newCall.cross_streets}
                      onFocus={() => snapshotLocation()}
                      onChange={(e) => setNewCall({ ...newCall, cross_streets: e.target.value })}
                      placeholder="Enter cross streets or click lookup"
                      className="input-dark bg-slate-800/50 flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (newCall.location && newCall.location.length >= 5) {
                          fetchCrossStreets(newCall.location, (crossStreets) => {
                            setNewCall(prev => ({ ...prev, cross_streets: crossStreets }));
                          });
                        } else {
                          toast.error("Enter a location first");
                        }
                      }}
                      disabled={crossStreetsLoading || !newCall.location}
                      className="px-3"
                    >
                      {crossStreetsLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <MapPin className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Direction of Flight</Label>
                  <Input
                    value={newCall.direction_of_flight}
                    onFocus={() => snapshotLocation()}
                    onChange={(e) => setNewCall({ ...newCall, direction_of_flight: e.target.value })}
                    placeholder="e.g., Northbound on Main St..."
                    className="input-dark"
                  />
                </div>
                </div>

                {/* Additional Locations */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs text-slate-400 uppercase">Additional Locations ({(newCall.additional_locations || []).length})</Label>
                    <Button type="button" variant="outline" size="sm" className="h-6 text-xs" onClick={() => openAddLocDialog()}>
                      <Plus className="w-3 h-3 mr-1" />Add Location
                    </Button>
                  </div>
                  {(newCall.additional_locations || []).map((loc, idx) => (
                    <div key={idx} className="p-2 bg-blue-950/30 border border-blue-800/40 rounded mb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          {loc.title && <p className="text-xs text-blue-300 font-semibold uppercase mb-0.5">{loc.title}</p>}
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-blue-400 shrink-0" />
                            <span className="text-sm font-medium text-slate-200 truncate">{loc.location}</span>
                          </div>
                          {loc.cross_streets && <p className="text-xs text-emerald-400 ml-4">✕ {loc.cross_streets}</p>}
                          {loc.location_notes && <p className="text-xs text-slate-400 ml-4 italic">{loc.location_notes}</p>}
                          {loc.direction_of_flight && <p className="text-xs text-amber-400 ml-4">→ {loc.direction_of_flight}</p>}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-400" onClick={() => openAddLocDialog(idx)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setDeleteLocIndex(idx)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <Label>Job Narrative</Label>
                  <Textarea
                    value={newCall.call_note}
                    onChange={(e) => setNewCall({ ...newCall, call_note: e.target.value })}
                    onFocus={(e) => {
                      // Auto-scroll the field into view when the user taps it.
                      // The previous version used scrollContainer.scrollTo with
                      // absolute math, which fought iOS Safari's own
                      // keyboard-shift logic and overshot the field. Keep the
                      // original scrollTo for Android + desktop where it
                      // worked, and on iOS use a longer delay (so the
                      // keyboard finishes animating in) plus scrollIntoView
                      // block:center, which respects iOS's viewport offset
                      // and reliably lands the field in view.
                      const target = e.target;
                      const isIOS =
                        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                      if (isIOS) {
                        setTimeout(() => {
                          if (!target) return;
                          // Pad the top so the field doesn't end up right
                          // under the iOS status / notification bar after
                          // scrollIntoView. scroll-margin-top is the right
                          // hook — block:"start" then aligns the textarea
                          // 80px below the scroll container's top edge,
                          // plus iOS's safe-area inset.
                          const prevMargin = target.style.scrollMarginTop;
                          target.style.scrollMarginTop = "calc(env(safe-area-inset-top, 0px) + 80px)";
                          target.scrollIntoView({ block: "start", behavior: "smooth" });
                          // Clear the override after the smooth scroll finishes
                          // so it doesn't affect any later programmatic scrolls.
                          setTimeout(() => { if (target) target.style.scrollMarginTop = prevMargin; }, 700);
                        }, 500);
                      } else {
                        setTimeout(() => {
                          const scrollContainer = target?.closest('.overflow-y-auto');
                          const block = target?.closest('div');
                          if (scrollContainer && block) {
                            scrollContainer.scrollTo({ top: block.offsetTop - scrollContainer.offsetTop, behavior: 'smooth' });
                          }
                        }, 300);
                      }
                    }}
                    placeholder="Call notes..."
                    className="input-dark min-h-16"
                    rows={3}
                  />
                </div>

                {/* Callback Numbers - Each with caller type */}
                <div className="space-y-2">
                  <Label>Callback Numbers</Label>
                  {/* Primary callback with caller type */}
                  <div className="flex gap-2 items-center flex-wrap">
                    <Input
                      value={newCall.callback_phone || ""}
                      onChange={(e) => setNewCall({ ...newCall, callback_phone: formatPhoneNumber(e.target.value) })}
                      placeholder="Primary number..."
                      className="input-dark flex-1 min-w-[120px]"
                      type="tel"
                      inputMode="numeric"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      title="Paste from clipboard"
                      onClick={async () => {
                        try {
                          const text = await navigator.clipboard.readText();
                          if (text) setNewCall({ ...newCall, callback_phone: formatPhoneNumber(text.trim()) });
                        } catch (e) { toast.error("Could not access clipboard"); }
                      }}
                      className="h-8 px-2"
                    >
                      <ClipboardPaste className="w-4 h-4" />
                    </Button>
                    <Popover open={showRecentCalls === "primary"} onOpenChange={(open) => { setShowRecentCalls(open ? "primary" : null); if (open) fetchRecentIncomingCalls(); }}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          title="Recent incoming calls"
                          className="h-8 px-2"
                        >
                          <PhoneIncoming className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[10001]">
                        <div className="text-xs text-slate-400 mb-2">Recent Incoming Calls (30 min)</div>
                        {loadingRecentCalls ? (
                          <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                        ) : recentIncomingCalls.length === 0 ? (
                          <div className="text-xs text-slate-500 text-center py-2">No recent calls</div>
                        ) : (
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {recentIncomingCalls.map((call, idx) => (
                              <Button
                                key={idx}
                                variant="ghost"
                                size="sm"
                                className="w-full justify-between text-xs h-8 px-2"
                                onClick={() => {
                                  setNewCall({ ...newCall, callback_phone: call.caller_number, is_hotline_call: true });
                                  setShowRecentCalls(null);
                                }}
                              >
                                <span className="flex items-center gap-1.5 truncate">
                                  <Phone className="w-3 h-3 shrink-0" />
                                  {call.caller_number}
                                  {call.contact_name && <span className="text-slate-400 truncate">{call.contact_name}</span>}
                                </span>
                                <span className="flex items-center gap-1 shrink-0 ml-1">
                                  <span className="text-[10px] text-slate-500">{formatCallTime(call.received_at)}</span>
                                  {call.contact_status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                                  {call.contact_status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">K</span>}
                                  {call.contact_status && !["flagged","known"].includes(call.contact_status) && <span className="text-[9px] text-slate-500 uppercase">{call.contact_status.slice(0,3)}</span>}
                                </span>
                              </Button>
                            ))}
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                    {newCall.callback_phone && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-400 hover:text-blue-400"
                        title="Lookup phone number"
                        onClick={() => setPhoneLookupNumber(newCall.callback_phone)}
                      >
                        <Search className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Select
                      value={newCall.cv_or_third_party || "none"}
                      onValueChange={(v) => setNewCall({ ...newCall, cv_or_third_party: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="input-dark w-[70px]">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="cv">CV</SelectItem>
                        <SelectItem value="3rd_party">3rd</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1">
                      <Checkbox
                        id="primary_visual"
                        checked={newCall.caller_has_visual || false}
                        onCheckedChange={(checked) => setNewCall({ ...newCall, caller_has_visual: checked })}
                      />
                      <Label htmlFor="primary_visual" className="text-xs cursor-pointer whitespace-nowrap">Vis</Label>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      title="Add another callback"
                      onClick={() => setNewCallbackNumber({ number: "", caller_type: "", has_visual: false, showInput: true })}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Primary callback contact info */}
                  {(() => {
                    const info = getContactInfo(newCall.callback_phone);
                    if (!info) return null;
                    return (
                      <div className="flex items-center gap-1.5 pl-1 -mt-1">
                        {info.status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                        {info.status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">KNOWN</span>}
                        {info.name && <span className="text-[10px] text-yellow-400/80">{info.name}</span>}
                      </div>
                    );
                  })()}

                  {/* Additional callback numbers with caller type */}
                  {newCall.callback_numbers?.length > 0 && (
                    <div className="space-y-1 pl-2 border-l-2 border-slate-700">
                      <p className="text-xs text-slate-500">Additional Numbers:</p>
                      {newCall.callback_numbers.map((cb, idx) => (
                        editingNewCallCallbackIdx === idx ? (
                          <div key={idx} className="flex gap-2 bg-slate-800 px-2 py-1 rounded flex-wrap">
                            <Input
                              value={editingNewCallCallbackData.number}
                              onChange={(e) => setEditingNewCallCallbackData({ ...editingNewCallCallbackData, number: formatPhoneNumber(e.target.value) })}
                              placeholder="Number..."
                              className="input-dark flex-1 min-w-[100px] h-7 text-sm"
                              type="tel"
                              inputMode="numeric"
                              autoFocus
                            />
                            <Select
                              value={editingNewCallCallbackData.caller_type || "none"}
                              onValueChange={(v) => setEditingNewCallCallbackData({ ...editingNewCallCallbackData, caller_type: v === "none" ? "" : v })}
                            >
                              <SelectTrigger className="input-dark w-24 h-7 text-xs">
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">—</SelectItem>
                                <SelectItem value="cv">CV</SelectItem>
                                <SelectItem value="3rd_party">3rd Party</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="flex items-center gap-1">
                              <Checkbox
                                id={`edit_new_cb_visual_${idx}`}
                                checked={editingNewCallCallbackData.has_visual || false}
                                onCheckedChange={(checked) => setEditingNewCallCallbackData({ ...editingNewCallCallbackData, has_visual: checked })}
                              />
                              <Label htmlFor={`edit_new_cb_visual_${idx}`} className="text-[10px] cursor-pointer">Visual</Label>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                if (editingNewCallCallbackData.number.trim()) {
                                  const updated = [...newCall.callback_numbers];
                                  updated[idx] = { ...editingNewCallCallbackData };
                                  setNewCall({ ...newCall, callback_numbers: updated });
                                }
                                setEditingNewCallCallbackIdx(null);
                              }}
                            >
                              Save
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => setEditingNewCallCallbackIdx(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div key={idx} className="flex items-center gap-2 text-sm bg-slate-800 px-2 py-1 rounded flex-wrap">
                            <Phone className="w-3 h-3 text-slate-400" />
                            <span>{formatPhoneNumber(cb.number)}</span>
                            {cb.label && <span className="text-slate-500">({cb.label})</span>}
                            {cb.caller_type && (
                              <Badge variant="outline" className="text-[10px]">
                                {cb.caller_type === '3rd_party' ? '3rd Party' : cb.caller_type === 'cv' ? 'CV' : cb.caller_type}
                              </Badge>
                            )}
                            {cb.has_visual && (
                              <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400">VISUAL</Badge>
                            )}
                            {(() => {
                              const info = getContactInfo(cb.number);
                              if (!info) return null;
                              return <>
                                {info.status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                                {info.status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">KNOWN</span>}
                                {info.name && <span className="text-[10px] text-yellow-400/80">{info.name}</span>}
                              </>;
                            })()}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="ml-auto h-6 w-6 p-0 text-slate-400 hover:text-blue-400"
                              title="Lookup phone number"
                              onClick={() => setPhoneLookupNumber(cb.number)}
                            >
                              <Search className="w-3 h-3" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-blue-400"
                              onClick={() => {
                                setEditingNewCallCallbackData({ number: cb.number, caller_type: cb.caller_type, has_visual: cb.has_visual });
                                setEditingNewCallCallbackIdx(idx);
                              }}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-400"
                              onClick={() => setNewCall({
                                ...newCall,
                                callback_numbers: newCall.callback_numbers.filter((_, i) => i !== idx)
                              })}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                  
                  {/* Input for adding new additional number */}
                  {newCallbackNumber.showInput && (
                    <div className="flex gap-2 pl-2 border-l-2 border-slate-700 flex-wrap">
                      <Input
                        value={newCallbackNumber.number}
                        onChange={(e) => setNewCallbackNumber({ ...newCallbackNumber, number: formatPhoneNumber(e.target.value) })}
                        placeholder="Number..."
                        className="input-dark flex-1 min-w-[120px]"
                        type="tel"
                        inputMode="numeric"
                        autoFocus
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        title="Paste from clipboard"
                        onClick={async () => {
                          try {
                            const text = await navigator.clipboard.readText();
                            if (text) setNewCallbackNumber({ ...newCallbackNumber, number: formatPhoneNumber(text.trim()) });
                          } catch (e) { toast.error("Could not access clipboard"); }
                        }}
                        className="h-8 px-2"
                      >
                        <ClipboardPaste className="w-4 h-4" />
                      </Button>
                      <Popover open={showRecentCalls === "newAdditional"} onOpenChange={(open) => { setShowRecentCalls(open ? "newAdditional" : null); if (open) fetchRecentIncomingCalls(); }}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            title="Recent incoming calls"
                            className="h-8 px-2"
                          >
                            <PhoneIncoming className="w-4 h-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[10001]">
                          <div className="text-xs text-slate-400 mb-2">Recent Incoming Calls (30 min)</div>
                          {loadingRecentCalls ? (
                            <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                          ) : recentIncomingCalls.length === 0 ? (
                            <div className="text-xs text-slate-500 text-center py-2">No recent calls</div>
                          ) : (
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {recentIncomingCalls.map((call, idx) => (
                                <Button
                                  key={idx}
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-between text-xs h-8 px-2"
                                  onClick={() => {
                                    setNewCallbackNumber({ ...newCallbackNumber, number: call.caller_number });
                                    setNewCall(prev => ({ ...prev, is_hotline_call: true }));
                                    setShowRecentCalls(null);
                                  }}
                                >
                                  <span className="flex items-center gap-1.5 truncate">
                                    <Phone className="w-3 h-3 shrink-0" />
                                    {call.caller_number}
                                    {call.contact_name && <span className="text-slate-400 truncate">{call.contact_name}</span>}
                                  </span>
                                  <span className="flex items-center gap-1 shrink-0 ml-1">
                                    <span className="text-[10px] text-slate-500">{formatCallTime(call.received_at)}</span>
                                    {call.contact_status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                                    {call.contact_status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">K</span>}
                                    {call.contact_status && !["flagged","known"].includes(call.contact_status) && <span className="text-[9px] text-slate-500 uppercase">{call.contact_status.slice(0,3)}</span>}
                                  </span>
                                </Button>
                              ))}
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                      <Select
                        value={newCallbackNumber.caller_type || ""}
                        onValueChange={(v) => setNewCallbackNumber({ ...newCallbackNumber, caller_type: v })}
                      >
                        <SelectTrigger className="input-dark w-28">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cv">CV</SelectItem>
                          <SelectItem value="3rd_party">3rd Party</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1">
                        <Checkbox
                          id="cb_visual"
                          checked={newCallbackNumber.has_visual || false}
                          onCheckedChange={(checked) => setNewCallbackNumber({ ...newCallbackNumber, has_visual: checked })}
                        />
                        <Label htmlFor="cb_visual" className="text-xs cursor-pointer whitespace-nowrap">Visual</Label>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (newCallbackNumber.number.trim()) {
                            setNewCall({
                              ...newCall,
                              callback_numbers: [...(newCall.callback_numbers || []), {
                                number: newCallbackNumber.number,
                                caller_type: newCallbackNumber.caller_type,
                                has_visual: newCallbackNumber.has_visual
                              }]
                            });
                          }
                          setNewCallbackNumber({ number: "", caller_type: "", has_visual: false });
                        }}
                      >
                        Add
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setNewCallbackNumber({ number: "", caller_type: "", has_visual: false })}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Perpetrators Section - Add before creating call (#16) */}
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800/50 rounded-sm space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-amber-700 dark:text-amber-400 uppercase font-medium flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Perpetrators
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white font-medium"
                      onClick={() => {
                        setNewPerp({
                          name: "", address: "", current_location: "", sex: "", race: "", age: "", height: "", weight: "",
                          build: "", complexion: "", hair_color_cut: "", eye_color: "", hat_color_style: "",
                          shirt: "", pants: "", jacket_coat: "", shoes: "", visible_marks: "", clothing: "",
                          direction_of_flight: "", has_weapon: false, weapon_description: "",
                          vehicle_description: "", license_plate: "", was_arrested: false, notes: ""
                        });
                        setShowNewCallPerpDialog(true);
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" />Add Perp
                    </Button>
                  </div>
                  {newCall.perp_descriptions?.length > 0 && (
                    <div className="space-y-1">
                      {newCall.perp_descriptions.map((p, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm bg-slate-800 px-2 py-1 rounded">
                          <AlertTriangle className="w-3 h-3 text-amber-400" />
                          <span className="font-medium">Perp #{idx + 1}</span>
                          <span className="text-slate-400 truncate flex-1">
                            {[p.name, p.sex, p.race, p.age && `~${p.age}`, p.height, p.clothing].filter(Boolean).join(', ') || 'No description'}
                          </span>
                          {p.has_weapon && <Badge variant="destructive" className="text-[10px]">ARMED</Badge>}
                          {p.was_arrested && <Badge className="bg-green-600 text-[10px]">ARRESTED</Badge>}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-blue-400"
                            onClick={() => {
                              setNewPerp({...p});
                              setEditingNewCallPerpIndex(idx);
                              setShowNewCallPerpDialog(true);
                            }}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-400"
                            onClick={() => {
                              setPendingDeleteNewCallPerpIndex(idx);
                              setShowDeleteNewCallPerpConfirm(true);
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label>Basic Description (if time limited to enter or if multiple perps)</Label>
                  <Textarea
                    value={newCall.description}
                    onChange={(e) => setNewCall({ ...newCall, description: e.target.value })}
                    placeholder="Basic call description..."
                    className="input-dark min-h-20"
                    data-testid="description-input"
                  />
                </div>

                <div>
                  <Label>Confirmation Status</Label>
                  <Select
                    value={newCall.confirmation_status || "no_confirm"}
                    onValueChange={(v) => setNewCall({ ...newCall, confirmation_status: v === "no_confirm" ? "" : v })}
                  >
                    <SelectTrigger className="input-dark">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_confirm">No Confirm</SelectItem>
                      <SelectItem value="unconfirmed">Unconfirmed</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Call Settings */}
                {renderNewCallExtras()}

              </div>
              )}
              </div>
              {/* Floating Footer */}
              <div className="flex-shrink-0 border-t border-slate-700 bg-slate-900 px-6 py-4">
                <div className={`grid grid-cols-2 ${createdCallForEdit ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-2`}>
                  {createdCallForEdit ? (
                    <>
                      <Button
                        onClick={() => setShowSubmitWithoutClosingConfirm(true)}
                        variant="outline"
                        className="w-full border-blue-600 text-blue-400 hover:bg-blue-950 text-xs sm:text-sm"
                      >
                        Update & Open
                      </Button>
                      <Button
                        onClick={handleUpdateAndClose}
                        variant="outline"
                        className="w-full border-teal-600 text-teal-400 hover:bg-teal-950 text-xs sm:text-sm"
                      >
                        Update & Close
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedCall(createdCallForEdit);
                          setShowUpdateDialog(true);
                        }}
                        variant="outline"
                        className="w-full border-purple-600 text-purple-400 hover:bg-purple-950 text-xs sm:text-sm"
                      >
                        <MessageSquare className="w-3 h-3 mr-1" />
                        Add Update
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => setShowCreateCallConfirm(true)}
                        variant="outline"
                        className="w-full border-green-600 text-green-400 hover:bg-green-950 text-xs sm:text-sm"
                        data-testid="create-call-btn"
                      >
                        Create Call
                      </Button>
                      <Button
                        onClick={() => setShowSubmitWithoutClosingConfirm(true)}
                        variant="outline"
                        className="w-full border-blue-600 text-blue-400 hover:bg-blue-950 text-xs sm:text-sm"
                      >
                        Submit Open
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={() => setShowSendToIncidentsConfirm(true)}
                    variant="outline"
                    className="w-full border-orange-600 text-orange-400 hover:bg-orange-950 text-xs sm:text-sm"
                  >
                    To Incidents
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showQuickCallDialog} onOpenChange={(open) => {
            if (!open) { resetNewCallForm(); setQuickFolds(QUICK_FOLDS_CLOSED); }
            setShowQuickCallDialog(open);
          }}>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-lg bg-slate-900 border-slate-800 max-h-[90vh] overflow-hidden flex flex-col p-0">
              <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
                <DialogTitle className="uppercase tracking-wider">New Live Call — Quick</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
                {renderQuickCallForm()}
              </div>
              {/* Floating Footer */}
              <div className="flex-shrink-0 border-t border-slate-700 bg-slate-900 px-6 py-4">
                <Button
                  onClick={() => setShowCreateCallConfirm(true)}
                  className="btn-primary w-full h-12 text-base font-semibold"
                  data-testid="quick-create-call-btn"
                >
                  Create Call
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* New Call Perpetrator Dialog - Full form (#4) */}
          <Dialog open={showNewCallPerpDialog} onOpenChange={setShowNewCallPerpDialog}>
            <DialogContent className="bg-slate-900 border-slate-800 w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle>{editingNewCallPerpIndex !== null ? 'Edit Perpetrator' : 'Add Perpetrator'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                {/* Current Location */}
                <div>
                  <Label className="text-xs text-amber-400">Current Location</Label>
                  <Input value={newPerp.current_location} onChange={(e) => setNewPerp({...newPerp, current_location: e.target.value})} className="input-dark h-8" placeholder="Where perp is now / last seen" />
                </div>

                {/* Direction of Flight */}
                <div>
                  <Label className="text-xs">Direction of Flight</Label>
                  <Input value={newPerp.direction_of_flight} onChange={(e) => setNewPerp({...newPerp, direction_of_flight: e.target.value})} className="input-dark h-8" placeholder="Northbound on Main St" />
                </div>

                {/* Physical Description */}
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Sex</Label>
                    <select value={newPerp.sex || ""} onChange={(e) => setNewPerp({...newPerp, sex: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                      <option value="">—</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Race</Label>
                    <select value={newPerp.race || ""} onChange={(e) => setNewPerp({...newPerp, race: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                      <option value="">—</option>
                      <option value="Black">Black</option>
                      <option value="White">White</option>
                      <option value="Hispanic">Hispanic</option>
                      <option value="Asian">Asian</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Age</Label>
                    <Input value={newPerp.age} onChange={(e) => setNewPerp({...newPerp, age: e.target.value})} className="input-dark h-8" placeholder="~25" />
                  </div>
                  <div>
                    <Label className="text-xs">Height</Label>
                    <Input value={newPerp.height} onChange={(e) => setNewPerp({...newPerp, height: e.target.value})} className="input-dark h-8" placeholder="5'10" />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Weight</Label>
                    <Input value={newPerp.weight} onChange={(e) => setNewPerp({...newPerp, weight: e.target.value})} className="input-dark h-8" placeholder="180 lbs" />
                  </div>
                  <div>
                    <Label className="text-xs">Build</Label>
                    <select value={newPerp.build || ""} onChange={(e) => setNewPerp({...newPerp, build: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                      <option value="">—</option>
                      <option value="Slim">Slim</option>
                      <option value="Medium">Medium</option>
                      <option value="Heavy">Heavy</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Complexion</Label>
                    <Input value={newPerp.complexion} onChange={(e) => setNewPerp({...newPerp, complexion: e.target.value})} className="input-dark h-8" placeholder="Light/Dark" />
                  </div>
                  <div>
                    <Label className="text-xs">Eye Color</Label>
                    <Input value={newPerp.eye_color} onChange={(e) => setNewPerp({...newPerp, eye_color: e.target.value})} className="input-dark h-8" placeholder="Brown" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Hair Color/Style</Label>
                    <Input value={newPerp.hair_color_cut} onChange={(e) => setNewPerp({...newPerp, hair_color_cut: e.target.value})} className="input-dark h-8" placeholder="Black, short" />
                  </div>
                  <div>
                    <Label className="text-xs">Visible Marks/Tattoos</Label>
                    <Input value={newPerp.visible_marks} onChange={(e) => setNewPerp({...newPerp, visible_marks: e.target.value})} className="input-dark h-8" placeholder="Scar on left cheek" />
                  </div>
                </div>
                
                {/* Clothing */}
                <p className="text-xs text-slate-500 uppercase pt-2">Clothing</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Hat</Label>
                    <Input value={newPerp.hat_color_style} onChange={(e) => setNewPerp({...newPerp, hat_color_style: e.target.value})} className="input-dark h-8" placeholder="Red baseball cap" />
                  </div>
                  <div>
                    <Label className="text-xs">Shirt</Label>
                    <Input value={newPerp.shirt} onChange={(e) => setNewPerp({...newPerp, shirt: e.target.value})} className="input-dark h-8" placeholder="White t-shirt" />
                  </div>
                  <div>
                    <Label className="text-xs">Jacket/Coat</Label>
                    <Input value={newPerp.jacket_coat} onChange={(e) => setNewPerp({...newPerp, jacket_coat: e.target.value})} className="input-dark h-8" placeholder="Black hoodie" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Pants</Label>
                    <Input value={newPerp.pants} onChange={(e) => setNewPerp({...newPerp, pants: e.target.value})} className="input-dark h-8" placeholder="Blue jeans" />
                  </div>
                  <div>
                    <Label className="text-xs">Shoes</Label>
                    <Input value={newPerp.shoes} onChange={(e) => setNewPerp({...newPerp, shoes: e.target.value})} className="input-dark h-8" placeholder="White sneakers" />
                  </div>
                  <div>
                    <Label className="text-xs">Other Clothing</Label>
                    <Input value={newPerp.clothing} onChange={(e) => setNewPerp({...newPerp, clothing: e.target.value})} className="input-dark h-8" placeholder="Backpack" />
                  </div>
                </div>
                
                {/* Status */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox id="newcall_perp_armed" checked={newPerp.has_weapon} onCheckedChange={(checked) => setNewPerp({...newPerp, has_weapon: checked})} />
                    <Label htmlFor="newcall_perp_armed" className="text-sm text-red-400">Armed</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="newcall_perp_arrested" checked={newPerp.was_arrested} onCheckedChange={(checked) => setNewPerp({...newPerp, was_arrested: checked})} />
                    <Label htmlFor="newcall_perp_arrested" className="text-sm text-green-400">Arrested</Label>
                  </div>
                </div>
                {newPerp.has_weapon && (
                  <div>
                    <Label className="text-xs">Weapon Description</Label>
                    <Input value={newPerp.weapon_description} onChange={(e) => setNewPerp({...newPerp, weapon_description: e.target.value})} className="input-dark h-8" placeholder="Handgun, knife, etc." />
                  </div>
                )}
                
                {/* Vehicle */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Vehicle</Label>
                    <Input value={newPerp.vehicle_description} onChange={(e) => setNewPerp({...newPerp, vehicle_description: e.target.value})} className="input-dark h-8" placeholder="Black sedan" />
                  </div>
                  <div>
                    <Label className="text-xs">License Plate</Label>
                    <Input value={newPerp.license_plate} onChange={(e) => setNewPerp({...newPerp, license_plate: e.target.value})} className="input-dark h-8" placeholder="ABC-1234" />
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea value={newPerp.notes} onChange={(e) => setNewPerp({...newPerp, notes: e.target.value})} className="input-dark" rows={2} placeholder="Additional notes..." />
                </div>

                {/* Name & Address */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Name (if known)</Label>
                    <Input value={newPerp.name} onChange={(e) => setNewPerp({...newPerp, name: e.target.value})} className="input-dark h-8" placeholder="Name" />
                  </div>
                  <div>
                    <Label className="text-xs">Address (if known)</Label>
                    <Input value={newPerp.address} onChange={(e) => setNewPerp({...newPerp, address: e.target.value})} className="input-dark h-8" placeholder="Address" />
                  </div>
                </div>

                {/* Link to Profile */}
                <div className="border-t border-slate-700 pt-3">
                  <p className="text-xs text-purple-400 font-medium uppercase mb-2">
                    <UserIcon className="w-3 h-3 inline mr-1" />Link to Profile
                  </p>
                  {newPerp.linked_profile_id ? (
                    <div className="flex items-center justify-between p-2 bg-purple-900/30 border border-purple-700 rounded">
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4 text-purple-400" />
                        <div>
                          <p className="text-sm font-medium text-purple-200">
                            {perpetratorProfiles.find(p => p.profile_id === newPerp.linked_profile_id)?.name || 'Unknown Profile'}
                          </p>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-red-400" onClick={() => setNewPerp({...newPerp, linked_profile_id: null, linked_profile_name: null})}>
                        <X className="w-3 h-3 mr-1" />Unlink
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2 mb-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            value={profileSearchQuery}
                            onChange={(e) => setProfileSearchQuery(e.target.value)}
                            placeholder="Search profiles by name..."
                            className="input-dark pl-8 h-8 text-sm"
                          />
                        </div>
                        <Button variant="outline" size="sm" className="h-8 px-2" title="Browse all profiles" onClick={() => setShowProfileBrowse(true)}>
                          <List className="w-4 h-4" />
                        </Button>
                      </div>
                      {profileSearchQuery && (
                        <ScrollArea className="max-h-32">
                          <div className="space-y-1">
                            {perpetratorProfiles
                              .filter(p => p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase()))
                              .slice(0, 5)
                              .map(profile => (
                                <div
                                  key={profile.profile_id}
                                  className="p-2 bg-slate-800 rounded cursor-pointer hover:bg-slate-700 flex items-center justify-between"
                                  onClick={() => {
                                    setNewPerp({...newPerp, linked_profile_id: profile.profile_id, linked_profile_name: profile.name});
                                    setProfileSearchQuery("");
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <UserIcon className="w-4 h-4 text-purple-400" />
                                    <p className="text-sm font-medium text-slate-200">{profile.name || 'Unknown'}</p>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {profile.status === "ready_for_arrest" && <Badge className="text-[9px] bg-amber-600 px-1">READY</Badge>}
                                    {profile.status === "warrant" && <Badge className="text-[9px] bg-red-600 px-1">WARRANT</Badge>}
                                    {profile.status === "arrested" && <Badge className="text-[9px] bg-emerald-600 px-1">ARRESTED</Badge>}
                                  </div>
                                </div>
                              ))}
                            {perpetratorProfiles.filter(p => p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase())).length === 0 && (
                              <p className="text-xs text-slate-500 p-2">No matching profiles found</p>
                            )}
                          </div>
                        </ScrollArea>
                      )}
                    </>
                  )}
                </div>
                
                <Button 
                  className="w-full btn-primary"
                  onClick={() => {
                    if (editingNewCallPerpIndex !== null) {
                      // Editing existing
                      const updated = [...(newCall.perp_descriptions || [])];
                      updated[editingNewCallPerpIndex] = { ...newPerp, perp_id: updated[editingNewCallPerpIndex].perp_id };
                      setNewCall({ ...newCall, perp_descriptions: updated });
                    } else {
                      // Adding new
                      setNewCall({
                        ...newCall,
                        perp_descriptions: [...(newCall.perp_descriptions || []), { ...newPerp, perp_id: `perp_${Date.now()}` }]
                      });
                    }
                    setShowNewCallPerpDialog(false);
                    setEditingNewCallPerpIndex(null);
                    setNewPerp({
                      name: "", address: "", current_location: "", sex: "", race: "", age: "", height: "", weight: "",
                      build: "", complexion: "", hair_color_cut: "", eye_color: "", hat_color_style: "",
                      shirt: "", pants: "", jacket_coat: "", shoes: "", visible_marks: "", clothing: "",
                      direction_of_flight: "", has_weapon: false, weapon_description: "",
                      vehicle_description: "", license_plate: "", was_arrested: false, notes: "",
                      linked_profile_id: null, linked_profile_name: null
                    });
                    setProfileSearchQuery("");
                  }}
                >
                  {editingNewCallPerpIndex !== null ? 'Update Perpetrator' : 'Add Perpetrator'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Split Pane Layout */}
      <div className="flex flex-1 min-h-0 gap-0 lg:gap-4 overflow-hidden w-full">
        {/* Left Panel - Call List (hidden on mobile when a call is selected) */}
        <div className={`split-pane-left bg-slate-900 rounded-sm min-w-0 w-full ${selectedCall ? 'hidden lg:block' : ''} ${listMinimized ? 'minimized' : ''}`} style={{ maxWidth: '100%', outline: '1px solid rgb(30 41 59)', outlineOffset: '-1px' }}>
          <ScrollArea className="h-full p-2">
            <div className="overflow-hidden">
            {sortedCalls.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Radio className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No active live calls</p>
              </div>
            ) : (
              sortedCalls.map((call) => {
                // Check if any perp is armed
                const hasArmedPerp = call.perp_descriptions?.some(p => p.has_weapon);
                
                return (
                <div
                  key={call.call_id}
                  onClick={() => setSelectedCall(call)}
                  data-testid={`live-call-${call.call_id}`}
                  className={`live-call-card overflow-hidden ${call.priority === "priority" ? "priority" : ""} ${
                    selectedCall?.call_id === call.call_id ? "selected" : ""
                  }`}
                >
                  {/* First line: Priority icon + all badges + time */}
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
                      {call.priority === "priority" && (
                        <AlertTriangle className="w-4 h-4 text-red-500 animate-pulse flex-shrink-0" />
                      )}
                      {/* Confirmation Status Badge - only show when explicitly set */}
                      {(call.confirmation_status === "confirmed" || call.confirmation_status === "unconfirmed") && (
                        <Badge
                          className={`text-[10px] px-1 py-0 flex-shrink-0 ${
                            call.confirmation_status === "confirmed" ? "bg-green-600 text-white" :
                            "bg-amber-600 text-white"
                          }`}
                        >
                          {call.confirmation_status.toUpperCase()}
                        </Badge>
                      )}
                      {/* Units Req'd Badge */}
                      {call.units_required && (
                        <Badge className="text-[10px] px-1 py-0 flex-shrink-0 bg-purple-500 text-white">
                          UNITS REQ'D
                        </Badge>
                      )}
                      {/* Slowdown Badge */}
                      {call.slowdown && (
                        <Badge className="text-[10px] px-1 py-0 flex-shrink-0 bg-purple-500 text-white">
                          SLOWDOWN
                        </Badge>
                      )}
                      {/* No Further Badge */}
                      {call.no_further && (
                        <Badge className="text-[10px] px-1 py-0 flex-shrink-0 bg-slate-600 text-white">
                          NO FURTHER
                        </Badge>
                      )}
                      {/* Forthwith Badge */}
                      {call.forthwith && (
                        <Badge className="text-[10px] px-1 py-0 flex-shrink-0 bg-pink-500 text-white">
                          FORTHWITH
                        </Badge>
                      )}
                      {/* Call Mode Badge - LIVE CALL or CALL 98 */}
                      {call.call_mode === "live_call" && (
                        <Badge className="text-[10px] px-1 py-0 flex-shrink-0 bg-blue-600 text-white">
                          LIVE CALL
                        </Badge>
                      )}
                      {call.call_mode === "call_98" && (
                        <Badge className="text-[10px] px-1 py-0 flex-shrink-0 bg-green-600 text-white">
                          CALL 98
                        </Badge>
                      )}
                      {/* 10-85/10-13 badges */}
                      {call.transmitted_10_85 && (
                        <Badge variant="destructive" className="text-[10px] px-1 py-0 flex-shrink-0 animate-pulse">10-85</Badge>
                      )}
                      {call.transmitted_10_13 && (
                        <Badge variant="destructive" className="text-[10px] px-1 py-0 flex-shrink-0 animate-pulse">10-13</Badge>
                      )}
                      {/* Armed Perp Badge */}
                      {hasArmedPerp && (
                        <Badge variant="destructive" className="text-[10px] px-1 py-0 flex-shrink-0 bg-red-700">
                          ⚠ ARMED
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0">
                      <span className="text-xs text-slate-500 font-mono whitespace-nowrap">
                        {formatTime(call.created_at)}
                      </span>
                      {call.created_by_name && (
                        <span className="text-[10px] text-slate-500 truncate max-w-[80px]">{call.created_by_name}</span>
                      )}
                      <span className="text-[10px] text-slate-600 font-mono">{call.call_id}</span>
                    </div>
                  </div>
                  
                  {/* Second line: Call type - BOLD UPPERCASE on its own line */}
                  <div className="mb-1">
                    <span className="font-bold text-sm uppercase tracking-wide">{call.call_type || "UNKNOWN"}</span>
                    {call.secondary_call_type && (
                      <span className="font-bold text-sm uppercase tracking-wide ml-1">/ {call.secondary_call_type}</span>
                    )}
                  </div>
                  
                  {/* Location */}
                  <div className="text-sm text-slate-400 mb-1">
                    <div className="flex items-start gap-1">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>
                        {call.location}
                        {call.location_notes && <span className="text-slate-500 italic"> - {call.location_notes}</span>}
                      </span>
                    </div>
                    {call.cross_streets && (
                      <div className="text-xs text-emerald-600 dark:text-emerald-400 ml-4 truncate">({call.cross_streets})</div>
                    )}
                  </div>
                  {/* Show assigned units with 84/98 status - badge format like Response */}
                  {call.assigned_units?.length > 0 && (
                    <div className="mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedUnitsCallIds(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(call.call_id)) {
                              newSet.delete(call.call_id);
                            } else {
                              newSet.add(call.call_id);
                            }
                            return newSet;
                          });
                        }}
                        className="text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 flex items-center gap-1"
                      >
                        <Users className="w-3 h-3" />
                        {call.assigned_units.length} unit{call.assigned_units.length !== 1 ? 's' : ''}
                        {expandedUnitsCallIds.has(call.call_id) ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                      {expandedUnitsCallIds.has(call.call_id) && (
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                          {[...call.assigned_units].sort((a, b) => {
                            const numA = parseInt(a.replace(/\D/g, '')) || 0;
                            const numB = parseInt(b.replace(/\D/g, '')) || 0;
                            if (numA !== numB) return numA - numB;
                            return a.localeCompare(b);
                          }).map((unit) => {
                            const unitStatus = call.unit_statuses?.[unit] || {};
                            return (
                              <Badge
                                key={unit}
                                variant="outline"
                                className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-slate-200 dark:bg-slate-800 border-slate-400 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                              >
                                {unit}
                                {unitStatus.status_84 && <span className="ml-1 text-blue-600 dark:text-blue-400">84</span>}
                                {unitStatus.status_98 && <span className="ml-1 text-green-600 dark:text-green-400">98</span>}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {call.updates?.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                      <MessageSquare className="w-3 h-3" />
                      <span>{call.updates.length} updates</span>
                    </div>
                  )}
                </div>
              );
              })
            )}
            </div>
          </ScrollArea>
        </div>

        {/* Right Panel - Call Details (full width on mobile when selected) */}
        <div ref={detailsPanelRef} className={`split-pane-right bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg overflow-y-auto ${selectedCall ? 'block' : 'hidden lg:block'} ${listMinimized ? 'maximized' : ''}`}>
          {selectedCall ? (
            <div className="p-4 pb-32 lg:pb-4">
              {/* Desktop (lg+): tabs at top — the masonry grid makes
                  "under the header" ambiguous. Mobile renders them under the
                  header card instead (below). */}
              <div className="hidden lg:block">{renderDetailTabsBar()}</div>
              {/* Desktop layout: a single 2-col grid that flattens the 3 row
                  containers via lg:contents so the cards can be reordered with
                  lg:order-N. Mobile keeps DOM order via the row containers. */}
              <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:items-stretch">
              {/* Call Header */}
              <div className="grid grid-cols-1 gap-4 mb-4 lg:contents">
                {/* Left: Call Info Box */}
                <Card id="section-header" className="bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-800 lg:order-1">
                  <CardContent className="p-4">
                    {/* Call Type with Back Button and Maximize Toggle */}
                    <div className="flex items-center gap-2 mb-3">
                      {/* Mobile: Back to list */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="lg:hidden h-7 px-2 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                        onClick={handleBackToList}
                        data-testid="back-to-list-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      {/* Desktop: Show list button (when minimized) */}
                      {listMinimized && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="hidden lg:flex h-7 px-2 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                          onClick={() => setListMinimized(false)}
                          title="Show call list"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                      )}
                      <h2 className="text-xl font-bold text-slate-900 dark:text-white flex-1">
                        {selectedCall.call_type || "No Type"}
                        {selectedCall.secondary_call_type && (
                          <span className="text-xl font-bold text-slate-900 dark:text-white ml-2">/ {selectedCall.secondary_call_type}</span>
                        )}
                      </h2>
                      {/* Desktop: Maximize/Minimize toggle */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hidden lg:flex h-7 px-2 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                        onClick={() => setListMinimized(!listMinimized)}
                        title={listMinimized ? "Show call list" : "Maximize call view"}
                      >
                        {listMinimized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                      </Button>
                    </div>

                    {/* Show / Hide button for the locations block. Hidden by
                        default since the same info is in the Location Info
                        card; this is a quick-glance shortcut. */}
                    <div className="flex items-center mb-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px] text-slate-500 hover:text-slate-300"
                        onClick={() => setShowHeaderLocations(!showHeaderLocations)}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        {showHeaderLocations ? 'Hide Locations' : 'Show Locations'}
                        {!showHeaderLocations && (selectedCall.location || (selectedCall.additional_locations?.length || 0) > 0) && (
                          <span className="ml-2 text-[10px] text-slate-600">
                            ({1 + (selectedCall.additional_locations?.length || 0)})
                          </span>
                        )}
                      </Button>
                    </div>

                    {showHeaderLocations && (
                    <>
                    {/* Address with Location Note */}
                    <div className="flex items-start gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        {selectedCall.location_title && <span className="text-[10px] text-amber-400 font-semibold uppercase mr-1">({selectedCall.location_title})</span>}
                        <span className="text-slate-700 dark:text-slate-200">{selectedCall.location || "No Location"}</span>
                        {selectedCall.location_notes && (
                          <span className="text-slate-500 dark:text-slate-400 italic ml-2">- {selectedCall.location_notes}</span>
                        )}
                        {selectedCall.location && (
                          <a
                            href={mapHrefFor(selectedCall.location)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 ml-2 text-xs"
                            title="Open in Google Maps"
                          >
                            [Map]
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Cross Streets */}
                    {selectedCall.cross_streets && (
                      <div className="mb-3 ml-6">
                        <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">✕ {selectedCall.cross_streets}</span>
                      </div>
                    )}

                    {/* Additional Locations */}
                    {selectedCall.additional_locations?.length > 0 && (
                      <div className="mb-3 space-y-1.5">
                        {selectedCall.additional_locations.map((loc, idx) => (
                          <div key={idx} className="p-2.5 bg-blue-950/30 border border-blue-800/30 rounded">
                            {loc.title && (
                              <p className="text-[10px] text-blue-300 font-semibold uppercase tracking-wide mb-1">{loc.title}</p>
                            )}
                            <div className="flex items-start gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm text-slate-200 font-medium">{loc.location}</span>
                                  {loc.location && (
                                    <a
                                      href={mapHrefFor(loc.location)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-400 hover:text-blue-300 text-xs"
                                    >[Map]</a>
                                  )}
                                </div>
                                {loc.cross_streets && (
                                  <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mt-0.5">✕ {loc.cross_streets}</p>
                                )}
                                {loc.location_notes && (
                                  <p className="text-xs text-slate-400 italic mt-0.5">{loc.location_notes}</p>
                                )}
                                {loc.direction_of_flight && (
                                  <p className="text-xs text-amber-400 font-medium mt-0.5">→ {loc.direction_of_flight}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    </>
                    )}

                    {/* Date/Time and Duration */}
                    <div className="flex items-center gap-4 text-slate-500 dark:text-slate-400 mb-3 pt-2 border-t border-slate-200 dark:border-slate-800">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        <span className="font-mono text-sm">
                          {selectedCall.created_at ? new Date(selectedCall.created_at).toLocaleString('en-US', {
                            timeZone: 'America/New_York',
                            hour12: false,
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'N/A'}
                        </span>
                      </div>
                      <span className="text-slate-600">•</span>
                      <span className="text-sm text-amber-400 font-medium">
                        {selectedCall.created_at ? (() => {
                          const mins = Math.floor((Date.now() - new Date(selectedCall.created_at).getTime()) / 60000);
                          if (mins < 60) return `${mins}m`;
                          const hrs = Math.floor(mins / 60);
                          const remainMins = mins % 60;
                          return `${hrs}h ${remainMins}m`;
                        })() : '--'}
                      </span>
                    </div>

                    {/* Call ID - Bottom */}
                    <div className="text-xs text-slate-600 font-mono">
                      ID: {selectedCall.call_id}
                    </div>
                    {/* Call Info now lives in its own card (section-call-info)
                        gated by the "Call Info" tab — a single instance on all
                        breakpoints, so there's no display:none twin to break the
                        popovers (the reason this used to be merged here). */}
                  </CardContent>
                </Card>

                {/* Mobile/tablet: floating tabs directly under the header. */}
                <div className="lg:hidden">{renderDetailTabsBar()}</div>

                {/* Actions box */}
                <div className={`flex flex-col gap-3 lg:order-6 ${detailTab === 'all' || detailTab === 'actions' ? '' : 'hidden'}`}>
                  {/* Actions Box */}
                  <Card className="bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-800">
                    <CardHeader className="py-2 pb-1">
                      <CardTitle className="text-xs uppercase text-slate-500 dark:text-slate-400 flex items-center justify-between">
                        <span>Actions</span>
                        <Button variant="ghost" size="sm" className="h-5 px-2 text-[10px]" onClick={() => setShowActions(!showActions)}>
                          <Eye className="w-2.5 h-2.5 mr-1" />{showActions ? "Hide" : "Show"}
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className={showActions ? "p-3 pt-0 space-y-1" : "p-0"}>
                      {showActions && (
                        <>
                        {/* Quick toggles — 2-column grid */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={selectedCall.send_notifications !== false}
                              onCheckedChange={async (checked) => {
                                try {
                                  const response = await axios.put(
                                    `${API}/live-calls/${selectedCall.call_id}`,
                                    { send_notifications: checked },
                                    { withCredentials: true }
                                  );
                                  setSelectedCall(response.data);
                                  setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                                  toast.success(checked ? "Notifications enabled" : "Notifications disabled");
                                } catch (error) {
                                  toast.error("Failed to update");
                                }
                              }}
                            />
                            <span className="text-sm">
                              <Bell className="w-3.5 h-3.5 inline mr-1 opacity-60" />Push
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={selectedCall.whatsapp_updates_enabled || false}
                              onCheckedChange={async (checked) => {
                                try {
                                  const response = await axios.put(
                                    `${API}/live-calls/${selectedCall.call_id}`,
                                    { whatsapp_updates_enabled: checked },
                                    { withCredentials: true }
                                  );
                                  setSelectedCall(response.data);
                                  setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                                  toast.success(checked ? "WA Alerts enabled — updates will be sent via WhatsApp" : "WA Alerts disabled");
                                } catch (error) {
                                  toast.error("Failed to update");
                                }
                              }}
                            />
                            <span className={`text-sm ${selectedCall.whatsapp_updates_enabled ? "text-green-400 font-bold" : ""}`}>
                              <MessageSquare className="w-3.5 h-3.5 inline mr-1 opacity-60" />WA Alerts
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={selectedCall.block_from_response || false}
                              onCheckedChange={async (checked) => {
                                try {
                                  const response = await axios.put(
                                    `${API}/live-calls/${selectedCall.call_id}`,
                                    { block_from_response: checked },
                                    { withCredentials: true }
                                  );
                                  setSelectedCall(response.data);
                                  setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                                  toast.success(checked ? "Blocked from Response" : "Visible on Response");
                                } catch (error) {
                                  toast.error("Failed to update");
                                }
                              }}
                            />
                            <span className="text-sm">
                              <Eye className="w-3.5 h-3.5 inline mr-1 opacity-60" />Hide from Response
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer" title="When off, no responder can self-respond to this call regardless of their permission">
                            <Checkbox
                              checked={selectedCall.self_respond_enabled !== false}
                              onCheckedChange={async (checked) => {
                                try {
                                  const response = await axios.put(
                                    `${API}/live-calls/${selectedCall.call_id}`,
                                    { self_respond_enabled: checked },
                                    { withCredentials: true }
                                  );
                                  setSelectedCall(response.data);
                                  setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                                  toast.success(checked ? "Self-respond enabled for this call" : "Self-respond disabled for this call");
                                } catch (error) {
                                  toast.error("Failed to update");
                                }
                              }}
                            />
                            <span className="text-sm">
                              <Users className="w-3.5 h-3.5 inline mr-1 opacity-60" />Allow Self-Respond
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={selectedCall.force_top_response || false}
                              onCheckedChange={async (checked) => {
                                try {
                                  const response = await axios.put(
                                    `${API}/live-calls/${selectedCall.call_id}`,
                                    { force_top_response: checked },
                                    { withCredentials: true }
                                  );
                                  setSelectedCall(response.data);
                                  setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                                  toast.success(checked ? "Forced to top of Response" : "Normal position on Response");
                                } catch (error) {
                                  toast.error("Failed to update");
                                }
                              }}
                            />
                            <span className="text-sm">
                              <ArrowUp className="w-3.5 h-3.5 inline mr-1 opacity-60" />Force Top
                            </span>
                          </label>
                        </div>

                        {/* Notify groups */}
                        {notificationGroups.length > 0 && (
                          <div className="border-t border-slate-700 pt-1" style={{ fontSize: '11px', lineHeight: '18px' }}>
                            <span className="text-xs text-slate-400 mr-1">Notify Indvl:</span>
                            {(() => {
                              const ids = selectedCall.notification_group_ids || [];
                              const isNone = ids.includes("__none__");
                              const isAll = !isNone && ids.length === 0;
                              const options = [
                                { group_id: "__all__", name: "All" },
                                { group_id: "__none__", name: "None" },
                                ...notificationGroups
                              ];
                              return options.map((g) => {
                                const checked = g.group_id === "__all__" ? isAll
                                  : g.group_id === "__none__" ? isNone
                                  : ids.includes(g.group_id);
                                return (
                                  <span key={g.group_id} className="mr-2 whitespace-nowrap" style={{ cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={async (e) => {
                                        try {
                                          let updated;
                                          if (g.group_id === "__all__") {
                                            updated = [];
                                          } else if (g.group_id === "__none__") {
                                            updated = ["__none__"];
                                          } else {
                                            const current = ids.filter(id => id !== "__none__");
                                            updated = e.target.checked ? [...current, g.group_id] : current.filter(id => id !== g.group_id);
                                          }
                                          const response = await axios.put(`${API}/live-calls/${selectedCall.call_id}`, { notification_group_ids: updated }, { withCredentials: true });
                                          setSelectedCall(response.data);
                                          setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                                        } catch (error) { toast.error("Failed to update"); }
                                      }}
                                      style={{ width: '12px', height: '12px', margin: '0 3px 0 0', verticalAlign: 'middle' }}
                                    />
                                    <span className={`${g.group_id === "__none__" ? "text-red-400" : "text-slate-300"}`} style={{ verticalAlign: 'middle' }}>{g.name}</span>
                                  </span>
                                );
                              })
                            })()}
                          </div>
                        )}
                        {/* WhatsApp bot group selector */}
                        {(waBotGroupCollections.length > 0 || waBotGroups.length > 0) && (
                          <div className="border-t border-slate-700 pt-1" style={{ fontSize: '11px', lineHeight: '18px' }}>
                            <div className="w-full mb-0.5">
                              <span className="text-xs text-slate-400">WA Groups</span>
                              <span className="text-[10px] text-slate-500/70 ml-1">— Specific WA Groups in addition to selected above</span>
                            </div>
                            {(() => {
                              const jids = selectedCall.wa_bot_group_jids || [];
                              const isNone = jids.includes("__none__");
                              const isAll = !isNone && jids.length === 0;
                              const saveJids = async (updated) => {
                                try {
                                  const response = await axios.put(`${API}/live-calls/${selectedCall.call_id}`, { wa_bot_group_jids: updated }, { withCredentials: true });
                                  setSelectedCall(response.data);
                                  setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                                } catch (error) { toast.error("Failed to update"); }
                              };
                              return (
                                <>
                                  <span className="mr-2 whitespace-nowrap" style={{ cursor: 'pointer' }}>
                                    <input type="checkbox" checked={isAll} onChange={() => saveJids([])} style={{ width: '12px', height: '12px', margin: '0 3px 0 0', verticalAlign: 'middle' }} />
                                    <span className="text-slate-300" style={{ verticalAlign: 'middle' }}>All</span>
                                  </span>
                                  <span className="mr-2 whitespace-nowrap" style={{ cursor: 'pointer' }}>
                                    <input type="checkbox" checked={isNone} onChange={() => saveJids(["__none__"])} style={{ width: '12px', height: '12px', margin: '0 3px 0 0', verticalAlign: 'middle' }} />
                                    <span className="text-red-400" style={{ verticalAlign: 'middle' }}>None</span>
                                  </span>
                                  {waBotGroupCollections.map((col) => {
                                    const colJids = col.jids || [];
                                    const selected = jids.filter(j => j !== "__none__");
                                    const allIncluded = colJids.length > 0 && colJids.every(j => selected.includes(j));
                                    return (
                                      <span key={col.name} className="mr-2 whitespace-nowrap" style={{ cursor: 'pointer' }}>
                                        <input
                                          type="checkbox"
                                          checked={allIncluded}
                                          onChange={async (e) => {
                                            let updated = [...selected];
                                            if (e.target.checked) {
                                              colJids.forEach(j => { if (!updated.includes(j)) updated.push(j); });
                                            } else {
                                              updated = updated.filter(j => !colJids.includes(j));
                                            }
                                            saveJids(updated);
                                          }}
                                          style={{ width: '12px', height: '12px', margin: '0 3px 0 0', verticalAlign: 'middle' }}
                                        />
                                        <span className="text-green-300 font-semibold" style={{ verticalAlign: 'middle' }}>{col.name}</span>
                                      </span>
                                    );
                                  })}
                                  {waBotGroups.map((g) => (
                                    <span key={g.id} className="mr-2 whitespace-nowrap" style={{ cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={jids.includes(g.id)}
                                        onChange={async (e) => {
                                          const current = jids.filter(j => j !== "__none__");
                                          const updated = e.target.checked ? [...current, g.id] : current.filter(id => id !== g.id);
                                          saveJids(updated);
                                        }}
                                        style={{ width: '12px', height: '12px', margin: '0 3px 0 0', verticalAlign: 'middle' }}
                                      />
                                      <span className="text-slate-300" style={{ verticalAlign: 'middle' }}>{g.name}</span>
                                    </span>
                                  ))}
                                </>
                              );
                            })()}
                          </div>
                        )}
                        </>
                      )}
                  {/* Edit Call Info Dialog (button moved to Status / Radio card) */}
                  <Dialog open={showEditCallDialog} onOpenChange={setShowEditCallDialog}>
                    <DialogContent className="w-[calc(100vw-2rem)] max-w-lg bg-slate-900 border-slate-800 max-h-[90vh] overflow-hidden flex flex-col p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
                      <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
                        <DialogTitle className="uppercase tracking-wider">Edit Call Information</DialogTitle>
                      </DialogHeader>
                      <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
                      <div className="space-y-4 mt-4 pb-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Priority</Label>
                            <Select value={editCallData.priority || "normal"} onValueChange={(v) => setEditCallData({...editCallData, priority: v})}>
                              <SelectTrigger className="input-dark">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="priority">Priority</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Call Type</Label>
                            <div className="relative">
                              <Input
                                value={editCallTypeSearch || editCallData.call_type || ""}
                                onChange={(e) => {
                                  setEditCallTypeSearch(e.target.value);
                                  setEditCallData({...editCallData, call_type: e.target.value});
                                  setShowEditCallTypeDropdown(true);
                                }}
                                onFocus={() => setShowEditCallTypeDropdown(true)}
                                onBlur={() => setTimeout(() => setShowEditCallTypeDropdown(false), 200)}
                                placeholder="Search or enter type..."
                                className="input-dark"
                              />
                              {showEditCallTypeDropdown && (
                                <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-40 overflow-y-auto">
                                  {incidentTypes
                                    .filter(type => type.name.toLowerCase().includes((editCallTypeSearch || editCallData.call_type || "").toLowerCase()))
                                    .sort((a, b) => { const aNum = /^\d/.test(a.name); const bNum = /^\d/.test(b.name); if (aNum !== bNum) return aNum ? 1 : -1; return a.name.localeCompare(b.name); })
                                    .map((type) => (
                                      <div
                                        key={type.type_id}
                                        className="px-3 py-2 hover:bg-slate-700 cursor-pointer text-sm select-none"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                          setEditCallData({...editCallData, call_type: type.name});
                                          setEditCallTypeSearch("");
                                          setShowEditCallTypeDropdown(false);
                                        }}
                                      >
                                        {type.name}
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label>Secondary Call Type (Optional)</Label>
                          <div className="relative">
                            <Input
                              value={editSecondaryCallTypeSearch || editCallData.secondary_call_type || ""}
                              onChange={(e) => {
                                setEditSecondaryCallTypeSearch(e.target.value);
                                setEditCallData({...editCallData, secondary_call_type: e.target.value});
                                setShowEditSecondaryCallTypeDropdown(true);
                              }}
                              onFocus={() => setShowEditSecondaryCallTypeDropdown(true)}
                              onBlur={() => setTimeout(() => setShowEditSecondaryCallTypeDropdown(false), 200)}
                              placeholder="Search or enter secondary type..."
                              className="input-dark"
                            />
                            {showEditSecondaryCallTypeDropdown && (
                              <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-40 overflow-y-auto">
                                {incidentTypes
                                  .filter(type => type.name.toLowerCase().includes((editSecondaryCallTypeSearch || editCallData.secondary_call_type || "").toLowerCase()))
                                  .sort((a, b) => { const aNum = /^\d/.test(a.name); const bNum = /^\d/.test(b.name); if (aNum !== bNum) return aNum ? 1 : -1; return a.name.localeCompare(b.name); })
                                  .map((type) => (
                                    <div
                                      key={type.type_id}
                                      className="px-3 py-2 hover:bg-slate-700 cursor-pointer text-sm select-none"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => {
                                        setEditCallData({...editCallData, secondary_call_type: type.name});
                                        setEditSecondaryCallTypeSearch("");
                                        setShowEditSecondaryCallTypeDropdown(false);
                                      }}
                                    >
                                      {type.name}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Original Location Card (Edit) */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label>Location</Label>
                            <Button type="button" variant="outline" size="sm" className="h-6 text-xs" onClick={() => {
                              setAddLocData({
                                title: editCallData.location_title || "",
                                location: editCallData.location || "",
                                location_notes: editCallData.location_notes || "",
                                cross_streets: editCallData.cross_streets || "",
                                direction_of_flight: editCallData.direction_of_flight || "",
                              });
                              setEditingLocIndex("original-edit");
                              setShowAddLocDialog(true);
                            }}>
                              <Edit className="w-3 h-3 mr-1" />Edit Location
                            </Button>
                          </div>
                          {editCallData.location ? (
                            <div className="p-2 bg-amber-950/30 border border-amber-800/40 rounded">
                              <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-600">
                                Original{editCallData.location_title ? `/${editCallData.location_title}` : ""}
                              </Badge>
                              <div className="flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                                <span className="text-sm font-medium text-slate-200">{editCallData.location}</span>
                              </div>
                              {editCallData.cross_streets && <p className="text-xs text-emerald-400 ml-4">✕ {editCallData.cross_streets}</p>}
                              {editCallData.location_notes && <p className="text-xs text-slate-400 ml-4 italic">{editCallData.location_notes}</p>}
                              {editCallData.direction_of_flight && <p className="text-xs text-amber-400 ml-4">→ {editCallData.direction_of_flight}</p>}
                            </div>
                          ) : (
                            <p className="text-xs text-red-400 italic">No location set</p>
                          )}
                        </div>
                        {/* Additional Locations in Edit */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs text-slate-400 uppercase">Additional Locations ({(editCallData.additional_locations || []).length})</Label>
                            <Button type="button" variant="outline" size="sm" className="h-6 text-xs" onClick={() => {
                              setAddLocData({ title: "", location: "", location_notes: "", cross_streets: "", direction_of_flight: "" });
                              setEditingLocIndex(null);
                              // Use a flag to know we're editing from edit dialog
                              setShowAddLocDialog("edit");
                            }}>
                              <Plus className="w-3 h-3 mr-1" />Add Location
                            </Button>
                          </div>
                          {(editCallData.additional_locations || []).map((loc, idx) => (
                            <div key={idx} className="p-2 bg-blue-950/30 border border-blue-800/40 rounded mb-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  {loc.title && <p className="text-xs text-blue-300 font-semibold uppercase mb-0.5">{loc.title}</p>}
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3 text-blue-400 shrink-0" />
                                    <span className="text-sm font-medium text-slate-200 truncate">{loc.location}</span>
                                  </div>
                                  {loc.cross_streets && <p className="text-xs text-emerald-400 ml-4">✕ {loc.cross_streets}</p>}
                                  {loc.location_notes && <p className="text-xs text-slate-400 ml-4 italic">{loc.location_notes}</p>}
                                  {loc.direction_of_flight && <p className="text-xs text-amber-400 ml-4">→ {loc.direction_of_flight}</p>}
                                </div>
                                <div className="flex gap-1 shrink-0">
                                  <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-400" onClick={() => {
                                    setAddLocData({ ...loc });
                                    setEditingLocIndex(idx);
                                    setShowAddLocDialog("edit");
                                  }}>
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setDeleteEditLocIndex(idx)}>
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div>
                          <Label>Job Narrative</Label>
                          <Textarea
                            value={editCallData.call_note || ""}
                            onChange={(e) => setEditCallData({...editCallData, call_note: e.target.value})}
                            className="input-dark"
                            rows={2}
                            placeholder="Job narrative notes..."
                          />
                        </div>
                        {/* Callback Section with caller type per number */}
                        <div className="space-y-2">
                          <Label>Callback Numbers</Label>
                          {/* Primary callback with caller type */}
                          <div className="flex gap-2 items-center flex-wrap">
                            <Input
                              value={editCallData.callback_phone || ""}
                              onChange={(e) => setEditCallData({...editCallData, callback_phone: formatPhoneNumber(e.target.value)})}
                              className="input-dark flex-[2] min-w-[140px]"
                              placeholder="Primary number..."
                              type="tel"
                              inputMode="numeric"
                            />
                            <Popover open={showRecentCalls === "editPrimary"} onOpenChange={(open) => { setShowRecentCalls(open ? "editPrimary" : null); if (open) fetchRecentIncomingCalls(); }}>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  title="Recent incoming calls"
                                  className="h-8 px-2 flex-shrink-0"
                                >
                                  <PhoneIncoming className="w-4 h-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[10001]">
                                <div className="text-xs text-slate-400 mb-2">Recent Incoming Calls (30 min)</div>
                                {loadingRecentCalls ? (
                                  <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                                ) : recentIncomingCalls.length === 0 ? (
                                  <div className="text-xs text-slate-500 text-center py-2">No recent calls</div>
                                ) : (
                                  <div className="space-y-1 max-h-48 overflow-y-auto">
                                    {recentIncomingCalls.map((call, idx) => (
                                      <Button
                                        key={idx}
                                        variant="ghost"
                                        size="sm"
                                        className="w-full justify-between text-xs h-8 px-2"
                                        onClick={() => {
                                          setEditCallData({...editCallData, callback_phone: call.caller_number, is_hotline_call: true});
                                          setShowRecentCalls(null);
                                        }}
                                      >
                                        <span className="flex items-center gap-1.5 truncate">
                                          <Phone className="w-3 h-3 shrink-0" />
                                          {call.caller_number}
                                          {call.contact_name && <span className="text-slate-400 truncate">{call.contact_name}</span>}
                                        </span>
                                        <span className="flex items-center gap-1 shrink-0 ml-1">
                                          <span className="text-[10px] text-slate-500">{formatCallTime(call.received_at)}</span>
                                          {call.contact_status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                                          {call.contact_status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">K</span>}
                                          {call.contact_status && !["flagged","known"].includes(call.contact_status) && <span className="text-[9px] text-slate-500 uppercase">{call.contact_status.slice(0,3)}</span>}
                                        </span>
                                      </Button>
                                    ))}
                                  </div>
                                )}
                              </PopoverContent>
                            </Popover>
                            <Select
                              value={editCallData.cv_or_third_party || "none"}
                              onValueChange={(v) => setEditCallData({...editCallData, cv_or_third_party: v === "none" ? "" : v})}
                            >
                              <SelectTrigger className="input-dark w-20 flex-shrink-0">
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">—</SelectItem>
                                <SelectItem value="cv">CV</SelectItem>
                                <SelectItem value="3rd_party">3rd</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Checkbox
                                id="edit_primary_visual"
                                checked={editCallData.caller_has_visual || false}
                                onCheckedChange={(checked) => setEditCallData({...editCallData, caller_has_visual: checked})}
                              />
                              <Label htmlFor="edit_primary_visual" className="text-xs cursor-pointer whitespace-nowrap">Vis</Label>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex-shrink-0"
                              title="Add another callback"
                              onClick={() => setEditCallbackInput({ number: "", caller_type: "", has_visual: false, showInput: true })}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Existing additional numbers */}
                          {editCallData.callback_numbers?.length > 0 && (
                            <div className="space-y-1 pl-2 border-l-2 border-slate-700">
                              <p className="text-xs text-slate-500">Additional Numbers:</p>
                              {editCallData.callback_numbers.map((cb, idx) => (
                                editingEditCallCallbackIdx === idx ? (
                                  <div key={idx} className="flex gap-2 bg-slate-800 px-2 py-1 rounded flex-wrap">
                                    <Input
                                      value={editingEditCallCallbackData.number}
                                      onChange={(e) => setEditingEditCallCallbackData({ ...editingEditCallCallbackData, number: formatPhoneNumber(e.target.value) })}
                                      placeholder="Number..."
                                      className="input-dark flex-1 min-w-[100px] h-7 text-sm"
                                      type="tel"
                                      inputMode="numeric"
                                      autoFocus
                                    />
                                    <Select
                                      value={editingEditCallCallbackData.caller_type || "none"}
                                      onValueChange={(v) => setEditingEditCallCallbackData({ ...editingEditCallCallbackData, caller_type: v === "none" ? "" : v })}
                                    >
                                      <SelectTrigger className="input-dark w-24 h-7 text-xs">
                                        <SelectValue placeholder="Type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">—</SelectItem>
                                        <SelectItem value="cv">CV</SelectItem>
                                        <SelectItem value="3rd_party">3rd Party</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <div className="flex items-center gap-1">
                                      <Checkbox
                                        id={`editing_edit_cb_visual_${idx}`}
                                        checked={editingEditCallCallbackData.has_visual || false}
                                        onCheckedChange={(checked) => setEditingEditCallCallbackData({ ...editingEditCallCallbackData, has_visual: checked })}
                                      />
                                      <Label htmlFor={`editing_edit_cb_visual_${idx}`} className="text-[10px] cursor-pointer">Visual</Label>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => {
                                        if (editingEditCallCallbackData.number.trim()) {
                                          const updated = [...editCallData.callback_numbers];
                                          updated[idx] = { ...editingEditCallCallbackData };
                                          setEditCallData({ ...editCallData, callback_numbers: updated });
                                        }
                                        setEditingEditCallCallbackIdx(null);
                                      }}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => setEditingEditCallCallbackIdx(null)}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <div key={idx} className="flex items-center gap-2 text-sm bg-slate-800 px-2 py-1 rounded">
                                    <Phone className="w-3 h-3 text-slate-400" />
                                    <span>{formatPhoneNumber(cb.number)}</span>
                                    {cb.label && <span className="text-slate-500">({cb.label})</span>}
                                    <Badge variant="outline" className="text-[10px]">
                                      {cb.caller_type === '3rd_party' ? '3rd Party' : 'CV'}
                                    </Badge>
                                    {cb.has_visual && (
                                      <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400">VISUAL</Badge>
                                    )}
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="ml-auto h-6 w-6 p-0 text-blue-400"
                                      onClick={() => {
                                        setEditingEditCallCallbackData({ number: cb.number, caller_type: cb.caller_type, has_visual: cb.has_visual });
                                        setEditingEditCallCallbackIdx(idx);
                                      }}
                                    >
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-red-400"
                                      onClick={() => setEditCallData({
                                        ...editCallData,
                                        callback_numbers: editCallData.callback_numbers.filter((_, i) => i !== idx)
                                      })}
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )
                              ))}
                            </div>
                          )}
                          
                          {/* Input for new additional number */}
                          {editCallbackInput.showInput && (
                            <div className="flex gap-2 pl-2 border-l-2 border-slate-700 flex-wrap">
                              <Input
                                value={editCallbackInput.number}
                                onChange={(e) => setEditCallbackInput({ ...editCallbackInput, number: e.target.value })}
                                placeholder="Number..."
                                className="input-dark flex-1 min-w-[100px]"
                                autoFocus
                              />
                              <Popover open={showRecentCalls === "editAdditional"} onOpenChange={(open) => { setShowRecentCalls(open ? "editAdditional" : null); if (open) fetchRecentIncomingCalls(); }}>
                                <PopoverTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    title="Recent incoming calls"
                                    className="h-8 px-2"
                                  >
                                    <PhoneIncoming className="w-4 h-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[10001]">
                                  <div className="text-xs text-slate-400 mb-2">Recent Incoming Calls (30 min)</div>
                                  {loadingRecentCalls ? (
                                    <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                                  ) : recentIncomingCalls.length === 0 ? (
                                    <div className="text-xs text-slate-500 text-center py-2">No recent calls</div>
                                  ) : (
                                    <div className="space-y-1 max-h-48 overflow-y-auto">
                                      {recentIncomingCalls.map((call, idx) => (
                                        <Button
                                          key={idx}
                                          variant="ghost"
                                          size="sm"
                                          className="w-full justify-between text-xs h-8 px-2"
                                          onClick={() => {
                                            setEditCallbackInput({ ...editCallbackInput, number: call.caller_number });
                                            setEditCallData(prev => ({ ...prev, is_hotline_call: true }));
                                            setShowRecentCalls(null);
                                          }}
                                        >
                                          <span className="flex items-center gap-1.5 truncate">
                                            <Phone className="w-3 h-3 shrink-0" />
                                            {call.caller_number}
                                            {call.contact_name && <span className="text-slate-400 truncate">{call.contact_name}</span>}
                                          </span>
                                          <span className="flex items-center gap-1 shrink-0 ml-1">
                                            <span className="text-[10px] text-slate-500">{formatCallTime(call.received_at)}</span>
                                            {call.contact_status === "flagged" && <Flag className="w-3 h-3 text-yellow-400 animate-pulse" />}
                                            {call.contact_status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">K</span>}
                                            {call.contact_status && !["flagged","known"].includes(call.contact_status) && <span className="text-[9px] text-slate-500 uppercase">{call.contact_status.slice(0,3)}</span>}
                                          </span>
                                        </Button>
                                      ))}
                                    </div>
                                  )}
                                </PopoverContent>
                              </Popover>
                              <Select
                                value={editCallbackInput.caller_type || "none"}
                                onValueChange={(v) => setEditCallbackInput({ ...editCallbackInput, caller_type: v === "none" ? "" : v })}
                              >
                                <SelectTrigger className="input-dark w-28">
                                  <SelectValue placeholder="Type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">—</SelectItem>
                                  <SelectItem value="cv">CV</SelectItem>
                                  <SelectItem value="3rd_party">3rd Party</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex items-center gap-1">
                                <Checkbox
                                  id="edit_new_cb_visual"
                                  checked={editCallbackInput.has_visual || false}
                                  onCheckedChange={(checked) => setEditCallbackInput({ ...editCallbackInput, has_visual: checked })}
                                />
                                <Label htmlFor="edit_new_cb_visual" className="text-xs cursor-pointer">Visual</Label>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (editCallbackInput.number.trim()) {
                                    setEditCallData({
                                      ...editCallData,
                                      callback_numbers: [...(editCallData.callback_numbers || []), {
                                        number: editCallbackInput.number,
                                        caller_type: editCallbackInput.caller_type,
                                        has_visual: editCallbackInput.has_visual
                                      }]
                                    });
                                  }
                                  setEditCallbackInput({ number: "", caller_type: "", has_visual: false });
                                }}
                              >
                                Add
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditCallbackInput({ number: "", caller_type: "", has_visual: false })}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                        {/* Perpetrators Section */}
                        <div className="border-t border-amber-800/50 pt-3 mt-3 bg-amber-950/20 -mx-4 px-4 pb-3">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs text-amber-400 uppercase font-medium flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Perpetrators ({editCallData.perp_descriptions?.length || 0})
                            </Label>
                            <Button type="button" size="sm" className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white font-medium"
                              onClick={() => { setEditingNewCallPerpIndex(null); setNewPerp({ name: "", address: "", current_location: "", sex: "", race: "", age: "", height: "", weight: "", build: "", complexion: "", hair_color_cut: "", eye_color: "", hat_color_style: "", shirt: "", pants: "", jacket_coat: "", shoes: "", visible_marks: "", clothing: "", direction_of_flight: "", has_weapon: false, weapon_description: "", vehicle_description: "", license_plate: "", was_arrested: false, notes: "" }); setShowEditCallPerpDialog(true); }}>
                              <Plus className="w-3 h-3 mr-1" />Add Perp
                            </Button>
                          </div>
                          {editCallData.perp_descriptions?.map((p, idx) => (
                            <div key={p.perp_id || idx} className="flex items-center gap-2 mb-2 bg-slate-800 px-2 py-1 rounded text-sm">
                              <Users className="w-3 h-3 text-slate-400" />
                              <span>#{idx + 1}: {p.sex} {p.race} {p.age && `(${p.age})`}</span>
                              {p.has_weapon && <span className="text-[9px] bg-red-500/20 text-red-400 px-1 rounded">ARMED</span>}
                              {p.was_arrested && <span className="text-[9px] bg-red-500/20 text-red-400 px-1 rounded">ARRESTED</span>}
                              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 ml-auto" onClick={() => { setEditingNewCallPerpIndex(idx); setNewPerp({ ...p }); setShowEditCallPerpDialog(true); }}><Edit className="w-3 h-3" /></Button>
                              <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => { setPendingDeleteEditCallPerpIndex(idx); setShowDeleteEditCallPerpConfirm(true); }}><X className="w-3 h-3" /></Button>
                            </div>
                          ))}
                        </div>

                        <div>
                          <Label>Basic Description (if time limited to enter or if multiple perps)</Label>
                          <Textarea
                            value={editCallData.description || ""}
                            onChange={(e) => setEditCallData({...editCallData, description: e.target.value})}
                            className="input-dark"
                            rows={3}
                            placeholder="General description..."
                          />
                        </div>
                        <div>
                          <Label>Confirmation Status</Label>
                          <Select value={editCallData.confirmation_status || "no_confirm"} onValueChange={(v) => setEditCallData({...editCallData, confirmation_status: v === "no_confirm" ? "" : v})}>
                            <SelectTrigger className="input-dark">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="no_confirm">No Confirm</SelectItem>
                              <SelectItem value="unconfirmed">Unconfirmed</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Call Settings — same as new call form */}
                        <div className="p-3 bg-slate-800 rounded-sm space-y-3">
                          <p className="text-xs text-slate-500 uppercase">Call Settings</p>

                          {/* Toggles — compact 2x2 */}
                          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox id="edit_send_notifications" checked={editCallData.send_notifications !== false} onCheckedChange={(checked) => setEditCallData({ ...editCallData, send_notifications: checked })} className="h-3.5 w-3.5" />
                              <span className="text-xs"><Bell className="w-3 h-3 inline mr-0.5 opacity-50" />Push</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox id="edit_send_whatsapp" checked={editCallData.send_whatsapp !== false} onCheckedChange={(checked) => setEditCallData({ ...editCallData, send_whatsapp: checked, send_whatsapp_to_bot_groups: checked })} className="h-3.5 w-3.5" />
                              <span className="text-xs"><MessageSquare className="w-3 h-3 inline mr-0.5 opacity-50" />WhatsApp</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox id="edit_wa_updates" checked={editCallData.whatsapp_updates_enabled || false} onCheckedChange={(checked) => setEditCallData({ ...editCallData, whatsapp_updates_enabled: checked })} className="h-3.5 w-3.5" />
                              <span className={`text-xs ${editCallData.whatsapp_updates_enabled ? "text-green-400 font-semibold" : ""}`}>WA Updates</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox id="edit_block_from_response" checked={editCallData.block_from_response || false} onCheckedChange={(checked) => setEditCallData({ ...editCallData, block_from_response: checked })} className="h-3.5 w-3.5" />
                              <span className="text-xs"><Eye className="w-3 h-3 inline mr-0.5 opacity-50" />Hide from Response</span>
                            </label>
                          </div>

                          {/* Notify groups */}
                          {notificationGroups.length > 0 && (editCallData.send_notifications || editCallData.send_whatsapp) && (
                            <div className="flex items-center gap-x-2.5 gap-y-1 flex-wrap pt-2 border-t border-slate-700/60">
                              <span className="text-[10px] text-slate-500 uppercase tracking-wider mr-0.5">Notify</span>
                              {(() => {
                                const ids = editCallData.notification_group_ids || [];
                                const isNone = ids.includes("__none__");
                                const isAll = !isNone && ids.length === 0;
                                return (
                                  <>
                                    <label className="flex items-center gap-1 cursor-pointer">
                                      <input type="checkbox" checked={isAll} onChange={() => setEditCallData({ ...editCallData, notification_group_ids: [] })} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                                      <span className="text-[11px] text-slate-300">All</span>
                                    </label>
                                    <label className="flex items-center gap-1 cursor-pointer">
                                      <input type="checkbox" checked={isNone} onChange={() => setEditCallData({ ...editCallData, notification_group_ids: ["__none__"] })} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                                      <span className="text-[11px] text-red-400">None</span>
                                    </label>
                                    {notificationGroups.map((g) => (
                                      <label key={g.group_id} className="flex items-center gap-1 cursor-pointer">
                                        <input type="checkbox" checked={ids.includes(g.group_id)} onChange={(e) => {
                                          const current = ids.filter(id => id !== "__none__");
                                          if (e.target.checked) setEditCallData({ ...editCallData, notification_group_ids: [...current, g.group_id] });
                                          else setEditCallData({ ...editCallData, notification_group_ids: current.filter(id => id !== g.group_id) });
                                        }} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                                        <span className="text-[11px] text-slate-300">{g.name}</span>
                                      </label>
                                    ))}
                                  </>
                                );
                              })()}
                            </div>
                          )}

                          {/* WA Groups selector */}
                          {(waBotGroupCollections.length > 0 || waBotGroups.length > 0) && editCallData.send_whatsapp !== false && (
                            <div className="flex items-center gap-x-2.5 gap-y-1 flex-wrap pt-2 border-t border-slate-700/60">
                              <div className="w-full mb-0.5">
                                <span className="text-[10px] text-slate-500 uppercase tracking-wider">WA Groups</span>
                                <span className="text-[10px] text-slate-500/70 ml-1">— Specific WA Groups in addition to selected above</span>
                              </div>
                              {(() => {
                                const jids = editCallData.wa_bot_group_jids || [];
                                const isNone = jids.includes("__none__");
                                const isAll = !isNone && jids.length === 0;
                                return (
                                  <>
                                    <label className="flex items-center gap-1 cursor-pointer">
                                      <input type="checkbox" checked={isAll} onChange={() => setEditCallData({ ...editCallData, wa_bot_group_jids: [] })} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                                      <span className="text-[11px] text-slate-300">All</span>
                                    </label>
                                    <label className="flex items-center gap-1 cursor-pointer">
                                      <input type="checkbox" checked={isNone} onChange={() => setEditCallData({ ...editCallData, wa_bot_group_jids: ["__none__"] })} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                                      <span className="text-[11px] text-red-400">None</span>
                                    </label>
                                    {waBotGroupCollections.map((col) => {
                                      const colJids = col.jids || [];
                                      const selected = jids.filter(j => j !== "__none__");
                                      const allIncluded = colJids.length > 0 && colJids.every(j => selected.includes(j));
                                      return (
                                        <label key={col.name} className="flex items-center gap-1 cursor-pointer">
                                          <input type="checkbox" checked={allIncluded} onChange={(e) => {
                                            let updated = [...selected];
                                            if (e.target.checked) colJids.forEach(j => { if (!updated.includes(j)) updated.push(j); });
                                            else updated = updated.filter(j => !colJids.includes(j));
                                            setEditCallData({ ...editCallData, wa_bot_group_jids: updated });
                                          }} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                                          <span className="text-[11px] text-green-300 font-semibold">{col.name}</span>
                                        </label>
                                      );
                                    })}
                                    {waBotGroups.map((g) => (
                                      <label key={g.id} className="flex items-center gap-1 cursor-pointer">
                                        <input type="checkbox" checked={jids.includes(g.id)} onChange={(e) => {
                                          const current = jids.filter(j => j !== "__none__");
                                          if (e.target.checked) setEditCallData({ ...editCallData, wa_bot_group_jids: [...current, g.id] });
                                          else setEditCallData({ ...editCallData, wa_bot_group_jids: current.filter(id => id !== g.id) });
                                        }} className="w-3 h-3 rounded border-slate-600 bg-slate-800" />
                                        <span className="text-[11px] text-slate-300">{g.name}</span>
                                      </label>
                                    ))}
                                  </>
                                );
                              })()}
                            </div>
                          )}

                          {/* Radio */}
                          <div className="flex items-center gap-3 pt-2 border-t border-slate-700/60 whitespace-nowrap">
                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Radio</span>
                            <label className={`flex items-center gap-1.5 cursor-pointer ${editCallData.transmitted_10_85 ? "text-red-400 font-bold" : ""}`}>
                              <Checkbox checked={editCallData.transmitted_10_85 || false} onCheckedChange={(checked) => setEditCallData({ ...editCallData, transmitted_10_85: checked })} className="h-3.5 w-3.5" />
                              <span className="text-xs">10-85</span>
                            </label>
                            <label className={`flex items-center gap-1.5 cursor-pointer ${editCallData.transmitted_10_13 ? "text-red-400 font-bold" : ""}`}>
                              <Checkbox checked={editCallData.transmitted_10_13 || false} onCheckedChange={(checked) => setEditCallData({ ...editCallData, transmitted_10_13: checked })} className="h-3.5 w-3.5" />
                              <span className="text-xs">10-13</span>
                            </label>
                            <label className={`flex items-center gap-1.5 cursor-pointer ${editCallData.whatsapp_8513_enabled ? "text-red-400 font-bold" : ""}`}>
                              <Checkbox checked={editCallData.whatsapp_8513_enabled || false} onCheckedChange={(checked) => setEditCallData({ ...editCallData, whatsapp_8513_enabled: checked })} className="h-3.5 w-3.5" />
                              <span className="text-xs">All 85/13</span>
                            </label>
                          </div>
                          {!editCallData.whatsapp_8513_enabled && (
                            <p className="text-[10px] text-amber-400/80">Override off — 10-85/10-13 follow each user's own notification settings</p>
                          )}
                        </div>

                        {/* Victims Section */}
                        <div className="p-3 bg-slate-800 rounded-sm space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs text-slate-500 uppercase">Victims ({editCallData.victims?.length || 0})</p>
                            <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs flex-shrink-0"
                              onClick={() => setEditCallData({ ...editCallData, victims: [...(editCallData.victims || []), { victim_id: `vic_${Date.now()}`, name: "", contact: "" }] })}>
                              <Plus className="w-3 h-3 mr-1" />Add
                            </Button>
                          </div>
                          {editCallData.victims?.map((v, idx) => (
                            <div key={idx} className="flex gap-2">
                              <Input value={v.name || ""} onChange={(e) => { const u = [...editCallData.victims]; u[idx] = { ...u[idx], name: e.target.value }; setEditCallData({ ...editCallData, victims: u }); }} placeholder="Name" className="input-dark flex-1 h-8" />
                              <Input value={v.contact || v.phone1 || ""} onChange={(e) => { const u = [...editCallData.victims]; u[idx] = { ...u[idx], contact: formatPhoneNumber(e.target.value) }; setEditCallData({ ...editCallData, victims: u }); }} placeholder="Phone" className="input-dark flex-1 h-8" />
                              <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400" onClick={() => setEditCallData({ ...editCallData, victims: editCallData.victims.filter((_, i) => i !== idx) })}><X className="w-4 h-4" /></Button>
                            </div>
                          ))}
                        </div>

                        {/* Witnesses Section */}
                        <div className="p-3 bg-slate-800 rounded-sm space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs text-slate-500 uppercase">Witnesses ({editCallData.witnesses?.length || 0})</p>
                            <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs flex-shrink-0"
                              onClick={() => setEditCallData({ ...editCallData, witnesses: [...(editCallData.witnesses || []), { witness_id: `wit_${Date.now()}`, name: "", contact: "" }] })}>
                              <Plus className="w-3 h-3 mr-1" />Add
                            </Button>
                          </div>
                          {editCallData.witnesses?.map((w, idx) => (
                            <div key={idx} className="flex gap-2">
                              <Input value={w.name || ""} onChange={(e) => { const u = [...editCallData.witnesses]; u[idx] = { ...u[idx], name: e.target.value }; setEditCallData({ ...editCallData, witnesses: u }); }} placeholder="Name" className="input-dark flex-1 h-8" />
                              <Input value={w.contact || w.phone1 || ""} onChange={(e) => { const u = [...editCallData.witnesses]; const v = e.target.value; u[idx] = { ...u[idx], contact: /^[\d\s\-\(\)\+]*$/.test(v) ? formatPhoneNumber(v) : v }; setEditCallData({ ...editCallData, witnesses: u }); }} placeholder="Phone" className="input-dark flex-1 h-8" />
                              <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400" onClick={() => setEditCallData({ ...editCallData, witnesses: editCallData.witnesses.filter((_, i) => i !== idx) })}><X className="w-4 h-4" /></Button>
                            </div>
                          ))}
                        </div>

                        <Button onClick={handleSaveCallInfo} className="w-full btn-primary">
                          Save Changes
                        </Button>
                      </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Edit Call Perp Dialog */}
                  {/* Edit Call Perp Dialog - Full form */}
                  <Dialog open={showEditCallPerpDialog} onOpenChange={setShowEditCallPerpDialog}>
                    <DialogContent className="bg-slate-900 border-slate-800 w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
                      <DialogHeader>
                        <DialogTitle>{editingNewCallPerpIndex !== null ? 'Edit Perpetrator' : 'Add Perpetrator'}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 mt-4">
                        {/* Current Location */}
                        <div>
                          <Label className="text-xs text-amber-400">Current Location</Label>
                          <Input value={newPerp.current_location || ""} onChange={(e) => setNewPerp({...newPerp, current_location: e.target.value})} className="input-dark h-8" placeholder="Where perp is now / last seen" />
                        </div>

                        {/* Direction of Flight */}
                        <div>
                          <Label className="text-xs">Direction of Flight</Label>
                          <Input value={newPerp.direction_of_flight || ""} onChange={(e) => setNewPerp({...newPerp, direction_of_flight: e.target.value})} className="input-dark h-8" placeholder="Northbound on Main St" />
                        </div>

                        {/* Physical Description */}
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <Label className="text-xs">Sex</Label>
                            <select value={newPerp.sex || ""} onChange={(e) => setNewPerp({...newPerp, sex: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                              <option value="">—</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Race</Label>
                            <select value={newPerp.race || ""} onChange={(e) => setNewPerp({...newPerp, race: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                              <option value="">—</option>
                              <option value="Black">Black</option>
                              <option value="White">White</option>
                              <option value="Hispanic">Hispanic</option>
                              <option value="Asian">Asian</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Age</Label>
                            <Input value={newPerp.age || ""} onChange={(e) => setNewPerp({...newPerp, age: e.target.value})} className="input-dark h-8" placeholder="~25" />
                          </div>
                          <div>
                            <Label className="text-xs">Height</Label>
                            <Input value={newPerp.height || ""} onChange={(e) => setNewPerp({...newPerp, height: e.target.value})} className="input-dark h-8" placeholder="5'10" />
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <Label className="text-xs">Weight</Label>
                            <Input value={newPerp.weight || ""} onChange={(e) => setNewPerp({...newPerp, weight: e.target.value})} className="input-dark h-8" placeholder="180 lbs" />
                          </div>
                          <div>
                            <Label className="text-xs">Build</Label>
                            <select value={newPerp.build || ""} onChange={(e) => setNewPerp({...newPerp, build: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                              <option value="">—</option>
                              <option value="Slim">Slim</option>
                              <option value="Medium">Medium</option>
                              <option value="Heavy">Heavy</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Complexion</Label>
                            <Input value={newPerp.complexion || ""} onChange={(e) => setNewPerp({...newPerp, complexion: e.target.value})} className="input-dark h-8" placeholder="Light/Dark" />
                          </div>
                          <div>
                            <Label className="text-xs">Eye Color</Label>
                            <Input value={newPerp.eye_color || ""} onChange={(e) => setNewPerp({...newPerp, eye_color: e.target.value})} className="input-dark h-8" placeholder="Brown" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Hair Color/Style</Label>
                            <Input value={newPerp.hair_color_cut || ""} onChange={(e) => setNewPerp({...newPerp, hair_color_cut: e.target.value})} className="input-dark h-8" placeholder="Black, short" />
                          </div>
                          <div>
                            <Label className="text-xs">Visible Marks/Tattoos</Label>
                            <Input value={newPerp.visible_marks || ""} onChange={(e) => setNewPerp({...newPerp, visible_marks: e.target.value})} className="input-dark h-8" placeholder="Scar on left cheek" />
                          </div>
                        </div>
                        
                        {/* Clothing */}
                        <p className="text-xs text-slate-500 uppercase pt-2">Clothing</p>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Hat</Label>
                            <Input value={newPerp.hat_color_style || ""} onChange={(e) => setNewPerp({...newPerp, hat_color_style: e.target.value})} className="input-dark h-8" placeholder="Red baseball cap" />
                          </div>
                          <div>
                            <Label className="text-xs">Shirt</Label>
                            <Input value={newPerp.shirt || ""} onChange={(e) => setNewPerp({...newPerp, shirt: e.target.value})} className="input-dark h-8" placeholder="White t-shirt" />
                          </div>
                          <div>
                            <Label className="text-xs">Jacket/Coat</Label>
                            <Input value={newPerp.jacket_coat || ""} onChange={(e) => setNewPerp({...newPerp, jacket_coat: e.target.value})} className="input-dark h-8" placeholder="Black hoodie" />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Pants</Label>
                            <Input value={newPerp.pants || ""} onChange={(e) => setNewPerp({...newPerp, pants: e.target.value})} className="input-dark h-8" placeholder="Blue jeans" />
                          </div>
                          <div>
                            <Label className="text-xs">Shoes</Label>
                            <Input value={newPerp.shoes || ""} onChange={(e) => setNewPerp({...newPerp, shoes: e.target.value})} className="input-dark h-8" placeholder="White sneakers" />
                          </div>
                          <div>
                            <Label className="text-xs">Other Clothing</Label>
                            <Input value={newPerp.clothing || ""} onChange={(e) => setNewPerp({...newPerp, clothing: e.target.value})} className="input-dark h-8" placeholder="Backpack" />
                          </div>
                        </div>
                        
                        {/* Status */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Checkbox id="editcall_perp_armed" checked={newPerp.has_weapon || false} onCheckedChange={(checked) => setNewPerp({...newPerp, has_weapon: checked})} />
                            <Label htmlFor="editcall_perp_armed" className="text-sm text-red-400">Armed</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="editcall_perp_arrested" checked={newPerp.was_arrested || false} onCheckedChange={(checked) => setNewPerp({...newPerp, was_arrested: checked})} />
                            <Label htmlFor="editcall_perp_arrested" className="text-sm text-green-400">Arrested</Label>
                          </div>
                        </div>
                        {newPerp.has_weapon && (
                          <div>
                            <Label className="text-xs">Weapon Description</Label>
                            <Input value={newPerp.weapon_description || ""} onChange={(e) => setNewPerp({...newPerp, weapon_description: e.target.value})} className="input-dark h-8" placeholder="Handgun, knife, etc." />
                          </div>
                        )}
                        
                        {/* Vehicle */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Vehicle</Label>
                            <Input value={newPerp.vehicle_description || ""} onChange={(e) => setNewPerp({...newPerp, vehicle_description: e.target.value})} className="input-dark h-8" placeholder="Black sedan" />
                          </div>
                          <div>
                            <Label className="text-xs">License Plate</Label>
                            <Input value={newPerp.license_plate || ""} onChange={(e) => setNewPerp({...newPerp, license_plate: e.target.value})} className="input-dark h-8" placeholder="ABC-1234" />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-xs">Notes</Label>
                          <Textarea value={newPerp.notes || ""} onChange={(e) => setNewPerp({...newPerp, notes: e.target.value})} className="input-dark" rows={2} placeholder="Additional notes..." />
                        </div>

                        {/* Name & Address */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Name (if known)</Label>
                            <Input value={newPerp.name || ""} onChange={(e) => setNewPerp({...newPerp, name: e.target.value})} className="input-dark h-8" placeholder="Name" />
                          </div>
                          <div>
                            <Label className="text-xs">Address (if known)</Label>
                            <Input value={newPerp.address || ""} onChange={(e) => setNewPerp({...newPerp, address: e.target.value})} className="input-dark h-8" placeholder="Address" />
                          </div>
                        </div>

                        {/* Link to Profile */}
                        <div className="border-t border-slate-700 pt-3">
                          <p className="text-xs text-purple-400 font-medium uppercase mb-2">
                            <UserIcon className="w-3 h-3 inline mr-1" />Link to Profile
                          </p>
                          {newPerp.linked_profile_id ? (
                            <div className="flex items-center justify-between p-2 bg-purple-900/30 border border-purple-700 rounded">
                              <div className="flex items-center gap-2">
                                <UserIcon className="w-4 h-4 text-purple-400" />
                                <div>
                                  <p className="text-sm font-medium text-purple-200">
                                    {perpetratorProfiles.find(p => p.profile_id === newPerp.linked_profile_id)?.name || 'Unknown Profile'}
                                  </p>
                                </div>
                              </div>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-red-400" onClick={() => setNewPerp({...newPerp, linked_profile_id: null, linked_profile_name: null})}>
                                <X className="w-3 h-3 mr-1" />Unlink
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="flex gap-2 mb-2">
                                <div className="relative flex-1">
                                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                                  <Input
                                    value={profileSearchQuery}
                                    onChange={(e) => setProfileSearchQuery(e.target.value)}
                                    placeholder="Search profiles by name..."
                                    className="input-dark pl-8 h-8 text-sm"
                                  />
                                </div>
                                <Button variant="outline" size="sm" className="h-8 px-2" title="Browse all profiles" onClick={() => setShowProfileBrowse(true)}>
                                  <List className="w-4 h-4" />
                                </Button>
                              </div>
                              {profileSearchQuery && (
                                <ScrollArea className="max-h-32">
                                  <div className="space-y-1">
                                    {perpetratorProfiles
                                      .filter(p => p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase()))
                                      .slice(0, 5)
                                      .map(profile => (
                                        <div
                                          key={profile.profile_id}
                                          className="p-2 bg-slate-800 rounded cursor-pointer hover:bg-slate-700 flex items-center justify-between"
                                          onClick={() => {
                                            setNewPerp({...newPerp, linked_profile_id: profile.profile_id, linked_profile_name: profile.name});
                                            setProfileSearchQuery("");
                                          }}
                                        >
                                          <div className="flex items-center gap-2">
                                            <UserIcon className="w-4 h-4 text-purple-400" />
                                            <p className="text-sm font-medium text-slate-200">{profile.name || 'Unknown'}</p>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            {profile.status === "ready_for_arrest" && <Badge className="text-[9px] bg-amber-600 px-1">READY</Badge>}
                                            {profile.status === "warrant" && <Badge className="text-[9px] bg-red-600 px-1">WARRANT</Badge>}
                                            {profile.status === "arrested" && <Badge className="text-[9px] bg-emerald-600 px-1">ARRESTED</Badge>}
                                          </div>
                                        </div>
                                      ))}
                                    {perpetratorProfiles.filter(p => p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase())).length === 0 && (
                                      <p className="text-xs text-slate-500 p-2">No matching profiles found</p>
                                    )}
                                  </div>
                                </ScrollArea>
                              )}
                            </>
                          )}
                        </div>
                        
                        <Button
                          className="w-full btn-primary"
                          onClick={() => {
                            if (editingNewCallPerpIndex !== null) {
                              const updated = [...(editCallData.perp_descriptions || [])];
                              updated[editingNewCallPerpIndex] = { ...newPerp, perp_id: updated[editingNewCallPerpIndex].perp_id };
                              setEditCallData({ ...editCallData, perp_descriptions: updated });
                            } else {
                              setEditCallData({
                                ...editCallData,
                                perp_descriptions: [...(editCallData.perp_descriptions || []), { ...newPerp, perp_id: `perp_${Date.now()}` }]
                              });
                            }
                            setShowEditCallPerpDialog(false);
                            setEditingNewCallPerpIndex(null);
                            setNewPerp({
                              name: "", address: "", current_location: "", sex: "", race: "", age: "", height: "", weight: "",
                              build: "", complexion: "", hair_color_cut: "", eye_color: "", hat_color_style: "",
                              shirt: "", pants: "", jacket_coat: "", shoes: "", visible_marks: "", clothing: "",
                              direction_of_flight: "", has_weapon: false, weapon_description: "",
                              vehicle_description: "", license_plate: "", was_arrested: false, notes: "",
                              linked_profile_id: null, linked_profile_name: null
                            });
                            setProfileSearchQuery("");
                          }}
                        >
                          {editingNewCallPerpIndex !== null ? 'Update Perpetrator' : 'Add Perpetrator'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Add Update Dialog (trigger button moved to Status / Radio card) */}
                  <Dialog open={showUpdateDialog} onOpenChange={(open) => {
                    if (open) {
                      savedScrollPositionRef.current = detailsPanelRef.current?.scrollTop || 0;
                      setUpdateDialogTab('update');
                    }
                    setShowUpdateDialog(open);
                  }}>
                    <DialogContent className="bg-slate-900 border-slate-800 max-h-[85vh] flex flex-col" onOpenAutoFocus={(e) => e.preventDefault()}>
                      <DialogHeader>
                        <DialogTitle>Add Update</DialogTitle>
                      </DialogHeader>
                      <div className="flex gap-1 border-b border-slate-700 mt-2">
                        <button
                          type="button"
                          onClick={() => setUpdateDialogTab('update')}
                          className={`px-3 py-1.5 text-xs font-medium transition-colors ${updateDialogTab === 'update' ? 'text-white border-b-2 border-blue-500' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          onClick={() => setUpdateDialogTab('radio')}
                          className={`px-3 py-1.5 text-xs font-medium transition-colors ${updateDialogTab === 'radio' ? 'text-white border-b-2 border-blue-500' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                          Status / Radio
                        </button>
                      </div>
                      {updateDialogTab === 'radio' && (
                        <div className="mt-4 overflow-y-auto flex-1 pr-2">
                          {renderStatusRadioCard(false, true, 'addUpdateDialog')}
                        </div>
                      )}
                      <div className={`space-y-4 mt-4 overflow-y-auto flex-1 pr-2 ${updateDialogTab === 'update' ? '' : 'hidden'}`}>
                        {(() => {
                          const hasAdditionals = (selectedCall?.additional_locations?.length || 0) > 0;
                          const hasPerps = (selectedCall?.perp_descriptions?.length || 0) > 0;
                          const targetSelected = (updateData.location_label !== null && updateData.location_label !== undefined)
                            || (updateData.perp_ids?.length || 0) > 0
                            || !!updateData.perp_id;
                          // Show the location/direction inputs only when there's no
                          // target picker (no perps + no additionals → the only target
                          // is the implicit original), or once the dispatcher has
                          // explicitly picked a target. Anything else would silently
                          // pre-select Original whenever the user happened to have a
                          // perp without additionals, which the dispatcher couldn't
                          // distinguish from a real selection.
                          const showLocFields = (!hasPerps && !hasAdditionals) || targetSelected;
                          const isOriginalSelected = updateData.location_label === "";
                          return (
                            <>
                              {/* Combined target picker: Original + perps + additional
                                  locations all render in the same row so the dispatcher
                                  can pick the update's destination in one place. */}
                              {(hasPerps || hasAdditionals) && (
                                <div>
                                  <Label className="mb-2 block">
                                    Update Target
                                    {hasPerps && <span className="text-xs text-slate-500 font-normal"> (perps multi-select)</span>}
                                  </Label>
                                  <div className="flex flex-wrap gap-2">
                                    <Button
                                      type="button"
                                      variant={isOriginalSelected ? "default" : "outline"}
                                      size="sm"
                                      className={isOriginalSelected ? "bg-amber-600 hover:bg-amber-700" : ""}
                                      onClick={() => {
                                        if (isOriginalSelected) {
                                          setUpdateData({ ...updateData, perp_id: "", perp_ids: [], location_label: null });
                                        } else {
                                          setUpdateData({ ...updateData, perp_id: "", perp_ids: [], location_label: "" });
                                        }
                                      }}
                                    >
                                      Original
                                    </Button>
                                    {hasPerps && selectedCall.perp_descriptions.map((p, idx) => {
                                      const ids = updateData.perp_ids || [];
                                      const isSelected = ids.includes(p.perp_id) || updateData.perp_id === p.perp_id;
                                      // If the perp is linked to a profile, lead with the
                                      // profile name so the dispatcher recognizes who they
                                      // are picking instead of staring at "#2 Male Black".
                                      const label = p.linked_profile_name
                                        ? `#${idx + 1} ${p.linked_profile_name}`
                                        : `#${idx + 1} ${p.sex || ''} ${p.race || ''}`.trim();
                                      return (
                                        <Button
                                          key={p.perp_id}
                                          type="button"
                                          variant={isSelected ? "default" : "outline"}
                                          size="sm"
                                          className={isSelected ? "bg-purple-600 hover:bg-purple-700" : ""}
                                          onClick={() => {
                                            const currentIds = updateData.perp_ids?.length > 0
                                              ? [...updateData.perp_ids]
                                              : updateData.perp_id ? [updateData.perp_id] : [];
                                            const newIds = currentIds.includes(p.perp_id)
                                              ? currentIds.filter(id => id !== p.perp_id)
                                              : [...currentIds, p.perp_id];
                                            // Picking a perp clears any additional-location
                                            // selection so the two target types stay
                                            // mutually exclusive (matches the additional
                                            // button's clear-perps behavior).
                                            const next = { ...updateData, perp_id: newIds[0] || "", perp_ids: newIds };
                                            if (newIds.length > 0) next.location_label = null;
                                            setUpdateData(next);
                                          }}
                                        >
                                          {label}
                                        </Button>
                                      );
                                    })}
                                    {hasAdditionals && selectedCall.additional_locations.map((loc, idx) => {
                                      const label = loc.title || `Additional #${idx + 1}`;
                                      const isSelected = updateData.location_label === label;
                                      return (
                                        <Button
                                          key={`add-${idx}`}
                                          type="button"
                                          size="sm"
                                          variant={isSelected ? "default" : "outline"}
                                          className={isSelected ? "bg-blue-600 hover:bg-blue-700" : ""}
                                          onClick={() => {
                                            if (isSelected) {
                                              setUpdateData({ ...updateData, location_label: null });
                                            } else {
                                              setUpdateData({ ...updateData, location_label: label, perp_id: "", perp_ids: [] });
                                            }
                                          }}
                                        >
                                          {label}
                                        </Button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {showLocFields && (
                                <div>
                                  <Label>Updated Location</Label>
                                  <Input
                                    value={updateData.updated_location}
                                    onChange={(e) => setUpdateData({ ...updateData, updated_location: e.target.value })}
                                    className="input-dark"
                                    placeholder="New location if changed..."
                                  />
                                </div>
                              )}
                              {showLocFields && (
                                <div>
                                  <Label>Direction of Flight</Label>
                                  <Input
                                    value={updateData.updated_direction}
                                    onChange={(e) => setUpdateData({ ...updateData, updated_direction: e.target.value })}
                                    className="input-dark"
                                    placeholder="e.g., Northbound on Main St..."
                                  />
                                </div>
                              )}
                            </>
                          );
                        })()}
                        {/* Perp status checkboxes - only show when a perp is selected */}
                        {(updateData.perp_id || (updateData.perp_ids || []).length > 0) && (
                          <div className="p-2 bg-slate-800 rounded border border-slate-700 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="update-arrested"
                                  checked={updateData.set_arrested}
                                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_arrested: checked })}
                                />
                                <Label htmlFor="update-arrested" className="text-sm cursor-pointer text-green-400">Arrested</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="update-armed"
                                  checked={updateData.set_armed}
                                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_armed: checked })}
                                />
                                <Label htmlFor="update-armed" className="text-sm cursor-pointer text-red-400">Armed</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="update-holding"
                                  checked={updateData.set_holding}
                                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_holding: checked })}
                                />
                                <Label htmlFor="update-holding" className="text-sm cursor-pointer text-amber-400">Holding</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="update-detained"
                                  checked={updateData.set_detained}
                                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_detained: checked })}
                                />
                                <Label htmlFor="update-detained" className="text-sm cursor-pointer text-orange-400">Detained</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="update-visual"
                                  checked={updateData.set_visual}
                                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_visual: checked })}
                                />
                                <Label htmlFor="update-visual" className="text-sm cursor-pointer text-cyan-400">Visual</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="update-positive-id"
                                  checked={updateData.set_positive_id}
                                  onCheckedChange={(checked) => setUpdateData({ ...updateData, set_positive_id: checked })}
                                />
                                <Label htmlFor="update-positive-id" className="text-sm cursor-pointer text-emerald-400">Positive ID</Label>
                              </div>
                            </div>
                            {/* Weapon description input - shows when Armed is checked */}
                            {updateData.set_armed && (
                              <Input
                                value={updateData.weapon_description}
                                onChange={(e) => setUpdateData({ ...updateData, weapon_description: e.target.value })}
                                className="input-dark"
                                placeholder="Weapon description (optional)..."
                              />
                            )}
                            {/* Holding/Detained per-status location fields removed
                                from this dialog — per dispatcher feedback the
                                inline "(optional)" inputs were noise. The perp's
                                edit dialog still owns those fields. */}
                          </div>
                        )}
                        <div>
                          <Label>{updateData.perp_id ? 'Perp Description Update' : 'Updated Perp Description'}</Label>
                          <Textarea
                            value={updateData.updated_perp_description}
                            onChange={(e) => setUpdateData({ ...updateData, updated_perp_description: e.target.value })}
                            className="input-dark"
                            placeholder={updateData.perp_id ? "Additional info about this perp..." : "Updated description..."}
                          />
                        </div>
                        <div>
                          <Label>Job Narrative</Label>
                          <Textarea
                            value={updateData.general_notes}
                            onChange={(e) => setUpdateData({ ...updateData, general_notes: e.target.value })}
                            className="input-dark"
                            data-testid="update-notes-input"
                            placeholder="Job narrative / call note..."
                          />
                        </div>
                        <div>
                          <Label>Updates <span className="text-xs text-slate-500 font-normal">(timeline note)</span></Label>
                          <Textarea
                            value={updateData.update_text}
                            onChange={(e) => setUpdateData({ ...updateData, update_text: e.target.value })}
                            className="input-dark"
                            placeholder="General update for timeline..."
                          />
                        </div>
                        <div className="pt-2 border-t border-slate-700">
                          <div className="flex items-center justify-between">
                            <Label>Additional Locations ({(selectedCall.additional_locations || []).length})</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setAddLocData({ title: "", location: "", location_notes: "", cross_streets: "", direction_of_flight: "" });
                                setEditingLocIndex("update-add");
                                setShowAddLocDialog(true);
                              }}
                              className="h-7 text-xs"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Location
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="pt-4 border-t border-slate-700 mt-2">
                        <Button onClick={() => setShowAddUpdateConfirm(true)} className="w-full btn-primary" data-testid="submit-update-btn">
                          Add Update
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                      </CardContent>
                  </Card>

                </div>
              </div>

              {/* Call Info + Radio Transmissions - Side by Side */}
              <div className="grid grid-cols-1 gap-4 mb-4 lg:contents">
                {/* Call Info — single instance on all breakpoints, gated by tab. */}
                <Card id="section-call-info" className={`bg-slate-800 border-slate-800 p-3 lg:order-1 ${detailTab === 'all' || detailTab === 'callinfo' ? '' : 'hidden'}`}>
                  {renderCallInfoBody()}
                </Card>

                {/* Status / Radio Transmissions - Merged Card. When the Status
                    tab is explicitly selected, force the (mobile-collapsed)
                    radio options open so the card isn't shown collapsed. */}
                <div className={`lg:order-2 ${detailTab === 'all' || detailTab === 'status' ? '' : 'hidden'}`}>{renderStatusRadioCard(detailTab === 'status')}</div>
              </div>

              {/* Assigned Units + Location Info - Side by Side */}
              <div className="grid grid-cols-1 gap-4 mb-4 items-stretch lg:contents">
                {/* Assigned Units */}
                <div id="section-units" className={`lg:order-3 lg:h-full ${detailTab === 'all' || detailTab === 'units' ? '' : 'hidden'}`}>{renderAssignedUnitsCard()}</div>

                {/* Location Info */}
                <Card id="section-location" className={`bg-slate-800 border-slate-800 h-full flex flex-col min-h-0 lg:order-4 ${detailTab === 'all' || detailTab === 'location' ? '' : 'hidden'}`}>
                  <CardHeader className="pb-2 flex-shrink-0">
                    <CardTitle className="text-xs uppercase text-slate-400 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-amber-500" />
                        Location Info
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs border-blue-600/50 text-blue-400 hover:bg-blue-600/20"
                          title="Add additional location"
                          onClick={() => {
                            setAddLocData({ title: "", location: "", location_notes: "", cross_streets: "", direction_of_flight: "" });
                            setEditingLocIndex("update-add");
                            setAddLocPredictions([]);
                            setShowAddLocPredictions(false);
                            setShowAddLocDialog(true);
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => setShowLocationInfo(!showLocationInfo)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          {showLocationInfo ? "Hide" : "Show"}
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  {showLocationInfo && (
                  <CardContent className="p-3 pt-0 flex-1 overflow-auto min-h-0">
                    <div className="space-y-2">
                      {/* Original Location Info - Combined Box */}
                      <div className="bg-slate-900 rounded-sm border border-slate-800 p-2.5 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-600">Original{selectedCall.location_title ? `/${selectedCall.location_title}` : ""}</Badge>
                            {(() => {
                              const bc = findBuildingCode(selectedCall.location);
                              return bc ? (
                                <Badge className="text-[10px] bg-cyan-700 text-white border-0">🏢 {bc}</Badge>
                              ) : null;
                            })()}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                              title="Edit original location"
                              onClick={() => {
                                setPendingLocEdit(() => () => {
                                  setAddLocData({
                                    title: selectedCall.location_title || "",
                                    location: selectedCall.location || "",
                                    location_notes: selectedCall.location_notes || "",
                                    cross_streets: selectedCall.cross_streets || "",
                                    direction_of_flight: selectedCall.direction_of_flight || "",
                                  });
                                  setEditingLocIndex("live-original");
                                  setAddLocPredictions([]);
                                  setShowAddLocPredictions(false);
                                  setShowAddLocDialog(true);
                                });
                              }}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <span className="text-[10px] text-slate-600 font-mono">{formatTime(selectedCall.created_at)}</span>
                          </div>
                        </div>
                        {/* Address + Location Notes on same line */}
                        <p className="text-sm text-slate-200 font-medium">
                          📍 {selectedCall.location ? (
                            <a
                              href={mapHrefFor(selectedCall.location)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:text-blue-400"
                            >{selectedCall.location}</a>
                          ) : "No location"}
                          {selectedCall.location_notes && (
                            <span className="text-slate-400 italic font-normal"> - {selectedCall.location_notes}</span>
                          )}
                        </p>
                        {/* Cross Streets */}
                        {selectedCall.cross_streets && (
                          <p className="text-xs text-emerald-400">
                            Cross: {selectedCall.cross_streets}
                          </p>
                        )}
                        {/* Direction of Flight */}
                        {selectedCall.direction_of_flight && (
                          <p className="text-sm text-amber-400 font-medium">
                            → Direction: {selectedCall.direction_of_flight}
                          </p>
                        )}
                        {/* Latest location/direction update tagged to the original location */}
                        {(() => {
                          const updates = (selectedCall.updates || [])
                            .filter(u => (u.updated_location || u.updated_direction) && !u.perp_id && !(u.location_label || "").trim())
                            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                          if (updates.length === 0) return null;
                          const expanded = isLocUpdatesShown('orig', true);
                          return (
                            <>
                              <div className="mt-1 pt-1 border-t border-slate-700/60">
                                <div className="flex items-center justify-between">
                                  <p className="text-[10px] text-amber-400/70 uppercase font-medium">Updates ({updates.length})</p>
                                  <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-200" onClick={() => toggleLocUpdates('orig', true)}>
                                    <Eye className="w-3 h-3 mr-1" />{expanded ? 'Hide' : 'Show'}
                                  </Button>
                                </div>
                                {expanded && (
                                  <div className="space-y-1 mt-1">
                                    {updates.map((u, i) => (
                                      <div key={u.update_id || i} className="text-[11px] bg-amber-50/10 dark:bg-amber-900/20 rounded px-1.5 py-1">
                                        <div className="text-slate-500 dark:text-slate-400 font-mono text-[10px]">
                                          {formatTime(u.timestamp)} · {u.updated_by}
                                        </div>
                                        {u.updated_location && (
                                          <div className="text-amber-700 dark:text-amber-400">📍 <a href={mapHrefFor(u.updated_location)} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-400">{u.updated_location}</a></div>
                                        )}
                                        {u.updated_direction && (
                                          <div className="text-amber-600 dark:text-amber-400">→ {u.updated_direction}</div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* Additional Locations */}
                      {selectedCall.additional_locations?.length > 0 && selectedCall.additional_locations.map((loc, idx) => (
                        <div key={idx} className="bg-slate-900 rounded-sm border border-blue-800/40 p-2.5 space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-600">
                                {loc.title || `Additional #${idx + 1}`}
                              </Badge>
                              {(() => {
                                const bc = findBuildingCode(loc.location);
                                return bc ? (
                                  <Badge className="text-[10px] bg-cyan-700 text-white border-0">🏢 {bc}</Badge>
                                ) : null;
                              })()}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 shrink-0"
                              title="Edit this location"
                              onClick={() => {
                                setPendingLocEdit(() => () => {
                                  setAddLocData({
                                    title: loc.title || "",
                                    location: loc.location || "",
                                    location_notes: loc.location_notes || "",
                                    cross_streets: loc.cross_streets || "",
                                    direction_of_flight: loc.direction_of_flight || "",
                                  });
                                  setEditingLocIndex(`live-add-${idx}`);
                                  setAddLocPredictions([]);
                                  setShowAddLocPredictions(false);
                                  setShowAddLocDialog(true);
                                });
                              }}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-sm text-slate-200 font-medium">
                            📍 {loc.location ? (
                              <a
                                href={mapHrefFor(loc.location)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-blue-400"
                              >{loc.location}</a>
                            ) : null}
                            {loc.location_notes && (
                              <span className="text-slate-400 italic font-normal"> - {loc.location_notes}</span>
                            )}
                          </p>
                          {loc.cross_streets && (
                            <p className="text-xs text-emerald-400">Cross: {loc.cross_streets}</p>
                          )}
                          {loc.direction_of_flight && (
                            <p className="text-sm text-amber-400 font-medium">→ Direction: {loc.direction_of_flight}</p>
                          )}
                          {/* Latest location/direction update tagged to this additional location */}
                          {(() => {
                            const label = loc.title || `Additional #${idx + 1}`;
                            const updates = (selectedCall.updates || [])
                              .filter(u => (u.updated_location || u.updated_direction) && !u.perp_id && (u.location_label || "") === label)
                              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                            if (updates.length === 0) return null;
                            const key = `add-${idx}`;
                            const expanded = isLocUpdatesShown(key, true);
                            return (
                              <>
                                <div className="mt-1 pt-1 border-t border-slate-700/60">
                                  <div className="flex items-center justify-between">
                                    <p className="text-[10px] text-blue-400/70 uppercase font-medium">Updates ({updates.length})</p>
                                    <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-200" onClick={() => toggleLocUpdates(key, true)}>
                                      <Eye className="w-3 h-3 mr-1" />{expanded ? 'Hide' : 'Show'}
                                    </Button>
                                  </div>
                                  {expanded && (
                                    <div className="space-y-1 mt-1">
                                      {updates.map((u, i) => (
                                        <div key={u.update_id || i} className="text-[11px] bg-blue-900/20 rounded px-1.5 py-1">
                                          <div className="text-slate-500 dark:text-slate-400 font-mono text-[10px]">
                                            {formatTime(u.timestamp)} · {u.updated_by}
                                          </div>
                                          {u.updated_location && (
                                            <div className="text-amber-700 dark:text-amber-400">📍 <a href={mapHrefFor(u.updated_location)} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-400">{u.updated_location}</a></div>
                                          )}
                                          {u.updated_direction && (
                                            <div className="text-amber-600 dark:text-amber-400">→ {u.updated_direction}</div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      ))}

                      {/* Perp Latest Locations — one card per perp that has a current location or direction */}
                      {(selectedCall.perp_descriptions || []).map((perp, idx) => {
                        if (!perp.current_location && !perp.direction_of_flight) return null;
                        const descBits = [];
                        // Sex + race render without labels and slash-joined to
                        // match the WhatsApp perp blast format ('Male / Black').
                        const sexRace = [perp.sex, perp.race].filter(Boolean).join(' / ');
                        if (sexRace) descBits.push(sexRace);
                        if (perp.age) descBits.push(`age/${perp.age}`);
                        const clothingBits = [
                          perp.hat_color_style && `hat/${perp.hat_color_style}`,
                          perp.shirt && `shirt/${perp.shirt}`,
                          perp.jacket_coat && `jacket/${perp.jacket_coat}`,
                          perp.pants && `pants/${perp.pants}`,
                          perp.shoes && `shoes/${perp.shoes}`,
                          perp.clothing && `clothing/${perp.clothing}`,
                        ].filter(Boolean);
                        const descLine = descBits.join(" | ");
                        const clothingLine = clothingBits.join(" | ");
                        // Match the per-perp card logic: highlight whichever of location / direction was updated most recently.
                        const locTime = perp.location_updated_at ? new Date(perp.location_updated_at) : null;
                        const dirTime = perp.direction_updated_at ? new Date(perp.direction_updated_at) : null;
                        const sameUpdate = locTime && dirTime && Math.abs(locTime.getTime() - dirTime.getTime()) < 2000;
                        const locationIsNewer = sameUpdate || (locTime && dirTime ? locTime > dirTime : locTime && !dirTime);
                        const directionIsNewer = sameUpdate || (dirTime && locTime ? dirTime > locTime : dirTime && !locTime);
                        return (
                          <div key={`perpLoc_${perp.perp_id || idx}`} className="bg-slate-900 rounded-sm border border-purple-800/40 p-2.5 space-y-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge variant="outline" className="text-[10px] text-purple-400 border-purple-600">
                                  Perp #{idx + 1}{perp.linked_profile_name ? ` — ${perp.linked_profile_name}` : ''}
                                </Badge>
                                {(() => {
                                  const bc = findBuildingCode(perp.current_location);
                                  return bc ? (
                                    <Badge className="text-[10px] bg-cyan-700 text-white border-0">🏢 {bc}</Badge>
                                  ) : null;
                                })()}
                                {(() => {
                                  // Show only the most recently set of HOLDING / DETAINED /
                                  // ARRESTED / VISUAL — they all describe a competing
                                  // "current state of the perp," and stacking all of them
                                  // is noisy. ARMED and POSITIVE ID render independently.
                                  const candidates = [
                                    (perp.was_arrested || perp.is_arrested) && { label: 'ARRESTED', cls: 'bg-emerald-700', ts: perp.was_arrested_at || perp.arrest_updated_at },
                                    perp.is_holding && { label: 'HOLDING', cls: 'bg-amber-700', ts: perp.is_holding_at },
                                    perp.is_detained && { label: 'DETAINED', cls: 'bg-orange-700', ts: perp.is_detained_at },
                                    perp.is_visual && { label: 'VISUAL', cls: 'bg-cyan-600', ts: perp.is_visual_at },
                                  ].filter(Boolean);
                                  if (candidates.length === 0) return null;
                                  candidates.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
                                  const latest = candidates[0];
                                  return <Badge className={`text-[10px] ${latest.cls} text-white border-0`}>{latest.label}</Badge>;
                                })()}
                                {perp.is_positive_id && <Badge className="text-[10px] bg-emerald-600 text-white border-0">POSITIVE ID</Badge>}
                                {perp.has_weapon && <Badge variant="destructive" className="text-[10px]">ARMED</Badge>}
                              </div>
                              {(perp.location_updated_at || perp.direction_updated_at) && (
                                <span className="text-[10px] text-slate-600 font-mono">
                                  {formatTimeOnly(perp.location_updated_at || perp.direction_updated_at)}
                                </span>
                              )}
                            </div>
                            {(descLine || clothingLine) && (
                              <p className="text-[11px] text-slate-400">
                                {descLine}
                                {descLine && clothingLine ? ' — ' : ''}
                                {clothingLine}
                              </p>
                            )}
                            {perp.current_location && (
                              <div className={`text-sm px-2 py-1 rounded flex flex-wrap items-center gap-1 ${locationIsNewer ? 'bg-green-500/20 text-green-400 font-medium border border-green-500/50' : 'text-slate-200 font-medium'}`}>
                                <span className="flex-1 min-w-0 break-words">
                                  📍 <a
                                    href={mapHrefFor(perp.current_location)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-blue-400"
                                  >
                                    {perp.current_location}
                                  </a>
                                </span>
                                <span className="flex items-center gap-1 shrink-0">
                                  {locationIsNewer && !sameUpdate && <Badge className="bg-green-600 text-white text-[9px] px-1 py-0">LATEST</Badge>}
                                  {perp.location_updated_at && (
                                    <span className="text-[10px] opacity-70">{formatTimeOnly(perp.location_updated_at)}</span>
                                  )}
                                </span>
                              </div>
                            )}
                            {perp.direction_of_flight && (
                              <div className={`text-sm px-2 py-1 rounded flex flex-wrap items-center gap-1 ${directionIsNewer ? 'bg-green-500/20 text-green-400 font-medium border border-green-500/50' : 'text-amber-400 font-medium'}`}>
                                <span className="flex-1 min-w-0 break-words">→ Direction: {perp.direction_of_flight}</span>
                                <span className="flex items-center gap-1 shrink-0">
                                  {directionIsNewer && !sameUpdate && <Badge className="bg-green-600 text-white text-[9px] px-1 py-0">LATEST</Badge>}
                                  {perp.direction_updated_at && (
                                    <span className="text-[10px] opacity-70">{formatTimeOnly(perp.direction_updated_at)}</span>
                                  )}
                                </span>
                              </div>
                            )}
                            {/* Per-perp updates list — same Show/Hide pattern as
                                Original / Additional, but defaults to hidden so
                                the perp box stays compact. */}
                            {(() => {
                              const perpUpdates = (selectedCall.updates || [])
                                .filter(u => (u.updated_location || u.updated_direction) && u.perp_id === perp.perp_id)
                                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                              if (perpUpdates.length === 0) return null;
                              const key = `perp-${perp.perp_id || idx}`;
                              const expanded = isLocUpdatesShown(key, false);
                              return (
                                <div className="mt-1 pt-1 border-t border-slate-700/60">
                                  <div className="flex items-center justify-between">
                                    <p className="text-[10px] text-purple-400/70 uppercase font-medium">Updates ({perpUpdates.length})</p>
                                    <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-200" onClick={() => toggleLocUpdates(key, false)}>
                                      <Eye className="w-3 h-3 mr-1" />{expanded ? 'Hide' : 'Show'}
                                    </Button>
                                  </div>
                                  {expanded && (
                                    <div className="space-y-1 mt-1">
                                      {perpUpdates.map((u, i) => (
                                        <div key={u.update_id || i} className="text-[11px] bg-purple-900/20 rounded px-1.5 py-1">
                                          <div className="text-slate-500 dark:text-slate-400 font-mono text-[10px]">
                                            {formatTime(u.timestamp)} · {u.updated_by}
                                          </div>
                                          {u.updated_location && (
                                            <div className="text-amber-700 dark:text-amber-400">📍 <a href={mapHrefFor(u.updated_location)} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-400">{u.updated_location}</a></div>
                                          )}
                                          {u.updated_direction && (
                                            <div className="text-amber-600 dark:text-amber-400">→ {u.updated_direction}</div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}

                      {/* Location Updates - Combined list of update locations + perp locations */}
                      {(() => {
                        // Combine location updates and perp locations into one list
                        const locationUpdates = (selectedCall.updates || [])
                          .filter(u => u.updated_location || u.updated_direction)
                          .map(u => {
                            return {
                              type: 'update',
                              id: u.update_id,
                              timestamp: u.timestamp,
                              updatedBy: u.updated_by,
                              location: u.updated_location,
                              direction: u.updated_direction || null,
                              locationLabel: u.location_label || null,
                              perpId: u.perp_id,
                              perpNum: u.perp_id ? (selectedCall.perp_descriptions?.findIndex(p => p.perp_id === u.perp_id) + 1) || null : null
                            };
                          });

                        // Use created_at for perp locations so they don't jump to top on unrelated edits
                        // Location updates from the updates array have their own timestamps when location specifically changed
                        // Filter out perps that already have location updates to avoid duplicates
                        const perpsWithLocationUpdates = new Set(locationUpdates.map(u => u.perpId).filter(Boolean));
                        const perpLocations = (selectedCall.perp_descriptions || [])
                          .map((perp, idx) => ({ perp, perpNum: idx + 1 }))
                          .filter(({ perp }) => perp.current_location || perp.direction_of_flight)
                          .filter(({ perp }) => !perpsWithLocationUpdates.has(perp.perp_id))
                          .map(({ perp, perpNum }) => ({
                            type: 'perp',
                            id: perp.perp_id,
                            timestamp: perp.created_at,
                            updatedBy: perp.created_by,
                            location: perp.current_location,
                            direction: perp.direction_of_flight,
                            perpId: perp.perp_id,
                            perpNum
                          }));

                        const allLocations = [...locationUpdates, ...perpLocations];

                        if (allLocations.length === 0) return null;

                        const sortedLocations = allLocations.sort((a, b) =>
                          locationSortOrder === "newest"
                            ? new Date(b.timestamp) - new Date(a.timestamp)
                            : new Date(a.timestamp) - new Date(b.timestamp)
                        );

                        return (
                          <div className="space-y-2 pt-2 border-t border-slate-800">
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-xs text-slate-500 uppercase">Location Updates ({allLocations.length})</p>
                              <div className="flex items-center gap-1">
                                {(showLocationUpdates || detailTab === 'location') && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setLocationSortOrder && setLocationSortOrder(locationSortOrder === "newest" ? "oldest" : "newest")}
                                    className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-300"
                                  >
                                    {locationSortOrder === "newest" ? (
                                      <><ChevronDown className="w-3 h-3" /> New</>
                                    ) : (
                                      <><ChevronUp className="w-3 h-3" /> Old</>
                                    )}
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowLocationUpdates(!showLocationUpdates)}
                                  className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-300"
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  {(showLocationUpdates || detailTab === 'location') ? 'Hide' : 'Show'}
                                </Button>
                              </div>
                            </div>
                            {/* Auto-expand when the Location tab is explicitly selected. */}
                            {(showLocationUpdates || detailTab === 'location') && (
                            <div className={`space-y-2 ${allLocations.length > 5 ? 'max-h-[280px] overflow-y-auto' : ''}`}>
                              {sortedLocations.map((item) => {
                                // Identify the update target: Perp #, additional-location title,
                                // or fall back to "Original" when neither is set.
                                const targetBadge = item.perpNum
                                  ? <Badge className="ml-2 text-xs bg-purple-600 text-white">Perp #{item.perpNum}</Badge>
                                  : item.locationLabel
                                    ? <Badge className="ml-2 text-xs bg-blue-600 text-white">{item.locationLabel}</Badge>
                                    : <Badge className="ml-2 text-xs bg-amber-600 text-white">Original</Badge>;
                                return (
                                  <div key={item.id} className="p-2 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800/50">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs text-amber-600 dark:text-amber-500 font-mono">{formatTime(item.timestamp)}</span>
                                      <span className="text-xs text-slate-500">by {item.updatedBy}</span>
                                    </div>
                                    {item.location && (
                                      <p className="text-sm text-amber-700 dark:text-amber-300">
                                        📍 {item.location}
                                        {targetBadge}
                                      </p>
                                    )}
                                    {item.direction && (
                                      <p className="text-sm text-amber-400">
                                        → {item.direction}
                                        {!item.location && targetBadge}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            )}
                          </div>
                        );
                      })()}

                      {!selectedCall.location_notes && !selectedCall.updates?.some(u => u.updated_location) && !selectedCall.location && !selectedCall.perp_descriptions?.some(p => p.current_location || p.direction_of_flight) && (
                        <p className="text-xs text-slate-500 italic">No location information</p>
                      )}
                    </div>
                  </CardContent>
                  )}
                </Card>
              </div>
              {/* Basic Description - Moved before Perp cards.
                  Perp status updates (arrested / holding / detained) used to
                  also render here, but they were moved to Timeline Updates so
                  the dispatcher reads call narrative + perp lifecycle in one
                  chronological place. */}
              {selectedCall.description && (
                <Card className="bg-slate-800 border-slate-800 p-3 mb-4 lg:order-5 lg:mb-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs uppercase text-slate-500">Basic Description</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 px-2 text-[10px]"
                      onClick={() => setShowBasicDescription(!showBasicDescription)}
                    >
                      <Eye className="w-2.5 h-2.5 mr-1" />
                      {showBasicDescription ? "Hide" : "Show"}
                    </Button>
                  </div>
                  {showBasicDescription && <div className="text-sm text-slate-300 space-y-1">
                    {selectedCall.description.split(/(?=\[\d{1,2}\/\d{1,2}\s+\d{1,2}:\d{2}\s*(?:AM|PM)?\])/).filter(Boolean).map((entry, idx) => (
                      <div key={idx} className="border-l-2 border-slate-700 pl-2 py-0.5">
                        {entry.trim()}
                      </div>
                    ))}
                  </div>}
                </Card>
              )}
              </div>


              {/* Inline Checklist — its own tab (no popup). Sits right after the
                  grid so that on the Checklist tab (everything else hidden) it
                  renders directly below the header. */}
              {detailTab === 'checklist' && selectedCall && (() => {
                const { items, completed } = computeChecklist();
                return (
                  <Card className="bg-slate-800 border-slate-800 mb-4">
                    <CardHeader className="py-2 pb-1">
                      <CardTitle className="text-xs uppercase text-slate-400 flex items-center gap-2">
                        <CheckSquare className="w-4 h-4 text-cyan-400" />
                        Dispatcher Checklist
                        <span className="ml-auto text-slate-400 font-normal normal-case">{completed}/{items.length}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 pt-0">
                      <p className="text-[10px] text-slate-500 mb-1">Auto-tracked from the call data. Make changes in the relevant tab, not here.</p>
                      {renderChecklistRows(items)}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Inline Add Update — its own tab (no popup). Renders a copy of the
                  Add Update dialog's form fields plus a submit button, sharing the
                  same updateData state and confirm path as the dialog. */}
              {detailTab === 'update' && selectedCall && (
                <Card className="bg-slate-800 border-slate-800 mb-4">
                  <CardHeader className="py-2 pb-1">
                    <CardTitle className="text-xs uppercase text-slate-400 flex items-center gap-2">
                      <Plus className="w-4 h-4 text-blue-400" />
                      Add Update
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    {renderAddUpdateForm()}
                    <div className="pt-4 border-t border-slate-700 mt-4">
                      <Button onClick={() => setShowAddUpdateConfirm(true)} className="w-full btn-primary" data-testid="inline-submit-update-btn">
                        Add Update
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Perpetrator Descriptions */}
              <Card id="section-perps" className={`bg-slate-800 border-slate-800 mb-4 overflow-hidden ${detailTab === 'all' || detailTab === 'perps' ? '' : 'hidden'}`}>
                <CardHeader className="pb-2 min-w-0">
                  <CardTitle className="text-xs uppercase text-slate-400 flex items-center justify-between gap-1 min-w-0">
                    <div className="flex items-center gap-1 min-w-0">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">Perps ({selectedCall.perp_descriptions?.length || 0})</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {selectedCall.perp_descriptions?.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-xs text-green-400 hover:text-green-300"
                          disabled={sendingAllPerpsWhatsApp}
                          onClick={() => setShowAllPerpsWhatsAppConfirm(true)}
                          title="WhatsApp all perp descriptions to notification group"
                        >
                          <MessageSquare className={`w-3 h-3 mr-0.5 ${sendingAllPerpsWhatsApp ? 'animate-pulse' : ''}`} />
                          WA All Perps
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-xs"
                        onClick={() => setShowPerps(!showPerps)}
                      >
                        <Eye className="w-3 h-3 mr-0.5" />
                        {showPerps ? "Hide" : "Show"}
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-1.5" onClick={() => openPerpDialog()}>
                        <Plus className="w-3 h-3 mr-0.5" />Add
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                {showPerps && (
                <CardContent className="p-3 pt-0">
                  {selectedCall.perp_descriptions?.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {selectedCall.perp_descriptions.map((perp, idx) => (
                        <div
                          key={perp.perp_id || idx}
                          className={`p-3 rounded text-sm cursor-pointer ${perp.was_arrested ? 'bg-green-100 dark:bg-emerald-950 border border-green-500 dark:border-emerald-800' : 'bg-slate-100 dark:bg-slate-900 border border-slate-800'}`}
                          onClick={() => setCollapsedPerps(prev => ({ ...prev, [perp.perp_id || idx]: !prev[perp.perp_id || idx] }))}
                        >
                          {/* Header with badges and edit/delete buttons */}
                          <div className="flex items-center justify-between gap-2 select-none">
                            <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
                              {collapsedPerps[perp.perp_id || idx] ? <ChevronDown className="w-3 h-3 text-slate-500 shrink-0" /> : <ChevronUp className="w-3 h-3 text-slate-500 shrink-0" />}
                              <Badge variant="outline" className="text-xs text-slate-700 dark:text-slate-300">Perp #{idx + 1}</Badge>
                              {perp.name && <span className="font-medium text-slate-200">{perp.name}</span>}
                              {perp.has_weapon && <Badge variant="destructive" className="text-[10px] px-1.5 py-0.5 inline-flex items-center">ARMED</Badge>}
                              {(() => {
                                // Show only the most recently stamped of
                                // ARRESTED / HOLDING / DETAINED / VISUAL —
                                // matches the Location Info perp card.
                                const candidates = [
                                  (perp.was_arrested || perp.is_arrested) && { label: 'ARRESTED', cls: 'bg-green-600', ts: perp.was_arrested_at || perp.arrest_updated_at },
                                  perp.is_holding && { label: 'HOLDING', cls: 'bg-orange-500', ts: perp.is_holding_at },
                                  perp.is_detained && { label: 'DETAINED', cls: 'bg-amber-500', ts: perp.is_detained_at },
                                  perp.is_visual && { label: 'VISUAL', cls: 'bg-cyan-500', ts: perp.is_visual_at },
                                ].filter(Boolean);
                                if (candidates.length === 0) return null;
                                candidates.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
                                const latest = candidates[0];
                                return <Badge className={`${latest.cls} text-white text-[10px] px-1.5 py-0.5 inline-flex items-center`}>{latest.label}</Badge>;
                              })()}
                              {perp.is_positive_id && <Badge className="bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 inline-flex items-center">POSITIVE ID</Badge>}
                            </div>
                            <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0"
                                disabled={sendingPerpNotif === perp.perp_id}
                                onClick={() => { setPerpNotifWhatsApp(true); setPerpNotifPush(false); setPerpNotifWaIndividuals(true); setPerpNotifWaBotGroups(true); setConfirmPerpNotif(perp.perp_id); }}
                                title="Send Perp Notification"
                              >
                                <MessageSquare className={`w-3 h-3 text-green-400 ${sendingPerpNotif === perp.perp_id ? 'animate-pulse' : ''}`} />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openPerpDialog(perp)}>
                                <Edit className="w-3 h-3 text-blue-400" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => {
                                setPendingDeletePerpId(perp.perp_id);
                                setShowDeletePerpConfirm(true);
                              }}>
                                <Trash2 className="w-3 h-3 text-red-400" />
                              </Button>
                            </div>
                          </div>
                          {/* Content - collapsible */}
                          {!collapsedPerps[perp.perp_id || idx] && <div className="mt-2">
                              
                              {/* Linked Profile - show at top of perp card */}
                              {perp.linked_profile_id && (() => {
                                const linkedProfile = perpetratorProfiles.find(p => p.profile_id === perp.linked_profile_id);
                                return (
                                  <div
                                    className="mb-2 p-1.5 bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-700/50 rounded flex items-center gap-2 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); prepareNavigate(); navigate(`/perpetrators?profile=${perp.linked_profile_id}`); }}
                                  >
                                    <Link className="w-3 h-3 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                                    <span className="text-xs text-purple-700 dark:text-purple-300 font-medium">{linkedProfile?.name || 'Linked Profile'}</span>
                                    {linkedProfile?.has_arrest_warrant && <Badge className="text-[9px] bg-red-600 px-1">WARRANT</Badge>}
                                  </div>
                                );
                              })()}
                              
                              {/* Physical Description */}
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-1 text-xs text-slate-400 mb-2">
                                {perp.sex && <span><span className="text-slate-500">Sex:</span> {perp.sex}</span>}
                                {perp.race && <span><span className="text-slate-500">Race:</span> {perp.race}</span>}
                                {perp.age && <span><span className="text-slate-500">Age:</span> {perp.age}</span>}
                                {perp.height && <span><span className="text-slate-500">Height:</span> {perp.height}</span>}
                                {perp.weight && <span><span className="text-slate-500">Weight:</span> {perp.weight}</span>}
                                {perp.build && <span><span className="text-slate-500">Build:</span> {perp.build}</span>}
                                {perp.complexion && <span><span className="text-slate-500">Complexion:</span> {perp.complexion}</span>}
                                {perp.hair_color_cut && <span><span className="text-slate-500">Hair:</span> {perp.hair_color_cut}</span>}
                                {perp.eye_color && <span><span className="text-slate-500">Eyes:</span> {perp.eye_color}</span>}
                              </div>
                              
                              {/* Clothing */}
                              {(perp.hat_color_style || perp.shirt || perp.pants || perp.jacket_coat || perp.shoes || perp.clothing) && (
                                <div className="text-xs text-slate-400 mb-2 border-t border-slate-800 pt-2">
                                  <span className="text-slate-500 uppercase text-[10px]">Clothing: </span>
                                  {[perp.hat_color_style && `Hat: ${perp.hat_color_style}`, perp.shirt && `Shirt: ${perp.shirt}`, perp.jacket_coat && `Jacket: ${perp.jacket_coat}`, perp.pants && `Pants: ${perp.pants}`, perp.shoes && `Shoes: ${perp.shoes}`, perp.clothing].filter(Boolean).join(' | ')}
                                </div>
                              )}
                              
                              {/* Marks */}
                              {perp.visible_marks && (
                                <p className="text-xs text-slate-400 mb-1"><span className="text-slate-500">Marks/Tattoos:</span> {perp.visible_marks}</p>
                              )}
                              
                              {/* Address if known */}
                              {perp.address && (
                                <p className="text-xs text-slate-400 mb-1"><span className="text-slate-500">Address:</span> {perp.address}</p>
                              )}
                              
                              {/* Current Location & Direction - show which is newer */}
                              {(() => {
                                const locTime = perp.location_updated_at ? new Date(perp.location_updated_at) : null;
                                const dirTime = perp.direction_updated_at ? new Date(perp.direction_updated_at) : null;
                                // If timestamps are within 2 seconds, consider them sent together
                                const sameUpdate = locTime && dirTime && Math.abs(locTime.getTime() - dirTime.getTime()) < 2000;
                                const locationIsNewer = sameUpdate || (locTime && dirTime ? locTime > dirTime : locTime && !dirTime);
                                const directionIsNewer = sameUpdate || (dirTime && locTime ? dirTime > locTime : dirTime && !locTime);
                                const hasMultipleUpdates = perp.current_location && perp.direction_of_flight && (locTime || dirTime);

                                return (
                                  <>
                                    {hasMultipleUpdates && !sameUpdate && (
                                      <p className="text-[10px] text-slate-500 mb-1 italic">Green = latest update</p>
                                    )}
                                    {perp.current_location && (
                                      <div className={`text-xs mb-1 px-2 py-1 rounded flex flex-wrap items-center gap-1 ${locationIsNewer ? 'bg-green-500/20 text-green-400 font-medium border border-green-500/50' : 'bg-amber-500/10 text-amber-400'}`}>
                                        <span className="flex-1 min-w-0 break-words">📍 Location: {perp.current_location}</span>
                                        <span className="flex items-center gap-1 shrink-0">
                                          {locationIsNewer && <Badge className="bg-green-600 text-white text-[9px] px-1 py-0">LATEST</Badge>}
                                          {perp.location_updated_at && (
                                            <span className="text-[10px] opacity-70">{formatTimeOnly(perp.location_updated_at)}</span>
                                          )}
                                        </span>
                                      </div>
                                    )}

                                    {perp.direction_of_flight && (
                                      <div className={`text-xs mb-1 px-2 py-1 rounded flex flex-wrap items-center gap-1 ${directionIsNewer ? 'bg-green-500/20 text-green-400 font-medium border border-green-500/50' : 'bg-amber-500/10 text-amber-400'}`}>
                                        <span className="flex-1 min-w-0 break-words">→ Direction: {perp.direction_of_flight}</span>
                                        <span className="flex items-center gap-1 shrink-0">
                                          {directionIsNewer && <Badge className="bg-green-600 text-white text-[9px] px-1 py-0">LATEST</Badge>}
                                          {perp.direction_updated_at && (
                                            <span className="text-[10px] opacity-70">{formatTimeOnly(perp.direction_updated_at)}</span>
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                              
                              {/* Weapon */}
                              {perp.has_weapon && perp.weapon_description && (
                                <p className="text-xs text-red-400 mb-1">⚠ Weapon: {perp.weapon_description}</p>
                              )}
                              
                              {/* Vehicle */}
                              {perp.vehicle_description && (
                                <p className="text-xs text-blue-400 mb-1">🚗 Vehicle: {perp.vehicle_description} {perp.license_plate && `(${perp.license_plate})`}</p>
                              )}
                              
                              {/* Notes */}
                              {perp.notes && (
                                <p className="text-xs text-slate-500 italic mt-1">{perp.notes}</p>
                              )}
                              
                              {/* Perp-specific Updates - line by line */}
                              {(() => {
                                const perpUpdates = selectedCall.updates?.filter(u =>
                                  u.perp_id === perp.perp_id && (u.updated_location || u.updated_direction || u.updated_perp_description)
                                ) || [];
                                if (perpUpdates.length === 0) return null;
                                const updKey = perp.perp_id || `idx_${idx}`;
                                const updatesHidden = !!collapsedPerpUpdates[updKey];
                                return (
                                  <div className="mt-2 pt-2 border-t border-slate-300 dark:border-slate-700 space-y-1">
                                    <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                                      <p className="text-[10px] text-purple-600 dark:text-purple-400 uppercase font-medium">Updates ({perpUpdates.length})</p>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-200"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCollapsedPerpUpdates(prev => ({ ...prev, [updKey]: !prev[updKey] }));
                                        }}
                                      >
                                        <Eye className="w-3 h-3 mr-1" />{updatesHidden ? 'Show' : 'Hide'}
                                      </Button>
                                    </div>
                                    {!updatesHidden && perpUpdates.map((u, uidx) => (
                                      <div key={u.update_id || uidx} className="text-xs bg-purple-100 dark:bg-purple-900/20 rounded px-2 py-1">
                                        <div className="text-slate-600 dark:text-slate-500 font-mono text-[10px]">
                                          {formatTime(u.timestamp)} - {u.updated_by}
                                        </div>
                                        {u.updated_location && (
                                          <div className="text-amber-700 dark:text-amber-400">📍 Perp #{idx + 1} Location: {u.updated_location}</div>
                                        )}
                                        {u.updated_direction && (
                                          <div className="text-amber-600 dark:text-amber-400">→ {u.updated_direction}</div>
                                        )}
                                        {u.updated_perp_description && !(u.updated_location && /^PERP #\d+ U\/D: (Holding|Detained) location:/.test(u.updated_perp_description)) && (
                                          <div className="text-purple-700 dark:text-purple-300">
                                            {u.updated_perp_description.match(/^PERP #\d|^Perp #\d/i)
                                              ? u.updated_perp_description
                                              : `Perp #${idx + 1} U/D: ${u.updated_perp_description}`
                                            }
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                              
                              {/* Arrest & Armed Controls */}
                              <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-800 flex-wrap" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-1.5">
                                  <Checkbox
                                    id={`arrest_${perp.perp_id}`}
                                    checked={perp.was_arrested || false}
                                    onCheckedChange={(checked) => handleTogglePerpArrest(perp.perp_id, checked)}
                                  />
                                  <Label htmlFor={`arrest_${perp.perp_id}`} className={`text-xs cursor-pointer ${perp.was_arrested ? 'text-emerald-400 font-semibold' : 'text-slate-400'}`}>
                                    Arrest
                                  </Label>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Checkbox
                                    id={`armed_${perp.perp_id}`}
                                    checked={perp.has_weapon || false}
                                    onCheckedChange={async (checked) => {
                                      if (checked) {
                                        openArmedDialog(perp);
                                      } else {
                                        try {
                                          const response = await axios.put(
                                            `${API}/live-calls/${selectedCall.call_id}/perps/${perp.perp_id}`,
                                            { ...perp, has_weapon: false, weapon_description: "" },
                                            { withCredentials: true }
                                          );
                                          setSelectedCall(response.data);
                                          setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? response.data : c));
                                          toast.success("Armed status cleared");
                                        } catch (error) {
                                          toast.error("Failed to update armed status");
                                        }
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`armed_${perp.perp_id}`} className={`text-xs cursor-pointer ${perp.has_weapon ? 'text-red-400 font-semibold' : 'text-slate-400'}`}>
                                    Armed
                                  </Label>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Checkbox
                                    id={`holding_${perp.perp_id}`}
                                    checked={perp.is_holding || false}
                                    onCheckedChange={() => openHoldingDialog(perp)}
                                  />
                                  <Label htmlFor={`holding_${perp.perp_id}`} className={`text-xs cursor-pointer ${perp.is_holding ? 'text-amber-400 font-semibold' : 'text-slate-400'}`}>
                                    Holding
                                  </Label>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Checkbox
                                    id={`detained_${perp.perp_id}`}
                                    checked={perp.is_detained || false}
                                    onCheckedChange={() => openDetainedDialog(perp)}
                                  />
                                  <Label htmlFor={`detained_${perp.perp_id}`} className={`text-xs cursor-pointer ${perp.is_detained ? 'text-orange-400 font-semibold' : 'text-slate-400'}`}>
                                    Detained
                                  </Label>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Checkbox
                                    id={`visual_${perp.perp_id}`}
                                    checked={perp.is_visual || false}
                                    onCheckedChange={() => openVisualDialog(perp)}
                                  />
                                  <Label htmlFor={`visual_${perp.perp_id}`} className={`text-xs cursor-pointer ${perp.is_visual ? 'text-cyan-400 font-semibold' : 'text-slate-400'}`}>
                                    Visual
                                  </Label>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Checkbox
                                    id={`positive_id_${perp.perp_id}`}
                                    checked={perp.is_positive_id || false}
                                    onCheckedChange={() => openPositiveIdDialog(perp)}
                                  />
                                  <Label htmlFor={`positive_id_${perp.perp_id}`} className={`text-xs cursor-pointer ${perp.is_positive_id ? 'text-emerald-400 font-semibold' : 'text-slate-400'}`}>
                                    Positive ID
                                  </Label>
                                </div>
                              </div>
                          </div>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">No perpetrator descriptions added</p>
                  )}
                </CardContent>
                )}
              </Card>

              {/* Victims & Witnesses moved into Call Info card above */}

              {/* Armed Dialog */}
              <Dialog open={showArmedDialog} onOpenChange={setShowArmedDialog}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-sm">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Mark as Armed
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm text-slate-400">Weapon Description (Optional)</Label>
                      <Input
                        value={weaponDescInput}
                        onChange={(e) => setWeaponDescInput(e.target.value)}
                        className="input-dark mt-1"
                        placeholder="e.g., Black handgun, knife, etc."
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-red-600 hover:bg-red-700"
                        onClick={() => handleTogglePerpArmed(true, weaponDescInput)}
                      >
                        Confirm Armed
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setShowArmedDialog(false);
                          setArmedDialogPerpId(null);
                          setWeaponDescInput("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Holding Dialog */}
              <Dialog open={showHoldingDialog} onOpenChange={setShowHoldingDialog}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-400">
                      {holdingDetainedPerp?.is_holding ? 'Edit Holding' : 'Holding 1'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm text-slate-400">Location (Optional)</Label>
                      <Input
                        value={holdingLocationInput}
                        onChange={(e) => setHoldingLocationInput(e.target.value)}
                        className="input-dark mt-1"
                        placeholder="e.g., NE corner, inside store, etc."
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-amber-500 hover:bg-amber-600"
                        onClick={() => handleHoldingUpdate(true, holdingLocationInput)}
                      >
                        {holdingDetainedPerp?.is_holding ? 'Update' : 'Confirm Holding'}
                      </Button>
                      {holdingDetainedPerp?.is_holding && (
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={() => handleHoldingUpdate(false, "")}
                        >
                          Clear
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowHoldingDialog(false);
                          setHoldingDetainedPerpId(null);
                          setHoldingDetainedPerp(null);
                          setHoldingLocationInput("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Detained Dialog */}
              <Dialog open={showDetainedDialog} onOpenChange={setShowDetainedDialog}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-orange-400">
                      {holdingDetainedPerp?.is_detained ? 'Edit Detained' : '1 Detained'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm text-slate-400">Location (Optional)</Label>
                      <Input
                        value={detainedLocationInput}
                        onChange={(e) => setDetainedLocationInput(e.target.value)}
                        className="input-dark mt-1"
                        placeholder="e.g., NE corner, inside store, etc."
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-orange-600 hover:bg-orange-700"
                        onClick={() => handleDetainedUpdate(true, detainedLocationInput)}
                      >
                        {holdingDetainedPerp?.is_detained ? 'Update' : 'Confirm Detained'}
                      </Button>
                      {holdingDetainedPerp?.is_detained && (
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={() => handleDetainedUpdate(false, "")}
                        >
                          Clear
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDetainedDialog(false);
                          setHoldingDetainedPerpId(null);
                          setHoldingDetainedPerp(null);
                          setDetainedLocationInput("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Visual Dialog */}
              <Dialog open={showVisualDialog} onOpenChange={setShowVisualDialog}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-cyan-400">
                      {holdingDetainedPerp?.is_visual ? 'Visual Status' : 'Mark Visual'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <p className="text-sm text-slate-400">
                      {holdingDetainedPerp?.is_visual
                        ? 'This perp is currently marked as VISUAL (eyes on perp).'
                        : 'Mark this perp as VISUAL (eyes on perp)?'}
                    </p>
                    <div className="flex gap-2">
                      {!holdingDetainedPerp?.is_visual && (
                        <Button
                          className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                          onClick={() => handleVisualUpdate(true)}
                        >
                          Confirm Visual
                        </Button>
                      )}
                      {holdingDetainedPerp?.is_visual && (
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={() => handleVisualUpdate(false)}
                        >
                          Clear Visual
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowVisualDialog(false);
                          setHoldingDetainedPerpId(null);
                          setHoldingDetainedPerp(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Positive ID Dialog */}
              <Dialog open={showPositiveIdDialog} onOpenChange={setShowPositiveIdDialog}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-emerald-400">
                      {holdingDetainedPerp?.is_positive_id ? 'Positive ID Status' : 'Mark Positive ID'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <p className="text-sm text-slate-400">
                      {holdingDetainedPerp?.is_positive_id
                        ? 'This perp is currently marked as POSITIVE ID.'
                        : 'Mark this perp as POSITIVE ID?'}
                    </p>
                    <div className="flex gap-2">
                      {!holdingDetainedPerp?.is_positive_id && (
                        <Button
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handlePositiveIdUpdate(true)}
                        >
                          Confirm Positive ID
                        </Button>
                      )}
                      {holdingDetainedPerp?.is_positive_id && (
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={() => handlePositiveIdUpdate(false)}
                        >
                          Clear Positive ID
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowPositiveIdDialog(false);
                          setHoldingDetainedPerpId(null);
                          setHoldingDetainedPerp(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Perp Dialog */}
              <Dialog open={showPerpDialog} onOpenChange={setShowPerpDialog}>
                <DialogContent className="bg-slate-900 border-slate-800 w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <DialogHeader>
                    <DialogTitle>{editingPerp ? 'Edit Perpetrator' : 'Add Perpetrator'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-4">
                    {/* Link Existing Profile Section - Show for both add and edit */}
                    <div className="p-3 bg-purple-950/50 border border-purple-800/50 rounded">
                      <p className="text-xs text-purple-400 uppercase font-semibold mb-2 flex items-center gap-1">
                        <Link className="w-3 h-3" /> Link to Existing Profile (Optional)
                      </p>
                      {newPerp.linked_profile_id ? (
                        <div className="flex items-center justify-between p-2 bg-purple-900/30 border border-purple-700 rounded">
                          <div className="flex items-center gap-2">
                            <UserIcon className="w-4 h-4 text-purple-400" />
                            <div>
                              <p className="text-sm font-medium text-purple-200">
                                {perpetratorProfiles.find(p => p.profile_id === newPerp.linked_profile_id)?.name || 'Unknown Profile'}
                              </p>
                              {perpetratorProfiles.find(p => p.profile_id === newPerp.linked_profile_id)?.address && (
                                <p className="text-xs text-slate-400">{perpetratorProfiles.find(p => p.profile_id === newPerp.linked_profile_id)?.address}</p>
                              )}
                            </div>
                            {perpetratorProfiles.find(p => p.profile_id === newPerp.linked_profile_id)?.has_arrest_warrant && (
                              <Badge className="text-[9px] bg-red-600 px-1">WARRANT</Badge>
                            )}
                          </div>
                          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => setNewPerp({...newPerp, linked_profile_id: ""})}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex gap-2 mb-2">
                            <div className="relative flex-1">
                              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                              <Input
                                value={profileSearchQuery}
                                onChange={(e) => setProfileSearchQuery(e.target.value)}
                                placeholder="Search profiles by name or address..."
                                className="input-dark pl-8 h-8 text-sm"
                              />
                            </div>
                            <Button variant="outline" size="sm" className="h-8 px-2" title="Browse all profiles" onClick={() => setShowProfileBrowse(true)}>
                              <List className="w-4 h-4" />
                            </Button>
                          </div>
                          {profileSearchQuery && (
                            <ScrollArea className="max-h-32">
                              <div className="space-y-1">
                                {perpetratorProfiles
                                  .filter(p => 
                                    (p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase()) ||
                                     p.address?.toLowerCase().includes(profileSearchQuery.toLowerCase()))
                                  )
                                  .slice(0, 5)
                                  .map(profile => (
                                    <div
                                      key={profile.profile_id}
                                      className="p-2 bg-slate-800 rounded cursor-pointer hover:bg-slate-700 flex items-center justify-between"
                                      onClick={() => {
                                        setNewPerp({...newPerp, linked_profile_id: profile.profile_id});
                                        setProfileSearchQuery("");
                                      }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <UserIcon className="w-4 h-4 text-purple-400" />
                                        <div>
                                          <p className="text-sm font-medium text-slate-200">{profile.name || 'Unknown'}</p>
                                          {profile.address && <p className="text-xs text-slate-400">{profile.address}</p>}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {profile.status === "warrant" && <Badge className="text-[9px] bg-red-500 px-1">Warrant</Badge>}
                                        {profile.status === "ready_for_arrest" && <Badge className="text-[9px] bg-amber-500 px-1">Ready for Arrest</Badge>}
                                        {profile.status === "arrested" && <Badge className="text-[9px] bg-emerald-500 px-1">Arrested</Badge>}
                                        <Link className="w-4 h-4 text-purple-400" />
                                      </div>
                                    </div>
                                  ))}
                                {perpetratorProfiles.filter(p => 
                                  (p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase()) ||
                                   p.address?.toLowerCase().includes(profileSearchQuery.toLowerCase()))
                                ).length === 0 && (
                                  <p className="text-xs text-slate-500 p-2">No matching profiles found</p>
                                )}
                                </div>
                              </ScrollArea>
                            )}
                          </>
                        )}
                      </div>
                    
                    {/* Show Call Description for Reference */}
                    {selectedCall?.description && (
                      <div className="p-3 bg-blue-950/50 border border-blue-800 rounded-sm">
                        <p className="text-xs text-blue-400 uppercase font-semibold mb-1">General / Basic Description</p>
                        <p className="text-sm text-slate-300">{selectedCall.description}</p>
                      </div>
                    )}
                    
                    {/* Current Location - highlighted */}
                    <div>
                      <Label className="text-xs text-amber-400">Current Location</Label>
                      <Input value={newPerp.current_location} onChange={(e) => setNewPerp({...newPerp, current_location: e.target.value})} className="input-dark h-8 border-amber-500/50" placeholder="Where perp is now / last seen" />
                    </div>

                    {/* Direction of Flight */}
                    <div>
                      <Label className="text-xs">Direction of Flight</Label>
                      <Input value={newPerp.direction_of_flight} onChange={(e) => setNewPerp({...newPerp, direction_of_flight: e.target.value})} className="input-dark h-8" placeholder="Northbound on Main St" />
                    </div>

                    {/* Basic Description */}
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">Sex</Label>
                        <select value={newPerp.sex || ""} onChange={(e) => setNewPerp({...newPerp, sex: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                          <option value="">—</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Race</Label>
                        <select value={newPerp.race || ""} onChange={(e) => setNewPerp({...newPerp, race: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                          <option value="">—</option>
                          <option value="Black">Black</option>
                          <option value="White">White</option>
                          <option value="Hispanic">Hispanic</option>
                          <option value="Asian">Asian</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Age</Label>
                        <Input value={newPerp.age} onChange={(e) => setNewPerp({...newPerp, age: e.target.value})} className="input-dark h-8" placeholder="~25" />
                      </div>
                      <div>
                        <Label className="text-xs">Build</Label>
                        <select value={newPerp.build || ""} onChange={(e) => setNewPerp({...newPerp, build: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200">
                          <option value="">—</option>
                          <option value="Slim">Slim</option>
                          <option value="Medium">Medium</option>
                          <option value="Heavy">Heavy</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">Height</Label>
                        <Input value={newPerp.height} onChange={(e) => setNewPerp({...newPerp, height: e.target.value})} className="input-dark h-8" placeholder="5'10" />
                      </div>
                      <div>
                        <Label className="text-xs">Weight</Label>
                        <Input value={newPerp.weight} onChange={(e) => setNewPerp({...newPerp, weight: e.target.value})} className="input-dark h-8" placeholder="180 lbs" />
                      </div>
                      <div>
                        <Label className="text-xs">Complexion</Label>
                        <Input value={newPerp.complexion} onChange={(e) => setNewPerp({...newPerp, complexion: e.target.value})} className="input-dark h-8" placeholder="Light/Dark" />
                      </div>
                      <div>
                        <Label className="text-xs">Eyes</Label>
                        <Input value={newPerp.eye_color} onChange={(e) => setNewPerp({...newPerp, eye_color: e.target.value})} className="input-dark h-8" placeholder="Brown" />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Hair (Color/Cut)</Label>
                        <Input value={newPerp.hair_color_cut} onChange={(e) => setNewPerp({...newPerp, hair_color_cut: e.target.value})} className="input-dark h-8" placeholder="Black, short" />
                      </div>
                      <div>
                        <Label className="text-xs">Visible Marks</Label>
                        <Input value={newPerp.visible_marks} onChange={(e) => setNewPerp({...newPerp, visible_marks: e.target.value})} className="input-dark h-8" placeholder="Tattoo, scar..." />
                      </div>
                    </div>

                    {/* Clothing Details */}
                    <p className="text-xs text-slate-500 uppercase pt-2">Clothing</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Hat</Label>
                        <Input value={newPerp.hat_color_style} onChange={(e) => setNewPerp({...newPerp, hat_color_style: e.target.value})} className="input-dark h-8" placeholder="Black cap" />
                      </div>
                      <div>
                        <Label className="text-xs">Shirt</Label>
                        <Input value={newPerp.shirt} onChange={(e) => setNewPerp({...newPerp, shirt: e.target.value})} className="input-dark h-8" placeholder="Blue t-shirt" />
                      </div>
                      <div>
                        <Label className="text-xs">Jacket/Coat</Label>
                        <Input value={newPerp.jacket_coat} onChange={(e) => setNewPerp({...newPerp, jacket_coat: e.target.value})} className="input-dark h-8" placeholder="Black hoodie" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Pants</Label>
                        <Input value={newPerp.pants} onChange={(e) => setNewPerp({...newPerp, pants: e.target.value})} className="input-dark h-8" placeholder="Blue jeans" />
                      </div>
                      <div>
                        <Label className="text-xs">Shoes</Label>
                        <Input value={newPerp.shoes} onChange={(e) => setNewPerp({...newPerp, shoes: e.target.value})} className="input-dark h-8" placeholder="White sneakers" />
                      </div>
                      <div>
                        <Label className="text-xs">Other Clothing</Label>
                        <Input value={newPerp.clothing} onChange={(e) => setNewPerp({...newPerp, clothing: e.target.value})} className="input-dark h-8" placeholder="Additional..." />
                      </div>
                    </div>

                    {/* Weapon & Arrest Status */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox id="perp_armed" checked={newPerp.has_weapon} onCheckedChange={(checked) => setNewPerp({...newPerp, has_weapon: checked})} />
                        <Label htmlFor="perp_armed" className="text-sm text-red-400">Armed / Has Weapon</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="perp_arrested" checked={newPerp.was_arrested} onCheckedChange={(checked) => setNewPerp({...newPerp, was_arrested: checked})} />
                        <Label htmlFor="perp_arrested" className="text-sm text-green-400">Arrested</Label>
                      </div>
                    </div>
                    {newPerp.has_weapon && (
                      <div>
                        <Label className="text-xs">Weapon Description</Label>
                        <Input value={newPerp.weapon_description} onChange={(e) => setNewPerp({...newPerp, weapon_description: e.target.value})} className="input-dark h-8" placeholder="Handgun, knife, etc." />
                      </div>
                    )}
                    
                    {/* Vehicle */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Vehicle</Label>
                        <Input value={newPerp.vehicle_description} onChange={(e) => setNewPerp({...newPerp, vehicle_description: e.target.value})} className="input-dark h-8" placeholder="Black sedan" />
                      </div>
                      <div>
                        <Label className="text-xs">License Plate</Label>
                        <Input value={newPerp.license_plate} onChange={(e) => setNewPerp({...newPerp, license_plate: e.target.value})} className="input-dark h-8" placeholder="ABC-1234" />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs">Notes</Label>
                      <Textarea value={newPerp.notes} onChange={(e) => setNewPerp({...newPerp, notes: e.target.value})} className="input-dark" rows={2} placeholder="Additional notes..." />
                    </div>

                    {/* Name & Address (if known) */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Name (if known)</Label>
                        <Input value={newPerp.name} onChange={(e) => setNewPerp({...newPerp, name: e.target.value})} className="input-dark h-8" placeholder="Perp name..." />
                      </div>
                      <div>
                        <Label className="text-xs">Address (if known)</Label>
                        <Input value={newPerp.address} onChange={(e) => setNewPerp({...newPerp, address: e.target.value})} className="input-dark h-8" placeholder="Perp address..." />
                      </div>
                    </div>
                    <Button onClick={editingPerp ? handleUpdatePerp : handleAddPerp} className="w-full btn-primary">
                      {editingPerp ? 'Update Perpetrator' : 'Add Perpetrator'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Profile Link Dialog */}
              <Dialog open={showProfileLinkDialog} onOpenChange={setShowProfileLinkDialog}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-md max-h-[70vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Link className="w-4 h-4" /> Link Perpetrator Profile
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-4">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        value={profileSearchQuery}
                        onChange={(e) => setProfileSearchQuery(e.target.value)}
                        placeholder="Search profiles by name..."
                        className="input-dark pl-8"
                      />
                    </div>
                    <ScrollArea className="h-60">
                      <div className="space-y-2">
                        {perpetratorProfiles
                          .filter(p => 
                            p.name.toLowerCase().includes(profileSearchQuery.toLowerCase()) &&
                            !selectedCall?.linked_perp_profiles?.includes(p.profile_id)
                          )
                          .map(profile => (
                            <div
                              key={profile.profile_id}
                              className="flex items-center justify-between p-2 bg-slate-800 rounded hover:bg-slate-700 cursor-pointer"
                              onClick={() => handleLinkProfile(profile.profile_id)}
                            >
                              <div className="flex items-center gap-2">
                                <UserIcon className="w-4 h-4 text-slate-400" />
                                <div>
                                  <p className="text-sm font-medium text-slate-200">{profile.name}</p>
                                  {profile.known_address && (
                                    <p className="text-xs text-slate-500">{profile.known_address}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {profile.status === "warrant" && (
                                  <Badge className="text-[10px] bg-red-500">Warrant</Badge>
                                )}
                                {profile.status === "ready_for_arrest" && (
                                  <Badge className="text-[10px] bg-amber-500">Ready for Arrest</Badge>
                                )}
                                {profile.status === "arrested" && (
                                  <Badge className="text-[10px] bg-emerald-500">Arrested</Badge>
                                )}
                                <Plus className="w-4 h-4 text-blue-400" />
                              </div>
                            </div>
                          ))}
                        {perpetratorProfiles.filter(p => 
                          p.name.toLowerCase().includes(profileSearchQuery.toLowerCase()) &&
                          !selectedCall?.linked_perp_profiles?.includes(p.profile_id)
                        ).length === 0 && (
                          <p className="text-sm text-slate-500 text-center py-4">
                            {profileSearchQuery ? 'No matching profiles found' : 'No profiles available to link'}
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Updates (Timeline Notes) */}
              <div id="section-timeline" className={`mb-4 ${detailTab === 'all' || detailTab === 'updates' ? '' : 'hidden'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase text-slate-500">Updates (Timeline Notes)</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-2 text-[10px]"
                    onClick={() => setShowUpdates(!showUpdates)}
                  >
                    <Eye className="w-2.5 h-2.5 mr-0.5" />
                    {showUpdates ? "Hide" : "Show"}
                  </Button>
                </div>
                {showUpdates && (
                <div className="bg-slate-800 border border-blue-800/50 rounded-sm p-3 space-y-2 min-h-[100px]">
                  {(() => {
                    // Timeline Updates includes free-text update_text entries
                    // PLUS perp lifecycle status changes (arrest/holding/
                    // detained), which previously rendered in the Basic
                    // Description card. Bringing them here keeps the call
                    // narrative + perp status changes in a single
                    // chronological view without losing any data.
                    // Any perp-related lifecycle/status entry — created,
                    // edited, removed, or a status flip — should show in
                    // the timeline. The action field is the most reliable
                    // signal (set by the perp endpoints), with fall-back
                    // keyword matches for legacy entries that pre-date the
                    // action field.
                    const isPerpStatusUpdate = (u) => {
                      const a = u.action || "";
                      if (a === "perp_added" || a === "perp_updated" || a === "perp_removed") return true;
                      const t = u.updated_perp_description || "";
                      const g = u.general_notes || "";
                      if (t.startsWith('PERP #') || g.startsWith('PERP ADDED') || g.startsWith('PERP #')) return true;
                      return (
                        t.includes('ARRESTED') ||
                        t.includes('Arrest cleared') ||
                        t.includes('HOLDING') ||
                        t.includes('Holding cleared') ||
                        t.includes('Holding location') ||
                        t.includes('DETAINED') ||
                        t.includes('Detained cleared') ||
                        t.includes('Detained location') ||
                        t.includes('VISUAL') ||
                        t.includes('Visual cleared') ||
                        t.includes('POSITIVE ID') ||
                        t.includes('Positive ID cleared') ||
                        t.includes('NOW ARMED') ||
                        t.includes('No longer armed')
                      );
                    };
                    const updateTextEntries = selectedCall.updates?.filter(u => u.update_text || isPerpStatusUpdate(u)) || [];
                    return updateTextEntries.length > 0 ? (
                      (updatesSortOrder === "newest" ? [...updateTextEntries].reverse() : updateTextEntries).map((update, idx) => {
                        // Border + text color follow the perp-status badge
                        // palette: ARRESTED=green, HOLDING=orange,
                        // DETAINED=amber, VISUAL=cyan, POSITIVE ID=emerald,
                        // ARMED=red. Cleared variants use the same hue at
                        // reduced opacity. Generic perp create/edit/remove
                        // entries (no specific status keyword) use purple
                        // to match the perp badges elsewhere. Default
                        // fallback is blue for free-text updates.
                        const perpDesc = update.updated_perp_description || "";
                        const generalNotes = update.general_notes || "";
                        const desc = `${perpDesc} ${generalNotes}`;
                        const isPerpLifecycle =
                          update.action === 'perp_added' ||
                          update.action === 'perp_updated' ||
                          update.action === 'perp_removed' ||
                          perpDesc.startsWith('PERP #') ||
                          generalNotes.startsWith('PERP ADDED') ||
                          generalNotes.startsWith('PERP #');
                        const borderClass = (
                          desc.includes('ARRESTED') ? 'border-green-500' :
                          desc.includes('Arrest cleared') ? 'border-green-500/50' :
                          (desc.includes('HOLDING') || desc.includes('Holding location')) ? 'border-orange-500' :
                          desc.includes('Holding cleared') ? 'border-orange-500/50' :
                          (desc.includes('DETAINED') || desc.includes('Detained location')) ? 'border-amber-500' :
                          desc.includes('Detained cleared') ? 'border-amber-500/50' :
                          desc.includes('NOW ARMED') ? 'border-red-500' :
                          desc.includes('No longer armed') ? 'border-red-500/50' :
                          desc.includes('POSITIVE ID') ? 'border-emerald-500' :
                          desc.includes('Positive ID cleared') ? 'border-emerald-500/50' :
                          desc.includes('VISUAL') ? 'border-cyan-500' :
                          desc.includes('Visual cleared') ? 'border-cyan-500/50' :
                          isPerpLifecycle ? 'border-purple-500' :
                          'border-blue-500'
                        );
                        const bodyText = update.update_text || perpDesc || generalNotes || "";
                        const bodyClass = (
                          desc.includes('ARRESTED') ? 'text-green-400' :
                          desc.includes('Arrest cleared') ? 'text-green-400/70' :
                          (desc.includes('HOLDING') || desc.includes('Holding location')) ? 'text-orange-400' :
                          desc.includes('Holding cleared') ? 'text-orange-400/70' :
                          (desc.includes('DETAINED') || desc.includes('Detained location')) ? 'text-amber-400' :
                          desc.includes('Detained cleared') ? 'text-amber-400/70' :
                          desc.includes('NOW ARMED') ? 'text-red-400' :
                          desc.includes('No longer armed') ? 'text-red-400/70' :
                          desc.includes('POSITIVE ID') ? 'text-emerald-400' :
                          desc.includes('Positive ID cleared') ? 'text-emerald-400/70' :
                          desc.includes('VISUAL') ? 'text-cyan-400' :
                          desc.includes('Visual cleared') ? 'text-cyan-400/70' :
                          isPerpLifecycle ? 'text-purple-300' :
                          'text-blue-300'
                        );
                        return (
                        <div key={update.update_id || idx} className={`border-l-2 ${borderClass} pl-3 py-1`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-blue-400 font-mono">{formatTime(update.timestamp)}</span>
                              <span className="text-slate-500 text-xs">by {update.updated_by}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-5 w-5 p-0"
                              title="Send this update via WhatsApp"
                              disabled={sendingUpdateNotif}
                              onClick={() => {
                                setUpdateNotifWhatsApp(true);
                                setUpdateNotifPush(false);
                                setUpdateNotifWaIndividuals(true);
                                setUpdateNotifWaBotGroups(true);
                                setConfirmUpdateNotif(bodyText);
                              }}
                            >
                              <MessageSquare className={`w-3 h-3 text-green-400 ${sendingUpdateNotif ? 'animate-pulse' : ''}`} />
                            </Button>
                          </div>
                          <p className={`text-sm ${bodyClass}`}>{bodyText}</p>
                        </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-slate-500 italic">No timeline notes</p>
                    );
                  })()}
                </div>
                )}
              </div>

              {/* Full Updates Timeline */}
              <div id="section-updates" className={`mb-4 ${detailTab === 'all' || detailTab === 'allupdates' ? '' : 'hidden'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase text-slate-500">All Updates</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => setShowAllUpdates(!showAllUpdates)}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      {showAllUpdates ? "Hide" : "Show"}
                    </Button>
                    {selectedCall.updates?.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUpdatesSortOrder(updatesSortOrder === "newest" ? "oldest" : "newest")}
                        className="h-6 px-2 text-xs text-slate-400 hover:text-slate-200"
                      >
                        {updatesSortOrder === "newest" ? (
                          <><ChevronDown className="w-3 h-3 mr-1" /> Newest</>
                        ) : (
                          <><ChevronUp className="w-3 h-3 mr-1" /> Oldest</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                {showAllUpdates && (selectedCall.updates?.length > 0 ? (
                  <div className="space-y-3">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <Input
                        placeholder="Search updates..."
                        value={allUpdatesSearch}
                        onChange={(e) => setAllUpdatesSearch(e.target.value)}
                        className="h-7 pl-7 text-xs bg-slate-900 border-slate-700"
                      />
                    </div>
                    {(updatesSortOrder === "newest" ? [...selectedCall.updates].reverse() : selectedCall.updates).filter(update => {
                      if (!allUpdatesSearch.trim()) return true;
                      const q = allUpdatesSearch.toLowerCase();
                      return (
                        (update.update_text || "").toLowerCase().includes(q) ||
                        (update.general_notes || "").toLowerCase().includes(q) ||
                        (update.updated_location || "").toLowerCase().includes(q) ||
                        (update.updated_direction || "").toLowerCase().includes(q) ||
                        (update.updated_perp_description || "").toLowerCase().includes(q) ||
                        (update.updated_by || "").toLowerCase().includes(q) ||
                        (update.action || "").toLowerCase().includes(q)
                      );
                    }).map((update, index) => {
                      // Determine update category for display
                      const perpInfo = update.perp_id ? selectedCall.perp_descriptions?.find(p => p.perp_id === update.perp_id) : null;
                      const perpIndex = perpInfo ? selectedCall.perp_descriptions.indexOf(perpInfo) + 1 : null;
                      const isLocationUpdate = !!update.updated_location;
                      const isPerpUpdate = !!update.updated_perp_description || (update.general_notes?.startsWith('Perp #') || update.general_notes?.includes('added:'));
                      const isMediaUpdate = update.action === 'media_uploaded' || update.general_notes?.includes('media file');
                      const isJobBodyUpdate = update.general_notes && !isPerpUpdate && !isMediaUpdate && !update.general_notes.includes('Call info');
                      
                      return (
                        <div key={update.update_id || index} className="bg-slate-800 rounded-sm p-3 border-l-2 border-blue-500">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-blue-400 font-mono">
                                {formatTime(update.timestamp)}
                              </span>
                              {/* Category badges — every location-bearing update
                                  identifies its target (Perp #, Additional title,
                                  or Original) so the dispatcher doesn't have to
                                  guess from the body. */}
                              {isLocationUpdate && perpIndex && (
                                <Badge className="bg-amber-900/50 text-amber-200 text-xs">Location · Perp #{perpIndex}</Badge>
                              )}
                              {isLocationUpdate && !perpIndex && update.location_label && (
                                <Badge className="bg-amber-900/50 text-amber-200 text-xs">Location · {update.location_label}</Badge>
                              )}
                              {isLocationUpdate && !perpIndex && !update.location_label && (
                                <Badge className="bg-amber-900/50 text-amber-200 text-xs">Location · Original</Badge>
                              )}
                              {isPerpUpdate && perpIndex && (
                                <Badge className="bg-purple-900/50 text-purple-200 text-xs">Perp #{perpIndex}</Badge>
                              )}
                              {update.transmitted_10_85 && <Badge variant="destructive" className="text-xs">10-85</Badge>}
                              {update.transmitted_10_13 && <Badge variant="destructive" className="text-xs">10-13</Badge>}
                              {update.no_further && <Badge className="bg-amber-900/50 text-amber-200 text-xs">NO FURTHER</Badge>}
                              {update.call_mode && (
                                <Badge className={`text-xs ${update.call_mode === 'call_98' ? 'bg-green-900/50 text-green-200' : update.call_mode === 'live_call' ? 'bg-blue-900/50 text-blue-200' : 'bg-slate-700 text-slate-300'}`}>
                                  {update.call_mode === 'call_98' ? 'CALL 98' : update.call_mode === 'live_call' ? 'LIVE CALL' : 'MODE CLEARED'}
                                </Badge>
                              )}
                              {update.units_84 && <Badge className="bg-blue-900/50 text-blue-200 text-xs">UNITS 84</Badge>}
                              {update.units_98 && <Badge className="bg-green-900/50 text-green-200 text-xs">UNITS 98</Badge>}
                              {update.call_reactivated && <Badge className="bg-purple-900/50 text-purple-200 text-xs">REACTIVATED</Badge>}
                              {isMediaUpdate && <Badge className="bg-cyan-900/50 text-cyan-200 text-xs">📷 MEDIA</Badge>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-500">
                                by {update.updated_by}
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-5 w-5 p-0"
                                title="Send this update via WhatsApp"
                                disabled={sendingUpdateNotif}
                                onClick={() => {
                                  const parts = [];
                                  if (update.updated_location) parts.push(`📍 Location: ${update.updated_location}`);
                                  if (update.updated_direction) parts.push(`→ Direction: ${update.updated_direction}`);
                                  if (update.updated_perp_description) parts.push(update.updated_perp_description);
                                  if (update.general_notes) parts.push(update.general_notes);
                                  if (update.update_text) parts.push(update.update_text);
                                  const text = parts.join("\n") || "Update";
                                  setUpdateNotifWhatsApp(true);
                                  setUpdateNotifPush(false);
                                  setUpdateNotifWaIndividuals(true);
                                  setUpdateNotifWaBotGroups(true);
                                  setConfirmUpdateNotif(text);
                                }}
                              >
                                <MessageSquare className={`w-3 h-3 text-green-400 ${sendingUpdateNotif ? 'animate-pulse' : ''}`} />
                              </Button>
                            </div>
                          </div>
                          {update.updated_location && (
                            <p className="text-sm mb-1 text-amber-300">
                              <span className="text-amber-500">📍 {perpIndex ? `Perp #${perpIndex} Location:` : 'Location:'}</span> {update.updated_location}
                            </p>
                          )}
                          {update.updated_direction && (
                            <p className="text-sm mb-1 text-amber-400">
                              <span className="text-amber-500">→ {perpIndex ? `Perp #${perpIndex} Direction:` : 'Direction:'}</span> {update.updated_direction}
                            </p>
                          )}
                          {update.updated_perp_description && (
                            <p className="text-sm mb-1 text-purple-400">
                              {update.updated_perp_description.match(/^PERP #\d|^Perp #\d/i)
                                ? update.updated_perp_description
                                : <><span className="text-purple-400">{perpIndex ? `Perp #${perpIndex} U/D:` : 'Perp Update:'}</span> {update.updated_perp_description}</>
                              }
                            </p>
                          )}
                          {update.general_notes && !isPerpUpdate && !isMediaUpdate && (
                            <p className="text-sm text-slate-300">
                              {isJobBodyUpdate && <span className="text-slate-500 text-xs mr-1">(Job Narrative)</span>}
                              {update.general_notes}
                            </p>
                          )}
                          {update.general_notes && isMediaUpdate && (
                            <p className="text-sm text-cyan-300">{update.general_notes}</p>
                          )}
                          {update.general_notes && isPerpUpdate && (
                            <p className="text-sm text-purple-300">{update.general_notes}</p>
                          )}
                          {update.update_text && (
                            <p className="text-sm text-blue-300"><span className="text-blue-500 text-xs">Update:</span> {update.update_text}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>No updates yet</p>
                  </div>
                ))}
              </div>

              {/* Media Section */}
              <div id="section-media" className={`mb-4 ${detailTab === 'all' || detailTab === 'media' ? '' : 'hidden'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase text-slate-500">Media Files</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => setShowMedia(!showMedia)}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      {showMedia ? "Hide" : "Show"}
                    </Button>
                    <input
                      type="file"
                      ref={mediaInputRef}
                      onChange={handleMediaUpload}
                      accept="image/*,video/*,audio/*,.pdf"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => mediaInputRef.current?.click()}
                      disabled={isUploadingMedia}
                    >
                      {isUploadingMedia ? (
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Upload className="w-3 h-3 mr-1" />
                      )}
                      Upload
                    </Button>
                  </div>
                </div>
                {/* Upload Progress Bar */}
                {isUploadingMedia && (
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-blue-600 h-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 min-w-[40px]">{uploadProgress}%</span>
                  </div>
                )}
                {showMedia && (loadingMedia ? (
                  <p className="text-xs text-slate-500 italic">Loading media...</p>
                ) : selectedCallMedia.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {selectedCallMedia.map((media, idx) => {
                      // Handle different URL formats
                      let mediaUrl = media;
                      if (!media.startsWith('data:')) {
                        if (media.startsWith('http://') || media.startsWith('https://')) {
                          mediaUrl = media;
                        } else if (media.startsWith('/api/')) {
                          mediaUrl = API.replace('/api', '') + media;
                        } else if (media.startsWith('/')) {
                          mediaUrl = API.replace('/api', '') + media;
                        } else {
                          mediaUrl = `${API}/uploads/${media}`;
                        }
                      }
                      // Add auth token to URL for img tags (admin sees clean images)
                      const displayUrl = getAuthenticatedMediaUrl(mediaUrl);
                      
                      const isImage = media.startsWith('data:image') || /\.(jpg|jpeg|png|gif|webp)($|\?)/i.test(media);
                      const isVideo = media.startsWith('data:video') || /\.(mp4|webm|mov)($|\?)/i.test(media);
                      
                      return (
                        <div
                          key={idx}
                          className="relative group overflow-hidden rounded"
                        >
                          {isImage ? (
                            <div
                              className="cursor-pointer"
                              onClick={() => { setLightboxImage(displayUrl); setLightboxMediaType('image'); }}
                            >
                              <img
                                src={displayUrl}
                                alt={`Media ${idx + 1}`}
                                className="w-full h-24 object-cover border border-slate-700 hover:opacity-80 transition-opacity"
                                draggable="false"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.parentElement.querySelector('.fallback-display') && (e.target.parentElement.parentElement.querySelector('.fallback-display').style.display = 'flex');
                                }}
                              />
                              {/* Server-side watermarking handles thumbnails */}
                            </div>
                          ) : isVideo ? (
                            <div
                              className="relative w-full h-24 bg-slate-900 cursor-pointer"
                              onClick={() => { setLightboxImage(displayUrl); setLightboxMediaType('video'); }}
                            >
                              <video
                                src={displayUrl}
                                className="w-full h-full object-cover pointer-events-none"
                                muted
                                playsInline
                                preload="metadata"
                                onLoadedMetadata={(e) => {
                                  e.target.currentTime = 1;
                                }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                                <div className="bg-black/50 rounded-full p-1.5">
                                  <Play className="w-5 h-5 text-white" />
                                </div>
                              </div>
                              {/* Server-side watermarking handles video thumbnails */}
                            </div>
                          ) : null}
                          {/* Fallback display - available to all users */}
                          <div
                            onClick={() => handleDownloadMedia(mediaUrl, idx)}
                            className={`w-full h-24 bg-slate-800 rounded border border-slate-700 flex flex-col items-center justify-center hover:bg-slate-700 cursor-pointer transition-colors ${isImage || isVideo ? 'hidden' : 'flex'}`}
                          >
                            <FileText className="w-6 h-6 text-slate-500" />
                            <span className="text-[10px] text-slate-500 mt-1">Tap to download</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">No media files attached</p>
                ))}
              </div>

              {/* Unit Notes Section */}
              <div className={`mb-4 ${detailTab === 'all' || detailTab === 'notes' ? '' : 'hidden'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase text-slate-500">Unit Notes {selectedCall.unit_notes?.length > 0 && `(${selectedCall.unit_notes.length})`}</h3>
                  <Button variant="ghost" size="sm" className="h-5 px-2 text-[10px]" onClick={() => setShowUnitNotes(!showUnitNotes)}>
                    <Eye className="w-2.5 h-2.5 mr-0.5" />{showUnitNotes ? "Hide" : "Show"}
                  </Button>
                </div>
                {showUnitNotes && (
                  <div className="bg-slate-800 border border-slate-700 rounded-sm p-3 space-y-2">
                    {/* Existing notes */}
                    {selectedCall.unit_notes?.length > 0 && (
                      <div className="space-y-1.5">
                        {selectedCall.unit_notes.map((un) => (
                          <div key={un.note_id} className="p-2 bg-blue-950/30 border border-blue-800/30 rounded">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-blue-400">{un.unit}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-slate-500 font-mono">
                                  {un.timestamp ? new Date(un.timestamp).toLocaleString('en-US', {
                                    timeZone: 'America/New_York',
                                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
                                  }) : ""}
                                </span>
                                {user?.role === "admin" && (
                                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400" onClick={() => setDeleteUnitNoteId(un.note_id)}>
                                    <X className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-slate-300 whitespace-pre-wrap">{un.note}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {(!selectedCall.unit_notes || selectedCall.unit_notes.length === 0) && (
                      <p className="text-xs text-slate-500 italic">No unit notes</p>
                    )}
                    {/* Add note */}
                    {user?.linked_unit && (
                      <div className="pt-2 border-t border-slate-700">
                        <Textarea
                          value={unitNoteText}
                          onChange={(e) => setUnitNoteText(e.target.value)}
                          placeholder={`Add a note as ${user.linked_unit}...`}
                          className="input-dark min-h-[50px] text-sm mb-2"
                          rows={2}
                        />
                        <Button
                          size="sm"
                          disabled={!unitNoteText.trim() || submittingUnitNote}
                          onClick={async () => {
                            if (!unitNoteText.trim()) return;
                            setSubmittingUnitNote(true);
                            try {
                              const res = await axios.post(`${API}/live-calls/${selectedCall.call_id}/unit-note`, {
                                note: unitNoteText.trim()
                              }, { withCredentials: true });
                              setSelectedCall(res.data);
                              setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? res.data : c));
                              setUnitNoteText("");
                              toast.success("Note added");
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to add note");
                            } finally {
                              setSubmittingUnitNote(false);
                            }
                          }}
                        >
                          {submittingUnitNote ? "Submitting..." : "Submit Note"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Delivered Receipts Section — collapsed by default */}
              <div className={`mb-4 ${detailTab === 'all' || detailTab === 'read' ? '' : 'hidden'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase text-slate-500">Delivered ({selectedCall.delivered_receipts?.length || 0})</h3>
                  <Button variant="ghost" size="sm" className="h-5 px-2 text-[10px]" onClick={() => setShowDelivered(!showDelivered)}>
                    <Eye className="w-2.5 h-2.5 mr-0.5" />{showDelivered ? "Hide" : "Show"}
                  </Button>
                </div>
                {showDelivered && (selectedCall.delivered_receipts?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedCall.delivered_receipts.map((receipt, idx) => (
                      <div key={idx} className="bg-slate-800 rounded px-2 py-1 text-xs flex items-center gap-1">
                        <Radio className="w-3 h-3 text-blue-500" />
                        <span className="text-slate-300">{receipt.user_name}</span>
                        <span className="text-slate-600">
                          {new Date(receipt.delivered_at).toLocaleString('en-US', {
                            timeZone: 'America/New_York',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">Not delivered yet</p>
                ))}
              </div>

              {/* Read Receipts Section — collapsed by default */}
              <div className={`mb-4 ${detailTab === 'all' || detailTab === 'read' ? '' : 'hidden'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase text-slate-500">Read Receipts ({selectedCall.read_receipts?.length || 0})</h3>
                  <Button variant="ghost" size="sm" className="h-5 px-2 text-[10px]" onClick={() => setShowReadReceipts(!showReadReceipts)}>
                    <Eye className="w-2.5 h-2.5 mr-0.5" />{showReadReceipts ? "Hide" : "Show"}
                  </Button>
                </div>
                {showReadReceipts && (selectedCall.read_receipts?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedCall.read_receipts.map((receipt, idx) => (
                      <div key={idx} className="bg-slate-800 rounded px-2 py-1 text-xs flex items-center gap-1">
                        <Eye className="w-3 h-3 text-green-500" />
                        <span className="text-slate-300">{receipt.user_name}</span>
                        {receipt.source && (
                          <span className={`text-[10px] font-bold ${receipt.source === "response" ? "text-blue-400" : "text-amber-400"}`} title={receipt.source === "response" ? "Response page" : "Dispatch page"}>
                            {receipt.source === "response" ? "R" : receipt.source === "live_calls" ? "L" : "?"}
                          </span>
                        )}
                        <span className="text-slate-600">
                          {new Date(receipt.read_at).toLocaleString('en-US', {
                            timeZone: 'America/New_York',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">No read receipts yet</p>
                ))}
              </div>

              {/* Linked Radio Recordings - Only for users with recording access */}
              {(user?.recording_admin_access || user?.role === 'admin') && (
                <Card className={`bg-slate-800 border-slate-800 mb-4 ${detailTab === 'all' || detailTab === 'radio' ? '' : 'hidden'}`}>
                  <CardHeader
                    className="py-2 pb-1 cursor-pointer"
                    onClick={() => setShowLinkedRecordings(!showLinkedRecordings)}
                  >
                    <CardTitle className="text-xs uppercase text-slate-400 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Headphones className="w-4 h-4" />
                        <span>Linked Radio ({linkedRecordings.length})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1"
                          onClick={(e) => { e.stopPropagation(); syncAndFetchLinkedRecordings(selectedCall); }}
                          disabled={loadingRecordings}
                        >
                          <RefreshCw className={`w-3 h-3 ${loadingRecordings ? 'animate-spin' : ''}`} />
                        </Button>
                        {linkedRecordings.some(r => !r.transcription) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-5 px-1 ${transcribingLinkedFile === "all" ? "text-red-400" : ""}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (transcribingLinkedFile === "all") {
                                cancelTranscribeAllRef.current = true;
                                return;
                              }
                              cancelTranscribeAllRef.current = false;
                              setTranscribingLinkedFile("all");
                              let count = 0;
                              for (const rec of linkedRecordings) {
                                if (cancelTranscribeAllRef.current) break;
                                if (rec.transcription) continue;
                                try {
                                  const fn = decodeURIComponent(rec.filename);
                                  const res = await axios.post(`${API}/radio/recordings/transcribe`, null, { params: { filename: fn }, withCredentials: true });
                                  setLinkedRecordings(prev => prev.map(r => r.filename === rec.filename ? { ...r, transcription: res.data.transcription, transcription_status: "completed" } : r));
                                  count++;
                                } catch {}
                              }
                              const stopped = cancelTranscribeAllRef.current;
                              cancelTranscribeAllRef.current = false;
                              setTranscribingLinkedFile(null);
                              if (count > 0) toast.success(`Transcribed ${count} recording${count !== 1 ? "s" : ""}${stopped ? " (stopped)" : ""}`);
                            }}
                            title={transcribingLinkedFile === "all" ? "Stop Transcribing" : "Transcribe All"}
                          >
                            {transcribingLinkedFile === "all" ? <X className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                          </Button>
                        )}
                        {(showLinkedRecordings || detailTab === 'radio') ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  {/* Auto-expand when the Radio tab is explicitly selected. */}
                  {(showLinkedRecordings || detailTab === 'radio') && (
                    <CardContent className="p-3 pt-0">
                      {loadingRecordings ? (
                        <div className="flex items-center gap-2 text-slate-400 text-sm">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading...
                        </div>
                      ) : linkedRecordings.length === 0 ? (
                        <p className="text-slate-500 text-sm">No recordings in time range</p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {linkedRecordings.map((rec) => (
                            <div key={rec.filename} className="bg-slate-900 rounded px-2 py-1.5">
                              <div className="flex items-center gap-2 text-sm">
                                <Badge variant="outline" className="text-xs">{rec.user_id}</Badge>
                                <span className="text-slate-300 flex-1">{rec.time}</span>
                                {!rec.transcription && (
                                  <button
                                    onClick={() => transcribeLinkedRecording(rec.filename)}
                                    disabled={transcribingLinkedFile === rec.filename}
                                    className="p-1 rounded text-slate-500 hover:text-slate-300 hover:bg-slate-800"
                                    title="Transcribe"
                                  >
                                    {transcribingLinkedFile === rec.filename ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                                  </button>
                                )}
                                <button
                                  onClick={() => playLinkedRecording(rec.filename)}
                                  className={`p-1 rounded ${playingRecording === rec.filename ? "text-green-400 bg-green-900/30" : "text-blue-400 hover:text-blue-300 hover:bg-slate-800"}`}
                                >
                                  {playingRecording === rec.filename ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                </button>
                              </div>
                              {rec.transcription && (
                                <p className="text-[11px] text-slate-400 italic mt-1 pl-1">
                                  <MessageSquare className="w-3 h-3 inline mr-1 text-slate-500" />{rec.transcription}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Footer */}
              <div className={`pt-4 mt-4 border-t border-slate-800 flex-wrap items-center justify-between gap-2 text-xs text-slate-500 ${detailTab === 'all' ? 'flex' : 'hidden'}`}>
                <span>Created: {formatTime(selectedCall.created_at)}</span>
                <span>By: {selectedCall.created_by_name}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-500">
              <div className="text-center">
                <Eye className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Select a call to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Standalone Add Update Dialog - works with FAB */}
      {selectedCall && (
        <Dialog open={showUpdateDialog} onOpenChange={(open) => {
          if (open) {
            savedScrollPositionRef.current = detailsPanelRef.current?.scrollTop || 0;
            setUpdateDialogTab('update');
          }
          setShowUpdateDialog(open);
        }}>
          <DialogContent
            className="bg-slate-900 border-slate-800 max-h-[85vh] flex flex-col"
            onOpenAutoFocus={(e) => e.preventDefault()}
            // Radix Dialog otherwise closes when the Other-Radio-Codes popover
            // (rendered via Portal at <body> root) is interacted with —
            // pointer-down on it gets read as "outside" the dialog. Ignore
            // those specific events so the popover can stay open without
            // dismissing the dialog.
            onPointerDownOutside={(e) => {
              const t = e.target;
              if (t?.closest?.('[data-radix-popper-content-wrapper]') ||
                  t?.closest?.('[data-radix-popover-content]')) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              const t = e.target;
              if (t?.closest?.('[data-radix-popper-content-wrapper]') ||
                  t?.closest?.('[data-radix-popover-content]')) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>Add Update to {selectedCall.call_type || "Call"}</DialogTitle>
            </DialogHeader>
            <div className="flex gap-1 border-b border-slate-700 mt-2">
              <button
                type="button"
                onClick={() => setUpdateDialogTab('update')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${updateDialogTab === 'update' ? 'text-white border-b-2 border-blue-500' : 'text-slate-400 hover:text-slate-200'}`}
              >
                Update
              </button>
              <button
                type="button"
                onClick={() => setUpdateDialogTab('radio')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${updateDialogTab === 'radio' ? 'text-white border-b-2 border-blue-500' : 'text-slate-400 hover:text-slate-200'}`}
              >
                Status / Radio
              </button>
            </div>
            {updateDialogTab === 'radio' && (
              <div className="mt-4 overflow-y-auto flex-1 pr-2">
                {renderStatusRadioCard(true, true, 'addUpdateStandalone')}
              </div>
            )}
            <div className={`space-y-4 mt-4 overflow-y-auto flex-1 pr-2 ${updateDialogTab === 'update' ? '' : 'hidden'}`}>
              {(() => {
                const hasAdditionals = (selectedCall?.additional_locations?.length || 0) > 0;
                const hasPerps = (selectedCall?.perp_descriptions?.length || 0) > 0;
                const targetSelected = (updateData.location_label !== null && updateData.location_label !== undefined)
                  || (updateData.perp_ids?.length || 0) > 0
                  || !!updateData.perp_id;
                // Same rule as the desktop dialog: hide the location/direction
                // inputs while a target picker is showing but nothing is picked.
                const showLocFields = (!hasPerps && !hasAdditionals) || targetSelected;
                const isOriginalSelected = updateData.location_label === "";
                return (
                  <>
                    {(hasPerps || hasAdditionals) && (
                      <div>
                        <Label className="mb-2 block">
                          Update Target
                          {hasPerps && <span className="text-xs text-slate-500 font-normal"> (perps multi-select)</span>}
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant={isOriginalSelected ? "default" : "outline"}
                            size="sm"
                            className={isOriginalSelected ? "bg-amber-600 hover:bg-amber-700" : ""}
                            onClick={() => {
                              if (isOriginalSelected) {
                                setUpdateData({ ...updateData, perp_id: "", perp_ids: [], location_label: null });
                              } else {
                                setUpdateData({ ...updateData, perp_id: "", perp_ids: [], location_label: "" });
                              }
                            }}
                          >
                            Original
                          </Button>
                          {hasPerps && selectedCall.perp_descriptions.map((p, idx) => {
                            const ids = updateData.perp_ids || [];
                            const isSelected = ids.includes(p.perp_id) || updateData.perp_id === p.perp_id;
                            const label = p.linked_profile_name
                              ? `#${idx + 1} ${p.linked_profile_name}`
                              : `#${idx + 1} ${p.sex || ''} ${p.race || ''}`.trim();
                            return (
                              <Button
                                key={p.perp_id}
                                type="button"
                                variant={isSelected ? "default" : "outline"}
                                size="sm"
                                className={isSelected ? "bg-purple-600 hover:bg-purple-700" : ""}
                                onClick={() => {
                                  const currentIds = updateData.perp_ids?.length > 0
                                    ? [...updateData.perp_ids]
                                    : updateData.perp_id ? [updateData.perp_id] : [];
                                  const newIds = currentIds.includes(p.perp_id)
                                    ? currentIds.filter(id => id !== p.perp_id)
                                    : [...currentIds, p.perp_id];
                                  // Picking a perp clears any additional-location
                                  // selection so the two target types stay mutually
                                  // exclusive.
                                  const next = { ...updateData, perp_id: newIds[0] || "", perp_ids: newIds };
                                  if (newIds.length > 0) next.location_label = null;
                                  setUpdateData(next);
                                }}
                              >
                                {label}
                              </Button>
                            );
                          })}
                          {hasAdditionals && selectedCall.additional_locations.map((loc, idx) => {
                            const label = loc.title || `Additional #${idx + 1}`;
                            const isSelected = updateData.location_label === label;
                            return (
                              <Button
                                key={`add-${idx}`}
                                type="button"
                                size="sm"
                                variant={isSelected ? "default" : "outline"}
                                className={isSelected ? "bg-blue-600 hover:bg-blue-700" : ""}
                                onClick={() => {
                                  if (isSelected) {
                                    setUpdateData({ ...updateData, location_label: null });
                                  } else {
                                    setUpdateData({ ...updateData, location_label: label, perp_id: "", perp_ids: [] });
                                  }
                                }}
                              >
                                {label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {showLocFields && (
                      <div>
                        <Label>Updated Location</Label>
                        <Input
                          value={updateData.updated_location}
                          onChange={(e) => setUpdateData({ ...updateData, updated_location: e.target.value })}
                          className="input-dark"
                          placeholder="New location if changed..."
                        />
                      </div>
                    )}
                    {showLocFields && (
                      <div>
                        <Label>Direction of Flight</Label>
                        <Input
                          value={updateData.updated_direction}
                          onChange={(e) => setUpdateData({ ...updateData, updated_direction: e.target.value })}
                          className="input-dark"
                          placeholder="e.g., Northbound on Main St..."
                        />
                      </div>
                    )}
                  </>
                );
              })()}
              {/* Perp status checkboxes - only show when a perp is selected */}
              {updateData.perp_id && (
                <div className="p-2 bg-slate-800 rounded border border-slate-700 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="fab-update-arrested"
                        checked={updateData.set_arrested}
                        onCheckedChange={(checked) => setUpdateData({ ...updateData, set_arrested: checked })}
                      />
                      <Label htmlFor="fab-update-arrested" className="text-sm cursor-pointer text-green-400">Arrested</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="fab-update-armed"
                        checked={updateData.set_armed}
                        onCheckedChange={(checked) => setUpdateData({ ...updateData, set_armed: checked })}
                      />
                      <Label htmlFor="fab-update-armed" className="text-sm cursor-pointer text-red-400">Armed</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="fab-update-holding"
                        checked={updateData.set_holding}
                        onCheckedChange={(checked) => setUpdateData({ ...updateData, set_holding: checked })}
                      />
                      <Label htmlFor="fab-update-holding" className="text-sm cursor-pointer text-amber-400">Holding</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="fab-update-detained"
                        checked={updateData.set_detained}
                        onCheckedChange={(checked) => setUpdateData({ ...updateData, set_detained: checked })}
                      />
                      <Label htmlFor="fab-update-detained" className="text-sm cursor-pointer text-orange-400">Detained</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="fab-update-visual"
                        checked={updateData.set_visual}
                        onCheckedChange={(checked) => setUpdateData({ ...updateData, set_visual: checked })}
                      />
                      <Label htmlFor="fab-update-visual" className="text-sm cursor-pointer text-cyan-400">Visual</Label>
                    </div>
                  </div>
                  {/* Weapon description input - shows when Armed is checked */}
                  {updateData.set_armed && (
                    <Input
                      value={updateData.weapon_description}
                      onChange={(e) => setUpdateData({ ...updateData, weapon_description: e.target.value })}
                      className="input-dark"
                      placeholder="Weapon description (optional)..."
                    />
                  )}
                  {/* Holding/Detained per-status location fields removed from
                      this dialog — see inline-dialog comment above. */}
                </div>
              )}
              <div>
                <Label>{updateData.perp_id ? 'Perp Description Update' : 'Updated Perp Description'}</Label>
                <Textarea
                  value={updateData.updated_perp_description}
                  onChange={(e) => setUpdateData({ ...updateData, updated_perp_description: e.target.value })}
                  className="input-dark"
                  placeholder={updateData.perp_id ? "Additional info about this perp..." : "Updated description..."}
                />
              </div>
              <div>
                <Label>Job Narrative</Label>
                <Textarea
                  value={updateData.general_notes}
                  onChange={(e) => setUpdateData({ ...updateData, general_notes: e.target.value })}
                  className="input-dark"
                  placeholder="Job narrative / call note..."
                />
              </div>
              <div>
                <Label>Updates <span className="text-xs text-slate-500 font-normal">(timeline note)</span></Label>
                <Textarea
                  value={updateData.update_text}
                  onChange={(e) => setUpdateData({ ...updateData, update_text: e.target.value })}
                  className="input-dark"
                  placeholder="General update for timeline..."
                />
              </div>
              <div className="pt-2 border-t border-slate-700">
                <div className="flex items-center justify-between">
                  <Label>Additional Locations ({(selectedCall.additional_locations || []).length})</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setAddLocData({ title: "", location: "", location_notes: "", cross_streets: "", direction_of_flight: "" });
                      setEditingLocIndex("update-add");
                      setShowAddLocDialog(true);
                    }}
                    className="h-7 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Location
                  </Button>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-slate-700 mt-2">
              <Button onClick={() => setShowAddUpdateConfirm(true)} className="w-full btn-primary">
                Add Update
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Quick Nav Backdrop - closes menu when tapping outside */}
      {selectedCall && showQuickNav && (
        <div
          className="fixed inset-0 z-[99]"
          onClick={() => {
            setShowQuickNav(false);
            setQuickNavSearch("");
          }}
        />
      )}

      {/* Quick Nav Floating Button - Only show when viewing a call */}
      {selectedCall && (
        <div className="fixed bottom-6 left-4 lg:left-auto lg:right-24 z-[100]">
          <Button
            className="w-12 h-12 rounded-full shadow-lg bg-slate-700 hover:bg-slate-600"
            onClick={() => setShowQuickNav(!showQuickNav)}
            title="Quick Navigation"
          >
            {showQuickNav ? <X className="w-5 h-5" /> : <List className="w-5 h-5" />}
          </Button>

          {/* Quick Nav Menu */}
          {showQuickNav && (
            <div className="absolute bottom-14 left-0 lg:left-auto lg:right-0 w-56 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-2 max-h-80 overflow-y-auto">
              <Input
                placeholder="Search sections..."
                value={quickNavSearch}
                onChange={(e) => setQuickNavSearch(e.target.value)}
                className="input-dark h-8 text-xs mb-2"
                inputMode="none"
              />
              <div className="space-y-1">
                {[
                  // Order matches the desktop visual layout:
                  //   Row 1: Header (Call Info Box) | Status / Radio
                  //   Row 2: Assigned Units         | Location Info
                  //   Row 3: Call Info / Narrative  | Actions
                  // Mobile keeps the same DOM order, so each item still scrolls
                  // to the right card via getElementById.
                  { id: 'section-header', label: 'Header', icon: '🔝' },
                  { id: 'section-radio', label: 'Status / Radio', icon: '📻' },
                  { id: 'section-units', label: 'Assigned Units', icon: '👥' },
                  { id: 'section-location', label: 'Location Info', icon: '📍' },
                  { id: 'section-call-info', label: 'Call Info & Job Narrative', icon: '📋' },
                  { id: 'section-perps', label: 'Perpetrators', icon: '⚠️' },
                  { id: 'section-victims', label: 'Victims & Witnesses', icon: '🧑' },
                  { id: 'section-timeline', label: 'Timeline Updates', icon: '📝' },
                  { id: 'section-updates', label: 'All Updates', icon: '🕒' },
                  { id: 'section-media', label: 'Media Files', icon: '📷' },
                ].filter(item =>
                  !quickNavSearch || item.label.toLowerCase().includes(quickNavSearch.toLowerCase())
                ).map(item => (
                  <button
                    key={item.id}
                    className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded flex items-center gap-2"
                    onClick={() => {
                      // On desktop, Call Info has been merged into the Header card
                      // (the standalone card is lg:hidden), so route the Call Info
                      // shortcut to scroll to the header instead.
                      const isDesktop = window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
                      const targetId = (isDesktop && item.id === 'section-call-info') ? 'section-header' : item.id;
                      const el = document.getElementById(targetId);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                      setShowQuickNav(false);
                      setQuickNavSearch("");
                    }}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating Action Button - Add Update when viewing a call, New Call otherwise */}
      <Button
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg bg-blue-600 hover:bg-blue-700 z-[100]"
        onClick={(e) => {
          e.stopPropagation();
          if (selectedCall) {
            savedScrollPositionRef.current = detailsPanelRef.current?.scrollTop || 0;
            setShowUpdateDialog(true);
          } else {
            setShowNewCallDialog(true);
          }
        }}
        title={selectedCall ? "Add Update" : "New Call"}
      >
        {selectedCall ? <MessageSquare className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
      </Button>

      {/* Image Lightbox */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage}
          alt="Call media"
          mediaType={lightboxMediaType}
          onClose={() => { setLightboxImage(null); setLightboxMediaType(null); }}
        />
      )}

      {/* Edit Location confirmation — opening the original / additional
          location edit dialog from the Location Info card is for fixing a
          mistyped address, NOT for logging a new location update. Reminding
          the dispatcher before the dialog opens so the call header doesn't
          get silently rewritten when they meant to file an Add Update. */}
      <AlertDialog open={!!pendingLocEdit} onOpenChange={(open) => { if (!open) setPendingLocEdit(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Edit this location?</AlertDialogTitle>
            <AlertDialogDescription>
              Only use Edit to <span className="font-semibold text-white">fix</span> this
              location — not to log a <span className="font-semibold text-white">new</span> location
              update for the call. For a moved suspect or an updated address, use
              <span className="font-semibold text-white"> Add Update</span> instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 text-white hover:bg-slate-600 border-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => {
                const run = pendingLocEdit;
                setPendingLocEdit(null);
                if (typeof run === 'function') run();
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unit Action Dialog (Unassign / 84 / 98) */}
      <AlertDialog open={showUnassignConfirm} onOpenChange={setShowUnassignConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Unit: {unitToUnassign}</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedCall?.assigned_units?.includes(unitToUnassign)
                ? <>Choose an action for <span className="font-bold text-white">{unitToUnassign}</span></>
                : <>Assign <span className="font-bold text-white">{unitToUnassign}</span> with a status</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 py-2">
            <Button
              className="bg-blue-600 hover:bg-blue-700 w-full"
              onClick={() => {
                if (unitToUnassign) handleUpdateUnitStatus(unitToUnassign, true, false);
                setUnitToUnassign(null);
                setShowUnassignConfirm(false);
              }}
            >
              Set 84
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 w-full"
              onClick={() => {
                if (unitToUnassign) handleUpdateUnitStatus(unitToUnassign, false, true);
                setUnitToUnassign(null);
                setShowUnassignConfirm(false);
              }}
            >
              Set 98
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 w-full"
              onClick={() => {
                if (unitToUnassign) handleUpdateUnitStatus(unitToUnassign, false, false, true);
                setUnitToUnassign(null);
                setShowUnassignConfirm(false);
              }}
            >
              Set Standby
            </Button>
            {selectedCall?.assigned_units?.includes(unitToUnassign) && (
              <Button
                className="bg-red-600 hover:bg-red-700 w-full"
                onClick={() => {
                  if (unitToUnassign) handleUnassignUnit(unitToUnassign);
                  setUnitToUnassign(null);
                  setShowUnassignConfirm(false);
                }}
              >
                Unassign
              </Button>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unassign Patrol Car Confirmation Dialog */}
      <AlertDialog open={showPatrolCarUnassignConfirm} onOpenChange={setShowPatrolCarUnassignConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Unassign Patrol Car?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unassign <span className="font-bold text-white">{patrolCarToUnassign}</span> from this call?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (patrolCarToUnassign) {
                  handleUnassignPatrolCar(patrolCarToUnassign);
                }
                setPatrolCarToUnassign(null);
              }}
            >
              Unassign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* All Units 84 Confirmation Dialog */}
      {/* Priority upgraded: offer to enable WA Alerts going forward + Resend */}
      <AlertDialog open={showPriorityUpDialog} onOpenChange={setShowPriorityUpDialog}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">
              {priorityUpReason === 'type' ? 'Type changed — push it out?' : 'Priority — push it out?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {priorityUpReason === 'type' ? (
                <>This call's <span className="text-white font-medium">type</span> changed. Turn on WA Alerts for all updates going forward, and pick what to re-broadcast right now.</>
              ) : (
                <>This call is now <span className="text-white font-medium">PRIORITY</span>. Turn on WA Alerts for all updates going forward, and pick what to re-broadcast right now.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-3 py-2">
            {/* WA Alerts toggle */}
            <label className={`flex items-center gap-2 cursor-pointer border rounded p-2 ${priorityUpEnableWa ? 'bg-green-500/10 border-green-700' : 'bg-slate-800 border-slate-700'}`}>
              <input
                type="checkbox"
                checked={priorityUpEnableWa}
                onChange={(e) => setPriorityUpEnableWa(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-800"
              />
              <span className="text-sm text-slate-200">Enable WA Alerts on all future updates for this call</span>
            </label>

            {/* Resend section — reuses the Resend dialog's option states */}
            <div className="border border-slate-700 rounded p-2 space-y-2">
              <p className="text-xs text-slate-400 uppercase">Re-broadcast now (optional)</p>

              {/* Delivery */}
              <div>
                <p className="text-[10px] text-slate-500 uppercase mb-1">Delivery</p>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={resendWhatsApp} onChange={(e) => setResendWhatsApp(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                    <span className="text-sm text-slate-300">WhatsApp</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={resendPush} onChange={(e) => setResendPush(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                    <span className="text-sm text-slate-300">Push</span>
                  </label>
                </div>
              </div>

              {/* WA targets */}
              {resendWhatsApp && (
                <div>
                  <p className="text-[10px] text-slate-500 uppercase mb-1">WhatsApp Send To</p>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={resendWaIndividuals} onChange={(e) => setResendWaIndividuals(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                      <span className="text-sm text-slate-300">Individuals</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={resendWaBotGroups} onChange={(e) => setResendWaBotGroups(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                      <span className="text-sm text-slate-300">WA Groups</span>
                    </label>
                  </div>
                </div>
              )}

              {/* What */}
              <div>
                <p className="text-[10px] text-slate-500 uppercase mb-1">What to Send</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={resendCall} onChange={(e) => setResendCall(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                  <span className="text-sm text-slate-300">Call Alert</span>
                  <span className="text-[10px] text-slate-500">({selectedCall?.call_type || "call"})</span>
                </label>
                {selectedCall?.perp_descriptions?.length > 0 && (
                  <div className="mt-1.5">
                    <p className="text-[10px] text-slate-500 mb-0.5">Perp Descriptions:</p>
                    <div className="flex flex-col gap-0.5 ml-2">
                      {selectedCall.perp_descriptions.map((perp, idx) => (
                        <label key={perp.perp_id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={resendPerpIds.includes(perp.perp_id)}
                            onChange={(e) => {
                              if (e.target.checked) setResendPerpIds([...resendPerpIds, perp.perp_id]);
                              else setResendPerpIds(resendPerpIds.filter(id => id !== perp.perp_id));
                            }}
                            className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800"
                          />
                          <span className="text-xs text-slate-300">
                            Perp #{idx + 1}{perp.name ? ` — ${perp.name}` : ''}{perp.sex || perp.race ? ` (${[perp.sex, perp.race].filter(Boolean).join(', ')})` : ''}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Custom message */}
              <div>
                <p className="text-[10px] text-slate-500 uppercase mb-1">Custom Message (optional)</p>
                <textarea
                  value={resendCustomMessage}
                  onChange={(e) => setResendCustomMessage(e.target.value)}
                  placeholder="Add a custom message..."
                  rows={2}
                  className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                />
              </div>

              {/* Group info — pulled from the Actions card so the dispatcher
                  knows who actually receives this. Mirrors the Resend dialog. */}
              <div className="space-y-0.5 pt-1 border-t border-slate-700/60">
                {(() => {
                  const ids = selectedCall?.notification_group_ids || [];
                  const isNone = ids.includes("__none__");
                  const notifyLabel = isNone ? "None" : ids.length > 0
                    ? notificationGroups.filter(g => ids.includes(g.group_id)).map(g => g.name).join(", ") || "Selected groups"
                    : "All Users";
                  return <p className="text-[10px] text-slate-500">Notify individuals: <span className={isNone ? "text-red-400" : "text-slate-400"}>{notifyLabel}</span></p>;
                })()}
                {resendWhatsApp && resendWaBotGroups && (() => {
                  const jids = selectedCall?.wa_bot_group_jids || [];
                  const isNone = jids.includes("__none__");
                  if (isNone) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-red-400">None</span></p>;
                  if (jids.length === 0) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">All configured groups</span></p>;
                  const names = jids.map(jid => {
                    const g = waBotGroups.find(g => g.id === jid);
                    if (g) return g.name;
                    const col = waBotGroupCollections.find(c => (c.jids || []).includes(jid));
                    return col ? `${col.name}` : jid.split("@")[0];
                  });
                  return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">{[...new Set(names)].join(", ")}</span></p>;
                })()}
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-slate-700 border-slate-600 hover:bg-slate-600"
              onClick={() => setShowPriorityUpDialog(false)}
            >
              Skip
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                setShowPriorityUpDialog(false);
                // 1. Enable WA Alerts going forward if requested
                if (priorityUpEnableWa && selectedCall && !selectedCall.whatsapp_updates_enabled) {
                  try {
                    const response = await axios.put(
                      `${API}/live-calls/${selectedCall.call_id}`,
                      { whatsapp_updates_enabled: true },
                      { withCredentials: true }
                    );
                    setSelectedCall(response.data);
                    setLiveCalls(prev => prev.map(c => c.call_id === response.data.call_id ? response.data : c));
                  } catch {
                    toast.error('Failed to enable WA Alerts');
                  }
                }
                // 2. If the user picked any resend method + content, fire a resend
                const wantsResend = (resendWhatsApp || resendPush) && (resendCall || resendPerpIds.length > 0 || resendCustomMessage.trim());
                if (wantsResend) {
                  await handleResendNotification();
                }
              }}
            >
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lightweight Change Type dialog — edits only call_type + secondary_call_type */}
      <Dialog open={showCallTypeOnlyDialog} onOpenChange={setShowCallTypeOnlyDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Tag className="w-4 h-4 text-purple-400" /> Change Call Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {/* Primary Type */}
            <div>
              <Label>Call Type</Label>
              <div className="relative">
                <Input
                  value={ctOnlyPrimarySearch || callTypeOnlyData.call_type || ""}
                  onChange={(e) => {
                    setCtOnlyPrimarySearch(e.target.value);
                    setCallTypeOnlyData({ ...callTypeOnlyData, call_type: e.target.value });
                    setShowCtOnlyPrimaryDropdown(true);
                  }}
                  onFocus={() => setShowCtOnlyPrimaryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCtOnlyPrimaryDropdown(false), 200)}
                  placeholder="Search or enter type..."
                  className="input-dark"
                />
                {showCtOnlyPrimaryDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {incidentTypes
                      .filter(t => t.name.toLowerCase().includes((ctOnlyPrimarySearch || callTypeOnlyData.call_type || "").toLowerCase()))
                      .sort((a, b) => { const aNum = /^\d/.test(a.name); const bNum = /^\d/.test(b.name); if (aNum !== bNum) return aNum ? 1 : -1; return a.name.localeCompare(b.name); })
                      .map((t) => (
                        <div
                          key={t.type_id}
                          className="px-3 py-2 hover:bg-slate-700 cursor-pointer text-sm select-none"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setCallTypeOnlyData({ ...callTypeOnlyData, call_type: t.name });
                            setCtOnlyPrimarySearch("");
                            setShowCtOnlyPrimaryDropdown(false);
                          }}
                        >
                          {t.name}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
            {/* Secondary Type */}
            <div>
              <Label>Secondary Call Type (Optional)</Label>
              <div className="relative">
                <Input
                  value={ctOnlySecondarySearch || callTypeOnlyData.secondary_call_type || ""}
                  onChange={(e) => {
                    setCtOnlySecondarySearch(e.target.value);
                    setCallTypeOnlyData({ ...callTypeOnlyData, secondary_call_type: e.target.value });
                    setShowCtOnlySecondaryDropdown(true);
                  }}
                  onFocus={() => setShowCtOnlySecondaryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCtOnlySecondaryDropdown(false), 200)}
                  placeholder="Search or enter secondary type..."
                  className="input-dark"
                />
                {showCtOnlySecondaryDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {incidentTypes
                      .filter(t => t.name.toLowerCase().includes((ctOnlySecondarySearch || callTypeOnlyData.secondary_call_type || "").toLowerCase()))
                      .sort((a, b) => { const aNum = /^\d/.test(a.name); const bNum = /^\d/.test(b.name); if (aNum !== bNum) return aNum ? 1 : -1; return a.name.localeCompare(b.name); })
                      .map((t) => (
                        <div
                          key={t.type_id}
                          className="px-3 py-2 hover:bg-slate-700 cursor-pointer text-sm select-none"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setCallTypeOnlyData({ ...callTypeOnlyData, secondary_call_type: t.name });
                            setCtOnlySecondarySearch("");
                            setShowCtOnlySecondaryDropdown(false);
                          }}
                        >
                          {t.name}
                        </div>
                      ))}
                  </div>
                )}
              </div>
              {callTypeOnlyData.secondary_call_type && (
                <button
                  type="button"
                  className="text-[10px] text-slate-500 hover:text-slate-300 mt-1"
                  onClick={() => { setCallTypeOnlyData({ ...callTypeOnlyData, secondary_call_type: "" }); setCtOnlySecondarySearch(""); }}
                >
                  Clear secondary type
                </button>
              )}
            </div>
            {/* Priority toggle — auto-ticked when either type is on the
                priority-call-types list, but the dispatcher can still
                override either way before saving. */}
            <label className={`flex items-center gap-2 cursor-pointer rounded p-2 border ${callTypeOnlyData.priority === 'priority' ? 'bg-red-500/10 border-red-700' : 'bg-slate-800 border-slate-700'}`}>
              <Checkbox
                checked={callTypeOnlyData.priority === 'priority'}
                onCheckedChange={(checked) => setCallTypeOnlyData({ ...callTypeOnlyData, priority: checked ? 'priority' : 'normal' })}
                className="h-4 w-4"
              />
              <span className={`text-sm ${callTypeOnlyData.priority === 'priority' ? 'text-red-300 font-semibold' : 'text-slate-300'}`}>
                Priority
              </span>
              {(priorityCallTypes.includes(callTypeOnlyData.call_type) || priorityCallTypes.includes(callTypeOnlyData.secondary_call_type)) && (
                <span className="text-[10px] text-red-400/70 ml-auto">auto from type</span>
              )}
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setShowCallTypeOnlyDialog(false)}>Cancel</Button>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" disabled={savingCallTypeOnly} onClick={saveCallTypeOnly}>
                {savingCallTypeOnly ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Priority normal + WA still on: ask whether to also turn off WA Alerts */}
      <AlertDialog open={showPriorityNormalWaConfirm} onOpenChange={setShowPriorityNormalWaConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Drop to Normal — Turn off WA Alerts?</AlertDialogTitle>
            <AlertDialogDescription>
              This call had WA Alerts auto-enabled because it was a priority call. You're switching it back to <span className="text-white font-medium">Normal</span>. Would you like to also turn off WA Alerts, or keep them on?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-slate-700 border-slate-600 hover:bg-slate-600"
              onClick={() => {
                setShowPriorityNormalWaConfirm(false);
                applyPriorityChange('normal', false);
              }}
            >
              Keep WA Alerts on
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                setShowPriorityNormalWaConfirm(false);
                applyPriorityChange('normal', true);
              }}
            >
              Turn WA Alerts off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showAll84Confirm} onOpenChange={setShowAll84Confirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark All Units as 84?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark all <span className="font-bold text-white">{selectedCall?.assigned_units?.length || 0}</span> assigned units as 84 (on scene).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleAllUnits84}
            >
              All 84
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* All Units 98 Confirmation Dialog */}
      <AlertDialog open={showAll98Confirm} onOpenChange={setShowAll98Confirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark All Units as 98?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark all <span className="font-bold text-white">{selectedCall?.assigned_units?.length || 0}</span> assigned units as 98 (available).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              onClick={handleAllUnits98}
            >
              All 98
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete New Call Perp Confirmation Dialog */}
      <AlertDialog open={showDeleteNewCallPerpConfirm} onOpenChange={setShowDeleteNewCallPerpConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Perpetrator?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this perpetrator from the new call?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (pendingDeleteNewCallPerpIndex !== null) {
                  setNewCall({
                    ...newCall,
                    perp_descriptions: newCall.perp_descriptions.filter((_, i) => i !== pendingDeleteNewCallPerpIndex)
                  });
                }
                setShowDeleteNewCallPerpConfirm(false);
                setPendingDeleteNewCallPerpIndex(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Callback Confirmation Dialog */}
      <AlertDialog open={deleteCallbackConfirm !== null} onOpenChange={(open) => { if (!open) setDeleteCallbackConfirm(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Remove Callback?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove callback {deleteCallbackConfirm?.number ? formatPhoneNumber(deleteCallbackConfirm.number) : ""}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                if (deleteCallbackConfirm && selectedCall) {
                  try {
                    if (deleteCallbackConfirm.isPrimary) {
                      const patch = { callback_phone: "", cv_or_third_party: "", caller_has_visual: false };
                      await axios.put(
                        `${API}/live-calls/${selectedCall.call_id}`,
                        patch,
                        { withCredentials: true }
                      );
                      setSelectedCall({ ...selectedCall, ...patch });
                      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? { ...c, ...patch } : c));
                    } else {
                      const updated = selectedCall.callback_numbers.filter((_, i) => i !== deleteCallbackConfirm.idx);
                      await axios.put(
                        `${API}/live-calls/${selectedCall.call_id}`,
                        { callback_numbers: updated },
                        { withCredentials: true }
                      );
                      setSelectedCall({ ...selectedCall, callback_numbers: updated });
                      setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? { ...c, callback_numbers: updated } : c));
                    }
                    toast.success("Callback removed");
                  } catch (error) {
                    toast.error("Failed to remove callback");
                  }
                }
                setDeleteCallbackConfirm(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Edit Call Perp Confirmation Dialog */}
      <AlertDialog open={showDeleteEditCallPerpConfirm} onOpenChange={setShowDeleteEditCallPerpConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Perpetrator?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this perpetrator? Click "Save Changes" to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (pendingDeleteEditCallPerpIndex !== null) {
                  setEditCallData({
                    ...editCallData,
                    perp_descriptions: editCallData.perp_descriptions.filter((_, i) => i !== pendingDeleteEditCallPerpIndex)
                  });
                }
                setShowDeleteEditCallPerpConfirm(false);
                setPendingDeleteEditCallPerpIndex(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Perp Confirmation Dialog */}
      <AlertDialog open={showDeletePerpConfirm} onOpenChange={setShowDeletePerpConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Perpetrator?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this perpetrator description? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (pendingDeletePerpId) {
                  handleDeletePerp(pendingDeletePerpId);
                }
                setShowDeletePerpConfirm(false);
                setPendingDeletePerpId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Perp WhatsApp Confirmation Dialog */}
      <AlertDialog open={!!confirmPerpNotif} onOpenChange={(open) => { if (!open) setConfirmPerpNotif(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-400">Send Perp Notification</AlertDialogTitle>
            <AlertDialogDescription>
              Send this perpetrator description to recipients.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 py-2">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={perpNotifWhatsApp} onChange={(e) => setPerpNotifWhatsApp(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                <span className="text-sm text-slate-300">WhatsApp</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={perpNotifPush} onChange={(e) => setPerpNotifPush(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                <span className="text-sm text-slate-300">Push</span>
              </label>
            </div>
            {perpNotifWhatsApp && (
              <div className="flex items-center gap-3 pt-1 border-t border-slate-700">
                <span className="text-xs text-slate-500">Send to:</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={perpNotifWaIndividuals} onChange={(e) => setPerpNotifWaIndividuals(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                  <span className="text-sm text-slate-300">Individuals</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={perpNotifWaBotGroups} onChange={(e) => setPerpNotifWaBotGroups(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                  <span className="text-sm text-slate-300">WA Groups</span>
                </label>
              </div>
            )}
            <div className="space-y-0.5">
              {(() => {
                const ids = selectedCall?.notification_group_ids || [];
                const isNone = ids.includes("__none__");
                const notifyLabel = isNone ? "None" : ids.length > 0
                  ? notificationGroups.filter(g => ids.includes(g.group_id)).map(g => g.name).join(", ") || "Selected groups"
                  : "All Users";
                return <p className="text-[10px] text-slate-500">Notify Indvl: <span className={isNone ? "text-red-400" : "text-slate-400"}>{notifyLabel}</span></p>;
              })()}
              {perpNotifWhatsApp && perpNotifWaBotGroups && (() => {
                const jids = selectedCall?.wa_bot_group_jids || [];
                const isNone = jids.includes("__none__");
                if (isNone) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-red-400">None</span></p>;
                if (jids.length === 0) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">All configured</span></p>;
                const names = jids.map(jid => {
                  const g = waBotGroups.find(g => g.id === jid);
                  if (g) return g.name;
                  const col = waBotGroupCollections.find(c => (c.jids || []).includes(jid));
                  return col ? col.name : jid.split("@")[0];
                });
                return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">{[...new Set(names)].join(", ")}</span></p>;
              })()}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              disabled={!perpNotifWhatsApp && !perpNotifPush}
              onClick={() => {
                if (confirmPerpNotif) {
                  handleSendPerpNotif(confirmPerpNotif);
                }
              }}
            >
              Send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Update Notification Dialog */}
      <AlertDialog open={!!confirmUpdateNotif} onOpenChange={(open) => { if (!open) setConfirmUpdateNotif(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-400">Send Update</AlertDialogTitle>
            <AlertDialogDescription>
              Send this update to recipients.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 py-2">
            <div className="p-2 bg-slate-800 rounded border border-slate-700">
              <p className="text-sm text-blue-300">{confirmUpdateNotif}</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={updateNotifWhatsApp} onChange={(e) => setUpdateNotifWhatsApp(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                <span className="text-sm text-slate-300">WhatsApp</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={updateNotifPush} onChange={(e) => setUpdateNotifPush(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                <span className="text-sm text-slate-300">Push</span>
              </label>
            </div>
            {updateNotifWhatsApp && (
              <div className="flex items-center gap-3 pt-1 border-t border-slate-700">
                <span className="text-xs text-slate-500">Send to:</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={updateNotifWaIndividuals} onChange={(e) => setUpdateNotifWaIndividuals(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                  <span className="text-sm text-slate-300">Individuals</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={updateNotifWaBotGroups} onChange={(e) => setUpdateNotifWaBotGroups(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                  <span className="text-sm text-slate-300">WA Groups</span>
                </label>
              </div>
            )}
            <div className="space-y-0.5">
              {(() => {
                const ids = selectedCall?.notification_group_ids || [];
                const isNone = ids.includes("__none__");
                const notifyLabel = isNone ? "None" : ids.length > 0
                  ? notificationGroups.filter(g => ids.includes(g.group_id)).map(g => g.name).join(", ") || "Selected groups"
                  : "All Users";
                return <p className="text-[10px] text-slate-500">Notify Indvl: <span className={isNone ? "text-red-400" : "text-slate-400"}>{notifyLabel}</span></p>;
              })()}
              {updateNotifWhatsApp && updateNotifWaBotGroups && (() => {
                const jids = selectedCall?.wa_bot_group_jids || [];
                const isNone = jids.includes("__none__");
                if (isNone) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-red-400">None</span></p>;
                if (jids.length === 0) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">All configured</span></p>;
                const names = jids.map(jid => {
                  const g = waBotGroups.find(g => g.id === jid);
                  if (g) return g.name;
                  const col = waBotGroupCollections.find(c => (c.jids || []).includes(jid));
                  return col ? col.name : jid.split("@")[0];
                });
                return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">{[...new Set(names)].join(", ")}</span></p>;
              })()}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              disabled={!updateNotifWhatsApp && !updateNotifPush}
              onClick={() => {
                if (confirmUpdateNotif) {
                  handleSendUpdateNotif(confirmUpdateNotif);
                }
              }}
            >
              Send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resend Notification Confirmation Dialog */}
      <AlertDialog open={showResendConfirm} onOpenChange={setShowResendConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-400">Send Notification</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Choose what to send and delivery method.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-3 py-2">
            {/* Delivery Method */}
            <div>
              <p className="text-xs text-slate-500 uppercase mb-1.5">Delivery Method</p>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={resendWhatsApp} onChange={(e) => setResendWhatsApp(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                  <span className="text-sm text-slate-300">WhatsApp</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={resendPush} onChange={(e) => setResendPush(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                  <span className="text-sm text-slate-300">Push</span>
                </label>
              </div>
            </div>

            {/* WhatsApp Target Options — uses call's actions card settings */}
            {resendWhatsApp && (
              <div>
                <p className="text-xs text-slate-500 uppercase mb-1.5">WhatsApp Send To</p>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={resendWaIndividuals} onChange={(e) => setResendWaIndividuals(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                    <span className="text-sm text-slate-300">Individuals</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={resendWaBotGroups} onChange={(e) => setResendWaBotGroups(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                    <span className="text-sm text-slate-300">WA Groups</span>
                  </label>
                </div>
              </div>
            )}

            {/* What to Send */}
            <div>
              <p className="text-xs text-slate-500 uppercase mb-1.5">What to Send</p>
              <div className="flex flex-col gap-2">
                {/* Call Alert */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={resendCall} onChange={(e) => setResendCall(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                  <span className="text-sm text-slate-300">Call Alert</span>
                  <span className="text-[10px] text-slate-500">({selectedCall?.call_type || "call"} notification)</span>
                </label>

                {/* Perp Descriptions */}
                {selectedCall?.perp_descriptions?.length > 0 && (
                  <div className="ml-0">
                    <p className="text-xs text-slate-400 mb-1">Perp Descriptions:</p>
                    <div className="flex flex-col gap-1 ml-2">
                      {selectedCall.perp_descriptions.map((perp, idx) => (
                        <label key={perp.perp_id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={resendPerpIds.includes(perp.perp_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setResendPerpIds([...resendPerpIds, perp.perp_id]);
                              } else {
                                setResendPerpIds(resendPerpIds.filter(id => id !== perp.perp_id));
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800"
                          />
                          <span className="text-xs text-slate-300">
                            Perp #{idx + 1}
                            {perp.name ? ` — ${perp.name}` : ""}
                            {perp.sex || perp.race ? ` (${[perp.sex, perp.race].filter(Boolean).join(", ")})` : ""}
                          </span>
                          {perp.was_arrested && <span className="text-[9px] bg-red-500/20 text-red-400 px-1 rounded">ARRESTED</span>}
                        </label>
                      ))}
                      {selectedCall.perp_descriptions.length > 1 && (
                        <button
                          type="button"
                          className="text-[10px] text-blue-400 hover:text-blue-300 text-left ml-5"
                          onClick={() => {
                            if (resendPerpIds.length === selectedCall.perp_descriptions.length) {
                              setResendPerpIds([]);
                            } else {
                              setResendPerpIds(selectedCall.perp_descriptions.map(p => p.perp_id));
                            }
                          }}
                        >
                          {resendPerpIds.length === selectedCall.perp_descriptions.length ? "Deselect All" : "Select All"}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Custom Message */}
            <div>
              <p className="text-xs text-slate-500 uppercase mb-1.5">Custom Message (optional)</p>
              <textarea
                value={resendCustomMessage}
                onChange={(e) => setResendCustomMessage(e.target.value)}
                placeholder="Add a custom message..."
                rows={2}
                className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>

            {/* Group info */}
            <div className="space-y-0.5">
              {(() => {
                const ids = selectedCall?.notification_group_ids || [];
                const isNone = ids.includes("__none__");
                const notifyLabel = isNone ? "None" : ids.length > 0
                  ? notificationGroups.filter(g => ids.includes(g.group_id)).map(g => g.name).join(", ") || "Selected groups"
                  : "All Users";
                return <p className="text-[10px] text-slate-500">Notify individuals: <span className={isNone ? "text-red-400" : "text-slate-400"}>{notifyLabel}</span></p>;
              })()}
              {resendWhatsApp && resendWaBotGroups && (() => {
                const jids = selectedCall?.wa_bot_group_jids || [];
                const isNone = jids.includes("__none__");
                if (isNone) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-red-400">None</span></p>;
                if (jids.length === 0) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">All configured groups</span></p>;
                // Resolve names
                const names = jids.map(jid => {
                  const g = waBotGroups.find(g => g.id === jid);
                  if (g) return g.name;
                  const col = waBotGroupCollections.find(c => (c.jids || []).includes(jid));
                  return col ? `${col.name}` : jid.split("@")[0];
                });
                return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">{[...new Set(names)].join(", ")}</span></p>;
              })()}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              disabled={(!resendWhatsApp && !resendPush) || (!resendCall && resendPerpIds.length === 0 && !resendCustomMessage.trim())}
              onClick={handleResendNotification}
            >
              {sendingResend ? "Sending..." : "Send"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* All Perps WhatsApp Confirmation Dialog */}
      <AlertDialog open={showAllPerpsWhatsAppConfirm} onOpenChange={setShowAllPerpsWhatsAppConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-400">WhatsApp All Perp Descriptions?</AlertDialogTitle>
            <AlertDialogDescription>
              Send all {selectedCall?.perp_descriptions?.length || 0} perpetrator description(s) via WhatsApp.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 py-2">
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Send to:</span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={perpNotifWaIndividuals} onChange={(e) => setPerpNotifWaIndividuals(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                <span className="text-sm text-slate-300">Individuals</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={perpNotifWaBotGroups} onChange={(e) => setPerpNotifWaBotGroups(e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-800" />
                <span className="text-sm text-slate-300">WA Groups</span>
              </label>
            </div>
            <div className="space-y-0.5">
              {(() => {
                const ids = selectedCall?.notification_group_ids || [];
                const isNone = ids.includes("__none__");
                const notifyLabel = isNone ? "None" : ids.length > 0
                  ? notificationGroups.filter(g => ids.includes(g.group_id)).map(g => g.name).join(", ") || "Selected groups"
                  : "All Users";
                return <p className="text-[10px] text-slate-500">Notify Indvl: <span className={isNone ? "text-red-400" : "text-slate-400"}>{notifyLabel}</span></p>;
              })()}
              {perpNotifWaBotGroups && (() => {
                const jids = selectedCall?.wa_bot_group_jids || [];
                const isNone = jids.includes("__none__");
                if (isNone) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-red-400">None</span></p>;
                if (jids.length === 0) return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">All configured</span></p>;
                const names = jids.map(jid => {
                  const g = waBotGroups.find(g => g.id === jid);
                  if (g) return g.name;
                  const col = waBotGroupCollections.find(c => (c.jids || []).includes(jid));
                  return col ? col.name : jid.split("@")[0];
                });
                return <p className="text-[10px] text-slate-500">WA Groups: <span className="text-slate-400">{[...new Set(names)].join(", ")}</span></p>;
              })()}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              disabled={!perpNotifWaIndividuals && !perpNotifWaBotGroups}
              onClick={handleSendAllPerpsWhatsApp}
            >
              Send All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* 10-85 Confirmation Dialog */}
      <AlertDialog open={show1085Confirm} onOpenChange={setShow1085Confirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-orange-400">{pending1085Value ? "Transmit 10-85?" : "Clear 10-85?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {pending1085Value
                ? "This will transmit 10-85 (Officer Needs Assistance) and automatically set call to PRIORITY."
                : "This will clear the 10-85 transmission."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pending1085Value && selectedCall?.whatsapp_8513_enabled === false && renderNotify8513Section()}
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={pending1085Value ? "bg-orange-600 hover:bg-orange-700" : "bg-slate-600 hover:bg-slate-700"}
              onClick={() => {
                handleUpdate1085(pending1085Value, { enabled: notify8513Enabled, mode: notify8513Mode });
                setShow1085Confirm(false);
              }}
            >
              {pending1085Value ? (notify8513Enabled ? "Transmit & Notify" : "Transmit 10-85") : "Clear 10-85"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 10-13 Confirmation Dialog */}
      <AlertDialog open={show1013Confirm} onOpenChange={setShow1013Confirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">{pending1013Value ? "Transmit 10-13?" : "Clear 10-13?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {pending1013Value
                ? "This will transmit 10-13 (Officer in Distress) and automatically set call to PRIORITY."
                : "This will clear the 10-13 transmission."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pending1013Value && selectedCall?.whatsapp_8513_enabled === false && renderNotify8513Section()}
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={pending1013Value ? "bg-red-600 hover:bg-red-700" : "bg-slate-600 hover:bg-slate-700"}
              onClick={() => {
                handleUpdate1013(pending1013Value, { enabled: notify8513Enabled, mode: notify8513Mode });
                setShow1013Confirm(false);
              }}
            >
              {pending1013Value ? (notify8513Enabled ? "Transmit & Notify" : "Transmit 10-13") : "Clear 10-13"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Call 10-85 Confirmation Dialog */}
      <AlertDialog open={showNewCall1085Confirm} onOpenChange={setShowNewCall1085Confirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-orange-400">Transmit 10-85?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the new call with 10-85 (Officer Needs Assistance) and automatically set call to PRIORITY.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => {
                setNewCall({ ...newCall, transmitted_10_85: true, priority: "priority" });
                setShowNewCall1085Confirm(false);
              }}
            >
              Transmit 10-85
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Call 10-13 Confirmation Dialog */}
      <AlertDialog open={showNewCall1013Confirm} onOpenChange={setShowNewCall1013Confirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Transmit 10-13?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the new call with 10-13 (Officer in Distress) and automatically set call to PRIORITY.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                setNewCall({ ...newCall, transmitted_10_13: true, priority: "priority" });
                setShowNewCall1013Confirm(false);
              }}
            >
              Transmit 10-13
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Live Call Confirmation Dialog */}
      <AlertDialog open={showCreateCallConfirm} onOpenChange={setShowCreateCallConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-blue-400">
              {createdCallForEdit ? 'Close WITHOUT Saving?' : 'Create Live Call?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {createdCallForEdit
                ? 'This will close the form. Any changes made since your last save will be LOST. Use "Update & Open" to save first.'
                : 'This will create the live call and dispatch it to units. The form will close after creation.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600 w-full sm:w-auto">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={`w-full sm:w-auto ${createdCallForEdit ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
              onClick={() => {
                setShowCreateCallConfirm(false);
                if (createdCallForEdit) {
                  setShowNewCallDialog(false);
                  resetNewCallForm();
                } else {
                  handleCreateCall();
                }
              }}
            >
              {createdCallForEdit ? 'Close Without Save' : 'Create Call'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submit Without Closing Confirmation Dialog */}
      <AlertDialog open={showSubmitWithoutClosingConfirm} onOpenChange={setShowSubmitWithoutClosingConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-blue-400">
              {createdCallForEdit ? 'Update Live Call?' : 'Submit Without Closing?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {createdCallForEdit
                ? 'This will save your changes to the existing call. The form will remain open for further updates.'
                : 'This will create the live call but keep the form open so you can continue updating it in real-time before closing.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                setShowSubmitWithoutClosingConfirm(false);
                handleSubmitWithoutClosing();
              }}
            >
              {createdCallForEdit ? 'Update & Open' : 'Submit & Open'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CV Obtained — unit prompt dialog */}
      <Dialog open={showCvObtainedDialog} onOpenChange={(open) => {
        setShowCvObtainedDialog(open);
        if (!open) { setCvObtainedPendingCode(null); setCvObtainedUnitInput(""); }
      }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-purple-400">CV Obtained — Which Unit?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input
              autoFocus
              value={cvObtainedUnitInput}
              onChange={(e) => setCvObtainedUnitInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmCvObtainedUnit(); } }}
              className="input-dark"
              placeholder="Unit (e.g. CH86)"
            />
            <div className="flex gap-2">
              <Button className="flex-1 bg-purple-600 hover:bg-purple-700" onClick={confirmCvObtainedUnit}>
                Add
              </Button>
              <Button variant="outline" onClick={() => setShowCvObtainedDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dispatcher Checklist Dialog */}
      {selectedCall && (() => {
        const { items, completed } = computeChecklist();
        return (
          <Dialog open={showDispatcherChecklist} onOpenChange={setShowDispatcherChecklist}>
            <DialogContent className="bg-slate-900 border-slate-800 max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-cyan-400" />
                  Dispatcher Checklist
                  <span className="ml-auto text-xs text-slate-400 font-normal">{completed}/{items.length}</span>
                </DialogTitle>
              </DialogHeader>
              <p className="text-[10px] text-slate-500">
                Auto-tracked from the call data. Make changes in the call detail, not here.
              </p>
              <div className="max-h-[60vh] overflow-y-auto pr-1">
                {renderChecklistRows(items)}
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Send to Incidents Confirmation Dialog */}
      <AlertDialog open={showSendToIncidentsConfirm && !showIncidentExtraDetails} onOpenChange={setShowSendToIncidentsConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-orange-400">Send Directly to Incidents?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>This will skip live calls entirely and create this as a logged incident immediately.
                Use this for calls that don't need active dispatch monitoring.</p>
                {createdCallForEdit && (
                  <p className="mt-2 text-yellow-400">
                    Note: A live call has already been created. This will create a separate incident with the current form data.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Button variant="outline" className="w-full text-sm border-slate-600 text-slate-400 hover:text-slate-200 hover:bg-slate-800" onClick={() => setShowIncidentExtraDetails(true)}>
            <FileText className="w-4 h-4 mr-2" />Additional Details
          </Button>
          <div className="flex items-center gap-2 px-1">
            <Checkbox
              id="to_inc_send_taru"
              checked={incidentExtras.send_whatsapp_to_taru !== false}
              onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, send_whatsapp_to_taru: !!checked })}
            />
            <Label htmlFor="to_inc_send_taru" className="text-sm cursor-pointer">
              Send WhatsApp to TARU
            </Label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => {
                setShowSendToIncidentsConfirm(false);
                handleSendToIncidents();
              }}
            >
              Send to Incidents
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* To Incidents - Additional Details Dialog */}
      <Dialog open={showIncidentExtraDetails} onOpenChange={setShowIncidentExtraDetails}>
        <DialogContent aria-describedby={undefined} className="max-w-lg bg-slate-900 border-slate-700 max-h-[85vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Additional Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Incident Date/Time — same control as the regular new-incident form. */}
            <div>
              <Label className="text-xs">Incident Date &amp; Time</Label>
              <Input
                type="datetime-local"
                value={(() => {
                  const v = incidentExtras.date_time;
                  if (!v) return "";
                  // Support both an already-local "YYYY-MM-DDTHH:MM" string
                  // and a full ISO timestamp.
                  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v) && !v.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(v)) {
                    return v.slice(0, 16);
                  }
                  try {
                    const d = new Date(v);
                    if (isNaN(d.getTime())) return "";
                    const pad = (n) => String(n).padStart(2, "0");
                    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                  } catch { return ""; }
                })()}
                onChange={(e) => {
                  const v = e.target.value;
                  setIncidentExtras({
                    ...incidentExtras,
                    date_time: v ? new Date(v).toISOString() : "",
                  });
                }}
                className="input-dark h-8 text-xs"
              />
              <p className="text-[10px] text-slate-500 mt-1">Leave blank to use the current time.</p>
            </div>

            {/* Dispatcher & Hotline */}
            <div className="grid grid-cols-2 gap-2 items-end">
              <div>
                <Label className="text-xs">Dispatcher</Label>
                <Select value={incidentExtras.dispatch_id || "none"} onValueChange={(v) => setIncidentExtras({ ...incidentExtras, dispatch_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="input-dark h-8 text-xs"><SelectValue placeholder="Select dispatcher..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {dispatchers.map((d) => <SelectItem key={d.unit_id} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 h-8">
                <Checkbox id="inc_hotline" checked={incidentExtras.is_hotline_call} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, is_hotline_call: checked })} />
                <Label htmlFor="inc_hotline" className={incidentExtras.is_hotline_call ? "text-amber-400 font-bold" : ""}>Hotline Call</Label>
              </div>
            </div>

            {/* Report Type, Status, Confirmation */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Report Type</Label>
                <Select value={incidentExtras.report_type} onValueChange={(v) => setIncidentExtras({ ...incidentExtras, report_type: v })}>
                  <SelectTrigger className="input-dark h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reported_live">Live</SelectItem>
                    <SelectItem value="reported_after">Past</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={incidentExtras.status} onValueChange={(v) => setIncidentExtras({ ...incidentExtras, status: v })}>
                  <SelectTrigger className="input-dark h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["log", "report", "investigating", "arrest", "closed"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ").toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Confirmation</Label>
                <Select value={incidentExtras.confirmation_status || "no_confirm"} onValueChange={(v) => setIncidentExtras({ ...incidentExtras, confirmation_status: v === "no_confirm" ? "" : v })}>
                  <SelectTrigger className="input-dark h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_confirm">No Confirm</SelectItem>
                    <SelectItem value="unconfirmed">Unconfirmed</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="unfounded">Unfounded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Assigned Detective */}
            <div>
              <Label className="text-xs">Assigned Detective / Officer</Label>
              <Input value={incidentExtras.assigned_detective || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, assigned_detective: e.target.value })} className="input-dark h-8" placeholder="Detective name..." />
            </div>

            {/* Hate Crime */}
            <div className="flex items-center justify-between border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2">
                <Checkbox id="inc_hate_crime" checked={incidentExtras.is_hate_crime || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, is_hate_crime: checked })} />
                <Label htmlFor="inc_hate_crime" className="text-sm">Hate Crime</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="inc_hctf" checked={incidentExtras.hctf_notified || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, hctf_notified: checked })} />
                <Label htmlFor="inc_hctf" className="text-sm">HCTF Notified</Label>
              </div>
            </div>

            {/* Weapon */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="inc_weapon" checked={incidentExtras.weapon_displayed || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, weapon_displayed: checked })} />
                <Label htmlFor="inc_weapon" className="text-sm">Weapon Displayed / Used</Label>
              </div>
              {incidentExtras.weapon_displayed && (
                <Textarea value={incidentExtras.weapon_notes || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, weapon_notes: e.target.value })} className="input-dark min-h-12" rows={2} placeholder="Weapon type/description..." />
              )}
            </div>

            {/* EMS */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="inc_ems" checked={incidentExtras.ems_required || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, ems_required: checked })} />
                <Label htmlFor="inc_ems" className="text-sm">EMS Required</Label>
              </div>
              {incidentExtras.ems_required && (
                <Input value={incidentExtras.ems_notes || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, ems_notes: e.target.value })} className="input-dark h-8" placeholder="EMS notes..." />
              )}
            </div>

            {/* Injuries */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="inc_injuries" checked={incidentExtras.injuries_reported || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, injuries_reported: checked })} />
                <Label htmlFor="inc_injuries" className="text-sm">Injuries Reported</Label>
              </div>
              {incidentExtras.injuries_reported && (
                <Input value={incidentExtras.injury_notes || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, injury_notes: e.target.value })} className="input-dark h-8" placeholder="Injury details..." />
              )}
            </div>

            {/* Police Follow-up */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="inc_police_followup" checked={incidentExtras.police_followup_required || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, police_followup_required: checked })} />
                <Label htmlFor="inc_police_followup" className="text-sm">Police Follow-up Required</Label>
              </div>
              {incidentExtras.police_followup_required && (
                <Input value={incidentExtras.police_followup_notes || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, police_followup_notes: e.target.value })} className="input-dark h-8" placeholder="Follow-up notes..." />
              )}
            </div>

            {/* Radio & PD */}
            <div className="border-t border-slate-700/60 pt-3 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Checkbox id="inc_10_85" checked={incidentExtras.transmitted_10_85 || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, transmitted_10_85: checked })} />
                <Label htmlFor="inc_10_85" className={`text-sm ${incidentExtras.transmitted_10_85 ? "text-red-400 font-bold" : ""}`}>10-85</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="inc_10_13" checked={incidentExtras.transmitted_10_13 || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, transmitted_10_13: checked })} />
                <Label htmlFor="inc_10_13" className={`text-sm ${incidentExtras.transmitted_10_13 ? "text-red-400 font-bold" : ""}`}>10-13</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="inc_pd" checked={incidentExtras.pd_activated || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, pd_activated: checked })} />
                <Label htmlFor="inc_pd" className="text-sm">PD Activated</Label>
              </div>
            </div>

            {/* Police Report Filed */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="inc_report_filed" checked={incidentExtras.police_report_filed || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, police_report_filed: checked })} />
                <Label htmlFor="inc_report_filed" className="text-sm">Police Report Filed</Label>
              </div>
              {incidentExtras.police_report_filed && (
                <Input value={incidentExtras.police_report_notes || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, police_report_notes: e.target.value })} className="input-dark h-8" placeholder="Report #, notes..." />
              )}
            </div>

            {/* Police Action Notes */}
            <div className="border-t border-slate-700/60 pt-3">
              <Label className="text-xs mb-1 block">Police Response / Action Notes</Label>
              <Textarea value={incidentExtras.police_action_notes || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, police_action_notes: e.target.value })} className="input-dark min-h-12" rows={2} placeholder="Police response details..." />
            </div>

            {/* Needs Follow-up */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="inc_followup" checked={incidentExtras.needs_followup || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, needs_followup: checked })} />
                <Label htmlFor="inc_followup" className="text-sm">Needs Follow-up</Label>
              </div>
              {incidentExtras.needs_followup && (
                <Input value={incidentExtras.followup_notes || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, followup_notes: e.target.value })} className="input-dark h-8" placeholder="Follow-up notes..." />
              )}
            </div>

            {/* Footage */}
            <div className="border-t border-slate-700/60 pt-3 space-y-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox id="inc_footage_req" checked={incidentExtras.footage_requested || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, footage_requested: checked })} />
                  <Label htmlFor="inc_footage_req" className="text-sm">Footage Requested</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="inc_footage_obt" checked={incidentExtras.footage_obtained || false} onCheckedChange={(checked) => setIncidentExtras({ ...incidentExtras, footage_obtained: checked })} />
                  <Label htmlFor="inc_footage_obt" className="text-sm">Footage Obtained</Label>
                </div>
              </div>
              {(incidentExtras.footage_requested || incidentExtras.footage_obtained) && (
                <Input value={incidentExtras.footage_locations || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, footage_locations: e.target.value })} className="input-dark h-8" placeholder="Footage locations / notes..." />
              )}
            </div>

            {/* Call Final */}
            <div className="border-t border-slate-700/60 pt-3">
              <Label className="text-xs mb-1 block">Call Final</Label>
              <Input value={incidentExtras.call_final || ""} onChange={(e) => setIncidentExtras({ ...incidentExtras, call_final: e.target.value })} className="input-dark h-8" placeholder="e.g., Report taken, Gone on arrival..." />
            </div>

            <div className="flex justify-end pt-2">
              <Button size="sm" onClick={() => setShowIncidentExtraDetails(false)}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Browse Profiles Dialog */}
      <Dialog open={showProfileBrowse} onOpenChange={(open) => { setShowProfileBrowse(open); if (!open) { setProfileBrowseFilter(""); setProfileBrowseStatusFilter("all"); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 w-[95vw] sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-sm uppercase">Select Profile</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={profileBrowseFilter}
                onChange={(e) => setProfileBrowseFilter(e.target.value)}
                placeholder="Search name or address..."
                className="input-dark pl-8 h-8 text-sm"
              />
            </div>
            <Select value={profileBrowseStatusFilter} onValueChange={setProfileBrowseStatusFilter}>
              <SelectTrigger className="input-dark w-28 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="ready_for_arrest">Ready</SelectItem>
                <SelectItem value="warrant">Warrant</SelectItem>
                <SelectItem value="arrested">Arrested</SelectItem>
                <SelectItem value="linked">Linked</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1 mt-2">
            {perpetratorProfiles
              .filter(p => {
                const q = profileBrowseFilter.toLowerCase();
                const matchesText = !q || p.name?.toLowerCase().includes(q) || p.address?.toLowerCase().includes(q);
                const matchesStatus = profileBrowseStatusFilter === "all" || p.status === profileBrowseStatusFilter;
                return matchesText && matchesStatus;
              })
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map(profile => (
                <div
                  key={profile.profile_id}
                  className="p-2 bg-slate-800 rounded cursor-pointer hover:bg-slate-700 flex items-center justify-between"
                  onClick={() => {
                    setNewPerp(prev => ({...prev, linked_profile_id: profile.profile_id, linked_profile_name: profile.name}));
                    setProfileSearchQuery("");
                    setShowProfileBrowse(false);
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {profile.mugshot_url ? (
                      <img
                        src={getAuthenticatedMediaUrl(`${API.replace('/api', '')}${profile.mugshot_url}/thumb?size=96`)}
                        alt=""
                        width={40}
                        height={40}
                        loading="lazy"
                        decoding="async"
                        onClick={(e) => { e.stopPropagation(); setLightboxImage(`${API.replace('/api', '')}${profile.mugshot_url}`); setLightboxMediaType('image'); }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling?.classList.remove('hidden'); }}
                        className="w-10 h-10 object-cover rounded shrink-0 cursor-zoom-in hover:ring-2 hover:ring-purple-400"
                      />
                    ) : null}
                    <div className={`w-10 h-10 rounded bg-slate-700 flex items-center justify-center shrink-0 ${profile.mugshot_url ? 'hidden' : ''}`}>
                      <UserIcon className="w-4 h-4 text-purple-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-200 truncate">{profile.name || 'Unknown'}</p>
                      {profile.address && <p className="text-xs text-slate-400 truncate">{profile.address}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {profile.status === "ready_for_arrest" && <Badge className="text-[9px] bg-amber-600 px-1">READY</Badge>}
                    {profile.status === "warrant" && <Badge className="text-[9px] bg-red-600 px-1">WARRANT</Badge>}
                    {profile.status === "arrested" && <Badge className="text-[9px] bg-emerald-600 px-1">ARRESTED</Badge>}
                    {profile.status === "linked" && <Badge className="text-[9px] bg-blue-600 px-1">LINKED</Badge>}
                  </div>
                </div>
              ))}
            {perpetratorProfiles.filter(p => {
              const q = profileBrowseFilter.toLowerCase();
              return (!q || p.name?.toLowerCase().includes(q) || p.address?.toLowerCase().includes(q)) &&
                (profileBrowseStatusFilter === "all" || p.status === profileBrowseStatusFilter);
            }).length === 0 && (
              <p className="text-xs text-slate-500 text-center py-4">No profiles found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Log Incident Confirmation Dialog */}
      <AlertDialog open={showLogConfirm} onOpenChange={setShowLogConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Log Incident?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to log this call as an incident? This will remove it from live calls.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                if (selectedCall) {
                  handleLogCall(selectedCall.call_id);
                }
              }}
            >
              Log Incident
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Patrol Car Assignment Confirmation Dialog */}
      <AlertDialog open={showPatrolCarConfirm} onOpenChange={setShowPatrolCarConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Assign Patrol Car?</AlertDialogTitle>
            <AlertDialogDescription>
              Assign patrol car <span className="font-bold text-white">{patrolCarToAssign?.name}</span> to this call?
              {patrolCarToAssign?.assigned_unit && (
                <span className="block mt-1">This will also assign unit: {patrolCarToAssign.assigned_unit}{patrolCarToAssign.joined_units?.length > 0 ? `, ${patrolCarToAssign.joined_units.join(', ')}` : ''}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                if (patrolCarToAssign) {
                  handleAssignPatrolCar(patrolCarToAssign.car_id);
                }
                setPatrolCarToAssign(null);
              }}
            >
              Assign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Update Confirmation Dialog */}
      <AlertDialog open={showAddUpdateConfirm} onOpenChange={setShowAddUpdateConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-blue-400">Submit Update?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to submit this update to the call?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                setShowAddUpdateConfirm(false);
                handleAddUpdate();
              }}
            >
              Submit Update
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Multi-Perp Confirmation Dialog */}
      <AlertDialog open={showMultiPerpConfirm} onOpenChange={setShowMultiPerpConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-purple-400">Apply to Multiple Perps?</AlertDialogTitle>
            <AlertDialogDescription>
              This update will be applied to <span className="text-white font-bold">{(updateData.perp_ids || []).length} perpetrators</span>:
              <span className="block mt-2 text-slate-300">
                {(updateData.perp_ids || []).map(pid => {
                  const idx = selectedCall?.perp_descriptions?.findIndex(p => p.perp_id === pid);
                  const p = selectedCall?.perp_descriptions?.[idx];
                  return `#${(idx ?? -1) + 1} ${p?.sex || ''} ${p?.race || ''}`.trim();
                }).join(", ")}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-purple-600 hover:bg-purple-700"
              onClick={() => {
                handleAddUpdate();
              }}
            >
              Apply to All {(updateData.perp_ids || []).length} Perps
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Additional Location Dialog */}
      <Dialog open={showAddLocDialog} onOpenChange={setShowAddLocDialog}>
        <DialogContent
          className="max-w-md bg-slate-900 border-slate-700"
          onPointerDownOutside={(e) => e.preventDefault()}
          // Don't auto-focus the first input on open — that pops the soft
          // keyboard immediately on mobile when the dispatcher just wanted
          // to view / tweak fields, not type a new address from scratch.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{editingLocIndex !== null ? "Edit Additional Location" : "Add Additional Location"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Label>Location</Label>
              <Input
                ref={addLocInputRef}
                value={addLocData.location}
                onChange={(e) => handleAddLocLocationChange(e.target.value)}
                onFocus={() => { if (addLocPredictions.length > 0) setShowAddLocPredictions(true); }}
                onBlur={() => {
                  setTimeout(() => setShowAddLocPredictions(false), 200);
                  crossStreetsOnBlur("addLoc", moveUnitOut(addLocData.location, setAddLocData),(cs) => setAddLocData(prev => ({ ...prev, cross_streets: cs })), addLocData.cross_streets);
                }}
                placeholder="Enter location..."
                className="input-dark"
                autoComplete="off"
              />
              {showAddLocPredictions && addLocPredictions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {addLocPredictions.map((prediction, idx) => (
                    <div
                      key={prediction.place_id || idx}
                      className="px-3 py-2 cursor-pointer text-sm hover:bg-slate-700 border-b border-slate-700/50 last:border-0"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectAddLocPrediction(prediction)}
                    >
                      <div className="font-medium text-slate-200">{prediction.main_text}</div>
                      {prediction.secondary_text && <div className="text-xs text-slate-400">{prediction.secondary_text}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Title <span className="text-xs text-slate-500 font-normal">(e.g. "Second Location", "Perp Last Seen")</span></Label>
              <Input
                value={addLocData.title || ""}
                onChange={(e) => setAddLocData({ ...addLocData, title: e.target.value })}
                placeholder="Location title..."
                className="input-dark"
              />
            </div>
            <div>
              <Label>Location Notes</Label>
              <Textarea
                value={addLocData.location_notes}
                onChange={(e) => setAddLocData({ ...addLocData, location_notes: e.target.value })}
                placeholder="Landmarks, apt #, etc..."
                className="input-dark min-h-12"
                rows={2}
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Label>Cross Streets</Label>
                  <Input
                    value={addLocData.cross_streets}
                    onChange={(e) => setAddLocData({ ...addLocData, cross_streets: e.target.value })}
                    placeholder="Cross streets..."
                    className="input-dark"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-5 px-3"
                  disabled={addLocCrossLoading || !addLocData.location}
                  onClick={() => {
                    if (addLocData.location.length >= 5) {
                      setAddLocCrossLoading(true);
                      fetchCrossStreets(addLocData.location, (cs) => {
                        setAddLocData(prev => ({ ...prev, cross_streets: cs }));
                        setAddLocCrossLoading(false);
                      });
                    }
                  }}
                >
                  {addLocCrossLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label>Direction of Flight</Label>
              <Input
                value={addLocData.direction_of_flight}
                onChange={(e) => setAddLocData({ ...addLocData, direction_of_flight: e.target.value })}
                placeholder="e.g., Northbound on Main St..."
                className="input-dark"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowAddLocDialog(false)}>Cancel</Button>
              <Button size="sm" onClick={saveAddLoc}>
                {editingLocIndex !== null ? "Save Changes" : "Add Location"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Additional Location Confirmation */}
      <AlertDialog open={deleteLocIndex !== null} onOpenChange={(open) => { if (!open) setDeleteLocIndex(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Remove Location?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteLocIndex !== null && newCall.additional_locations?.[deleteLocIndex] && (
                <span>
                  Remove <span className="text-white font-medium">
                    {newCall.additional_locations[deleteLocIndex].title || newCall.additional_locations[deleteLocIndex].location}
                  </span>? This cannot be undone.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDeleteAddLoc}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Additional Location Confirmation (Edit Call) */}
      <AlertDialog open={deleteEditLocIndex !== null} onOpenChange={(open) => { if (!open) setDeleteEditLocIndex(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Remove Location?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteEditLocIndex !== null && editCallData.additional_locations?.[deleteEditLocIndex] && (
                <span>
                  Remove <span className="text-white font-medium">
                    {editCallData.additional_locations[deleteEditLocIndex].title || editCallData.additional_locations[deleteEditLocIndex].location}
                  </span>? This cannot be undone.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => {
              if (deleteEditLocIndex !== null) {
                const locs = (editCallData.additional_locations || []).filter((_, i) => i !== deleteEditLocIndex);
                setEditCallData({ ...editCallData, additional_locations: locs });
                setDeleteEditLocIndex(null);
              }
            }}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Unit Note Confirmation */}
      <AlertDialog open={deleteUnitNoteId !== null} onOpenChange={(open) => { if (!open) setDeleteUnitNoteId(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Unit Note?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this unit note? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 hover:bg-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={async () => {
              if (!deleteUnitNoteId || !selectedCall) return;
              try {
                const res = await axios.delete(`${API}/live-calls/${selectedCall.call_id}/unit-note/${deleteUnitNoteId}`, { withCredentials: true });
                setSelectedCall(res.data);
                setLiveCalls(liveCalls.map(c => c.call_id === selectedCall.call_id ? res.data : c));
                toast.success("Unit note deleted");
              } catch (err) {
                toast.error(err.response?.data?.detail || "Failed to delete note");
              }
              setDeleteUnitNoteId(null);
            }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phone Lookup Popup */}
      <PhoneLookupPopup
        phone={phoneLookupNumber}
        open={!!phoneLookupNumber}
        onOpenChange={(open) => { if (!open) setPhoneLookupNumber(null); }}
      />
    </div>
  );
}
